(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    root.ToeePro = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var PREDICTIVE_SEARCH_URL = '/search/suggest.json';
  var PRO_VARIANTS_VIEW = 'pro-variants';
  /**
   * App Proxy base every Dealer Pro call is built on. The real value is handed down by
   * Liquid on <body data-pro-proxy-base> (templates/page.pro.liquid) from the store setting
   * `settings.dealer_pro_proxy_base`, so moving Dealer Pro between apps is a settings change,
   * not a theme deploy.
   *
   * The literal below is a FALLBACK, not the source of truth — it is what runs under the
   * fake DOM in tests/toee-pro-fake-dom.js and on any page that renders this script without
   * the attribute. It deliberately matches the setting's own default so the two can never
   * disagree. Dealer Pro now lives in the standalone `dealer-pro` app; nothing here points
   * back at paymentstatussync any more, so a missing attribute degrades to the live app
   * rather than silently reviving the old one.
   *
   * NOT the delivery / warehouse-inventory / customer-account proxy — those call a different
   * app and must keep their own paths.
   */
  var PRO_PROXY_BASE_FALLBACK = '/apps/pro';

  /**
   * Resolved per call rather than once at module load: this file is required by tests before
   * any fake DOM exists, and the sections it drives can be re-rendered by the Section
   * Rendering API. Reading at call time means the value can never be captured from a document
   * that had not been built yet.
   */
  function proProxyBase() {
    var el =
      typeof document !== 'undefined' && document.querySelector
        ? document.querySelector('[data-pro-proxy-base]')
        : null;
    var raw = el && el.getAttribute ? el.getAttribute('data-pro-proxy-base') : '';
    raw = String(raw || '').trim() || PRO_PROXY_BASE_FALLBACK;
    // Tolerate a value typed with a trailing slash — every caller appends its own segment.
    return raw.replace(/\/+$/, '');
  }

  function proProxyUrl(segment) {
    return proProxyBase() + segment;
  }

  /**
   * Google Places API key (2026-09-09), same store-setting-to-body-tag
   * pattern as proProxyBase above — real value handed down by Liquid on
   * <body data-pro-places-key> (templates/page.pro.liquid, and its twin
   * dealer-pro's api/routes/pro/GET.ts) from settings.dealer_pro_google_places_api_key.
   * Blank when the store hasn't configured one; loadPlacesLibrary treats
   * that the same as a failed load — the address field's own
   * autocomplete="street-address" (tests/toee-pro-address-autocomplete.test.js)
   * is the fallback either way.
   */
  function proPlacesApiKey() {
    var el =
      typeof document !== 'undefined' && document.querySelector
        ? document.querySelector('[data-pro-places-key]')
        : null;
    var raw = el && el.getAttribute ? el.getAttribute('data-pro-places-key') : '';
    return String(raw || '').trim();
  }

  var placesLibraryPromise = null;

  /**
   * Resolves with google.maps.places once the Maps JS API has loaded.
   * Rejects — never throws — when no key is configured or the script
   * fails; callers treat both the same way (keep the plain input).
   * A successful load is cached for the page's lifetime; a failed one is
   * not, so a transient network blip doesn't lock a dealer out of
   * autocomplete for the rest of their session.
   */
  function loadPlacesLibrary() {
    if (placesLibraryPromise) return placesLibraryPromise;
    var key = proPlacesApiKey();
    if (!key || typeof document === 'undefined' || !document.createElement || !document.head) {
      return Promise.reject(new Error('places: no key configured'));
    }
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      placesLibraryPromise = Promise.resolve(google.maps.places);
      return placesLibraryPromise;
    }
    placesLibraryPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&libraries=places';
      script.async = true;
      script.onload = function () {
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          resolve(google.maps.places);
        } else {
          placesLibraryPromise = null;
          reject(new Error('places: script loaded without google.maps.places'));
        }
      };
      script.onerror = function () {
        placesLibraryPromise = null;
        reject(new Error('places: script failed to load'));
      };
      document.head.appendChild(script);
    });
    return placesLibraryPromise;
  }

  /**
   * Attaches Google's own Autocomplete to the real <input> — the legacy
   * class, not the newer PlaceAutocompleteElement, precisely because it
   * enhances an existing input in place (value, styling, the rest of this
   * form's [data-pro-field-input] handling all keep working untouched)
   * instead of replacing it with a separate Google-styled element that
   * also has no documented way to be pre-filled with an already-saved
   * address (verified against Google's live docs, 2026-09-09). Idempotent
   * — safe to call every time settings opens.
   */
  function attachAddressAutocomplete(inputEl, placesLib) {
    if (!inputEl || inputEl._proAutocomplete) return;
    var Autocomplete = placesLib && placesLib.Autocomplete;
    if (typeof Autocomplete !== 'function') return;
    // Esc while Google's suggestion list shows belongs to the list (Google closes it): the key
    // is marked used, so the Settings drawer around the field (ProDrawer skips a used key)
    // stays open. Added before Google's own listeners, so it sees the list as it was.
    if (inputEl.addEventListener) {
      inputEl.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && placesSuggestionsShown()) e.preventDefault();
      });
    }
    var autocomplete = new Autocomplete(inputEl, {
      fields: ['formatted_address'],
      types: ['address']
    });
    autocomplete.addListener('place_changed', function () {
      var place = autocomplete.getPlace();
      if (place && place.formatted_address) {
        inputEl.value = place.formatted_address;
      }
    });
    inputEl._proAutocomplete = autocomplete;
  }

  /** Google's suggestion list (a .pac-container on <body>, display:none while closed) is on screen. */
  function placesSuggestionsShown() {
    var lists = typeof document !== 'undefined' && document.querySelectorAll ? document.querySelectorAll('.pac-container') : [];
    for (var i = 0; i < lists.length; i++) {
      if (lists[i].offsetParent != null && !(lists[i].style && lists[i].style.display === 'none')) return true;
    }
    return false;
  }

  var RESULT_LIMIT = 5;
  /** Owner ruling (2026-09-08): an exact stock count below this paints red. */
  var LOW_STOCK_BELOW = 4;
  var CALL_PHONE = '+19053378266'; // fallback only — real number arrives via data-i18n-call-phone
  /** dealer_pro.stock.* in English, for a page cached before those strings reached its data-i18n-* attributes. */
  var STOCK_WORDS = { inStock: 'In stock', low: 'Low stock', available: '{count} available', availablePlus: '{count}+ available' };

  // Renderer functions, defined only in the browser-wiring block below (they
  // need `document`). Declared here so the module's CommonJS export can
  // expose them for tests that install a fake `document` before requiring
  // this file — see tests/toee-pro-fake-dom.js.
  var fmtHMS, buildPromiseRow, buildResultCard, startCountdowns, buildNoResultsFallback, ProSearch, resolveNearestAlternative, ProSettingsPanel;
  // DP-7 — quote card renderer + screen wiring, same require-time-only rule.
  var drawQuoteCard, drawHistoricalQuoteCard, ProQuoteScreen, ProQuoteHistoryPanel;
  // Final review finding #4 — same require-time-only rule.
  var loadLastOfflineResult;

  // ---------------------------------------------------------------------
  // Pure view-model logic (contract: dealer-pro-quote-batch-contract-v1.md
  // §4.3 stock, §4.4 promise, §4.5 price, §4.7 degradation). Each field is
  // built independently from `errors[]` so one failure never blocks another.
  // ---------------------------------------------------------------------

  function getFieldError(errors, field) {
    var list = errors || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].field === field) return list[i];
    }
    return null;
  }

  /** §4.3 — pass warehouse rows through untouched (never recompute or re-bucket them); the card
   * draws the one total summed from them. */
  function buildStockView(stock, errors, phone) {
    var err = getFieldError(errors, 'stock');
    if (err || !stock) {
      return { state: 'call', phone: phone || CALL_PHONE, rows: [] };
    }
    var rows = Array.isArray(stock.warehouses) ? stock.warehouses : [];
    var total = 0;
    var atLeast = false;
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i]) continue;
      if (rows[i].displayMode === 'capped') {
        total += CAPPED_ROW_PROVES;
        atLeast = true;
      } else if (typeof rows[i].quantity === 'number' && rows[i].quantity > 0) {
        total += rows[i].quantity;
      }
    }
    // The card shows one total, not the warehouses. "More than 20" at a warehouse counts as 20,
    // so a total that rests on one is a lower bound (atLeast) and reads "36+".
    return { state: 'ok', rows: rows, total: total, atLeast: atLeast };
  }

  /**
   * §4.4 — two promiseWindow states.
   * Countdown shows when countdownSeconds > 0 AND promiseWindow is
   * same_day or next_day — not only when status === 'same_day_available'.
   * Same-day badge shows only when promiseWindow === 'same_day'.
   */
  function buildPromiseView(promise, errors) {
    var err = getFieldError(errors, 'promise');
    if (err || !promise) {
      return { visible: false };
    }
    if (promise.status === 'guest_static') {
      return { visible: false };
    }
    var win = promise.promiseWindow || '';
    var seconds = typeof promise.countdownSeconds === 'number' ? promise.countdownSeconds : 0;
    var showCountdown = seconds > 0 && (win === 'same_day' || win === 'next_day');
    return {
      visible: true,
      status: promise.status || '',
      promiseWindow: win,
      showCountdown: showCountdown,
      countdownSeconds: showCountdown ? seconds : 0,
      showSameDayBadge: win === 'same_day',
      extraDay: promise.status === 'extra_day_required' || promise.status === 'transfer_required',
      // quote-batch forwards the promise service's own sentence (2026-09-08)
      // for the one case this file has no wording of its own — route
      // unavailable with no promise window. Passed through untouched.
      displayLine: typeof promise.displayLine === 'string' ? promise.displayLine : ''
    };
  }

  /**
   * §4.5 / §3 — guest responses carry price: null with no error (never gated
   * client-side); a genuine failure carries price: null plus an errors[] entry.
   * Never render an estimated number.
   */
  function buildPriceView(price, errors, phone) {
    var err = getFieldError(errors, 'price');
    if (err) return { state: 'call', phone: phone || CALL_PHONE };
    if (price == null) return { state: 'guest' };
    return { state: 'ok', unit: price.unit, lineSubtotal: price.lineSubtotal, total: price.total };
  }

  /**
   * Composes one quote-batch result row into a render-ready view model.
   * `phone` is the dealer call-to-confirm number, sourced from markup
   * (data-i18n-call-phone) by the caller — CALL_PHONE is only a fallback
   * for callers (tests, mainly) that don't have a DOM to read it from.
   */
  /**
   * Keeps only the quotes worth a card. A dealer quoting a tire he cannot get
   * wastes his own customer's time, so a line the warehouses confirmed they
   * have none of is dropped before render.
   *
   * Only a POSITIVE denial counts. quote-batch reports a failed inventory
   * lookup as `stock: null` + `{ field: 'stock', code: 'inventory_unavailable' }`,
   * never as anyAvailable:false — treating that as out of stock would silently
   * hide tires that are sitting in Oakville, and the dealer would never learn
   * they exist. Unknown keeps its card; the stock row already says it's unknown.
   */
  function sellableQuotes(quotes) {
    if (!Array.isArray(quotes)) return [];
    return quotes.filter(function (q) {
      return !(q && q.stock && q.stock.anyAvailable === false);
    });
  }

  /**
   * Owner ruling (2026-09-08): the promise service answers "out of stock"
   * when the REQUESTED quantity cannot be filled anywhere, even though the
   * tire is in stock — a dealer reading "out of stock" next to "Oakville
   * has only 1 pcs" sees a contradiction. When the stock rows say the tire
   * exists, the card says the real thing: how many there are against how
   * many were asked for.
   *
   * Owner ruling (2026-10-22): the same answer is a hard block on quoting
   * (quoteShortfall below). Fail closed unless the quantity is proven:
   * - a failed stock or promise lookup (an errors[] entry) is unknown, and
   *   the card already says so: not blocked;
   * - a promise asked at this very quantity (`promiseQuantity`, stamped on a
   *   live quote-batch answer only) decides: "out_of_stock" blocks whatever
   *   the rows say (a capped row, a location holding negative stock), with
   *   `count` only when every row is an exact count, 1 or more, below
   *   `quantity`; anything else fills;
   * - a promise asked at another quantity (the stepper, a service-worker
   *   answer, an offline replay) counts only as far as the rows prove it
   *   (provenStock), and never when it said "out_of_stock": blocked with no
   *   count, never a made-up number.
   */
  function buildShortStock(quote, quantity) {
    if (!quote) return null;
    if (getFieldError(quote.errors, 'stock') || getFieldError(quote.errors, 'promise')) return null;
    var wanted = quantity || 1;
    var out = !!(quote.promise && quote.promise.status === 'out_of_stock');
    if (quote.promiseQuantity === wanted) {
      if (!out) return null;
      var rows = quote.stock && quote.stock.anyAvailable && Array.isArray(quote.stock.warehouses) ? quote.stock.warehouses : [];
      var count = 0;
      for (var i = 0; i < rows.length; i++) {
        if (!rows[i] || rows[i].displayMode === 'capped' || typeof rows[i].quantity !== 'number') return { quantity: wanted };
        count += rows[i].quantity;
      }
      return count >= 1 && count < wanted ? { count: count, quantity: wanted } : { quantity: wanted };
    }
    return !out && provenStock(quote) >= wanted ? null : { quantity: wanted };
  }

  // ponytail: 20 mirrors dealer-pro's warehouse display threshold (env var read by
  // readDisplayThresholdFromEnv in productWarehouseInventoryForRequest.ts): a "capped" row means
  // MORE than that many. Lowering the env var below 20 makes this over-prove; negative stock at a
  // location is invisible here (rows clamp to 0). Upgrade path: dealer-pro sends the threshold in `stock`.
  var CAPPED_ROW_PROVES = 20;

  /** How many the warehouse rows alone prove are there: each exact count, 20 per capped row. */
  function provenStock(quote) {
    var rows = quote && quote.stock && Array.isArray(quote.stock.warehouses) ? quote.stock.warehouses : [];
    var n = 0;
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i]) continue;
      if (rows[i].displayMode === 'capped') n += CAPPED_ROW_PROVES;
      else if (typeof rows[i].quantity === 'number' && rows[i].quantity > 0) n += rows[i].quantity;
    }
    return n;
  }

  /** The parts of a quote, each with the quantity it is quoted at: the quote itself, a package's two
   * halves (both at the package quantity), or a front / rear pair's axles (each at its own). */
  function quoteParts(quote) {
    if (quote.kind === 'package') {
      return [quote.tire, quote.rim].map(function (part) { return { part: part, quantity: quote.requestedQuantity }; });
    }
    if (Array.isArray(quote.axleQuotes)) {
      return quote.axleQuotes.map(function (axle) { return { part: axle, quantity: axle && axle.requestedQuantity }; });
    }
    return [{ part: quote, quantity: quote.requestedQuantity }];
  }

  /** Whether every part's delivery promise was asked at the quantity it is quoted at. Same-day,
   * countdown and "Valid until" claims are only true then. */
  function promiseAnswersQuantity(quote) {
    if (!quote) return false;
    return quoteParts(quote).every(function (p) { return !!p.part && p.part.promiseQuantity === (p.quantity || 1); });
  }

  /**
   * The hard block (owner ruling 2026-10-22): { count, quantity, title? } when the stock
   * cannot fill a quote, else null. Covers every shape the quote screen opens: one tire
   * (a result card), a package ({ kind: 'package', tire, rim }, both halves priced at its
   * requestedQuantity) and a front / rear pair (axleQuotes, each axle at its own quantity,
   * see ProSearch#_combinedStaggeredQuote). Short in any part is short; `title` names the part.
   */
  function quoteShortfall(quote) {
    if (!quote) return null;
    var parts = quoteParts(quote);
    var named = parts.length > 1;
    for (var i = 0; i < parts.length; i++) {
      var shortfall = parts[i].part ? buildShortStock(parts[i].part, parts[i].quantity) : null;
      if (shortfall) {
        if (named) shortfall.title = parts[i].part.title || '';
        return shortfall;
      }
    }
    return null;
  }

  // dealer_pro.promise.short_blocked_card / short_unproven_card / short_set_quantity /
  // short_blocked_screen / short_unproven_screen, for a page cached before those strings reached
  // its data-i18n-* attributes. The *Unproven sentences carry no count: there is none to give.
  var SHORTFALL_WORDS = {
    card: "Can't quote {quantity}: only {count} in stock.",
    cardUnproven: "Can't quote {quantity}: not enough stock.",
    setQuantity: 'Set quantity to {count}',
    screen: 'Not enough stock: only {count} of {quantity} available. Lower the quantity to {count}.',
    screenUnproven: 'Not enough stock for {quantity}. Lower the quantity or try again.'
  };

  /** A quoteShortfall answer in words: the page's `template` (else the English above) with
   * the numbers filled in, or, for an answer with no count, `unprovenTemplate` (else the English
   * count-less sentence); on the quote screen, a package half or an axle is named first. */
  function shortfallText(kind, template, shortfall, unprovenTemplate) {
    var unproven = shortfall.count == null && kind !== 'setQuantity';
    var chosen = unproven ? unprovenTemplate || SHORTFALL_WORDS[kind + 'Unproven'] : template || SHORTFALL_WORDS[kind];
    // split / join: the screen sentence carries {count} twice.
    var words = String(chosen)
      .split('{count}').join(String(shortfall.count))
      .split('{quantity}').join(String(shortfall.quantity));
    return kind === 'screen' && shortfall.title ? shortfall.title + ' — ' + words : words;
  }

  function buildResultViewModel(quote, phone) {
    quote = quote || {};
    var errors = quote.errors || [];
    var promiseView = buildPromiseView(quote.promise, errors);
    // The block reads this whether or not there is a promise to show.
    promiseView.shortStock = quoteShortfall(quote);
    // A promise asked at another quantity makes no same-day claim (no badge, no countdown).
    if (promiseView.visible && !promiseAnswersQuantity(quote)) {
      promiseView.showCountdown = false;
      promiseView.countdownSeconds = 0;
      promiseView.showSameDayBadge = false;
    }
    return {
      title: quote.title || '',
      // Contract §4.2.1 — null whenever identity itself couldn't be resolved, or the product has
      // no featured image. Never invented; the renderer below skips the <img> entirely when null
      // rather than showing a broken image or a placeholder.
      imageUrl: quote.imageUrl || null,
      stock: buildStockView(quote.stock, errors, phone),
      promise: promiseView,
      price: buildPriceView(quote.price, errors, phone),
      // Package (plan 2026-09-11) — which half of a package this card can
      // fill. Only set when the search that produced it knows (a size
      // search's tires, a fitment result); a free-text hit could be anything.
      packageSide: quote.fitment && (quote.fitment.kind === 'tire' || quote.fitment.kind === 'rim') ? quote.fitment.kind : null,
      // DP-7 — the raw quote, carried through so a "generate quote"
      // button on this row can hand the untouched cost ledger + promise
      // straight to buildQuoteCardViewModel later, without a second fetch.
      raw: quote
    };
  }

  // English words for the hint codes, used when the page carries none of its own.
  var FITMENT_HINT_WORDS = { diameter: 'Different diameter', bolt: 'Different bolt pattern', bore: 'Bore too small' };

  /**
   * Package (plan 2026-09-11) — the one line a fitment result card adds
   * under its title. A wheel: "17×7.0 · 5×114.3 · ET50 · CB67.1". A tire
   * found for a wheel against the size on the car: "704.7 mm · +0.4% vs
   * current tire" (`deltaTemplate` is data-i18n-fitment-delta). A plain
   * tire from a size search has nothing to add. `omitHubRing`: the card
   * shows the hub ring as its own link, so the line must not say it twice.
   * `labels` ({ hubRing, oe, hints: { code: words } }, from the page's
   * data-i18n-fitment-* attributes) is the words of the wheel line; "ET" and
   * "CB" stay as the spec abbreviations. Without them the English below is used.
   */
  function buildFitmentLine(fitment, deltaTemplate, omitHubRing, labels) {
    if (!fitment) return '';
    labels = labels || {};
    if (fitment.kind === 'rim' && fitment.spec) {
      var s = fitment.spec;
      var parts = [s.diameterIn + '×' + Number(s.widthIn).toFixed(1)];
      if (s.pcds && s.pcds.length) {
        parts.push(s.pcds.map(function (p) { return String(p).replace('x', '×'); }).join('/'));
      }
      if (s.etMm != null) parts.push('ET' + s.etMm);
      if (s.cbMm != null) parts.push('CB' + s.cbMm);
      if (!omitHubRing && fitment.hubRing && fitment.hubRing.outerMm != null && fitment.hubRing.innerMm != null) {
        parts.push(
          String(labels.hubRing || 'Hub ring {from} → {to}')
            .replace('{from}', fitment.hubRing.outerMm)
            .replace('{to}', fitment.hubRing.innerMm)
        );
      }
      // The inch mark is not in the locale value: a quote character would end the HTML attribute that carries it.
      if (fitment.oeDiameterHint != null) parts.push(String(labels.oe || 'OE {size}').replace('{size}', fitment.oeDiameterHint + '"'));
      if (fitment.hints && fitment.hints.length) {
        parts.push(
          fitment.hints
            .map(function (code) { return (labels.hints && labels.hints[code]) || FITMENT_HINT_WORDS[code] || code; })
            .join(', ')
        );
      }
      return parts.join(' · ');
    }
    if (fitment.kind === 'tire' && typeof fitment.deltaPct === 'number' && typeof fitment.odMm === 'number') {
      var delta = (fitment.deltaPct > 0 ? '+' : '') + fitment.deltaPct.toFixed(1) + '%';
      return String(deltaTemplate || '{od} mm · {delta}').replace('{od}', fitment.odMm.toFixed(1)).replace('{delta}', delta);
    }
    return '';
  }

  /**
   * Package mode — would this card's pick clash with what is already in the
   * package? Answers with the first of 'diameter' / 'width' / 'unknown' (the
   * same codes, and the same strings, the package bar shows), or ''. A
   * quantity difference is not a clash: adding the pick reprices the whole
   * package to match (PackageController#addToPackage). The rule itself lives
   * in assets/toee-pro-package.js, a sibling script that loads after this
   * one, so it is looked up at call time.
   */
  function packageConflictFor(currentPackage, side, quote) {
    var api = typeof ToeeProPackage !== 'undefined' ? ToeeProPackage : null;
    if (!api || !currentPackage || !side) return '';
    var codes = api.packageConflicts(api.setPackageSide(currentPackage, side, quote));
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] !== 'quantity') return codes[i];
    }
    return '';
  }

  /**
   * After a quote is made from a result card, that card's primary button
   * becomes the secondary "Quoted" until the next render replaces the card.
   * The label rides on the button itself (data-pro-quoted-label, written by
   * buildResultCard), so the front / rear Quote button and the package bar's
   * Generate button, which carry none, are left alone. The button keeps its
   * quote data and, when the caller says which quote it made, that quote's id:
   * tapping it again shows the same quote (ProQuoteScreen#open with that id)
   * rather than making a new one.
   */
  function markCardQuoted(button, quoteId) {
    var label = button && button.getAttribute && button.getAttribute('data-pro-quoted-label');
    if (!label) return false;
    if ((button.className || '').indexOf('tt-pro-generate-quote--quoted') === -1) {
      button.className += ' tt-pro-generate-quote--quoted';
    }
    button.textContent = label;
    button.setAttribute('data-pro-quoted', '1');
    if (quoteId) button.setAttribute('data-pro-quoted-id', quoteId);
    return true;
  }

  // ---------------------------------------------------------------------
  // Search — direct text input only (DP-3 cascading dropdowns are out of
  // scope). Resolves a size string to variant ids via Shopify's own
  // predictive search, then calls quote-batch. No login gate on search.
  // ---------------------------------------------------------------------

  /**
   * Pulls up to `limit` variant ids out of a /search/suggest.json body.
   *
   * Only takes the id when a product has exactly one variant. Per
   * docs/domains/fin-gateway/fin-product-lookup-contract-v1.md §2.5,
   * variant_id is only meaningful when variant_count == 1 (570 of 718
   * products — all tires); multi-variant products (e.g. hub centric rings)
   * have no well-defined "first" variant, so variants[0] there would be an
   * arbitrary guess, not an answer. Skip those rather than silently
   * quoting the wrong one.
   */
  function extractVariantIds(predictiveJson, limit) {
    limit = limit || RESULT_LIMIT;
    var products =
      (predictiveJson &&
        predictiveJson.resources &&
        predictiveJson.resources.results &&
        predictiveJson.resources.results.products) ||
      [];
    var ids = [];
    for (var i = 0; i < products.length && ids.length < limit; i++) {
      var variants = products[i] && products[i].variants;
      if (variants && variants.length === 1 && variants[0].id != null) {
        ids.push(String(variants[0].id));
      }
    }
    return ids;
  }

  /**
   * Design round 3 — the quantity a search is quoted at: the stepper on the
   * results screen when the page has one, else the dealer's saved default.
   * Whole number, 1–99. Pure so the fetch and the stepper's own clamp agree.
   */
  function readSearchQuantity(input, settings) {
    var fallback = Math.max(1, Math.floor(Number((settings && settings.defaultQuantity) || 1)) || 1);
    var raw = input && input.value != null ? String(input.value).trim() : '';
    var n = raw === '' ? NaN : Number(raw);
    if (!isFinite(n)) return fallback;
    return Math.min(99, Math.max(1, Math.floor(n)));
  }

  /** Whether the dealer's last input was a key rather than a pointer, mouse or touch press.
   * Chrome and Android focus a button they tap, so `document.activeElement` alone cannot tell a
   * keyboard user from a tap; a tap-driven hand-off would raise a phone's keyboard and jump the
   * page. False until the first key: no hand-off before then. Capture phase, so a handler that
   * stops propagation cannot hide the input from it. */
  var lastInputWasKeyboard = false;
  if (typeof document !== 'undefined' && document && typeof document.addEventListener === 'function') {
    document.addEventListener('keydown', function () { lastInputWasKeyboard = true; }, true);
    var markPointer = function () { lastInputWasKeyboard = false; };
    // mousedown / touchstart: engines without pointer events.
    document.addEventListener('pointerdown', markPointer, true);
    document.addEventListener('mousedown', markPointer, true);
    document.addEventListener('touchstart', markPointer, { capture: true, passive: true });
  }

  /** A control that hides or disables itself takes keyboard focus with it (to <body>). Called
   * after the change: if the last input was a key (see lastInputWasKeyboard) and `fromEl` has
   * focus, or holds the focused element, focus goes on to `toEl`. A tap or click, a control the
   * dealer never focused (a tap in Safari) or focus put elsewhere is left alone, and so is a
   * target that cannot take focus. Guarded for the plain fake DOM, which has no activeElement. */
  function handOffFocus(fromEl, toEl) {
    if (!lastInputWasKeyboard) return false;
    var active = typeof document !== 'undefined' ? document.activeElement : null;
    if (!active || !fromEl || !toEl || toEl.disabled || typeof toEl.focus !== 'function') return false;
    if (active !== fromEl && !(typeof fromEl.contains === 'function' && fromEl.contains(active))) return false;
    toEl.focus();
    return true;
  }

  /** A stepper's minus / plus turn off where its input stops them: at its min and max
   * (1 and 99), which readSearchQuantity clamps to. An empty or non-numeric field is
   * mid-edit, so neither limit is reached. `stepAttr` holds each button's step. Shared by
   * the results bar and the Settings drawer's default quantity. */
  function syncStepperButtons(input, buttons, stepAttr) {
    var raw = input.value == null ? '' : String(input.value).trim();
    var n = raw === '' ? NaN : Number(raw);
    var min = Number(input.min) || 1;
    var max = Number(input.max) || 99;
    for (var i = 0; i < buttons.length; i++) {
      var step = Number(buttons[i].getAttribute(stepAttr)) || 0;
      buttons[i].disabled = step < 0 ? n <= min : step > 0 ? n >= max : false;
    }
    // A button that just turned itself off hands focus to the other one, never to the number
    // field (that would raise a phone's keyboard). The pair is [minus, plus].
    for (var j = 0; j < buttons.length; j++) {
      if (buttons[j].disabled) handOffFocus(buttons[j], buttons[1 - j]);
    }
  }

  function buildQuoteBatchBody(variantIds, quantity) {
    return { variantIds: variantIds, quantity: quantity || 1 };
  }

  function staggeredState(input) {
    input = input || {};
    if (input.mode !== 'front' && input.mode !== 'rear') return { mode: 'single' };
    return {
      mode: input.mode,
      front: input.front || null,
      rear: input.rear || null
    };
  }

  function staggeredAfterPick(state, axle, tire) {
    state = staggeredState(state);
    if (state.mode === 'single' || (axle !== 'front' && axle !== 'rear')) return state;
    var next = { mode: axle, front: state.front, rear: state.rear };
    next[axle] = tire;
    var other = axle === 'front' ? 'rear' : 'front';
    if (!next[other]) next.mode = other;
    return next;
  }

  function staggeredExit() {
    return { mode: 'single' };
  }

  function staggeredControlLabels(root) {
    function label(attribute, fallback) {
      var value = root && root.getAttribute(attribute);
      return value == null ? fallback : value;
    }
    return {
      open: label('data-pro-staggered-open', 'Front / rear'),
      // The locale value carries its own chevron (the whole string is the link text), so the fallback does too.
      exit: label('data-pro-staggered-exit', 'One size ›'),
      quote: label('data-pro-staggered-quote', 'Quote'),
      front: label('data-pro-staggered-use-front', 'Front'),
      rear: label('data-pro-staggered-use-rear', 'Rear'),
      useAsFront: label('data-pro-staggered-use-as-front', 'Use as front'),
      useAsRear: label('data-pro-staggered-use-as-rear', 'Use as rear')
    };
  }

  function staggeredOpenQuantity(currentQuantity, quantityTouched) {
    var quantity = Number(currentQuantity) || 1;
    return quantity === 4 && !quantityTouched ? 2 : quantity;
  }

  function tireQuoteQuantity(tire) {
    return Number(tire && (tire.qty || tire.requestedQuantity || tire.quantity)) || 1;
  }

  function staggeredQuoteBody(state) {
    state = state || {};
    var front = state.front;
    var rear = state.rear;
    if (!front) return null;
    var body = {
      variantId: front.variantId,
      qty: tireQuoteQuantity(front),
      dealerTotal: fromCents(toCents(front.dealerTotal))
    };
    if (!rear) return body;
    body.dealerTotal = fromCents(toCents(front.dealerTotal) + toCents(rear.dealerTotal));
    if (String(front.variantId) === String(rear.variantId)) {
      body.qty += tireQuoteQuantity(rear);
      return body;
    }
    body.rearVariantId = rear.variantId;
    body.rearQty = tireQuoteQuantity(rear);
    return body;
  }

  /**
   * A dealer checking per-warehouse stock needs to see a tire that's out of
   * stock too — the Fin product gateway deliberately returns everything, in
   * stock or not, for this reason. Do not pass unavailable_products=hide.
   */
  function predictiveSearchUrl(query, limit) {
    return (
      PREDICTIVE_SEARCH_URL +
      '?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=' +
      (limit || RESULT_LIMIT)
    );
  }

  /**
   * Variant-id lookup for one exact size.
   *
   * Dealer Pro used to read variant ids out of /search/suggest.json. On this
   * store that endpoint returns products whose `variants` array is empty —
   * checked live against four parameter combinations, including
   * resources[options][fields] — so no ids were ever extracted, the batch
   * call never fired, and a search for a size with stock showed nothing and
   * fell through to "closest in-stock size", which then named the size that
   * had just been searched.
   *
   * templates/collection.pro-variants.liquid walks variants server-side for
   * a tag combination and returns them all, out-of-stock included. Tag
   * handles follow assets/size-combo-index.js's convention; the axes are
   * plain integers here, so they need no handleising.
   */
  function proVariantsUrl(collectionUrl, parsed) {
    if (!parsed || parsed.width == null || parsed.aspect == null || parsed.diameter == null) {
      return null;
    }
    var base = String(collectionUrl || '').replace(/\/+$/, '');
    // Without a collection to hang the tags off, the join would produce a
    // rootless "/width_205+..." that resolves against whatever page is open.
    // Returning null instead lets the caller fall back to predictive search.
    if (!base) return null;
    var tags = [
      'width_' + parsed.width,
      'aspect_' + parsed.aspect,
      'diameter_' + parsed.diameter,
    ];
    return base + '/' + tags.join('+') + '?view=' + PRO_VARIANTS_VIEW;
  }

  /** Ids from a pro-variants payload, capped at `limit`, in-stock first.
   *
   * Only `limit` ids are ever sent to quote-batch and the renderer drops
   * confirmed-sold-out lines (sellableQuotes), so plain document order would
   * let sold-out SKUs eat the batch — a size whose first three SKUs are out
   * would show two cards while five sellable tires sat unasked-about. The
   * view already emits `available`, so this ordering costs no extra request.
   *
   * Out-of-stock ids still fill any remaining slots rather than being
   * dropped: `available` is Shopify's aggregate answer, while the truth that
   * matters is per-warehouse and lives in the app. If Shopify is wrong about
   * one, quote-batch is still asked and its answer is what decides.
   */
  function extractProVariantIds(json, limit) {
    limit = limit || RESULT_LIMIT;
    var list = (json && json.variants) || [];
    var inStock = [];
    var rest = [];
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if (!v || v.id == null) continue;
      // Absent `available` counts as in-stock — an unknown must not be demoted.
      (v.available === false ? rest : inStock).push(String(v.id));
    }
    return inStock.concat(rest).slice(0, limit);
  }

  function chooseSeason(season) {
    return season === 'winter' ? 'winter' : 'all';
  }

  function seasonVariantIds(payload, season, limit) {
    var variants = (payload && payload.variants) || [];
    var pool = variants;
    if (chooseSeason(season) === 'winter') {
      pool = variants.filter(function (variant) {
        return !!variant && variant.winter === true;
      });
    }
    return extractProVariantIds({ variants: pool }, limit);
  }

  function winterSeasonLabels(root) {
    function label(attribute, fallback) {
      var value = root && root.getAttribute && root.getAttribute(attribute);
      return value == null || value === '' ? fallback : value;
    }
    return {
      all: label('data-pro-season-all', 'All'),
      winter: label('data-pro-season-winter', 'Winter'),
      empty: label('data-pro-season-empty', 'No winter tire in this size.'),
      showAll: label('data-pro-season-show-all', 'Show all seasons ›')
    };
  }

  function rememberSizeVariants(bySize, variants) {
    if (!bySize) return null;
    return Array.isArray(variants) ? variants : [];
  }

  function applySearchSeason(state, season) {
    state = state || {};
    var next = chooseSeason(season);
    var variants = Array.isArray(state.sizeVariants) ? state.sizeVariants : null;
    var quoteIds = null;
    var emptyWinter = false;
    if (variants) {
      quoteIds = seasonVariantIds({ variants: variants }, next, state.limit || 5);
      emptyWinter = next === 'winter' && quoteIds.length === 0 && variants.length > 0;
    }
    return {
      season: next,
      staggered: state.staggered,
      quoteIds: quoteIds,
      emptyWinter: emptyWinter
    };
  }

  function quoteListShouldReplace(askedIds, batch) {
    if (!batch || batch.success !== true || !Array.isArray(batch.quotes)) return false;
    var asked = (askedIds || []).map(String);
    var got = batch.quotes.map(function (quote) {
      return quote && quote.variantId != null ? String(quote.variantId) : '';
    });
    if (got.indexOf('') !== -1 || got.length !== asked.length) return false;
    var seen = {};
    got.forEach(function (id) { seen[id] = (seen[id] || 0) + 1; });
    return asked.every(function (id) {
      if (!seen[id]) return false;
      seen[id] -= 1;
      return true;
    });
  }

  /** One size search prices every tire in the list, up to the quote-batch cap.
   * All / Winter then only filters that priced list. */
  var SIZE_QUOTE_LIMIT = 20;

  function quotesForSeason(quotedById, sizeVariants, season, limit) {
    if (!quotedById || !Array.isArray(sizeVariants)) return null;
    var ids = seasonVariantIds({ variants: sizeVariants }, season, limit);
    var quotes = [];
    for (var i = 0; i < ids.length; i++) {
      var quote = quotedById[String(ids[i])];
      if (!quote) return null;
      quotes.push(quote);
    }
    return quotes;
  }

  // ---------------------------------------------------------------------
  // Cascading dropdowns (DP-3). The cascade engine itself — parsing tags,
  // building the ?view=size-options URL, filling selects without a race —
  // is assets/size-combo-index.js, reused unchanged (loaded as
  // window.ToeeSizeCombo). Everything below is Dealer-Pro-specific glue:
  // turning a completed width/aspect/diameter combo into the exact same
  // size string the text-input path already searches on, and picking a
  // nearest in-stock alternative when a search comes back empty.
  // ---------------------------------------------------------------------

  /** Mirrors ToeeSizeCombo.suffixOfTag — kept local so this file has no
   * load-order dependency on size-combo-index.js for its pure logic. */
  function suffixOfHandle(handle) {
    var raw = String(handle == null ? '' : handle);
    var i = raw.indexOf('_');
    return i === -1 ? '' : raw.slice(i + 1);
  }

  /** Same "W/AR" shape as the search placeholder and product titles
   * (e.g. "225/65R17"), so the same predictive-search query works whether
   * it was typed or assembled from the three dropdowns. */
  function formatSizeString(width, aspect, diameter) {
    if (!width || !aspect || !diameter) return '';
    return width + '/' + aspect + 'R' + diameter;
  }

  function buildCascadeSizeString(widthHandle, aspectHandle, diameterHandle) {
    return formatSizeString(suffixOfHandle(widthHandle), suffixOfHandle(aspectHandle), suffixOfHandle(diameterHandle));
  }

  /** Pulls width/aspect/diameter out of a typed or cascaded query string.
   * Returns null when the string isn't shaped like a tire size — the
   * no-results fallback then just offers the call action, no guess. */
  function parseSizeQuery(str) {
    if (str == null) return null;
    var m = /^(\d{2,3})\D+(\d{2,3})\D+(\d{2,3})\D*$/.exec(String(str).trim());
    if (!m) return null;
    return { width: parseInt(m[1], 10), aspect: parseInt(m[2], 10), diameter: parseInt(m[3], 10) };
  }

  /** Smallest absolute distance from `target` among a size-options group's
   * { label, handle } entries. Returns null on an empty/unusable list —
   * callers must never invent an alternative that isn't actually in stock. */
  function nearestOption(target, options) {
    var list = options || [];
    var best = null;
    var bestDiff = Infinity;
    for (var i = 0; i < list.length; i++) {
      var opt = list[i];
      var val = opt && parseFloat(opt.label);
      if (opt == null || isNaN(val)) continue;
      var diff = Math.abs(val - target);
      if (diff < bestDiff) {
        best = opt;
        bestDiff = diff;
      }
    }
    return best;
  }

  /**
   * View model for the no-results fallback (PRD §9.2): "don't show an
   * empty list — offer the nearest in-stock size plus a call-to-transfer
   * action." `alt` is { width, aspect, diameter } raw suffix strings
   * (already proven in-stock together by the caller walking the cascade),
   * or null when no alternative could be resolved. Either way the view
   * always carries a phone number — there is no all-empty state.
   */
  function buildNoResultsView(alt, phone, searched) {
    if (!alt) return { hasAlternative: false, phone: phone || CALL_PHONE };
    var altQuery = formatSizeString(alt.width, alt.aspect, alt.diameter);
    var view = { hasAlternative: true, altQuery: altQuery, altLabel: altQuery, phone: phone || CALL_PHONE };
    // The size that came back empty, when the caller has it: the sentence names it.
    if (searched) view.searched = searched;
    return view;
  }

  // ---------------------------------------------------------------------
  // DP-10 — PWA install platform detection + offline banner formatting.
  // Pure logic, no DOM, same reasoning as the view-model builders above:
  // the wrong answer here is the failure mode (showing iOS Chrome a guide
  // that cannot work, or a stale quote with no time on it), so it is
  // covered directly rather than only through the DOM it eventually feeds.
  // ---------------------------------------------------------------------

  /** iPhone/iPad only — Android and desktop never see the iOS install guide. */
  function isIOS(ua) {
    return /iPad|iPhone|iPod/.test(ua || '');
  }

  /**
   * True only for real Safari on iOS. Every other iOS browser (Chrome,
   * Firefox, Edge, Opera) runs on the same WebKit engine and carries
   * "Safari" in its UA string too, but each also carries its own *iOS
   * token (CriOS/FxiOS/EdgiOS/OPiOS) — checked first so none of them is
   * mistaken for Safari. None of them can install a PWA on iOS at all
   * (PRD §4.1): showing them the "tap Share" guide would send a dealer to
   * a Share sheet with no "Add to Home Screen" option in it.
   */
  function isIOSSafari(ua) {
    var s = ua || '';
    if (!isIOS(s)) return false;
    if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(s)) return false;
    return /Safari/.test(s);
  }

  // Shell-header artboard D: "Not now" (or a declined install dialog) hides the install
  // box for 14 days. One key for both boxes, since a device only ever shows one of them.
  // Storage can be missing or throw (private windows, blocked site data), so every read
  // and write is wrapped: without it the page just asks again.
  var INSTALL_DISMISS_KEY = 'toee-pro-install-dismissed';
  var INSTALL_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

  function installStore(storage) {
    try {
      return storage || (typeof localStorage !== 'undefined' ? localStorage : null);
    } catch (e) {
      return null;
    }
  }

  function installDismissedRecently(storage, nowMs) {
    try {
      var store = installStore(storage);
      if (!store) return false;
      var at = Number(store.getItem(INSTALL_DISMISS_KEY));
      // Not a time, or a time in the future (the clock was set back), must not hide the box for good.
      return at > 0 && nowMs >= at && nowMs - at < INSTALL_DISMISS_MS;
    } catch (e) {
      return false;
    }
  }

  function rememberInstallDismissal(storage, nowMs) {
    try {
      var store = installStore(storage);
      if (store) store.setItem(INSTALL_DISMISS_KEY, String(nowMs));
    } catch (e) {
      // Storage refused: the box comes back next time, and nothing else depends on it.
    }
  }

  /**
   * Splices a captured-at timestamp (ms epoch) as local "HH:MM" into a
   * locale-provided template carrying a single "{time}" token, e.g.
   * "Offline data · {time}" / "Données hors ligne · {time}". Returns ''
   * when there is no timestamp — the banner must never claim an offline
   * result is current, and an empty label is safer than inventing one.
   */
  function formatOfflineLabel(capturedAtMs, template) {
    if (capturedAtMs == null) return '';
    var d = new Date(capturedAtMs);
    var hh = d.getHours();
    var mm = d.getMinutes();
    var hhStr = hh < 10 ? '0' + hh : '' + hh;
    var mmStr = mm < 10 ? '0' + mm : '' + mm;
    // The 12-hour clock the business hours use ("2:14 PM"), not 24-hour.
    return String(template || '').replace('{time}', formatTime12h(hhStr + ':' + mmStr));
  }

  // ---------------------------------------------------------------------
  // DP-5 — dealer settings (contract §6, dp-5-dealer-settings.md). Saving
  // must never block quoting: every write lands in localStorage
  // synchronously and takes effect immediately; the backend PUT is
  // fire-and-forget and its failure is swallowed entirely — the dealer is
  // standing in front of a customer, a sync hiccup must not interrupt him.
  // The backend is authoritative on load (§6.3): a GET overwrites the
  // local cache as a whole object (never a field-level merge), UNLESS the
  // local copy carries a strictly newer updatedAt — that means it holds an
  // edit that hasn't reached the backend yet, and last-write-wins keeps it
  // rather than silently discarding an offline change.
  // ---------------------------------------------------------------------

  var SETTINGS_STORAGE_KEY = 'toee-pro-settings';

  function settingsDefaults() {
    return {
      shopName: '',
      phone: '',
      address: '',
      // Owner ruling (2026-09-08) — weekdays/weekend open+start+end, mirroring
      // dealer-pro/api/services/dealerPro/dealerProSettingsService.ts's
      // BusinessHours default. Weekdays default open (a wholesale counter
      // usually is); weekend defaults closed.
      businessHours: {
        weekdays: { open: true, start: '09:00', end: '17:00' },
        weekend: { open: false, start: '09:00', end: '17:00' }
      },
      markupMode: 'none',
      markupValue: 0,
      installFeeMode: 'none',
      installFeeValue: 0,
      cardTheme: 'graphite',
      defaultQuantity: 4,
      // Owner ruling — markup mandatory on first run. Toee's cost to the
      // dealer already includes a 10% delivery surcharge (contract §4.5);
      // an undecided markup silently sells below the dealer's own cost.
      // This tracks *decided*, never the value itself — markupValue 0 is a
      // legitimate "sells at cost" choice, so needsFirstRun below cannot
      // use markupValue/markupMode alone to detect "never touched".
      markupConfigured: false,
      updatedAt: null
    };
  }

  /** Overlays one section's edited fields onto the current settings object
   * and stamps a fresh updatedAt — the timestamp last-write-wins resolves
   * on. Fields outside `patch` (i.e. every other section) pass through
   * untouched, which is what makes each section's save independent. */
  function mergeSettingsSection(current, patch) {
    var next = {};
    var base = current || settingsDefaults();
    Object.keys(base).forEach(function (k) {
      next[k] = base[k];
    });
    Object.keys(patch || {}).forEach(function (k) {
      next[k] = patch[k];
    });
    // The first save that ever carries a markupValue IS the dealer's
    // markup decision (dp-16 owner ruling). A bare value with the mode
    // still 'none' would apply no markup at all (applyMarkupToLineCents
    // short-circuits on 'none' regardless of value), so default the mode
    // to 'percent' — 0% behaves identically to 'none', so this changes
    // nothing for a dealer who genuinely sells at cost. Only fires once:
    // a later save can never re-trigger it, so a mode chosen afterwards
    // via the quote-defaults section is never clobbered by this field
    // resubmitting its value.
    if (Object.prototype.hasOwnProperty.call(patch || {}, 'markupValue') && !base.markupConfigured) {
      if (!Object.prototype.hasOwnProperty.call(patch || {}, 'markupMode')) {
        next.markupMode = 'percent';
      }
      next.markupConfigured = true;
    }
    next.updatedAt = new Date().toISOString();
    return next;
  }

  /**
   * Contract §6.3 — the backend is authoritative on load, but an
   * unconditional overwrite would silently discard an edit made offline
   * that hasn't reached the backend yet. If local carries a strictly newer
   * updatedAt than what the backend returned, that IS the not-yet-synced
   * case: keep local (the next save retries the push). Otherwise the
   * backend value wins, wholesale — never a field-level merge.
   */
  /**
   * Pulls the settings object out of whatever GET /pro/settings returned.
   *
   * The route answers `{ success: true, settings: {...} }`. Reading that body
   * as if it *were* the settings is what wiped dealers' saved values: every
   * field came back undefined, the envelope was cached to localStorage, and
   * the next save PUT it back — and the backend is a whole-object overwrite,
   * so everything outside the saved section reset to default.
   *
   * A bare settings object is accepted too, matching the `?? response` style
   * the app-side readers already use: more than one shape is real, and this
   * codebase has now been bitten three times by assuming only one.
   *
   * Returns null for anything with no recognisable settings field, so the
   * caller keeps its local copy instead of overwriting a dealer with junk —
   * fails closed, which is what would have contained the original bug.
   */
  function unwrapSettingsPayload(body) {
    if (!body || typeof body !== 'object') return null;
    var known = Object.keys(settingsDefaults());
    var looksLikeSettings = function (o) {
      if (!o || typeof o !== 'object') return false;
      for (var i = 0; i < known.length; i++) {
        if (Object.prototype.hasOwnProperty.call(o, known[i])) return true;
      }
      return false;
    };
    // Outer first, and that order is load-bearing. A browser that ran the buggy
    // version cached the envelope and then had a save merged on top of it, so its
    // stored object holds the dealer's REAL values at the top level with the
    // stale, all-empty envelope still nested in `settings` beside them. Reaching
    // for `.settings` whenever it exists would read the dead half and throw away
    // the live one — on exactly the dealers already bitten once.
    if (looksLikeSettings(body)) return body;
    if (looksLikeSettings(body.settings)) return body.settings;
    return null;
  }

  function resolveSettingsOnLoad(local, remote) {
    if (!remote) return local || settingsDefaults();
    if (!local) return remote;
    var localTime = local.updatedAt ? Date.parse(local.updatedAt) : 0;
    var remoteTime = remote.updatedAt ? Date.parse(remote.updatedAt) : 0;
    var chosen = localTime > remoteTime ? local : remote;
    // Live bug, 2026-09-09 — markupConfigured is a client-only bookkeeping
    // flag (dealerProSettingsService.ts's DEALER_PRO_SETTINGS_DEFAULTS has
    // no such field; the backend contract cannot carry it), so a remote
    // payload can never legitimately say the dealer hasn't decided a markup
    // — it simply has no opinion. Whole-object overwrite is otherwise
    // correct and untouched here: every backend-owned field still resolves
    // straight from whichever side won. This flag only ever goes false ->
    // true, so once either side has it, the result keeps it.
    if (!chosen.markupConfigured && local.markupConfigured) {
      chosen = Object.assign({}, chosen, { markupConfigured: true });
    }
    return chosen;
  }

  /** PRD §5.3.6 / dp-5, extended by the owner's markup ruling — send the
   * dealer to the settings screen when there's nothing usable to put on a
   * quote card yet, OR when he has a shop name but has never decided a
   * markup. The second half also catches an existing dealer from before
   * this field existed, which is intentional: his markup was silently 0
   * before too, this just now surfaces it once. `markupConfigured` (never
   * markupValue/markupMode) is the check, so an explicit 0 is never
   * mistaken for "never touched". */
  /**
   * Design round 4 (2026-09-08) — the /pro header is the dealer's, not
   * Toee's: a saved shop name is the wordmark with "Dealer Pro" as its
   * eyebrow and the tab title reads "Shop · Dealer Pro"; with no name
   * yet the wordmark is the product label and the eyebrow is hidden.
   */
  function shopIdentity(settings, productLabel) {
    var label = productLabel || 'Dealer Pro';
    var name = settings && settings.shopName ? String(settings.shopName).trim() : '';
    if (!name) return { wordmark: label, eyebrowVisible: false, title: label };
    return { wordmark: name, eyebrowVisible: true, title: name + ' · ' + label };
  }

  /**
   * Writes shopIdentity into the page. `els` is injectable for tests; the
   * page finds its own slots (sections/toee-pro-search.liquid). The eyebrow
   * element's own text is the localized product label, so no string is
   * hardcoded here.
   */
  function applyShopIdentity(settings, els) {
    els = els || {};
    var doc = els.doc || (typeof document !== 'undefined' ? document : null);
    var wordmark = els.wordmark || (doc && doc.querySelectorAll ? doc.querySelectorAll('[data-pro-shop-name]')[0] : null);
    var eyebrow = els.eyebrow || (doc && doc.querySelectorAll ? doc.querySelectorAll('[data-pro-shop-eyebrow]')[0] : null);
    if (!wordmark) return null;
    var id = shopIdentity(settings, (eyebrow && eyebrow.textContent) || wordmark.textContent);
    wordmark.textContent = id.wordmark;
    if (eyebrow) eyebrow.hidden = !id.eyebrowVisible;
    if (doc && typeof doc.title === 'string') doc.title = id.title;
    return id;
  }

  /** The Dealer Pro blue (--toee-blue), the same value as the manifest's theme_color in
   * snippets/toee-pro-manifest.liquid. */
  var PRO_THEME_COLOR = '#1478f2';

  /**
   * The browser's title-bar colour on /pro. The shared snippets/head.liquid (store-wide, so
   * not edited here) renders <meta name="theme-color"> from the storefront's old accent
   * setting; this sets it to the Dealer Pro blue, creating the meta if the head has none.
   * `doc` is injectable for tests.
   */
  function applyThemeColor(doc) {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || typeof doc.querySelector !== 'function') return null;
    var meta = doc.querySelector('meta[name="theme-color"]');
    if (!meta) {
      if (!doc.head || typeof doc.createElement !== 'function') return null;
      meta = doc.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      doc.head.appendChild(meta);
    }
    meta.setAttribute('content', PRO_THEME_COLOR);
    return meta;
  }

  /** Owner ruling (2026-09-08) — a percent markup shows a "%" suffix next
   * to the amount so 20 reads as 20%, never as a fixed dollar figure. */
  function markupSuffixVisible(mode) {
    return mode === 'percent';
  }

  /** Owner ruling (2026-09-08) — a fixed-dollar mode (never a percentage)
   * shows a "$" prefix on its amount, the same emphasis "%" already gives
   * a percent markup. Shared by the markup amount (fixed_per_tire only —
   * percent gets the suffix instead) and Install fee's amount (which has
   * no percent option at all, so this is its only decoration). */
  function amountPrefixVisible(mode) {
    return mode === 'fixed_per_tire' || mode === 'fixed_per_order';
  }

  /** Toggles one markup pair's prefix/suffix from its own mode select —
   * fixed_per_tire shows "$", percent shows "%", the two never both show.
   * Pure DOM plumbing: reads nothing outside the pair element handed to
   * it, so it works the same at panel-populate time and on a live change.
   * The prefix element is optional (older markup, and the fake-DOM test
   * for "a pair missing a piece") — only the mode select is required. */
  function syncMarkupPair(pair) {
    if (!pair || !pair.querySelector) return;
    var mode = pair.querySelector('[data-pro-markup-mode]');
    if (!mode) return;
    var prefix = pair.querySelector('[data-pro-markup-prefix]');
    var suffix = pair.querySelector('[data-pro-markup-suffix]');
    if (prefix) prefix.hidden = !amountPrefixVisible(mode.value);
    if (suffix) suffix.hidden = !markupSuffixVisible(mode.value);
  }

  /** Same idea for Install fee: no percent option ever, so this only
   * toggles the "$" prefix — hidden for "none", shown for either fixed
   * mode. A separate pairing from markup's since it is a different field
   * with its own mode/value/prefix trio. */
  function syncInstallFeePair(pair) {
    if (!pair || !pair.querySelector) return;
    var mode = pair.querySelector('[data-pro-install-fee-mode]');
    var prefix = pair.querySelector('[data-pro-install-fee-prefix]');
    if (!mode || !prefix) return;
    prefix.hidden = !amountPrefixVisible(mode.value);
  }

  // ---------------------------------------------------------------------
  // Rollout Task 10 — the Settings drawer. The strings the script writes reach it as
  // data-i18n-<key> on the drawer root (sections/toee-pro-settings.liquid, from the EN
  // and FR locale files); SETTINGS_TEXT is the English fallback, so a page rendered
  // before a key existed still reads as a sentence, never as a blank.
  // ---------------------------------------------------------------------

  var SETTINGS_TEXT = {
    'field-required': 'This field is required.',
    'save-shop': 'Save shop details',
    'save-quote': 'Save quote defaults',
    'save-card': 'Save card appearance',
    saved: 'Saved',
    'markup-example': 'On a {base} tire, your price shows as {price}.',
    'markup-example-none': 'Nothing is added. The card shows the selling price.',
    'error-shop-name': 'Enter your shop name. It appears on every quote card.',
    'error-phone': 'Enter a phone number customers can call.',
    'error-markup-mode': 'Choose a markup: none, fixed per tire or percent.',
    'error-markup-value': 'Enter your markup amount. Use 0 to add nothing.'
  };

  /** The message an empty required field shows: what to fix, by the field's name. */
  var SETTINGS_FIELD_ERROR = {
    shopName: 'error-shop-name',
    phone: 'error-phone',
    markupMode: 'error-markup-mode',
    markupValue: 'error-markup-value'
  };

  function settingsAttr(root, key) {
    var value = root && root.getAttribute ? root.getAttribute('data-i18n-' + key) : null;
    return value ? String(value) : '';
  }

  function settingsText(root, key) {
    return settingsAttr(root, key) || SETTINGS_TEXT[key] || '';
  }

  /** A page without the field's own sentence (cached older markup) falls back to the
   * localized "This field is required." before any English. */
  function settingsFieldError(root, name) {
    var key = SETTINGS_FIELD_ERROR[name] || 'field-required';
    return settingsAttr(root, key) || settingsAttr(root, 'field-required') || SETTINGS_TEXT[key];
  }

  /** What a $200.00 tire sells for with this markup, through applyMarkupToLineCents, the
   * same maths every price on /pro goes through. Empty until there is a markup and an
   * amount that is a number; "none" says so in words. */
  var MARKUP_EXAMPLE_CENTS = 20000;

  function markupExampleText(mode, amount, root) {
    if (mode === 'none') return settingsText(root, 'markup-example-none');
    var raw = amount == null ? '' : String(amount).trim();
    if (!mode || raw === '' || !isFinite(Number(raw))) return '';
    var price = applyMarkupToLineCents(MARKUP_EXAMPLE_CENTS, 1, mode, Number(raw));
    return settingsText(root, 'markup-example')
      .replace('{base}', formatMoney(fromCents(MARKUP_EXAMPLE_CENTS)))
      .replace('{price}', formatMoney(fromCents(price)));
  }

  /** The rest of a markup pair that follows its mode: the amount waits (disabled, with
   * its "choose a markup first" placeholder) until a markup is chosen, and the Quotes
   * pair's example line. Same shape as syncMarkupPair: reads only the pair. */
  function syncMarkupExtras(pair, root) {
    if (!pair || !pair.querySelector) return;
    var mode = pair.querySelector('[data-pro-markup-mode]');
    if (!mode) return;
    var amount = pair.querySelector('[data-pro-markup-value]');
    if (amount) {
      amount.disabled = !mode.value;
      var waiting = amount.getAttribute ? amount.getAttribute('data-pro-markup-placeholder') : null;
      if (waiting != null) amount.placeholder = amount.disabled ? waiting : '';
    }
    var example = pair.querySelector('[data-pro-markup-example]');
    if (example) {
      example.textContent = markupExampleText(mode.value, amount ? amount.value : '', root);
      example.hidden = !example.textContent;
    }
  }

  /** A segmented control standing in for a native control: the select or checkbox stays
   * the value the save reads, the buttons only show and set it. A checkbox reads as
   * "true" / "false". */
  function segSourceValue(source) {
    if (source.type === 'checkbox') return String(!!source.checked);
    return source.value == null ? '' : String(source.value);
  }

  /** Presses the button matching the control's value and slides the indicator under it.
   * Nothing matching (first run, before a markup is chosen) presses nothing and hides it. */
  function syncSettingsSeg(seg, source) {
    if (!seg || !source || !seg.querySelectorAll) return;
    var value = segSourceValue(source);
    var buttons = seg.querySelectorAll('[data-pro-seg-value]');
    var at = -1;
    for (var i = 0; i < buttons.length; i++) {
      var on = buttons[i].getAttribute('data-pro-seg-value') === value;
      buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) at = i;
    }
    var indicator = seg.querySelector('.tt-pro-seg__ind');
    if (!indicator) return;
    indicator.hidden = at < 0;
    if (at >= 0 && indicator.style) indicator.style.transform = 'translateX(' + at * 100 + '%)';
  }

  /** Writes a button's value into the control and fires the control's own "change", so
   * everything already wired to it (the $ / % sync, the hours times, the card preview)
   * runs exactly as when the dealer used the control itself. */
  function setSegSource(source, value) {
    if (source.type === 'checkbox') source.checked = value === 'true';
    else source.value = value;
    if (typeof source.dispatchEvent === 'function' && typeof Event === 'function') {
      source.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function toggleClassName(node, name, on) {
    var classes = (node.className || '').split(/\s+/).filter(function (c) {
      return c && c !== name;
    });
    if (on) classes.push(name);
    node.className = classes.join(' ');
  }

  /** "09:00" -> "9:00 AM". Empty for anything that is not a valid 24h HH:MM. */
  function formatTime12h(hhmm) {
    var m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm || ''));
    if (!m) return '';
    var hour24 = Number(m[1]);
    var suffix = hour24 >= 12 ? 'PM' : 'AM';
    var hour12 = hour24 % 12 || 12;
    return hour12 + ':' + m[2] + ' ' + suffix;
  }

  function formatHoursGroupLabel(label, group) {
    if (!group || !group.open) return '';
    var start = formatTime12h(group.start);
    var end = formatTime12h(group.end);
    if (!start || !end) return '';
    return label + ' ' + start + '\u2013' + end;
  }

  /** Owner ruling (2026-09-08) — the structured {weekdays, weekend} shape,
   * rendered as one line for the quote card and the admin dashboard: each
   * OPEN group as "Weekdays 9:00 AM\u20135:00 PM", joined with " \u00b7 ";
   * a closed group is omitted entirely (never "Weekdays Closed" filler);
   * empty when both are closed, same as the old blank free-text field. */
  function formatBusinessHours(hours) {
    if (!hours) return '';
    var parts = [];
    var weekdays = formatHoursGroupLabel('Weekdays', hours.weekdays);
    var weekend = formatHoursGroupLabel('Weekends', hours.weekend);
    if (weekdays) parts.push(weekdays);
    if (weekend) parts.push(weekend);
    return parts.join(' \u00b7 ');
  }

  /** Reads one weekdays/weekend group off its [data-pro-hours-group] wrapper. */
  function readBusinessHoursGroup(groupEl) {
    var openBox = groupEl.querySelector('[data-pro-hours-open]');
    var startInput = groupEl.querySelector('[data-pro-hours-start]');
    var endInput = groupEl.querySelector('[data-pro-hours-end]');
    return {
      open: !!(openBox && openBox.checked),
      start: (startInput && startInput.value) || '',
      end: (endInput && endInput.value) || ''
    };
  }

  /** Assembles both groups under `root` into the {weekdays, weekend} shape
   * PUT /pro/settings expects — never routed through the generic
   * [data-pro-field-input] loop, since that only ever writes one flat
   * `patch[name] = value`, not a nested object. */
  function readBusinessHours(root) {
    var out = {};
    var groups = root.querySelectorAll('[data-pro-hours-group]');
    for (var i = 0; i < groups.length; i++) {
      var key = groups[i].getAttribute('data-pro-hours-group');
      out[key] = readBusinessHoursGroup(groups[i]);
    }
    return out;
  }

  /** Shows a group's time pickers only while it is open — the same rule
   * whether the checkbox was just clicked or the panel just populated. */
  function syncHoursGroupVisibility(groupEl) {
    if (!groupEl || !groupEl.querySelector) return;
    var openBox = groupEl.querySelector('[data-pro-hours-open]');
    var times = groupEl.querySelector('[data-pro-hours-times]');
    if (times) times.hidden = !(openBox && openBox.checked);
  }

  function populateBusinessHoursGroup(groupEl, group) {
    var openBox = groupEl.querySelector('[data-pro-hours-open]');
    var startInput = groupEl.querySelector('[data-pro-hours-start]');
    var endInput = groupEl.querySelector('[data-pro-hours-end]');
    if (openBox) openBox.checked = !!(group && group.open);
    if (startInput) startInput.value = (group && group.start) || '';
    if (endInput) endInput.value = (group && group.end) || '';
    syncHoursGroupVisibility(groupEl);
  }

  /** Writes a loaded settings.businessHours back onto the group controls —
   * the [data-pro-hours-group] controls carry no [data-pro-field-input],
   * so the generic populateForm loop never reaches them. */
  function populateBusinessHours(root, hours) {
    var groups = root.querySelectorAll('[data-pro-hours-group]');
    for (var i = 0; i < groups.length; i++) {
      var key = groups[i].getAttribute('data-pro-hours-group');
      populateBusinessHoursGroup(groups[i], hours && hours[key]);
    }
  }

  function needsFirstRun(settings) {
    return !settings || !settings.shopName || !settings.markupConfigured;
  }

  function readLocalSettings(storage) {
    try {
      var store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!store) return null;
      var raw = store.getItem(SETTINGS_STORAGE_KEY);
      // Same guard as the network side, and for the same reason: browsers that
      // ran the envelope bug have a cached { success, settings } object sitting
      // here. Unwrapping it on read heals them on the next page load instead of
      // leaving them blank until they clear site data.
      return raw ? unwrapSettingsPayload(JSON.parse(raw)) : null;
    } catch (e) {
      return null;
    }
  }

  function writeLocalSettings(settings, storage) {
    try {
      var store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!store) return;
      store.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      // localStorage can throw (private browsing, quota) — a settings
      // write must never block the dealer, so this fails silently.
    }
  }

  /** Fire-and-forget PUT — never rejects. A failed push shows no error and
   * blocks nothing; it just retries on the next save. */
  function pushSettings(settings, fetchImpl) {
    var f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!f) return Promise.resolve(false);
    return f(proProxyUrl('/settings'), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    })
      .then(function (r) {
        return !!(r && r.ok);
      })
      .catch(function () {
        return false;
      });
  }

  /**
   * One section's Save button: writes locally and returns immediately
   * (the effect is synchronous), then kicks off the backend push without
   * the caller having to await it. `opts.fetchImpl` / `opts.storage` are
   * test seams; in the browser both default to the real thing.
   */
  function saveSettingsSection(current, patch, opts) {
    opts = opts || {};
    var next = mergeSettingsSection(current, patch);
    writeLocalSettings(next, opts.storage);
    var pushPromise = pushSettings(next, opts.fetchImpl);
    return { settings: next, pushPromise: pushPromise };
  }

  // ---------------------------------------------------------------------
  // DP-5 — Form States (DESIGN.md, first real consumer). State is carried
  // by a class (border colour in the stylesheet) plus a message, never a
  // background fill; success is transient, cleared by the caller-supplied
  // timer; a loading button keeps its label and only disables — it never
  // swaps to a spinner.
  // ---------------------------------------------------------------------

  function setInputState(inputEl, state) {
    if (!inputEl) return;
    var classes = (inputEl.className || '')
      .split(/\s+/)
      .filter(function (c) {
        return c && c.indexOf('tt-pro-input--') !== 0;
      });
    if (state) classes.push('tt-pro-input--' + state);
    inputEl.className = classes.join(' ');
  }

  var fieldMessageCount = 0;

  /** Adds or removes `id` in the field's aria-describedby, keeping the ids already there
   * (most Settings fields point at their hint). */
  function describeFieldBy(inputEl, id, on) {
    var ids = (inputEl.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) {
      return x && x !== id;
    });
    if (on) ids.push(id);
    if (ids.length) inputEl.setAttribute('aria-describedby', ids.join(' '));
    else inputEl.removeAttribute('aria-describedby');
  }

  /** The red border, aria-invalid, and the message, which the field's aria-describedby
   * points at (the message gets an id the first time) so a screen reader says why. */
  function applyFieldError(inputEl, messageEl, message) {
    setInputState(inputEl, 'error');
    if (inputEl) inputEl.setAttribute('aria-invalid', 'true');
    if (messageEl) {
      messageEl.textContent = message || '';
      messageEl.hidden = false;
      if (!messageEl.getAttribute('id')) {
        fieldMessageCount += 1;
        messageEl.setAttribute('id', 'tt-pro-field-message-' + fieldMessageCount);
      }
      if (inputEl) describeFieldBy(inputEl, messageEl.getAttribute('id'), true);
    }
  }

  /** Undoes applyFieldError: state class, aria-invalid, the message and its describedby entry. */
  function clearFieldError(inputEl, messageEl) {
    setInputState(inputEl, null);
    if (inputEl) inputEl.removeAttribute('aria-invalid');
    if (messageEl) {
      messageEl.hidden = true;
      if (inputEl && messageEl.getAttribute('id')) describeFieldBy(inputEl, messageEl.getAttribute('id'), false);
    }
  }

  /** Success is transient — DESIGN.md: "clear within ~2s". `timerFn`
   * defaults to setTimeout but is injectable so tests don't need to wait
   * on a real timer. */
  function applyFieldSuccess(inputEl, messageEl, delayMs, timerFn) {
    setInputState(inputEl, 'success');
    if (messageEl) {
      messageEl.textContent = '';
      messageEl.hidden = true;
    }
    var timer = timerFn || (typeof setTimeout !== 'undefined' ? setTimeout : null);
    if (!timer) return null;
    return timer(function () {
      setInputState(inputEl, null);
    }, delayMs == null ? 2000 : delayMs);
  }

  /** The label text is never touched — a control sized for the French
   * string must not resize mid-submit, and a vanished label costs the
   * dealer the context of what he pressed (DESIGN.md Form States). */
  function setButtonLoading(buttonEl, loading) {
    if (!buttonEl) return;
    var classes = (buttonEl.className || '')
      .split(/\s+/)
      .filter(function (c) {
        return c && c !== 'tt-pro-settings__save--loading';
      });
    if (loading) classes.push('tt-pro-settings__save--loading');
    buttonEl.className = classes.join(' ');
    buttonEl.disabled = !!loading;
  }

  // ---------------------------------------------------------------------
  // DP-7 — quote card. Two ledgers, never confused (contract §4.5,
  // dp-7-quote-card-render.md):
  //   - cost ledger (Toee -> dealer): quote.price, exactly as returned by
  //     draftOrderCalculate. Nothing in this block ever recomputes or
  //     overwrites one of its fields.
  //   - quote ledger (dealer -> car owner): buildQuoteLedger below. The
  //     dealer's own markup + install fee, computed here client-side —
  //     that arithmetic is correct and expected for THIS ledger only
  //     (contract §4.5's "zero amount arithmetic" rule is about the cost
  //     ledger, never this one).
  // buildQuoteLedger builds the tires row from `costPrice.lineSubtotal`
  // (already authoritative for the full requested quantity) and never
  // reads `costPrice.unit` at all — that is what makes a "unit price x
  // quantity" cost computation structurally absent here, not just avoided
  // by convention.
  //
  // CARD_LABELS below are hardcoded English on purpose (dp-7 hard
  // constraint / PRD §5.0.5): the quote card is not a Toee surface and
  // sits outside the site's bilingual locale system. Do not "fix" this by
  // moving them into locales/*.json — a future maintainer finding these
  // untranslated is expected, not a bug.
  //
  // Owner name/plate/vehicle model (readOwnerDetails/writeOwnerDetails)
  // are a PIPEDA commitment (PRD §5.3.4.2, §8.6): they exist only in
  // localStorage. Nothing below ever calls fetch — see the "no fetch()"
  // guard test in tests/toee-pro-quote-card-render.test.js. Do not add one.
  // ---------------------------------------------------------------------

  // dp-7 acceptance: labels are Tires / Recycling Fee / Installation / HST;
  // Total / In Stock / Same Day / Valid until are the same kind of
  // card-only literal and follow the same exemption.
  var CARD_LABELS = {
    tires: 'Tires',
    recyclingFee: 'Recycling Fee',
    deliveryFee: 'Delivery Fee',
    deliveryFeeNote: '* One tire has a delivery fee.',
    deliveryFeeNoteMore: 'Pickup, or more than one tire, has none.',
    installation: 'Installation',
    hst: 'HST',
    total: 'Total',
    inStock: 'In Stock',
    sameDay: 'Same Day',
    validUntil: 'Valid until',
    // Card redesign (design canvas, "Quote card · 4 themes") — one more
    // card-only literal under the same exemption.
    preparedFor: 'Prepared for',
    // 2026-09-11 — tire+wheel package card. Same hardcoded-English exemption.
    wheels: 'Wheels',
    // 2026-09-09 — quote history's "reopen as a card" mode. Same
    // hardcoded-English exemption as every other CARD_LABELS entry.
    historicalBadge: 'Historical price — please confirm',
    historicalQuotedOn: 'Quoted on',
    unavailableTitle: 'This product is no longer available',
    // Rollout Task 12 (card artboard): the footer's quantity cell and the
    // historical card's "Quantity 4". Same exemption.
    quantity: 'Quantity',
    tirePackage: 'Package'
  };

  // dp-15 — two more hardcoded-English literals, same exemption as
  // CARD_LABELS above (they go straight to the car owner via buildShareText
  // below, not through this screen's own EN/FR chrome), but NOT the same
  // wording the canvas card renders: drawQuoteCard has no "quantity in pcs"
  // line and no "Ready today" phrase (its own same-day signal is
  // labels.sameDay, the "Same Day" badge, nothing more). Named here instead
  // of left as anonymous string literals inside buildShareText, so a future
  // maintainer sees them as part of the documented exemption rather than an
  // untranslated leftover to "fix".
  var SHARE_TEXT_LABELS = {
    quantityUnit: 'pcs',
    readyToday: 'Ready today'
  };

  // Dealer's own invoice to the car owner (quote ledger) only — Ontario
  // HST. The cost ledger's own tax figure (quote.price.tax) is untouched
  // and never read by this block.
  var ONTARIO_HST_RATE = 0.13;

  function toCents(str) {
    var n = parseFloat(str);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function fromCents(cents) {
    return (cents / 100).toFixed(2);
  }

  /** Money as the dealer reads it: "$1,609.12" in English, "1 609,12 $" on a French page
   * (Canadian French; U+00A0 between the groups and before "$" so a figure never wraps).
   * `locale` defaults to the page's <html lang>; the customer quote card and its share text
   * pass 'en'. Display only: the digits are the same toFixed(2) /pro always printed, and
   * nothing parses this back (ledgers, payloads and data-* keep the raw "1609.12").
   * A missing or non-numeric amount keeps today's "$" + raw text. Returns the parts so a
   * renderer can put the symbol in its own span; formatMoney joins them. */
  function moneyParts(value, locale) {
    if (locale == null) {
      var html = typeof document !== 'undefined' && document ? document.documentElement : null;
      locale = (html && html.lang) || '';
    }
    var fr = /^fr/i.test(String(locale));
    var n = Number(value);
    var sign = '';
    var digits = String(value == null ? '' : value);
    if (value != null && value !== '' && isFinite(n)) {
      var fixed = n.toFixed(2);
      if (fixed.charAt(0) === '-') {
        fixed = fixed.slice(1);
        if (/[1-9]/.test(fixed)) sign = '-'; // "-0.00" prints as 0.00
      }
      var dot = fixed.indexOf('.');
      // From 1e21 toFixed writes exponent form ("1e+21", "2.5e+22"): written whole, as the old
      // "$" + toFixed(2) did, rather than sliced at a "." that is not a decimal point.
      digits = fixed.indexOf('e') > -1 ? fixed : fixed.slice(0, dot).replace(/\B(?=(\d{3})+$)/g, fr ? '\u00a0' : ',') + (fr ? ',' : '.') + fixed.slice(dot + 1);
    }
    return fr ? { before: sign, digits: digits, after: '\u00a0$' } : { before: sign + '$', digits: digits, after: '' };
  }

  function formatMoney(value, locale) {
    var p = moneyParts(value, locale);
    return p.before + p.digits + p.after;
  }

  /** Markup is applied to the authoritative lineSubtotal, never to a
   * per-unit figure recovered by dividing it back out — that would just
   * be unit-price arithmetic wearing a different hat. */
  function applyMarkupToLineCents(lineCents, qty, markupMode, markupValue) {
    var v = markupValue == null ? 0 : Number(markupValue);
    if (markupMode === 'percent') return Math.round(lineCents * (1 + v / 100));
    if (markupMode === 'fixed_per_tire') return lineCents + Math.round(v * 100) * qty;
    return lineCents; // 'none' or unrecognized
  }

  function installFeeCentsTotal(installFeeMode, installFeeValue, qty) {
    var cents = Math.round((installFeeValue == null ? 0 : Number(installFeeValue)) * 100);
    if (installFeeMode === 'fixed_per_tire') return cents * (qty || 1);
    if (installFeeMode === 'fixed_per_order') return cents;
    return 0;
  }

  /**
   * The dealer-facing quote ledger. `costPrice` is quote.price from the
   * contract — read-only here (only .lineSubtotal and .recyclingFee are
   * ever touched, both already correct for `quantity`). `dealerTerms` is
   * { markupMode, markupValue, installFeeMode, installFeeValue }, either
   * the dealer's saved settings or a temporary per-quote override — this
   * function has no opinion on which, and never writes back to either.
   */
  function buildQuoteLedger(costPrice, quantity, dealerTerms) {
    dealerTerms = dealerTerms || {};
    var qty = quantity || 1;
    var lineCents = toCents(costPrice && costPrice.lineSubtotal);
    var tiresCents = applyMarkupToLineCents(lineCents, qty, dealerTerms.markupMode, dealerTerms.markupValue);
    // Itemised pass-through (dp-7 hard constraint) — never merged into
    // the tires row above, and never adjusted for markup.
    var recyclingCents = toCents(costPrice && costPrice.recyclingFee);
    var installCents = installFeeCentsTotal(dealerTerms.installFeeMode, dealerTerms.installFeeValue, qty);
    // One piece on delivery only. The amount is quote-batch's own figure (10% of the goods
    // line); markup never touches it. Two or more pieces drop it even if a stale amount is still on the price.
    var deliveryCents = qty === 1 ? toCents(costPrice && costPrice.deliverySurcharge) : 0;
    var taxableCents = tiresCents + recyclingCents + installCents + deliveryCents;
    var hstCents = Math.round(taxableCents * ONTARIO_HST_RATE);
    var totalCents = taxableCents + hstCents;
    return {
      tires: fromCents(tiresCents),
      recyclingFee: fromCents(recyclingCents),
      deliveryFee: fromCents(deliveryCents),
      deliveryFeeVisible: deliveryCents > 0,
      installFee: fromCents(installCents),
      // dp-7 hard constraint: a zero install fee hides the whole row,
      // never renders as $0.00 — some shops sell tires with no fitting.
      installFeeVisible: installCents > 0,
      hst: fromCents(hstCents),
      total: fromCents(totalCents)
    };
  }

  /**
   * Tire+wheel package ledger (plan 2026-09-11). Same rules as
   * buildQuoteLedger — markup on each goods line's authoritative
   * lineSubtotal, recycling fee passed through untouched, Ontario HST on
   * the dealer's own invoice — with two owner rulings of its own:
   *   - the wheels get the same markup rule as the tires (result cards
   *     already price any product that way, so the card agrees with them);
   *   - `installAmount` is this package's install line. A new package starts
   *     from the saved install fee (per tire times the quantity, or once per
   *     order). Blank means the dealer cleared it, and the row stays off.
   * The recycling fee only ever comes from the tire's own quote — a wheel
   * has none.
   */
  function buildPackageLedger(tirePrice, rimPrice, quantity, dealerTerms, installAmount) {
    dealerTerms = dealerTerms || {};
    var qty = quantity || 1;
    var tiresCents = applyMarkupToLineCents(toCents(tirePrice && tirePrice.lineSubtotal), qty, dealerTerms.markupMode, dealerTerms.markupValue);
    var wheelsCents = applyMarkupToLineCents(toCents(rimPrice && rimPrice.lineSubtotal), qty, dealerTerms.markupMode, dealerTerms.markupValue);
    var recyclingCents = toCents(tirePrice && tirePrice.recyclingFee);
    var typed = Number(installAmount);
    var installCents = isFinite(typed) && typed > 0 ? Math.round(typed * 100) : 0;
    var taxableCents = tiresCents + wheelsCents + recyclingCents + installCents;
    var hstCents = Math.round(taxableCents * ONTARIO_HST_RATE);
    return {
      tires: fromCents(tiresCents),
      wheels: fromCents(wheelsCents),
      recyclingFee: fromCents(recyclingCents),
      installFee: fromCents(installCents),
      installFeeVisible: installCents > 0,
      hst: fromCents(hstCents),
      total: fromCents(taxableCents + hstCents)
    };
  }

  /**
   * Round 5 (owner ruling, 2026-09-08) — what a result card may show: the
   * dealer's own price, i.e. Toee's cost with the dealer's markup applied
   * by the same rule the quote ledger uses. Never the cost: the dealer
   * works this screen with the car owner beside them. Returns null for a
   * price that is not an authoritative figure (guest / call), and
   * { configured: false } while the markup is still undecided so the card
   * shows a prompt instead of any number.
   */
  function buildSellingPrice(priceView, quantity, settings) {
    if (!priceView || priceView.state !== 'ok') return null;
    if (!settings || !settings.markupConfigured) return { configured: false };
    var qty = quantity || 1;
    var unitCents = toCents(priceView.unit);
    var lineCents = priceView.lineSubtotal ? toCents(priceView.lineSubtotal) : unitCents * qty;
    return {
      configured: true,
      unit: fromCents(applyMarkupToLineCents(unitCents, 1, settings.markupMode, settings.markupValue)),
      batch: fromCents(applyMarkupToLineCents(lineCents, qty, settings.markupMode, settings.markupValue))
    };
  }

  var CANADIAN_PROVINCE_CODES = ['ON', 'QC', 'BC', 'AB', 'MB', 'SK', 'NS', 'NB', 'PE', 'NL', 'YT', 'NT', 'NU'];

  function detectProvinceCode(address) {
    var re = new RegExp('\\b(' + CANADIAN_PROVINCE_CODES.join('|') + ')\\b', 'i');
    var m = re.exec(String(address || ''));
    return m ? m[1].toUpperCase() : null;
  }

  /**
   * dp-7 acceptance: "省外地址拒绝出卡" — buildQuoteLedger hardcodes the
   * Ontario HST rate, so a shop address outside Ontario would get a
   * silently wrong tax line. An address with no recognizable province
   * code (most free-text entries) is let through rather than blocked on
   * formatting this can't parse — only an explicit non-ON code refuses.
   */
  function isQuoteCardEligible(address) {
    var code = detectProvinceCode(address);
    return !code || code === 'ON';
  }

  /** dp-7 acceptance: validity is a clock time derived from the promise's
   * countdownSeconds, never an arbitrary number of days. */
  function computeQuoteValidityLabel(countdownSeconds, nowMs) {
    var seconds = typeof countdownSeconds === 'number' ? countdownSeconds : 0;
    if (seconds <= 0) return '';
    var d = new Date((nowMs == null ? Date.now() : nowMs) + seconds * 1000);
    var hh = d.getHours();
    var mm = d.getMinutes();
    return (hh < 10 ? '0' + hh : '' + hh) + ':' + (mm < 10 ? '0' + mm : '' + mm);
  }

  /**
   * Top-level view model for the canvas-rendered quote card. `overrides`
   * is the quote screen's "temporary, for this quote only" panel
   * (dp-7-quote-card-render.md "临时改价") — read here and never written
   * back into `settings`, so a favour for one customer never changes the
   * dealer's saved defaults.
   */
  function buildQuoteCardViewModel(quote, settings, overrides, ownerDetails, opts) {
    quote = quote || {};
    settings = settings || settingsDefaults();
    overrides = overrides || {};
    ownerDetails = ownerDetails || {};
    opts = opts || {};

    if (!isQuoteCardEligible(settings.address)) {
      return { eligible: false, reason: 'out_of_province' };
    }

    var terms = {
      markupMode: overrides.markupMode || settings.markupMode,
      markupValue: overrides.markupValue != null ? overrides.markupValue : settings.markupValue,
      installFeeMode: overrides.installFeeMode || settings.installFeeMode,
      installFeeValue: overrides.installFeeValue != null ? overrides.installFeeValue : settings.installFeeValue
    };
    // Final review finding #1 (CRITICAL) — the rendered quantity MUST be
    // the exact quantity quote.price was fetched for (ProSearch#_search
    // stamps this on at fetch time). It is never re-derived from
    // settings.defaultQuantity or an override here: those are a second,
    // independent source that can silently disagree with what the price
    // is actually authoritative for — which is exactly how a card used to
    // show "4 pcs" priced off a 1-tire lineSubtotal. quote.requestedQuantity
    // is the one and only source; falling back to 1 only covers a quote
    // object that was never run through a real fetch (e.g. this file's own
    // CARD_PREVIEW_SAMPLE_QUOTE never touches the network either).
    var quantity = quote.requestedQuantity || 1;
    var ledger = buildQuoteLedger(quote.price, quantity, terms);
    var promiseView = buildPromiseView(quote.promise, quote.errors);
    // A promise asked at another quantity (stepper, offline answer) promises nothing for this one.
    var promiseFresh = promiseAnswersQuantity(quote);
    var lines = Array.isArray(quote.lines) && quote.lines.length
      ? quote.lines
      : [{ axle: 'front', title: quote.title || '', quantity: quantity }];

    return {
      eligible: true,
      labels: CARD_LABELS,
      shopName: settings.shopName || '',
      phone: settings.phone || '',
      // Card redesign — the shop's address and hours were already in
      // settings (address also gates Ontario eligibility above) but never
      // drawn; a car owner holding the quote needs both.
      address: settings.address || '',
      businessHours: formatBusinessHours(settings.businessHours),
      title: quote.title || '',
      quantity: quantity,
      lines: lines,
      tires: ledger.tires,
      recyclingFee: ledger.recyclingFee,
      deliveryFee: ledger.deliveryFee,
      deliveryFeeVisible: ledger.deliveryFeeVisible,
      installFee: ledger.installFee,
      installFeeVisible: ledger.installFeeVisible,
      hst: ledger.hst,
      total: ledger.total,
      inStock: !!(quote.stock && quote.stock.anyAvailable) && !quoteShortfall(quote),
      sameDay: promiseFresh && promiseView.showSameDayBadge,
      validUntil: promiseFresh ? computeQuoteValidityLabel(promiseView.countdownSeconds, opts.now) : '',
      owner: {
        name: ownerDetails.name || '',
        plate: ownerDetails.plate || '',
        vehicleModel: ownerDetails.vehicleModel || ''
      },
      vehicleLine: vehicleCardLine(opts.vehicleCard),
      detailLine: quoteDetailLine(opts.vehicleDetail)
    };
  }

  function vehicleCardLine(card) {
    var api = loadedVehicleApi();
    if (!card || !api || !api.quoteCardVehicleLine) return '';
    // Older saved quotes have no tire size. The session line always prints a size,
    // so those cards keep the name they were stored with.
    if (!card.tireSize) {
      var name = [card.year, card.makeName || card.make, card.modelName || card.model].filter(Boolean).join(' ');
      return [card.vin, name].filter(Boolean).join(' · ');
    }
    return api.quoteCardVehicleLine(card);
  }

  function quoteDetailLine(vehicle) {
    if (!vehicle) return '';
    var sizes = vehicle.tireSize
      ? (vehicle.rearTireSize ? vehicle.tireSize + ' / ' + vehicle.rearTireSize : vehicle.tireSize)
      : '';
    var market = vehicle.market === 'US' ? 'US' : (vehicle.market === 'CA' ? 'CA' : '');
    return [sizes, market, vehicle.boltPattern, vehicle.hubBoreMm != null ? vehicle.hubBoreMm + ' mm' : ''].filter(Boolean).join(' · ');
  }

  /**
   * The package counterpart of buildQuoteCardViewModel — same eligibility
   * gate, same shop/owner block, one extra title and one extra money row.
   * `pkgQuote` is ToeeProPackage.buildPackageQuote()'s output; both halves
   * were priced at pkgQuote.requestedQuantity (the package bar refuses to
   * build one otherwise), which is the only quantity used here.
   */
  function buildPackageQuoteCardViewModel(pkgQuote, settings, installAmount, ownerDetails, opts) {
    pkgQuote = pkgQuote || {};
    settings = settings || settingsDefaults();
    ownerDetails = ownerDetails || {};
    opts = opts || {};
    if (!isQuoteCardEligible(settings.address)) {
      return { eligible: false, reason: 'out_of_province' };
    }
    var tire = pkgQuote.tire || {};
    var rim = pkgQuote.rim || {};
    var quantity = pkgQuote.requestedQuantity || 1;
    var ledger = buildPackageLedger(tire.price, rim.price, quantity, { markupMode: settings.markupMode, markupValue: settings.markupValue }, installAmount);
    var tirePromise = buildPromiseView(tire.promise, tire.errors);
    var rimPromise = buildPromiseView(rim.promise, rim.errors);
    // The package is only as fast as its slower half, and only valid as long as its shorter window.
    var windows = [tirePromise.countdownSeconds, rimPromise.countdownSeconds].filter(function (s) {
      return typeof s === 'number' && s > 0;
    });
    // A half whose promise was asked at another quantity promises nothing for the package.
    var promiseFresh = promiseAnswersQuantity(pkgQuote);
    return {
      eligible: true,
      labels: CARD_LABELS,
      shopName: settings.shopName || '',
      phone: settings.phone || '',
      address: settings.address || '',
      businessHours: formatBusinessHours(settings.businessHours),
      title: tire.title || '',
      wheelTitle: rim.title || '',
      quantity: quantity,
      tires: ledger.tires,
      wheels: ledger.wheels,
      recyclingFee: ledger.recyclingFee,
      installFee: ledger.installFee,
      installFeeVisible: ledger.installFeeVisible,
      hst: ledger.hst,
      total: ledger.total,
      inStock: !!(tire.stock && tire.stock.anyAvailable && rim.stock && rim.stock.anyAvailable) && !quoteShortfall(pkgQuote),
      sameDay: promiseFresh && !!(tirePromise.showSameDayBadge && rimPromise.showSameDayBadge),
      validUntil: promiseFresh && windows.length ? computeQuoteValidityLabel(Math.min.apply(null, windows), opts.now) : '',
      owner: {
        name: ownerDetails.name || '',
        plate: ownerDetails.plate || '',
        vehicleModel: ownerDetails.vehicleModel || ''
      },
      vehicleLine: vehicleCardLine(opts.vehicleCard)
    };
  }

  var OWNER_STORAGE_KEY = 'toee-pro-quote-owner';

  /** PRD §5.3.4.2 / §8.6 — owner name/plate/vehicle model live ONLY here,
   * same read/write-swallows-errors shape as settings storage above. No
   * function anywhere in this file reads this key into a fetch body. */
  function readOwnerDetails(storage) {
    try {
      var store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!store) return null;
      var raw = store.getItem(OWNER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeOwnerDetails(details, storage) {
    try {
      var store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!store) return;
      store.setItem(OWNER_STORAGE_KEY, JSON.stringify(details));
    } catch (e) {
      // Same rationale as writeLocalSettings — never block the dealer.
    }
  }

  var OWNER_HISTORY_STORAGE_KEY = 'toee-pro-quote-owner-history';
  var OWNER_HISTORY_LIMIT = 15; // mirrors dealer-pro's GET /pro/quotes RECENT_QUOTES_LIMIT — the
  // list can never show more than this many rows, so the local store never needs more either.

  /** PRD §5.3.4.2/§8.6 — same local-only guarantee as readOwnerDetails/
   * writeOwnerDetails above: a quoteId-keyed snapshot of owner name/vehicle
   * model, so the Recent Quotes list can show "whose car" without ever
   * sending owner data anywhere. No function here ever calls fetch. */
  /** The stored snapshot for a quote exactly as saved, or null. */
  function findOwnerHistoryEntry(quoteId, storage) {
    if (!quoteId) return null;
    try {
      var store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!store) return null;
      var raw = store.getItem(OWNER_HISTORY_STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].quoteId === quoteId) return list[i];
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function readOwnerHistorySnapshot(quoteId, storage) {
    var found = findOwnerHistoryEntry(quoteId, storage);
    return found ? { name: found.name || '', vehicleModel: found.vehicleModel || '' } : null;
  }

  function recordOwnerHistorySnapshot(quoteId, owner, storage) {
    if (!quoteId) return;
    owner = owner || {};
    var name = owner.name || '';
    var vehicleModel = owner.vehicleModel || '';
    // The plate is kept for "Find a quote" only: the list shows name and vehicle, never the plate.
    var plate = owner.plate || '';
    if (!name && !vehicleModel && !plate) return;
    try {
      var store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!store) return;
      var raw = store.getItem(OWNER_HISTORY_STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      list = list.filter(function (entry) { return entry.quoteId !== quoteId; });
      list.push({ quoteId: quoteId, name: name, vehicleModel: vehicleModel, plate: plate });
      if (list.length > OWNER_HISTORY_LIMIT) list = list.slice(list.length - OWNER_HISTORY_LIMIT);
      store.setItem(OWNER_HISTORY_STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Same rationale as writeOwnerDetails — never block the dealer.
    }
  }

  /** Recent-quotes-list-only presentation of a Task-1 snapshot — same
   * [name, vehicleModel].filter(Boolean).join(' · ') convention
   * drawQuoteCard's own "Prepared for" footer already uses, so the
   * list never invents a second way to say the same thing. */
  function ownerHistoryLabel(quoteId, storage) {
    var snap = readOwnerHistorySnapshot(quoteId, storage);
    if (!snap) return '';
    return [snap.name, snap.vehicleModel].filter(Boolean).join(' · ');
  }

  /** Everything the dealer typed about the owner of a quote (name, plate, vehicle) as one
   * string, for the Recent quotes search box. Local only, like the label above. */
  function ownerHistorySearchText(quoteId, storage) {
    var found = findOwnerHistoryEntry(quoteId, storage);
    return found ? [found.name, found.plate, found.vehicleModel].filter(Boolean).join(' ') : '';
  }

  // ---------------------------------------------------------------------
  // DP-8 — four quote-card colour themes (DESIGN.md "Dealer-Branded
  // Surfaces", {quote-card-themes}). The concrete hex for each theme lives
  // in exactly one place — the `:root` custom properties defined in
  // sections/toee-pro-quote.liquid — and is never duplicated here. This
  // file only ever knows the CSS custom property NAME for a theme;
  // resolveCardThemeAccent reads its computed value through an injected
  // getter (real callers back it with getComputedStyle against
  // document.documentElement, since the tokens are defined at `:root` and
  // so are readable from either dealer-pro screen on the page; tests
  // inject a fake getter) — the same dependency-injection shape this file
  // already uses for storage/fetch (readLocalSettings, pushSettings).
  // ---------------------------------------------------------------------

  var CARD_THEME_ACCENT_VAR = {
    graphite: '--tt-pro-card-theme-graphite',
    forest: '--tt-pro-card-theme-forest',
    oxblood: '--tt-pro-card-theme-oxblood',
    bronze: '--tt-pro-card-theme-bronze'
  };

  /** Falls back to graphite for a missing/unrecognized theme — dp-8: the
   * default when nothing is stored is graphite, same as settingsDefaults(). */
  function cardThemeAccentVarName(theme) {
    return CARD_THEME_ACCENT_VAR[theme] || CARD_THEME_ACCENT_VAR.graphite;
  }

  /** `getCssVar` is `(name) => string`. Never returns a literal accent
   * value that wasn't first resolved through the named token. */
  function resolveCardThemeAccent(theme, getCssVar) {
    var value = getCssVar(cardThemeAccentVarName(theme));
    var trimmed = value ? String(value).trim() : '';
    return trimmed || undefined;
  }

  // A fixed, representative sample quote — enough for a real drawQuoteCard
  // pass, so the settings-screen theme preview renders an actual card (PRD
  // §5.7.1: "the preview is the feature"), never a static swatch. Never
  // sent anywhere; exists purely to feed the canvas renderer with
  // plausible numbers while the dealer is picking a colour, independent of
  // whatever he's actually searched for.
  var CARD_PREVIEW_SAMPLE_QUOTE = {
    title: '225/60R17 SAMPLE TIRE 79H',
    stock: { warehouses: [{ warehouseLabel: 'Sample Warehouse', quantity: 4 }], anyAvailable: true },
    promise: { status: 'same_day_available', promiseWindow: 'same_day', countdownSeconds: 3600 },
    price: { lineSubtotal: '320.00', recyclingFee: '17.00' },
    errors: [],
    // This sample never goes through a real fetch, so there is no real
    // "fetched quantity" to bind to — 4 here is just a representative
    // sample count, same rationale as the rest of this fixture.
    requestedQuantity: 4,
    // ...and its sample promise reads as one asked at that count, so the preview shows Same Day.
    promiseQuantity: 4
  };

  /** The settings-screen live preview view model (dp-8) — the exact same
   * builder DP-7 uses for a real quote, fed a fixed sample quote instead so
   * the card shows something real before the dealer has searched anything.
   * Carries the dealer's actual saved shop name/phone/terms — never
   * placeholder text for those, per PRD §5.7.1 ("a dealer cannot judge a
   * theme without seeing his own shop name on it"). */
  function buildCardPreviewViewModel(settings) {
    return buildQuoteCardViewModel(CARD_PREVIEW_SAMPLE_QUOTE, settings, {}, {}, {});
  }

  var HISTORICAL_DATE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** UTC, not local time — deterministic in tests regardless of the
   * machine's timezone, and the canvas card has no timezone context of
   * its own to be "more correct" about anyway. */
  function formatHistoricalQuoteDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return HISTORICAL_DATE_MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
  }

  /** One title for a history row or reopened card: a package reads
   * "<tire> + <wheel>" (plan 2026-09-11); a tire quote is unchanged. */
  function historyTitle(entry) {
    entry = entry || {};
    var title = entry.title || CARD_LABELS.unavailableTitle;
    return entry.rimTitle ? title + ' + ' + entry.rimTitle : title;
  }

  // ---------------------------------------------------------------------
  // Recent quotes drawer: the pure parts (rollout Task 9; ProQuoteHistoryPanel
  // below does the DOM). English fallbacks only: the page's own wording is
  // dealer_pro.quote_history.*, handed down by Liquid as data-i18n-history-<key>
  // on the drawer root (key in kebab case: countOne -> data-i18n-history-count-one,
  // locale quote_history.count_one). An older cached page without an attribute
  // still gets English, never an empty label. The rest of the drawer's text is
  // plain markup rendered by Liquid and is not repeated here.
  // ---------------------------------------------------------------------

  var QUOTE_HISTORY_TEXT = {
    countOne: '{n} quote',
    countOther: '{n} quotes',
    chipAll: 'All',
    chipTire: 'Tire',
    chipPackage: 'Package',
    chipOrdered: 'Ordered',
    dayToday: 'Today',
    dayYesterday: 'Yesterday',
    // The locale the short date and the time are written in (Intl): en-US gives "Oct 14" and "2:14 PM".
    intlLocale: 'en-US',
    tagPackage: 'Package',
    tagFrontRear: 'Front / rear',
    noMatch: 'No quotes match “{q}”.',
    limitNote: 'Showing the latest {n} quotes'
  };

  /** The drawer's strings, read once from the root's data-i18n-history-* attributes. */
  function quoteHistoryText(root) {
    var t = {};
    Object.keys(QUOTE_HISTORY_TEXT).forEach(function (key) {
      var attr = 'data-i18n-history-' + key.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); });
      t[key] = (root && root.getAttribute && root.getAttribute(attr)) || QUOTE_HISTORY_TEXT[key];
    });
    return t;
  }

  /** A Date from an ISO string; null when there is none or it is not a date. */
  function historyDate(iso) {
    if (!iso) return null;
    var d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  function historyDayStart(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** "Today", "Yesterday", then a short date ("Oct 14", "14 oct." in French), with the year
   * once it is not this year's. Days are the dealer's own calendar days, not 24-hour blocks. */
  function historyDayLabel(date, now, t) {
    if (!date || isNaN(date.getTime())) return '';
    var days = Math.round((historyDayStart(now).getTime() - historyDayStart(date).getTime()) / 86400000);
    if (days <= 0) return t.dayToday;
    if (days === 1) return t.dayYesterday;
    var withYear = date.getFullYear() !== now.getFullYear();
    try {
      return date.toLocaleDateString(t.intlLocale, withYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' });
    } catch (e) {
      // No Intl, or a locale tag it does not know: English month names.
      return HISTORICAL_DATE_MONTHS[date.getMonth()] + ' ' + date.getDate() + (withYear ? ', ' + date.getFullYear() : '');
    }
  }

  /** "2:14 PM" in English, "14 h 14" in French: the dealer's local time. */
  function historyTimeLabel(date, t) {
    if (!date || isNaN(date.getTime())) return '';
    try {
      return date.toLocaleTimeString(t.intlLocale, { hour: 'numeric', minute: '2-digit' });
    } catch (e) {
      return date.getHours() + ':' + ('0' + date.getMinutes()).slice(-2);
    }
  }

  /** The order a quote carries, by the same rule rememberPlacedOrder uses:
   * null (none), or { status: 'ordered' | 'cancelled', number: string }. */
  function historyOrderState(entry) {
    entry = entry || {};
    var number = entry.orderNumber == null || entry.orderNumber === '' ? '' : String(entry.orderNumber);
    if (!entry.orderedAt && !number) return null;
    return { status: entry.orderStatus === 'cancelled' ? 'cancelled' : 'ordered', number: number };
  }

  /** The title a history row shows: "<tire> + <wheel>" for a package, "<front> + <rear>" for a
   * front / rear quote, the tire alone otherwise. */
  function historyRowTitle(entry) {
    var title = historyTitle(entry);
    if (entry && entry.rearVariantId) {
      title += ' + ' + (entry.rearTitle || entry.rearProductTitle || CARD_LABELS.unavailableTitle);
    }
    return title;
  }

  /** What "x 4" says on a row: "× 4" for tires, "2 + 2" for front / rear, "4 + 4" for tires + wheels. */
  function historyQuantityText(entry, isPackage) {
    if (entry.rearVariantId) return entry.qty + ' + ' + entry.rearQty;
    if (isPackage) return entry.qty + ' + ' + entry.qty;
    return '× ' + entry.qty;
  }

  /** Lower case with the accents taken off, so "jose" finds "José". */
  function foldHistoryText(text) {
    var s = String(text == null ? '' : text).toLowerCase();
    if (typeof s.normalize === 'function') s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s;
  }

  /** Letters and digits only, so "abcd123" finds the plate "ABCD 123" and "20555" finds "205/55R16". */
  function squashHistoryText(text) {
    return text.replace(/[^a-z0-9]/g, '');
  }

  /**
   * View-model for a REOPENED historical quote (design spec: dealer-pro's
   * docs/superpowers/specs/2026-09-09-dealer-pro-quote-history-design.md).
   * `entry` is one row from GET /pro/quotes: { quoteId, variantId, title,
   * qty, dealerTotal, createdAt }. Deliberately flat — no ledger fields —
   * see this file's header comment in tests/toee-pro-quote-history.test.js
   * for why recomputing one from current settings would be dishonest.
   */
  function buildHistoricalQuoteCardViewModel(entry, settings) {
    entry = entry || {};
    settings = settings || settingsDefaults();
    var lines = [{ axle: 'front', title: entry.title || CARD_LABELS.unavailableTitle, quantity: entry.qty || 0 }];
    if (entry.rearVariantId) {
      lines.push({
        axle: 'rear',
        title: entry.rearTitle || entry.rearProductTitle || CARD_LABELS.unavailableTitle,
        quantity: entry.rearQty || 0
      });
    }
    return {
      labels: CARD_LABELS,
      shopName: settings.shopName || '',
      phone: settings.phone || '',
      address: settings.address || '',
      businessHours: formatBusinessHours(settings.businessHours),
      title: historyTitle(entry),
      quantity: entry.qty || 0,
      lines: entry.rearVariantId ? lines : undefined,
      total: entry.dealerTotal || '0.00',
      quotedAt: formatHistoricalQuoteDate(entry.createdAt),
      vehicleLine: vehicleCardLine(entry.vehicle),
      detailLine: quoteDetailLine(entry.vehicle)
    };
  }

  // ---------------------------------------------------------------------
  // DP-9 — share the quote card (dp-9-share-sheet.md). One system share
  // sheet via navigator.share({files, text}), degrading to a manual save
  // only when navigator.canShare({files}) says files can't be shared — a
  // capability check, never a user-agent sniff (dp-9 acceptance: "代码中无
  // UA嗅探"). buildShareText is pulled from the exact same view model
  // buildQuoteCardViewModel already built for the canvas card, so the
  // copied text and the rendered card can never drift apart.
  // ---------------------------------------------------------------------

  /** True only when the browser itself says it can share a file of this
   * shape — never inferred by sniffing which browser or device this is.
   * A throwing canShare (some browsers throw rather than return false for
   * an unsupported shape) degrades the same way a missing canShare does:
   * to false, i.e. the save-image fallback. */
  function canShareFiles(nav, file) {
    try {
      return !!(nav && typeof nav.canShare === 'function' && nav.canShare({ files: [file] }));
    } catch (e) {
      return false;
    }
  }

  /** dp-9 acceptance: "复制文案...含品牌/规格/数量/总价/店名/电话" — every
   * one of those pulled from the same vm buildQuoteCardViewModel already
   * built for the canvas card, never re-derived from the raw quote. Validity
   * wording reuses CARD_LABELS.validUntil verbatim; the quantity-unit and
   * same-day phrasing are this function's own literals, named on
   * SHARE_TEXT_LABELS above — same hardcoded-English exemption as
   * CARD_LABELS (this text goes straight to the car owner, not through this
   * screen's own EN/FR chrome — see tests/toee-pro-quote-locale.test.js's
   * file header), just not identical wording to what the card itself draws. */
  function buildShareText(vm) {
    vm = vm || {};
    var parts = [];
    if (Array.isArray(vm.lines) && vm.lines.length > 1) {
      vm.lines.forEach(function (line) {
        if (line.title) parts.push(line.title);
        if (line.quantity) parts.push(line.quantity + ' ' + SHARE_TEXT_LABELS.quantityUnit);
      });
    } else {
      if (vm.title) parts.push(vm.wheelTitle ? vm.title + ' + ' + vm.wheelTitle : vm.title);
      if (vm.quantity) parts.push(vm.quantity + ' ' + SHARE_TEXT_LABELS.quantityUnit);
    }
    if (vm.vehicleLine) parts.push(vm.vehicleLine);
    if (vm.total) parts.push(formatMoney(vm.total, 'en')); // English, like the card it goes with
    // 2026-09-09 fix (final review finding) — a historical vm's only
    // distinguishing signal is quotedAt (buildHistoricalQuoteCardViewModel
    // is the only builder that ever sets it); a live vm never has it, so
    // this is mutually exclusive with the sameDay/validUntil branch
    // below, same as those two already are with each other. Without
    // this, "copy text"/the share-sheet caption for a reopened quote
    // reads identically to a fresh one — the one thing this feature
    // exists to disclose would never reach a dealer who shares the text
    // without the image.
    if (vm.quotedAt) {
      parts.push(CARD_LABELS.historicalBadge);
    } else if (vm.sameDay) {
      parts.push(SHARE_TEXT_LABELS.readyToday);
    } else if (vm.validUntil) {
      parts.push(CARD_LABELS.validUntil + ' ' + vm.validUntil);
    }
    var line = parts.join(' · ');
    var contact = [vm.shopName, vm.phone].filter(Boolean).join(' ');
    if (contact) line += (line ? ' — ' : '') + contact;
    return line;
  }

  /** The capability branch itself (dp-9 acceptance: "降级用能力检测" and
   * "分享失败或被用户取消时不报错、不重复弹窗"). A cancelled or failed
   * share is swallowed silently — it is the dealer's own choice, not a
   * fault — and never falls through to the save-image path, which would
   * surprise a dealer who deliberately dismissed the sheet. */
  function shareQuoteCard(nav, file, shareText, callbacks) {
    callbacks = callbacks || {};
    if (canShareFiles(nav, file)) {
      return Promise.resolve(nav.share({ files: [file], text: shareText }))
        .then(function () {
          if (callbacks.onShared) callbacks.onShared();
        })
        .catch(function () {
          // Cancelled (AbortError) or failed — either way, silent, and
          // never a fallback to save-image; the dealer already chose the
          // system sheet.
        });
    }
    if (callbacks.onSaveFallback) callbacks.onSaveFallback();
    return Promise.resolve();
  }

  /**
   * 2026-09-09 — one dealerProEvent per action on a generated quote card
   * (generating it, sharing it, saving it, copying its text), all carrying
   * the same quoteId so the app side can group them (dealerProEventService's
   * (shop, quoteId, type) dedupe). Pure shape-building only — see
   * postDealerProEvent for the network call.
   */
  function buildDealerProEventBody(type, quoteId, quote, vm, vehicle) {
    var body = {
      type: type,
      quoteId: quoteId,
      variantId: (quote && quote.variantId) || '',
      qty: (vm && vm.quantity) || 0
    };
    if (vm && vm.total != null) body.dealerTotal = String(vm.total);
    if (quote && quote.rimVariantId) body.rimVariantId = String(quote.rimVariantId);
    if (quote && quote.rimVariantId && vm && vm.installFee != null && vm.installFee !== '') {
      body.installAmount = String(vm.installFee);
    }
    if (quote && quote.rearVariantId) {
      body.qty = quote.frontQty || body.qty;
      body.rearVariantId = quote.rearVariantId;
      body.rearQty = quote.rearQty;
    }
    if (vehicle) body.vehicle = vehicle;
    return body;
  }

  /**
   * Fire-and-forget, same "telemetry must never break what it measures"
   * contract as recordDealerProSearchEvent on the app side: never throws,
   * never surfaces a rejection. `fetchImpl` is injectable the same way as
   * pushSettings' own fetchImpl — real callers get the real global fetch.
   */
  function postDealerProEvent(type, quoteId, quote, vm, fetchImpl) {
    var f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!f || !quoteId) return;
    var vehicle = type === 'quote' ? activeVehicleController().quoteVehicle() : null;
    try {
      f(proProxyUrl('/event'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildDealerProEventBody(type, quoteId, quote, vm, vehicle))
      }).catch(function () {
        // Offline, guest, or the request failed — never surfaced to the dealer.
      });
    } catch (e) {
      // Same rationale — e.g. no usable fetch/URL in this environment.
    }
  }

  // ---------------------------------------------------------------
  // One vehicle for this Dealer Pro session (Tires, Wheels, Quotes).
  // It stays until Clear vehicle. A typed size, brand, SKU, or wheel spec
  // does not drop it. The record is toee-pro-vehicle on this browser.
  // State lives in assets/toee-pro-vehicle.js.
  // ---------------------------------------------------------------
  function loadedVehicleApi() {
    if (typeof ToeeProVehicle !== 'undefined') return ToeeProVehicle;
    if (typeof require === 'function') {
      try { return require('./toee-pro-vehicle.js'); } catch (e) { return null; }
    }
    return null;
  }

  function vehicleReducer() {
    var api = loadedVehicleApi();
    if (api && api.nextVehicleState) return api.nextVehicleState;
    return function (state) { return state || {}; };
  }

  function vehicleChoiceBody(chosen) {
    if (!chosen) return null;
    var api = loadedVehicleApi();
    if (api && api.vehicleChoiceBody) return api.vehicleChoiceBody(chosen);
    return {
      vin: chosen.vin == null ? null : chosen.vin,
      year: chosen.year,
      make: chosen.make,
      model: chosen.model,
      market: chosen.market,
      tireSize: chosen.tireSize,
      rearTireSize: chosen.rearTireSize == null ? null : chosen.rearTireSize,
      boltPattern: chosen.boltPattern,
      hubBoreMm: chosen.hubBoreMm == null ? null : chosen.hubBoreMm
    };
  }

  function searchKindForQuery(query) {
    if (parseSizeQuery(String(query || ''))) return 'size';
    return /\d/.test(String(query || '')) ? 'sku' : 'brand';
  }

  function vehicleQuery(params) {
    return Object.keys(params)
      .filter(function (key) { return params[key] != null && String(params[key]) !== ''; })
      .map(function (key) { return encodeURIComponent(key) + '=' + encodeURIComponent(String(params[key])); })
      .join('&');
  }

  function afterFitmentPicked(form, chosen) {
    var pkg = form && form._proPackage;
    if (pkg && pkg.wheelPath === 'vehicle' && pkg.pkg && pkg.pkg.tire && typeof pkg.useChosenVehicle === 'function') {
      pkg.useChosenVehicle();
      return true;
    }
    return false;
  }

  function createVehicleController(opts) {
    opts = opts || {};
    var reduce = vehicleReducer();
    var fetchImpl = opts.fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
    var vehicleApi = loadedVehicleApi();
    var browserStore = null;
    try {
      if (typeof localStorage !== 'undefined') browserStore = localStorage;
    } catch (e) {
      browserStore = null;
    }
    var carry = vehicleApi && vehicleApi.createCarryStore ? vehicleApi.createCarryStore(browserStore) : null;
    var controller = {
      state: carry ? carry.readState() : {
        options: null, fitments: [], chosen: null, card: null, detail: null,
        vin: null, year: null, make: null, model: null, trim: null,
        yearName: null, makeName: null, modelName: null, trimName: null, chosenIndex: null
      }
    };

    function dispatch(event) {
      controller.state = reduce(controller.state, event);
      if (carry) {
        if (event && event.type === 'clearVehicle') carry.clear();
        else carry.save(controller.state);
      }
      if (opts.onChange) opts.onChange(controller.state, event);
      return controller.state;
    }
    function getJson(segment) {
      if (!fetchImpl) return Promise.reject(new Error('vehicle: no fetch'));
      return fetchImpl(proProxyUrl(segment), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); });
    }

    controller.loadOptions = function (params) {
      params = params || {};
      var query = vehicleQuery({ year: params.year, make: params.make, model: params.model });
      return getJson('/vehicle/options?' + query).then(function (body) {
        var options = body && body.success && Array.isArray(body.options) ? body.options : [];
        dispatch({ type: 'options', options: options });
        return options;
      });
    };

    function listFitments(vehicle, vin) {
      vehicle = vehicle || {};
      var query = vehicleQuery({ year: vehicle.year, make: vehicle.make, model: vehicle.model, trim: vehicle.trim });
      return getJson('/vehicle/fitment?' + query).then(function (body) {
        return dispatch({
          type: 'listFitments',
          value: {
            vin: vin == null ? null : vin,
            year: vehicle.year || null,
            make: vehicle.make || null,
            model: vehicle.model || null,
            trim: vehicle.trim || null,
            yearName: vehicle.yearName || null,
            makeName: vehicle.makeName || null,
            modelName: vehicle.modelName || null,
            trimName: vehicle.trimName || null,
            fitments: body && body.success && Array.isArray(body.fitments) ? body.fitments : []
          }
        });
      });
    }

    controller.findFitments = function (vehicle) {
      return listFitments(vehicle || {}, null);
    };

    controller.decodeVin = function (rawVin) {
      var vin = String(rawVin || '').trim().toUpperCase();
      return getJson('/vehicle/vin?' + vehicleQuery({ vin: vin })).then(function (body) {
        if (!body || !body.success || !body.vehicle) {
          return dispatch({
            type: 'listFitments',
            value: {
              vin: null, year: null, make: null, model: null, trim: null,
              yearName: null, makeName: null, modelName: null, trimName: null, fitments: []
            }
          });
        }
        return listFitments(body.vehicle, vin);
      });
    };

    controller.pickFitment = function (index, pickOpts) {
      dispatch({ type: 'pickFitment', index: index });
      var chosen = controller.state.chosen;
      if (!chosen) return Promise.resolve(null);
      var source = pickOpts && pickOpts.source;
      var search = true;
      if (source && vehicleApi && vehicleApi.tireSearchOnPick) search = vehicleApi.tireSearchOnPick(source);
      else if (pickOpts && pickOpts.search === false) search = false;
      var posted = fetchImpl
        ? Promise.resolve(fetchImpl(proProxyUrl('/vehicle/choice'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(vehicleChoiceBody(chosen))
        })).catch(function () {})
        : Promise.resolve();
      return posted.then(function () {
        if (search && opts.onSearchSize) opts.onSearchSize(chosen);
        return chosen;
      });
    };

    controller.clearVehicle = function () {
      return dispatch({ type: 'clearVehicle' });
    };

    controller.typedSearch = function (query) {
      return dispatch({ type: 'clearForSearch', kind: searchKindForQuery(query) });
    };

    controller.wheelSpecChanged = function () {
      return dispatch({ type: 'clearForSearch', kind: 'wheel-spec' });
    };

    controller.readPhoto = function (imageBase64) {
      if (!fetchImpl) return Promise.reject(new Error('vehicle: no fetch'));
      return fetchImpl(proProxyUrl('/vehicle/vin-photo'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: imageBase64 })
      }).then(function (r) { return r.json(); }).then(function (body) {
        if (!body || !body.vin) {
          return dispatch({
            type: 'listFitments',
            value: {
              vin: null, year: null, make: null, model: null, trim: null,
              yearName: null, makeName: null, modelName: null, trimName: null, fitments: []
            }
          });
        }
        return controller.decodeVin(body.vin);
      });
    };

    controller.packageWheels = function (mode, tireDiameter, wheels) {
      var api = loadedVehicleApi();
      if (!api || !api.selectPackageWheels) return [];
      var chosen = controller.state.chosen || {};
      var fitment = (controller.state.fitments || []).filter(function (row) {
        return row.boltPattern === chosen.boltPattern && row.front && row.front.tireSize === chosen.tireSize;
      })[0] || { boltPattern: chosen.boltPattern, hubBoreMm: chosen.hubBoreMm, front: {} };
      return api.selectPackageWheels({
        mode: mode,
        tireDiameter: tireDiameter,
        fitment: fitment,
        wheels: wheels
      });
    };

    controller.quoteVehicle = function () {
      return vehicleChoiceBody(controller.state.chosen);
    };

    return controller;
  }

  var currentVehicleController = null;
  function activeVehicleController() {
    if (!currentVehicleController) currentVehicleController = createVehicleController();
    return currentVehicleController;
  }
  function useVehicleController(controller) {
    currentVehicleController = controller || null;
  }

  // ---------------------------------------------------------------
  // Place one COD order from a quote (dealer-pro POST /pro/place-order).
  // The route always answers JSON { success, result }, including HTTP 502
  // ({ status: "failed" }), so `result` is read whether or not r.ok. The
  // request carries ONLY { quoteId, fulfilment, pickupWarehouseId } — the
  // owner name, plate and vehicle never leave this device (PIPEDA).
  // Amounts shown here are quote-card totals; a wholesale figure never
  // reaches this text.
  // ---------------------------------------------------------------

  // English fallbacks only. The page's own wording (EN or FR) is the
  // dealer_pro.order.* locale, handed down by Liquid as data-i18n-order-<key>
  // on the Quote screen root (key in kebab case: placeIt -> data-i18n-order-place-it,
  // locale dealer_pro.order.place_it). A cached page without an attribute still
  // gets a usable English label here, never an empty control.
  var PLACE_ORDER_TEXT = {
    start: 'Place order',
    fulfilment: 'Fulfilment',
    delivery: 'Delivery',
    pickup: 'Pick up',
    shipTo: 'Ship to',
    addressBlank: 'Select an address',
    addAddressLink: 'Add an address ›',
    addAddress: 'Add an address',
    street: 'Street',
    city: 'City',
    postalCode: 'Postal code',
    useAddress: 'Use this address',
    pickupWarehouse: 'Pickup warehouse',
    warehouseBlank: 'Select warehouse',
    placeIt: 'Place it',
    placing: 'Placing',
    wait: 'Keep this screen open until it finishes.',
    ordered: 'Ordered',
    orderedNumber: 'Ordered · {n}',
    cancelled: 'Cancelled',
    cancelledNumber: 'Cancelled · {n}',
    replace: 'Replace order',
    replaced: 'Replaced {n}',
    transfer: 'This stock has to move to that warehouse before it can be picked up.',
    deliveryDelay: 'Not everything is at the supplying warehouse, so it may not arrive today.',
    failed: 'The order was not placed. Check your connection and try again.',
    retry: 'Try again',
    stale: 'These prices are out of date: {quoted} on the quote, {current} today. Generate the quote again to order at today\'s price.',
    refresh: 'Refresh quote',
    short: 'Not enough stock: {items}. Pick another warehouse or lower the quantity.',
    shortDelivery: 'Not enough stock to deliver this order: {items}. Lower the quantity, or switch to Pick up and choose a warehouse that has it.',
    shortItem:'{title} has {remaining} left',
    changeWarehouse: 'Change warehouse',
    inFlight: 'This quote is being ordered. Please wait.',
    stuck: 'This quote was not ordered. Please make a new quote.',
    unavailable: 'This quote can\'t be ordered right now. Try again later.',
    noDeliveryAddress: 'Add a delivery address to ship this order.',
    pickupNotAllowed: '{warehouse} can\'t fulfil pickup orders for this quote. Pick another warehouse.',
    pickWarehouse: 'Select a warehouse first.',
    selectAddress: 'Enter a street, or select an address.'
  };

  /** The Quote screen root: the one given, else the first on the page. */
  function quoteScreenRoot(screenRoot) {
    if (screenRoot) return screenRoot;
    var roots = typeof document !== 'undefined' && document.querySelectorAll ? document.querySelectorAll('[data-pro-quote-screen]') : [];
    return roots && roots.length ? roots[0] : null;
  }

  /** The place-order strings, read once from the root's data-i18n-order-* attributes. */
  function placeOrderText(screenRoot) {
    var root = quoteScreenRoot(screenRoot);
    var t = {};
    Object.keys(PLACE_ORDER_TEXT).forEach(function (key) {
      var attr = 'data-i18n-order-' + key.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); });
      t[key] = (root && root.getAttribute && root.getAttribute(attr)) || PLACE_ORDER_TEXT[key];
    });
    return t;
  }

  /** "{n}"-style placeholders; an unknown one is left as written. */
  function fillPlaceOrderText(template, values) {
    return String(template).replace(/\{(\w+)\}/g, function (all, name) {
      return values[name] != null ? String(values[name]) : all;
    });
  }

  /** Quotes already ordered: quoteId -> order number. A refresh fills this from
   * GET /pro/quotes, which reads the order that quote became. */
  var placedOrders = {};

  function rememberPlacedOrder(quoteId, orderNumber, orderedAt, orderStatus, replacedOrderNumber, progress) {
    if (!quoteId) return;
    if (!orderedAt && (orderNumber == null || orderNumber === '')) return;
    placedOrders[quoteId] = {
      status: orderStatus === 'cancelled' ? 'cancelled' : 'ordered',
      orderNumber: orderNumber == null || orderNumber === '' ? '' : String(orderNumber),
      replacedOrderNumber: replacedOrderNumber == null || replacedOrderNumber === '' ? '' : String(replacedOrderNumber),
      progress: progress == null ? '' : String(progress)
    };
  }

  /** True when the quote has a live order. A cancelled one can be replaced. */
  function hasLiveOrder(quoteId) {
    return !!quoteId && Object.prototype.hasOwnProperty.call(placedOrders, quoteId) && placedOrders[quoteId].status !== 'cancelled';
  }

  /** Pure: one place-order `result` -> { kind, text }. `t` is a placeOrderText()
   * table (English by default); `ctx.warehouse` names the pickup warehouse that
   * was asked for; `ctx.fulfilment` ('delivery' | 'pickup') is the side that was
   * sent, which picks the "short" sentence. A placed order also carries its bare `orderNumber` and the
   * notice as `detail`, so the screen can show "Ordered · #n" over one detail line. */
  function placeOrderView(result, t, ctx) {
    var r = result && typeof result === 'object' ? result : {};
    t = t || PLACE_ORDER_TEXT;
    ctx = ctx || {};
    switch (r.status) {
      case 'placed':
        var notice = r.notice === 'transfer' ? t.transfer : r.notice === 'delivery_delay' ? t.deliveryDelay : '';
        return { kind: 'placed', text: notice ? r.orderNumber + ' ' + notice : String(r.orderNumber), orderNumber: String(r.orderNumber), detail: notice };
      case 'already_ordered':
        return { kind: 'already_ordered', text: String(r.orderNumber), orderNumber: String(r.orderNumber), detail: '' };
      case 'stale':
        return { kind: 'stale', text: fillPlaceOrderText(t.stale, { quoted: formatMoney(r.quotedAmount), current: formatMoney(r.currentAmount) }) };
      case 'short':
        var items = (Array.isArray(r.shortages) ? r.shortages : []).map(function (s) {
          return fillPlaceOrderText(t.shortItem, { title: s.title, remaining: s.remaining });
        });
        // A delivery order has no warehouse to change: it gets its own sentence.
        return { kind: 'short', text: fillPlaceOrderText(ctx.fulfilment === 'delivery' ? t.shortDelivery : t.short, { items: items.join('; ') }) };
      case 'in_flight':
        return { kind: 'in_flight', text: t.inFlight };
      case 'stuck':
        return { kind: 'stuck', text: t.stuck };
      case 'unavailable':
        return { kind: 'unavailable', text: t.unavailable };
      case 'no_delivery_address':
        return { kind: 'no_delivery_address', text: t.noDeliveryAddress };
      case 'pickup_warehouse_not_allowed':
        return { kind: 'pickup_warehouse_not_allowed', text: fillPlaceOrderText(t.pickupNotAllowed, { warehouse: ctx.warehouse || t.pickupWarehouse }) };
      default:
        return { kind: 'failed', text: t.failed };
    }
  }

  /** The warehouses a dealer may pick up from (dealer-pro GET /pro/pickup-warehouses),
   * fetched once for the whole page and shared by the live card, the reopened
   * card and every history row. Only { id, name } and the serving default. */
  var pickupWarehouseStore = { loaded: false, warehouses: [], defaultWarehouseId: null, promise: null, waiting: [], generation: 0 };

  function resetPickupWarehouses() {
    pickupWarehouseStore.loaded = false;
    pickupWarehouseStore.warehouses = [];
    pickupWarehouseStore.defaultWarehouseId = null;
    pickupWarehouseStore.promise = null;
    pickupWarehouseStore.waiting = [];
    pickupWarehouseStore.generation += 1;
  }

  /** Runs `cb` once, after the list has loaded. Not called at all if it never loads. */
  function whenPickupWarehousesLoaded(cb) {
    if (!pickupWarehouseStore.loaded) pickupWarehouseStore.waiting.push(cb);
  }

  /** One GET. Already loaded -> no request; one in flight -> the same promise;
   * a failed earlier try -> asks again. Never rejects: offline or guest just
   * leaves the list empty, and a pickup click then asks for a warehouse. */
  function loadPickupWarehouses(fetchImpl) {
    var store = pickupWarehouseStore;
    if (store.loaded) return Promise.resolve(store);
    if (store.promise) return store.promise;
    var f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!f) return Promise.resolve(store);
    var generation = store.generation;
    var request;
    try {
      request = f(proProxyUrl('/pickup-warehouses'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    } catch (e) {
      request = Promise.reject(e);
    }
    store.promise = Promise.resolve(request)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (generation !== store.generation) return;
        store.promise = null;
        if (!body || !Array.isArray(body.warehouses)) return;
        store.warehouses = body.warehouses
          .filter(function (w) { return w && w.id != null; })
          .map(function (w) { return { id: String(w.id), name: String(w.name == null ? '' : w.name) }; });
        var def = body.defaultWarehouseId != null ? String(body.defaultWarehouseId) : null;
        store.defaultWarehouseId = def && store.warehouses.some(function (w) { return w.id === def; }) ? def : null;
        store.loaded = true;
        var waiting = store.waiting;
        store.waiting = [];
        waiting.forEach(function (cb) { cb(); });
      })
      .catch(function () {
        if (generation === store.generation) store.promise = null;
      })
      .then(function () { return store; });
    return store.promise;
  }

  /** The dealer's own addresses (GET /pro/delivery-addresses). One load for the page. */
  var deliveryAddressStore = { loaded: false, addresses: [], promise: null, generation: 0 };

  function loadDeliveryAddresses(fetchImpl) {
    var store = deliveryAddressStore;
    if (store.loaded) return Promise.resolve(store);
    if (store.promise) return store.promise;
    var f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!f) return Promise.resolve(store);
    var generation = store.generation;
    var request;
    try {
      request = f(proProxyUrl('/delivery-addresses'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    } catch (e) {
      request = Promise.reject(e);
    }
    store.promise = Promise.resolve(request)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (generation !== store.generation) return;
        store.promise = null;
        if (!body || !Array.isArray(body.addresses)) return;
        store.addresses = body.addresses
          .filter(function (a) { return a && a.id != null; })
          .map(function (a) {
            return { id: String(a.id), label: String(a.label == null ? '' : a.label), isDefault: a.isDefault === true };
          });
        store.loaded = true;
      })
      .catch(function () {
        if (generation === store.generation) store.promise = null;
      })
      .then(function () { return store; });
    return store.promise;
  }

  /** The whole block built in script: the same markup the Quote screen renders
   * in Liquid (sections/toee-pro-quote.liquid). No page calls it since history
   * rows lost their own place-order block; tests build their fixtures with it.
   * Place order opens Fulfilment (Delivery | Pick up). Delivery then asks for an
   * address; pickup asks for a warehouse. Place it sends the order. `text` is a
   * placeOrderText() table (the page's own by default). */
  function buildPlaceOrderParts(text) {
    var t = text || placeOrderText();
    function el(tag, className, attr, label) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (attr) node.setAttribute(attr, '');
      if (label != null) node.textContent = label;
      return node;
    }
    function button(attr, className, label) {
      var node = el('button', className, attr, label);
      node.setAttribute('type', 'button');
      return node;
    }
    function hide(node) {
      node.hidden = true;
      return node;
    }
    function field(attr, label, control) {
      var wrap = el('label', 'tt-pro-place-order__field', attr);
      wrap.appendChild(el('span', 'tt-pro-place-order__label', null, label));
      wrap.appendChild(control);
      return wrap;
    }
    function input(attr, autocomplete) {
      var node = el('input', 'tt-pro-input tt-pro-place-order__input', attr);
      node.setAttribute('type', 'text');
      node.setAttribute('autocomplete', autocomplete);
      return node;
    }

    var wrap = el('div', 'tt-pro-place-order', 'data-pro-place-order');
    var start = button('data-pro-place-start', 'tt-pro-share-btn tt-pro-share-btn--secondary tt-pro-btn2 tt-pro-place-order__btn', t.start);

    var fulfil = hide(el('div', 'tt-pro-place-order__fulfil', 'data-pro-place-fulfil'));
    var fulfilLabel = el('span', 'tt-pro-place-order__label', null, t.fulfilment);
    fulfilLabel.setAttribute('aria-hidden', 'true');
    var seg = el('div', 'tt-pro-seg tt-pro-place-order__seg');
    seg.setAttribute('role', 'group');
    seg.setAttribute('aria-label', t.fulfilment);
    var indicator = hide(el('span', 'tt-pro-seg__ind'));
    indicator.setAttribute('aria-hidden', 'true');
    var delivery = hide(button('data-pro-place-delivery', 'tt-pro-seg__btn', t.delivery));
    var pickup = hide(button('data-pro-place-pickup', 'tt-pro-seg__btn', t.pickup));
    delivery.setAttribute('aria-pressed', 'false');
    pickup.setAttribute('aria-pressed', 'false');
    seg.appendChild(indicator);
    seg.appendChild(delivery);
    seg.appendChild(pickup);
    fulfil.appendChild(fulfilLabel);
    fulfil.appendChild(seg);

    var addressSelect = hide(el('select', 'tt-pro-input tt-pro-place-order__select', 'data-pro-address-select'));
    var addressField = hide(field('data-pro-place-address-field', t.shipTo, addressSelect));
    var addAddress = hide(button('data-pro-address-add', 'tt-pro-link tt-pro-place-order__link', t.addAddressLink));
    addAddress.setAttribute('aria-expanded', 'false');

    var form = hide(el('div', 'tt-pro-place-order__form', 'data-pro-address-form'));
    var street = input('data-pro-address-street', 'address-line1');
    var city = input('data-pro-address-city', 'address-level2');
    var zip = input('data-pro-address-zip', 'postal-code');
    var pair = el('div', 'tt-pro-place-order__pair');
    pair.appendChild(field(null, t.city, city));
    pair.appendChild(field(null, t.postalCode, zip));
    var useAddress = button('data-pro-address-use', 'tt-pro-btn2', t.useAddress);
    form.appendChild(field(null, t.street, street));
    form.appendChild(pair);
    form.appendChild(useAddress);

    var select = hide(el('select', 'tt-pro-input tt-pro-place-order__select', 'data-pro-pickup-select'));
    var pickupField = hide(field('data-pro-place-pickup-field', t.pickupWarehouse, select));
    var confirm = hide(button('data-pro-place-confirm', 'tt-pro-share-btn tt-pro-share-btn--primary tt-pro-place-order__btn', t.placeIt));
    var wait = hide(el('p', 'tt-pro-place-order__wait', 'data-pro-place-wait', t.wait));

    var result = hide(el('div', 'tt-pro-place-order__result', 'data-pro-place-result'));
    var head = el('div', 'tt-pro-place-order__head');
    var marker = hide(el('span', 'tt-pro-marker', 'data-pro-place-marker'));
    marker.setAttribute('aria-hidden', 'true');
    var status = hide(el('p', 'tt-pro-place-order__status', 'data-pro-place-status'));
    var progress = hide(el('p', 'tt-pro-place-order__progress', 'data-pro-place-progress'));
    var replaced = hide(el('p', 'tt-pro-place-order__replaced', 'data-pro-place-replaced'));
    var action = hide(button('data-pro-place-action', 'tt-pro-btn2 tt-pro-place-order__action'));
    head.appendChild(marker);
    head.appendChild(status);
    result.appendChild(head);
    result.appendChild(progress);
    result.appendChild(replaced);
    result.appendChild(action);

    [start, fulfil, addressField, addAddress, form, pickupField, confirm, wait, result].forEach(function (node) {
      wrap.appendChild(node);
    });
    return {
      element: wrap,
      start: start,
      fulfil: fulfil,
      indicator: indicator,
      delivery: delivery,
      pickup: pickup,
      addressField: addressField,
      addressSelect: addressSelect,
      addAddress: addAddress,
      form: form,
      street: street,
      city: city,
      zip: zip,
      useAddress: useAddress,
      pickupField: pickupField,
      select: select,
      confirm: confirm,
      wait: wait,
      result: result,
      marker: marker,
      status: status,
      progress: progress,
      replaced: replaced,
      action: action
    };
  }

  /**
   * Wires `parts` ({ delivery, pickup, select?, status } elements) to the route.
   * opts: fetchImpl, getQuoteId(),
   * getWarehouses() -> { warehouses: [{ id, name }], defaultWarehouseId }
   * (defaults to the page-wide list), text (a placeOrderText() table, the
   * page's own by default), refreshQuote() (the "Refresh quote" action on a
   * stale answer; without it that answer shows no button) and canRefreshQuote()
   * (optional; false = no "Refresh quote" for the quote that is open now).
   *
   * One set of buttons serves whichever quote is open, so "in flight" is kept
   * per quote id: quote A still placing never locks quote B, and A's answer
   * only writes its message when A is still the open quote.
   * `placing` is true while any quote is in flight.
   */
  function createPlaceOrderActions(parts, opts) {
    var t = opts.text || placeOrderText();
    var self = {
      parts: parts,
      fetchImpl: opts.fetchImpl,
      placing: false,
      // The warehouse list stays closed until the dealer chooses pickup.
      pickupOpen: false,
      // undefined = the dealer has not been given a default yet; null = nothing chosen.
      selectedId: undefined,
      // start → choose Delivery or Pick up → address or warehouse → Place it.
      step: 'start',
      selectedAddressId: undefined,
      inlineAddress: null,
      addressFormOpen: false
    };
    var inFlight = {};
    var builtFor = null;
    var addressBuiltFor = null;
    var openForQuote = null;

    // The block's own wrapper: Delivery and Pick up now sit inside the switch,
    // so their parent is not the block any more. Older markup has no wrapper
    // around them, and then their parent is where the rest lives.
    var parent = parts.delivery && parts.delivery.parentNode;
    for (var up = parent; up; up = up.parentNode) {
      if (up.getAttribute && up.getAttribute('data-pro-place-order') !== null) {
        parent = up;
        break;
      }
    }
    function ensureButton(attr, primary) {
      if (!parent || !parent.querySelector) return null;
      var found = parent.querySelector('[' + attr + ']');
      if (found) return found;
      var el = document.createElement('button');
      el.type = 'button';
      el.className = 'tt-pro-share-btn ' + (primary ? 'tt-pro-share-btn--primary' : 'tt-pro-share-btn--secondary') + ' tt-pro-place-order__btn';
      el.setAttribute(attr, '');
      parent.appendChild(el);
      return el;
    }
    function ensureSelect(attr, label) {
      if (!parent || !parent.querySelector) return null;
      var found = parent.querySelector('[' + attr + ']');
      if (found) return found;
      var el = document.createElement('select');
      el.className = 'tt-pro-place-order__select';
      el.setAttribute(attr, '');
      el.setAttribute('aria-label', label);
      parent.appendChild(el);
      return el;
    }
    if (parent && parent.querySelector) {
      parts.start = parts.start || ensureButton('data-pro-place-start', false);
      parts.addressSelect = parts.addressSelect || ensureSelect('data-pro-address-select', t.shipTo);
      parts.addAddress = parts.addAddress || ensureButton('data-pro-address-add', false);
      parts.confirm = parts.confirm || ensureButton('data-pro-place-confirm', true);
      // Optional: a cached older page has none of the ones below, and works without them.
      var optional = {
        form: 'data-pro-address-form',
        street: 'data-pro-address-street',
        city: 'data-pro-address-city',
        zip: 'data-pro-address-zip',
        useAddress: 'data-pro-address-use',
        fulfil: 'data-pro-place-fulfil',
        addressField: 'data-pro-place-address-field',
        pickupField: 'data-pro-place-pickup-field',
        wait: 'data-pro-place-wait',
        result: 'data-pro-place-result',
        marker: 'data-pro-place-marker',
        replaced: 'data-pro-place-replaced',
        action: 'data-pro-place-action'
      };
      Object.keys(optional).forEach(function (name) {
        parts[name] = parts[name] || parent.querySelector('[' + optional[name] + ']');
      });
      parts.indicator = parts.indicator || parent.querySelector('.tt-pro-seg__ind');
    }

    // One live region per block: the result box (or, on older markup, the status
    // line itself). A status region for passive news; an alert while it shows a failure.
    var live = parts.result || parts.status;
    if (live && live.setAttribute) live.setAttribute('role', 'status');

    function warehouseList() {
      var w = (opts.getWarehouses ? opts.getWarehouses() : pickupWarehouseStore) || {};
      return {
        list: Array.isArray(w.warehouses) ? w.warehouses : [],
        defaultId: w.defaultWarehouseId != null ? String(w.defaultWarehouseId) : null
      };
    }

    /** The warehouse the dealer has chosen ({ id, name }), or null. Only the
     * serving default is ever preselected; no other warehouse is guessed. */
    function chosen() {
      var w = warehouseList();
      if (self.selectedId === undefined && w.list.length) self.selectedId = w.defaultId;
      if (self.selectedId == null) return null;
      for (var i = 0; i < w.list.length; i++) {
        if (String(w.list[i].id) === String(self.selectedId)) return { id: String(w.list[i].id), name: String(w.list[i].name || '') };
      }
      return null;
    }

    function syncSelect(pick) {
      var select = parts.select;
      if (!select) return;
      var w = warehouseList();
      var key = w.list.map(function (x) { return x.id + '\u0000' + x.name; }).join('\u0001');
      if (key !== builtFor) {
        builtFor = key;
        var options = [];
        var blank = document.createElement('option');
        blank.value = '';
        blank.textContent = t.warehouseBlank;
        options.push(blank);
        w.list.forEach(function (x) {
          var option = document.createElement('option');
          option.value = String(x.id);
          option.textContent = String(x.name || '');
          options.push(option);
        });
        select.replaceChildren.apply(select, options);
      }
      select.value = pick ? pick.id : '';
    }

    // Passive news gets a square marker in plain text; every other kind is a
    // failure: the error callout (1px error border, no fill) and, where there is
    // one, the button that does what the message asks.
    var PASSIVE_MARKER = { placed: 'ok', cancelled: 'muted', in_flight: 'warn' };
    var currentAction = null;

    function focusOn(el) {
      if (el && typeof el.focus === 'function') el.focus();
    }

    function actionFor(kind) {
      if (kind === 'failed') return { label: t.retry, run: confirmPlace };
      if (kind === 'stale' && opts.refreshQuote && (!opts.canRefreshQuote || opts.canRefreshQuote())) {
        return { label: t.refresh, run: opts.refreshQuote };
      }
      // Only a pickup order has a warehouse to change. A delivery order that came
      // back short must never be flipped into a pickup order from here.
      if (kind === 'short' && self.step === 'pickup') {
        return {
          label: t.changeWarehouse,
          run: function () {
            self.step = 'pickup';
            self.pickupOpen = true;
            self.refresh();
            focusOn(parts.select);
          }
        };
      }
      if (kind === 'no_delivery_address') {
        return {
          label: t.addAddress,
          run: function () {
            self.step = 'delivery';
            self.pickupOpen = false;
            self.addressFormOpen = true;
            self.refresh();
            focusOn(parts.street);
          }
        };
      }
      return null;
    }

    function setStatus(view) {
      if (!parts.status) return;
      var marker = view ? PASSIVE_MARKER[view.kind] : '';
      var failure = !!view && !marker;
      // A live region announces text that is added while it is on screen, under the role it
      // has at that moment: so the role first, the box next, the words last.
      if (view && live) live.setAttribute('role', failure ? 'alert' : 'status');
      if (parts.result) {
        parts.result.className = 'tt-pro-place-order__result' + (failure ? ' tt-pro-callout tt-pro-callout--error' : '');
        parts.result.hidden = !view;
      }
      if (parts.marker) {
        parts.marker.className = 'tt-pro-marker tt-pro-marker--' + (marker || 'error');
        parts.marker.hidden = !marker;
      }
      currentAction = failure ? actionFor(view.kind) : null;
      if (parts.action) {
        parts.action.textContent = currentAction ? currentAction.label : '';
        parts.action.hidden = !currentAction;
      }
      if (view) parts.status.setAttribute('data-pro-place-status-kind', view.kind);
      parts.status.hidden = !view;
      parts.status.textContent = view ? view.text : '';
    }

    function showWarehouseList(visible) {
      if (parts.select) parts.select.hidden = !visible;
    }

    /** The clicked button stays fully visible; the other one just sits disabled. */
    function markBusy(button, busy) {
      if (!button || !button.setAttribute) return;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    function chosenAddress() {
      if (self.inlineAddress && self.inlineAddress.address1) return { id: '', label: self.inlineAddress.address1, inline: self.inlineAddress };
      var list = deliveryAddressStore.addresses;
      if (self.selectedAddressId === undefined && list.length) {
        var preferred = null;
        for (var i = 0; i < list.length; i++) if (list[i].isDefault) preferred = list[i];
        self.selectedAddressId = (preferred || list[0]).id;
      }
      if (!self.selectedAddressId) return null;
      for (var j = 0; j < list.length; j++) {
        if (list[j].id === self.selectedAddressId) return list[j];
      }
      return null;
    }

    function syncAddressSelect(pick) {
      var select = parts.addressSelect;
      if (!select) return;
      var list = deliveryAddressStore.addresses;
      var key = list.map(function (x) { return x.id + '\u0000' + x.label; }).join('\u0001');
      if (key !== addressBuiltFor) {
        addressBuiltFor = key;
        var options = [];
        var blank = document.createElement('option');
        blank.value = '';
        blank.textContent = t.addressBlank;
        options.push(blank);
        list.forEach(function (x) {
          var option = document.createElement('option');
          option.value = x.id;
          option.textContent = x.label;
          options.push(option);
        });
        select.replaceChildren.apply(select, options);
      }
      select.value = pick && pick.id ? pick.id : '';
    }

    function showEl(el, visible) {
      if (el) el.hidden = !visible;
    }

    /** Re-points the buttons at the quote that is open now. */
    self.refresh = function () {
      var quoteId = opts.getQuoteId();
      if (quoteId !== openForQuote) {
        self.pickupOpen = false;
        self.step = 'start';
        self.inlineAddress = null;
        self.addressFormOpen = false;
        self.selectedAddressId = undefined;
        openForQuote = quoteId;
        // The previous quote's message is not this quote's. (A quote that is still
        // sending skips the setStatus calls below, so it would keep that message.)
        setStatus(null);
      }
      var pick = chosen();
      syncSelect(pick);
      var address = chosenAddress();
      syncAddressSelect(address);
      var stored = quoteId && Object.prototype.hasOwnProperty.call(placedOrders, quoteId) ? placedOrders[quoteId] : null;
      var ordered = !!stored && stored.status !== 'cancelled';
      var cancelled = !!stored && stored.status === 'cancelled';
      var pending = quoteId ? inFlight[quoteId] : '';
      if (parts.replaced) {
        var replacedNumber = stored && stored.replacedOrderNumber ? stored.replacedOrderNumber : '';
        parts.replaced.textContent = replacedNumber ? fillPlaceOrderText(t.replaced, { n: replacedNumber }) : '';
        parts.replaced.hidden = !replacedNumber;
      }
      if (parts.start) parts.start.textContent = cancelled ? t.replace : t.start;
      if (parts.delivery) parts.delivery.textContent = t.delivery;
      if (parts.pickup) parts.pickup.textContent = t.pickup;
      if (parts.addAddress) parts.addAddress.textContent = t.addAddressLink;
      if (parts.useAddress) parts.useAddress.textContent = t.useAddress;
      if (parts.confirm) {
        // Busy keeps the button and a label on it ("Placing"); only the look changes.
        parts.confirm.textContent = pending ? t.placing : t.placeIt;
        markBusy(parts.confirm, !!pending);
      }
      if (parts.delivery) parts.delivery.disabled = !!pending;
      if (parts.pickup) parts.pickup.disabled = !!pending;
      if (ordered) {
        [parts.start, parts.fulfil, parts.delivery, parts.pickup, parts.addressField, parts.addressSelect, parts.addAddress,
          parts.form, parts.pickupField, parts.select, parts.confirm, parts.wait].forEach(function (el) { showEl(el, false); });
        var number = stored.orderNumber;
        setStatus({ kind: 'placed', text: number ? fillPlaceOrderText(t.orderedNumber, { n: number }) : t.ordered });
        if (parts.progress) {
          parts.progress.textContent = stored.progress || '';
          parts.progress.hidden = !parts.progress.textContent;
        }
        return;
      }
      if (parts.progress) {
        parts.progress.textContent = '';
        parts.progress.hidden = true;
      }
      var step = pending === 'delivery' ? 'delivery' : pending === 'pickup' ? 'pickup' : self.step;
      var chosenSide = step === 'delivery' || step === 'pickup';
      // Closed: just Place order. Open: the Fulfilment switch stays on screen
      // whichever side is chosen, so switching back never loses what was entered.
      var holdStart = opts.orderSuppressed && opts.orderSuppressed();
      showEl(parts.start, step === 'start' && !holdStart);
      [parts.fulfil, parts.delivery, parts.pickup].forEach(function (el) { showEl(el, step !== 'start'); });
      if (parts.delivery) parts.delivery.setAttribute('aria-pressed', step === 'delivery' ? 'true' : 'false');
      if (parts.pickup) parts.pickup.setAttribute('aria-pressed', step === 'pickup' ? 'true' : 'false');
      if (parts.indicator) {
        // Like the Tires switch: an ink block one segment wide, slid by whole segments.
        parts.indicator.hidden = !chosenSide;
        if (parts.indicator.style) parts.indicator.style.transform = 'translateX(' + (step === 'pickup' ? 100 : 0) + '%)';
      }
      showEl(parts.addressField, step === 'delivery');
      showEl(parts.addressSelect, step === 'delivery');
      showEl(parts.addAddress, step === 'delivery');
      if (parts.addAddress) parts.addAddress.setAttribute('aria-expanded', self.addressFormOpen ? 'true' : 'false');
      showEl(parts.form, step === 'delivery' && self.addressFormOpen);
      showEl(parts.pickupField, step === 'pickup');
      showEl(parts.select, step === 'pickup');
      var ready = (step === 'delivery' && address) || (step === 'pickup' && pick);
      // Place it shows once a side is chosen and stays disabled until it can send.
      showEl(parts.confirm, chosenSide);
      if (parts.confirm) parts.confirm.disabled = !!pending || !ready;
      showEl(parts.wait, !!pending);
      if (!pending) {
        if (cancelled && step === 'start') {
          var was = stored.orderNumber;
          setStatus({ kind: 'cancelled', text: was ? fillPlaceOrderText(t.cancelledNumber, { n: was }) : t.cancelled });
        } else {
          setStatus(null);
        }
      }
    };

    self.selectWarehouse = function (id) {
      self.pickupOpen = true;
      self.step = 'pickup';
      self.selectedId = id ? String(id) : null;
      self.refresh();
    };

    self.place = function (fulfilment) {
      var quoteId = opts.getQuoteId();
      if (!quoteId || inFlight[quoteId] || hasLiveOrder(quoteId)) {
        return Promise.resolve(null);
      }
      if (fulfilment === 'delivery') {
        self.pickupOpen = false;
        showWarehouseList(false);
      }
      var pickupId = null;
      if (fulfilment === 'pickup') {
        if (!self.pickupOpen) {
          // First click only opens the list. Nothing is sent yet.
          self.pickupOpen = true;
          self.step = 'pickup';
          self.refresh();
          return Promise.resolve(null);
        }
        var pick = chosen();
        if (!pick) {
          // The list is open and nothing is chosen: ask, and send nothing.
          var ask = { kind: 'pick_warehouse', text: t.pickWarehouse };
          setStatus(ask);
          return Promise.resolve(ask);
        }
        pickupId = pick.id;
      }
      var f = self.fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
      inFlight[quoteId] = fulfilment;
      self.placing = true;
      self.step = fulfilment === 'pickup' ? 'pickup' : 'delivery';
      self.refresh();
      // A new attempt: the last failure's callout does not sit beside "Placing".
      setStatus(null);

      var payload = { quoteId: quoteId, fulfilment: fulfilment, pickupWarehouseId: pickupId };
      if (fulfilment === 'delivery') {
        var address = chosenAddress();
        if (address && address.inline) payload.deliveryAddress = address.inline;
        else if (address && address.id) payload.deliveryAddressId = address.id;
      }

      var request;
      try {
        request = f(proProxyUrl('/place-order'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (e) {
        request = Promise.reject(e);
      }

      return Promise.resolve(request)
        .then(function (r) {
          // Read the body even when !r.ok: a 502 carries { result: { status: "failed" } }.
          return r.json().then(
            function (body) { return body && body.result; },
            function () { return null; }
          );
        })
        .catch(function () { return null; })
        .then(function (result) {
          var view = placeOrderView(result, t, { warehouse: pick ? pick.name : '', fulfilment: fulfilment });
          delete inFlight[quoteId];
          self.placing = Object.keys(inFlight).length > 0;
          if (view.kind === 'placed' || view.kind === 'already_ordered') {
            var before = Object.prototype.hasOwnProperty.call(placedOrders, quoteId) ? placedOrders[quoteId] : null;
            // The number alone goes on the bold line; a notice is the detail line under it.
            placedOrders[quoteId] = {
              status: 'ordered',
              orderNumber: view.orderNumber,
              replacedOrderNumber: before && before.status === 'cancelled' ? before.orderNumber : '',
              progress: view.detail
            };
          }
          // Always re-point the buttons at the quote open now; write this
          // answer's text only if that quote is still the one clicked.
          self.refresh();
          if (opts.getQuoteId() === quoteId && view.kind !== 'placed' && view.kind !== 'already_ordered') setStatus(view);
          return view;
        });
    };

    if (parts.start && parts.start.addEventListener) {
      parts.start.addEventListener('click', function () {
        if (parts.start.disabled) return;
        self.step = 'choose';
        self.refresh();
        // Place order is gone now: the Fulfilment switch that replaced it takes the keyboard.
        handOffFocus(parts.start, parts.delivery);
      });
    }
    if (parts.delivery && parts.delivery.addEventListener) {
      parts.delivery.addEventListener('click', function () {
        self.step = 'delivery';
        self.pickupOpen = false;
        self.refresh();
        loadDeliveryAddresses(self.fetchImpl).then(function () { self.refresh(); });
      });
    }
    if (parts.pickup && parts.pickup.addEventListener) {
      parts.pickup.addEventListener('click', function () {
        self.step = 'pickup';
        self.pickupOpen = true;
        self.refresh();
      });
    }
    /** Place it (and "Try again"): sends for the side that is chosen. */
    function confirmPlace() {
      if (self.step === 'pickup' || (inFlight[opts.getQuoteId()] === 'pickup')) {
        self.pickupOpen = true;
        self.place('pickup');
        return;
      }
      if (!chosenAddress()) {
        setStatus({ kind: 'select_address', text: t.selectAddress });
        return;
      }
      self.place('delivery');
    }
    if (parts.confirm && parts.confirm.addEventListener) parts.confirm.addEventListener('click', confirmPlace);
    if (parts.action && parts.action.addEventListener) {
      parts.action.addEventListener('click', function () {
        if (currentAction) currentAction.run();
      });
    }
    if (parts.addressSelect && parts.addressSelect.addEventListener) {
      parts.addressSelect.addEventListener('change', function () {
        self.inlineAddress = null;
        self.selectedAddressId = parts.addressSelect.value || null;
        self.refresh();
      });
    }
    if (parts.addAddress && parts.addAddress.addEventListener) {
      parts.addAddress.addEventListener('click', function () {
        self.addressFormOpen = !self.addressFormOpen;
        self.refresh();
      });
    }
    if (parts.useAddress && parts.useAddress.addEventListener) {
      parts.useAddress.addEventListener('click', function () {
        var street = parts.street && parts.street.value ? String(parts.street.value).trim() : '';
        var city = parts.city && parts.city.value ? String(parts.city.value).trim() : '';
        var zip = parts.zip && parts.zip.value ? String(parts.zip.value).trim() : '';
        if (!street) {
          setStatus({ kind: 'select_address', text: t.selectAddress });
          return;
        }
        self.inlineAddress = { address1: street, city: city, zip: zip };
        self.selectedAddressId = null;
        self.addressFormOpen = false;
        self.step = 'delivery';
        self.refresh();
        // The form folded away with its button: the next step is Place it.
        handOffFocus(parts.useAddress, parts.confirm);
      });
    }
    if (parts.select && parts.select.addEventListener) {
      parts.select.addEventListener('change', function () { self.selectWarehouse(parts.select.value); });
    }
    if (!opts.getWarehouses) whenPickupWarehousesLoaded(function () { self.refresh(); });
    self.refresh();
    return self;
  }

  /**
   * ponytail (2026-09-09) — iOS Safari ignores <a download> and opens the
   * image instead of saving it (see _saveImage's own comment); not cheaply
   * fixable, and the primary Share button already covers a real save via
   * the system sheet on a capable browser. What WAS simply wrong: the
   * status message claimed "Saved to your device" there too, which isn't
   * true — nothing is saved yet. This only swaps which locale key reads;
   * the save mechanism itself is untouched.
   */
  function shareSavedMessageKey(ua) {
    return isIOSSafari(ua) ? 'share-saved-ios' : 'share-saved';
  }

  /**
   * dp-7/2026-09-10 — below this width, search/results and the quote
   * screen stack instead of sitting side by side (matches .tt-pro-layout's
   * own @media (min-width: 1024px) — the same breakpoint, not a separate
   * guess). A freshly generated quote can render far below the fold there
   * with nothing on screen to say it happened; at/above it the card
   * already renders beside search, so there is nothing to scroll to.
   */
  function shouldScrollToGeneratedQuote(viewportWidth) {
    return viewportWidth < 1024;
  }

  /** A correlation id for one generated quote — every action on the card
   * it produces (share/save/copy) reports against this same id. Not a
   * security token, just a grouping key, so a short random string is fine
   * when crypto.randomUUID isn't available. */
  function generateQuoteId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'q-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // ---------------------------------------------------------------------
  // Drawer — the behaviour Recent quotes and Settings share (rollout Task 8;
  // the frame is the .tt-pro-drawer CSS in sections/toee-pro-search.liquid).
  //
  //   var drawer = new ProDrawer(rootEl, { onClose: fn });
  //   drawer.open(openerEl);  // show, lock the page, focus in
  //   drawer.close();         // hide, unlock, focus back on openerEl, then onClose
  //
  // rootEl holds .tt-pro-drawer__scrim and .tt-pro-drawer__panel (the dialog).
  // Esc, a click on the scrim and the Tab trap work for as long as it is open.
  // A second drawer opening closes the first, so there is never more than one
  // scrim on screen. `document` is read when a method runs, never at load, so
  // the plain fake in tests/toee-pro-fake-dom.js is enough for the older tests.
  // ---------------------------------------------------------------------

  /** Class on <html> while a drawer is open; the CSS turns it into overflow: hidden. */
  var DRAWER_LOCK_CLASS = 'tt-pro-drawer-open';
  /** What Tab can reach: the usual controls that are not disabled, hidden or taken out of the order. */
  var DRAWER_FOCUSABLE =
    'a[href]:not([tabindex="-1"]), ' +
    'button:not([disabled]):not([tabindex="-1"]), ' +
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), ' +
    'select:not([disabled]):not([tabindex="-1"]), ' +
    'textarea:not([disabled]):not([tabindex="-1"]), ' +
    '[tabindex]:not([tabindex="-1"])';
  /** The drawer on screen now (at most one). */
  var openDrawer = null;

  function focusNode(node) {
    // preventScroll: coming back to the header icon must not undo a scroll the page just made
    // (a reopened quote scrolls its card into view while the history drawer closes).
    if (node && typeof node.focus === 'function') node.focus({ preventScroll: true });
  }

  function focusedElement() {
    var active = typeof document !== 'undefined' ? document.activeElement : null;
    return active && active !== document.body ? active : null;
  }

  /** Adds or removes the drawer's key listener; the plain test document can lack either method. */
  function listenOnDocument(method, handler) {
    if (typeof document !== 'undefined' && typeof document[method] === 'function') document[method]('keydown', handler);
  }

  /** Locks (or unlocks) page scroll. A lock this drawer did not add is left as it was. */
  function lockPageScroll(drawer, on) {
    var html = typeof document !== 'undefined' ? document.documentElement : null;
    if (!html || !html.classList) return;
    if (on) {
      drawer._lockedHere = !html.classList.contains(DRAWER_LOCK_CLASS);
      html.classList.add(DRAWER_LOCK_CLASS);
    } else if (drawer._lockedHere) {
      html.classList.remove(DRAWER_LOCK_CLASS);
      drawer._lockedHere = false;
    }
  }

  function ProDrawer(root, options) {
    var self = this;
    this.root = root;
    this.onClose = (options && options.onClose) || null;
    this.isOpen = false;
    this.opener = null;
    this._lockedHere = false;
    this.scrim = root.querySelector('.tt-pro-drawer__scrim');
    this.panel = root.querySelector('.tt-pro-drawer__panel') || root;
    this._onKeydown = function (e) {
      self._keydown(e);
    };
    this._ensureDialog();
    // A click whose target IS the scrim; a click on the panel or anything in it is not.
    root.addEventListener('click', function (e) {
      if (self.isOpen && e && (e.target === self.scrim || e.target === self.root)) self.close();
    });
  }

  ProDrawer.FOCUSABLE = DRAWER_FOCUSABLE;

  /** The dialog attributes, for a panel whose markup does not carry them (the Liquid does). */
  ProDrawer.prototype._ensureDialog = function () {
    var panel = this.panel;
    if (!panel.getAttribute('role')) panel.setAttribute('role', 'dialog');
    if (!panel.getAttribute('aria-modal')) panel.setAttribute('aria-modal', 'true');
    if (!panel.getAttribute('aria-labelledby')) {
      var title = this.root.querySelector('.tt-pro-drawer__title');
      var id = title && title.getAttribute('id');
      if (id) panel.setAttribute('aria-labelledby', id);
    }
  };

  /** `opener` is where focus goes back to on close; without one, whatever has focus now. */
  ProDrawer.prototype.open = function (opener) {
    if (this.isOpen) {
      this.root.hidden = false;
      return;
    }
    var previous = openDrawer && openDrawer !== this ? openDrawer : null;
    // Replacing a drawer inherits what opened it: the replaced one is about to be hidden,
    // and focus inside it would be no place to come back to.
    this.opener = opener || (previous ? previous.opener : focusedElement());
    if (previous) previous._close(false);
    openDrawer = this;
    this.isOpen = true;
    this.root.hidden = false;
    lockPageScroll(this, true);
    listenOnDocument('addEventListener', this._onKeydown);
    this._focusIn();
  };

  ProDrawer.prototype.close = function () {
    this._close(true);
  };

  ProDrawer.prototype._close = function (restoreFocus) {
    this.root.hidden = true;
    if (!this.isOpen) return;
    this.isOpen = false;
    if (openDrawer === this) openDrawer = null;
    listenOnDocument('removeEventListener', this._onKeydown);
    lockPageScroll(this, false);
    var opener = this.opener;
    this.opener = null;
    if (restoreFocus) focusNode(opener);
    if (this.onClose) this.onClose();
  };

  /** The controls a person can reach now (a closed first-run section, say, does not count). */
  ProDrawer.prototype._focusables = function () {
    var found = this.panel.querySelectorAll(DRAWER_FOCUSABLE);
    var out = [];
    for (var i = 0; i < found.length; i++) {
      if (!found[i].getClientRects || found[i].getClientRects().length) out.push(found[i]);
    }
    return out;
  };

  ProDrawer.prototype._focusIn = function () {
    var target = this._focusables()[0];
    if (!target) {
      // Nothing to tab to: the panel itself takes focus, so Esc and the trap still work.
      target = this.panel;
      if (!target.getAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    }
    focusNode(target);
  };

  ProDrawer.prototype._keydown = function (e) {
    // Something else already used the key (an open address suggestion list, say).
    if (!this.isOpen || !e || e.defaultPrevented) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (!e.isComposing) this.close();
    } else if (e.key === 'Tab') {
      this._trapTab(e);
    }
  };

  /** Tab wraps at either end, and from outside the drawer it comes in at the near end. */
  ProDrawer.prototype._trapTab = function (e) {
    var items = this._focusables();
    var panel = this.panel;
    if (!items.length) {
      e.preventDefault();
      focusNode(panel);
      return;
    }
    var active = document.activeElement;
    var inside = !!active && active !== panel && (!panel.contains || panel.contains(active));
    if (e.shiftKey) {
      if (!inside || active === items[0]) {
        e.preventDefault();
        focusNode(items[items.length - 1]);
      }
    } else if (!inside || active === items[items.length - 1]) {
      e.preventDefault();
      focusNode(items[0]);
    }
  };

  /** The drawer of a panel, made the first time it is needed (a test can build a panel without its constructor). */
  function drawerOf(owner, onClose) {
    if (!owner.drawer) owner.drawer = new ProDrawer(owner.root, { onClose: onClose });
    return owner.drawer;
  }

  // ---------------------------------------------------------------------
  // Browser wiring — only runs where `document` exists (keeps this file
  // requirable from Node for the pure logic above).
  // ---------------------------------------------------------------------

  var initVehiclePanelRef = function () {};
  var initFindModesRef = function () {};
  var initInstallPromptRef = function () {};
  var initIOSGuideRef = function () {};
  if (typeof document !== 'undefined') {
    (function () {
      // Every visible string comes from data-i18n-* attributes rendered by
      // Liquid from the locale files — no English fallback text lives here,
      // so a missing attribute is a markup bug that shows up as blank, not
      // as an English string quietly masking it in French.
      function text(root, name) {
        return (root && root.getAttribute('data-i18n-' + name)) || '';
      }

      fmtHMS = function (totalSec) {
        var t = Math.max(0, Math.floor(totalSec));
        var h = Math.floor(t / 3600);
        var m = Math.floor((t % 3600) / 60);
        var s = t % 60;
        var str = '';
        if (h > 0) str += h + 'h ';
        str += (m < 10 ? '0' + m : '' + m) + 'm ';
        str += (s < 10 ? '0' + s : '' + s) + 's';
        return str.trim();
      };

      function el(tag, className, textContent) {
        var node = document.createElement(tag);
        if (className) node.className = className;
        if (textContent != null) node.textContent = textContent;
        return node;
      }

      var SVG_NS = 'http://www.w3.org/2000/svg';

      /**
       * Line drawings the result card uses: the promise band's clock and truck, the
       * phone beside "Call to confirm", and the neutral tire / wheel shown in the
       * photo box when a result has no photo. [tag, attributes] pairs, drawn in the
       * SVG namespace with stroke="currentColor", so CSS decides the colour and no
       * hex lives in the markup.
       */
      var CARD_ICONS = {
        truck: [
          ['path', { d: 'M2 6h11v9H2zM13 9h4l4 3v3h-8' }],
          ['circle', { cx: 7, cy: 17, r: 1.8 }],
          ['circle', { cx: 17, cy: 17, r: 1.8 }]
        ],
        clock: [
          ['circle', { cx: 12, cy: 12, r: 9 }],
          ['path', { d: 'M12 7v5l3 2' }]
        ],
        phone: [['path', { d: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z' }]]
      };
      var CARD_GLYPHS = {
        tire: [
          ['circle', { cx: 24, cy: 24, r: 21 }],
          ['circle', { cx: 24, cy: 24, r: 13 }],
          ['circle', { cx: 24, cy: 24, r: 4 }],
          ['path', { d: 'M24 3v6M24 39v6M3 24h6M39 24h6M9 9l4 4M35 35l4 4M39 9l-4 4M13 35l-4 4' }]
        ],
        wheel: [
          ['circle', { cx: 24, cy: 24, r: 21 }],
          ['circle', { cx: 24, cy: 24, r: 17 }],
          ['circle', { cx: 24, cy: 24, r: 4 }],
          ['path', { d: 'M24 7v13M24 28v13M7 24h13M28 24h13M12 12l9 9M27 27l9 9M36 12l-9 9M21 27l-9 9' }]
        ]
      };

      /** One of the drawings above as an <svg size x size> on a `box`-unit grid. */
      function svgIcon(shapes, size, box, strokeWidth) {
        var svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('width', String(size));
        svg.setAttribute('height', String(size));
        svg.setAttribute('viewBox', '0 0 ' + box + ' ' + box);
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', String(strokeWidth));
        svg.setAttribute('stroke-linecap', 'round');
        svg.setAttribute('stroke-linejoin', 'round');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        shapes.forEach(function (shape) {
          var node = document.createElementNS(SVG_NS, shape[0]);
          Object.keys(shape[1]).forEach(function (name) {
            node.setAttribute(name, String(shape[1][name]));
          });
          svg.appendChild(node);
        });
        return svg;
      }

      /** The 8px square beside a stock sentence. Decorative: the sentence says the same thing in words. */
      function stockMarker(level) {
        var marker = el('span', 'tt-pro-marker tt-pro-marker--' + level);
        marker.setAttribute('aria-hidden', 'true');
        return marker;
      }

      // ---------------------------------------------------------------
      // The status family (rollout Task 3, the Status artboards). Each state is a
      // sentence with exactly one role - "status" for what is only passive, "alert"
      // for what failed - and at most one action. No live-region attribute is added by
      // hand: the roles are the live regions, and a second one would announce twice.
      // ---------------------------------------------------------------

      function statusLine(className, content, role) {
        var line = el('p', className, content);
        line.setAttribute('role', role || 'status');
        return line;
      }

      function actionButton(className, label, hook) {
        var button = el('button', className, label);
        button.type = 'button';
        button.setAttribute(hook, '');
        return button;
      }

      /** Two skeleton rows the shape of a result card. Static: prices and stock appear with no animation. */
      function buildSkeletonList() {
        var list = el('div', 'tt-pro-loading-list');
        list.setAttribute('aria-hidden', 'true');
        for (var i = 0; i < 2; i++) {
          var row = el('div', 'tt-pro-loading-row');
          row.appendChild(el('span', 'tt-pro-skeleton tt-pro-loading-row__photo'));
          var lines = el('div', 'tt-pro-loading-row__lines');
          lines.appendChild(el('span', 'tt-pro-skeleton tt-pro-loading-row__title'));
          lines.appendChild(el('span', 'tt-pro-skeleton tt-pro-loading-row__line'));
          lines.appendChild(el('span', 'tt-pro-skeleton tt-pro-loading-row__short'));
          row.appendChild(lines);
          row.appendChild(el('span', 'tt-pro-skeleton tt-pro-loading-row__action'));
          list.appendChild(row);
        }
        return list;
      }

      /** "Searching 225/55R18" (or plain "Searching…" with no size to name) over the skeletons. */
      function buildLoadingState(root, size) {
        var template = text(root, 'searching-size');
        var message = size && template ? template.replace('{size}', size) : text(root, 'searching');
        var state = el('div', 'tt-pro-state tt-pro-state--loading');
        state.appendChild(statusLine('tt-pro-note', message));
        state.appendChild(buildSkeletonList());
        return state;
      }

      /** The search failed: a bordered callout, the sentence as the alert, a secondary re-run. */
      function buildSearchError(root) {
        var box = el('div', 'tt-pro-callout tt-pro-callout--error');
        box.appendChild(statusLine('tt-pro-callout__message', text(root, 'error'), 'alert'));
        // A page cached before the retry strings existed has no attribute for them: an
        // English word beats a button with no words.
        box.appendChild(actionButton('tt-pro-btn2', text(root, 'retry') || 'Try again', 'data-pro-retry'));
        return box;
      }

      /** The earlier results are still on screen but the season re-quote failed: one row, a link to re-run it. */
      function buildKeptQuoteError(root, retryAttr) {
        var row = el('div', 'tt-pro-state-row');
        row.setAttribute('data-pro-quote-error', '');
        row.appendChild(stockMarker('error'));
        // A page from before the season sentence existed only has the search one.
        row.appendChild(statusLine('tt-pro-note tt-pro-state-row__text', text(root, 'season-error') || text(root, 'error'), 'alert'));
        row.appendChild(actionButton('tt-pro-link', text(root, 'retry-link') || 'Try again ›', retryAttr || 'data-pro-season-retry'));
        return row;
      }

      /** No winter tire in this size: the sentence, and a link back to every season. */
      function buildSeasonEmpty(labels) {
        var state = el('div', 'tt-pro-state');
        state.appendChild(statusLine('tt-pro-state__text', labels.empty));
        state.appendChild(actionButton('tt-pro-link', labels.showAll, 'data-pro-show-all-seasons'));
        return state;
      }

      /** DP-8 — the real `getCssVar` backing resolveCardThemeAccent in the
       * browser. The four quote-card-theme tokens are declared at `:root`
       * in sections/toee-pro-quote.liquid, so they're readable off
       * document.documentElement regardless of which dealer-pro screen
       * (the real quote card or the settings-screen preview) is asking. */
      function readCssVarFromDocument(name) {
        if (typeof getComputedStyle === 'undefined') return '';
        return getComputedStyle(document.documentElement).getPropertyValue(name);
      }

      function buildStockRow(root, stockView) {
        var row = el('div', 'tt-pro-row tt-pro-row--stock');
        if (stockView.state === 'call') {
          row.appendChild(stockMarker('warn'));
          var call = el('a', 'tt-pro-call');
          call.href = 'tel:' + stockView.phone;
          call.textContent = text(root, 'stock-call');
          row.appendChild(call);
          return row;
        }
        // One status and one total for the car owner, not a line per warehouse. The total is
        // summed in buildStockView. Rows that prove no count cannot say "In stock", so the card
        // falls back to the call-to-confirm link.
        if (!(stockView.total > 0)) {
          row.appendChild(stockMarker('warn'));
          var unproven = el('a', 'tt-pro-call');
          unproven.href = 'tel:' + (stockView.phone || CALL_PHONE);
          unproven.textContent = text(root, 'stock-call');
          row.appendChild(unproven);
          return row;
        }
        // Owner ruling (2026-09-08), now on the total: under LOW_STOCK_BELOW is red.
        var low = stockView.total < LOW_STOCK_BELOW;
        var box = el('div', 'tt-pro-stock');
        var statusText = low
          ? text(root, 'stock-low') || STOCK_WORDS.low
          : text(root, 'stock-in') || STOCK_WORDS.inStock;
        box.appendChild(
          el('span', 'tt-pro-stock__status ' + (low ? 'tt-pro-stock__status--low' : 'tt-pro-stock__status--ok'), statusText)
        );
        var countTemplate = stockView.atLeast
          ? text(root, 'stock-available-plus') || STOCK_WORDS.availablePlus
          : text(root, 'stock-available') || STOCK_WORDS.available;
        box.appendChild(el('span', 'tt-pro-stock__count', countTemplate.split('{count}').join(String(stockView.total))));
        row.appendChild(box);
        return row;
      }

      /**
       * Same-store path only. Shopify's login form rejects an absolute or
       * protocol-relative return_to, and we never want this link to leave
       * the shop. Missing location (node tests) falls back to "/".
       */
      function currentReturnPath() {
        var loc = typeof location !== 'undefined' ? location : null;
        var path = loc && loc.pathname ? String(loc.pathname) : '/';
        var search = loc && loc.search ? String(loc.search) : '';
        var combined = path + search;
        if (combined.charAt(0) !== '/' || combined.charAt(1) === '/' || combined.charAt(1) === '\\') return '/';
        return combined;
      }

      function guestLoginHref(root) {
        var base = (root && root.getAttribute && root.getAttribute('data-pro-login-url')) || '';
        if (!base && typeof document !== 'undefined' && document.querySelector) {
          var loginEl = document.querySelector('[data-pro-login-url]');
          base = (loginEl && loginEl.getAttribute('data-pro-login-url')) || '';
        }
        base = String(base || '/account/login');
        var sep = base.indexOf('?') === -1 ? '?' : '&';
        return base + sep + 'return_url=' + encodeURIComponent(currentReturnPath());
      }

      function buildPriceRow(root, priceView, quantity, settings, unitKey) {
        var row = el('div', 'tt-pro-row tt-pro-row--price');
        if (priceView.state === 'call') {
          // A text link with a phone icon; the locale string ends in its own "›".
          var call = el('a', 'tt-pro-link tt-pro-call');
          call.href = 'tel:' + priceView.phone;
          call.appendChild(svgIcon(CARD_ICONS.phone, 16, 24, 2));
          call.appendChild(el('span', null, text(root, 'price-call')));
          row.appendChild(call);
        } else if (priceView.state === 'guest') {
          // The /pro page has no site header, so this sentence is the login
          // door. return_url sends the dealer back to this page after sign-in.
          var guest = el('a', 'tt-pro-link tt-pro-price__guest', text(root, 'price-guest'));
          guest.href = guestLoginHref(root);
          row.appendChild(guest);
        } else {
          // Round 5 — the dealer's selling price only (buildSellingPrice):
          // the large figure is the per-tire selling price, the caption
          // "4 for $392.08" is the selling batch. Toee's cost (price.unit,
          // price.total) never reaches the DOM. No markup decided yet: a
          // prompt in place of any figure (the click opens settings in
          // first-run mode, see ProSearch#_onResultsClick).
          var qty = quantity || 1;
          var selling = buildSellingPrice(priceView, qty, settings);
          if (!selling || !selling.configured) {
            var prompt = el('button', 'tt-pro-btn2 tt-pro-price__setup', text(root, 'price-markup-needed'));
            prompt.type = 'button';
            prompt.setAttribute('data-pro-markup-needed', '1');
            row.appendChild(prompt);
            return row;
          }
          // "$98.02" / "98,02 $" (formatMoney): the symbol keeps its own span, on
          // whichever side the page's language writes it.
          var unitMoney = moneyParts(selling.unit);
          var amount = el('span', 'tt-pro-price__amount');
          if (unitMoney.before) amount.appendChild(el('span', 'tt-pro-price__currency', unitMoney.before));
          amount.appendChild(el('span', 'tt-pro-price__unit', unitMoney.digits));
          if (unitMoney.after) amount.appendChild(el('span', 'tt-pro-price__currency', unitMoney.after));
          row.appendChild(amount);
          var caption = el('span', 'tt-pro-price__caption');
          // "Per tire · 4 for $392.08": the batch wording is the locale's own
          // string (dealer_pro.price.batch_total), its {total} the formatted
          // money; with none on the page the caption is just the unit label
          // rather than invented English.
          var tpl = text(root, 'price-batch-total');
          // HTML an installed app cached before Release 6 still carries the string with its own
          // "$" ("{count} for ${total}", "{count} pour {total} $"): drop it, formatMoney writes it.
          // split / join, not replace: "$" is special in a replacement string.
          if (tpl) tpl = tpl.split('${total}').join('{total}').split('{total} $').join('{total}');
          if (qty > 1 && tpl) {
            var parts = tpl.replace('{count}', String(qty)).split('{total}');
            var batchMoney = moneyParts(selling.batch);
            // The lead-in is its own element. A text node plus a child span drops the
            // words under the test DOM, which only reads element children.
            var lead = text(root, unitKey || 'price-per-tire') + ' · ' + parts[0] + batchMoney.before;
            if (lead) caption.appendChild(el('span', null, lead));
            caption.appendChild(el('span', 'tt-pro-price__batch', batchMoney.digits));
            var tail = batchMoney.after + (parts[1] || '');
            if (tail) caption.appendChild(el('span', null, tail));
          } else {
            caption.textContent = text(root, unitKey || 'price-per-tire');
          }
          if (caption.textContent) row.appendChild(caption);
        }
        return row;
      }

      /**
       * §4.4 — a live countdown always wins over the extraDay label: a
       * quote can be extra_day_required and still have an open ordering
       * window (countdownSeconds > 0), and losing that window silently
       * would be the same class of bug as freezing the digits (finding
       * #1) — the dealer stops seeing a deadline that still applies. The
       * extraDay label is the fallback wording once the window is gone.
       *
       * Final review finding #3 — `isStale` (true for an offline/cached
       * result — see ProSearch#_render's offlineInfo) suppresses the
       * countdown text specifically, not just the interval that used to
       * tick it. The old fix only stopped startCountdowns from running;
       * this function still wrote "order within 1h 00m 00s" from the
       * cached countdownSeconds into the card text every time, which read
       * as a live deadline hours after it was captured. A stale result
       * falls through to the same static wording a live result with no
       * countdown at all would show.
       */
      buildPromiseRow = function (root, promiseView, isStale) {
        if (!promiseView.visible) return null;
        var row = el('div', 'tt-pro-row tt-pro-row--promise');
        var badge = promiseView.showSameDayBadge
          ? el('span', 'tt-pro-badge tt-pro-badge--same-day', text(root, 'same-day-badge'))
          : null;
        var line = el('span', 'tt-pro-promise__line');
        // Design round 3 — the delivery line is its own band with an icon, so
        // it never reads as one more stock row. Rollout Task 2 (Cards
        // artboards): the truck means a delivery is coming (the same-day
        // countdown, or the server's own sentence), the clock means it comes
        // later (next business day, or an extra day), and the warning truck,
        // the only orange one, means we cannot fill the whole quantity.
        var live = promiseView.showCountdown && !isStale;
        var icon = 'truck';
        if (!live && promiseView.shortStock) icon = 'truck-warning';
        else if (!live && (promiseView.extraDay || promiseView.promiseWindow === 'next_day')) icon = 'clock';
        if (live) {
          var prefix = text(root, 'countdown-prefix');
          var suffix =
            promiseView.promiseWindow === 'next_day' ? text(root, 'next-business-day') : text(root, 'same-day-suffix');
          line.textContent = prefix + fmtHMS(promiseView.countdownSeconds) + suffix;
          row.setAttribute('data-pro-countdown', String(promiseView.countdownSeconds));
          row.setAttribute('data-pro-countdown-prefix', prefix);
          row.setAttribute('data-pro-countdown-suffix', suffix);
        } else if (promiseView.shortStock && promiseView.shortStock.count != null) {
          // With no count there is no number to say here; the card's reason says why it cannot quote.
          line.textContent = text(root, 'short-stock')
            .replace('{count}', String(promiseView.shortStock.count))
            .replace('{quantity}', String(promiseView.shortStock.quantity));
        } else if (promiseView.extraDay) {
          line.textContent = text(root, 'extra-day');
        } else if (promiseView.promiseWindow === 'next_day') {
          line.textContent = text(root, 'next-business-day-static');
        } else {
          // Nothing of our own to say: fall back to the server's sentence,
          // e.g. "In stock at your serving warehouse — same-day delivery
          // isn't available to your area today". Empty when the API is
          // older than this file, which the guard below handles.
          line.textContent = promiseView.displayLine || '';
        }
        // Live finding (2026-09-08): quote-batch answers route_unavailable
        // with promiseWindow null when same-day is not offered to the
        // dealer's area today, and forwards no wording for it — so this
        // row used to render as 44px of nothing under every such card.
        // No badge and no line means no row.
        if (!promiseView.showSameDayBadge && !line.textContent) return null;
        var iconEl = el('span', 'tt-pro-promise__icon tt-pro-promise__icon--' + icon);
        iconEl.setAttribute('aria-hidden', 'true');
        iconEl.appendChild(svgIcon(CARD_ICONS[icon === 'clock' ? 'clock' : 'truck'], 20, 24, 1.75));
        row.appendChild(iconEl);
        if (badge) row.appendChild(badge);
        row.appendChild(line);
        return row;
      };

      /**
       * imageUrl is null whenever identity couldn't confirm this variant (contract §4.2.1) — never
       * render an empty <img> or a broken-image icon for that case. The 80x80 box is still drawn,
       * so the card never shifts: a soft-fill plate holding a neutral tire (or wheel) glyph.
       * Explicit width/height (DESIGN.md Image Behavior) plus loading="lazy" so a long
       * result list doesn't jank the page as it scrolls into view. Styling follows
       * DESIGN.md's model-card-photo: {rounded.none}, {colors.surface-card} background, image
       * edge-to-edge within its plate (see .tt-pro-card-photo in toee-pro-search.liquid).
       */
      function buildResultPhoto(vm) {
        if (!vm.imageUrl) {
          var kind = vm.packageSide === 'rim' ? 'wheel' : 'tire';
          var empty = el('div', 'tt-pro-card-photo tt-pro-card-photo--empty tt-pro-card-photo--' + kind);
          empty.appendChild(svgIcon(CARD_GLYPHS[kind], 48, 48, 1.5));
          return empty;
        }
        var wrap = el('div', 'tt-pro-card-photo');
        var img = el('img', 'tt-pro-card-photo__img');
        img.setAttribute('src', vm.imageUrl);
        // Decorative: the title is printed right beside the photo, so naming the image would
        // only read the title twice (Task 13). Never a made-up description either.
        img.setAttribute('alt', '');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('width', '80');
        img.setAttribute('height', '80');
        wrap.appendChild(img);
        return wrap;
      }

      // Contract `title` already contains brand and size — concatenating a
      // separate brand field here just prints the brand twice.
      // `isStale` (finding #3) is only ever passed through to
      // buildPromiseRow — nothing else on the card changes for a cached
      // result, it's already labelled by the offline banner above it.
      /** Option B — "3 tires · 205/55R16" above the list. Nothing when the
       * page carries no strings for it (older fixtures) so the list itself
       * never depends on it. */
      function buildResultsHead(root, count, query) {
        var label =
          count === 1 ? text(root, 'results-count-one') : text(root, 'results-count-other').replace('{count}', String(count));
        if (!label) return null;
        if (typeof query === 'string' && query.trim()) label += ' · ' + query.trim();
        return el('p', 'tt-pro-results-head', label);
      }

      /** Package mode, a card with no button: the 8px error square and the reason, in the stock line's voice.
       * Null when the page has no words for it (an older cached page): a square with no sentence says nothing. */
      function buildCardReason(message) {
        if (!message) return null;
        var reason = el('div', 'tt-pro-card__reason');
        reason.appendChild(stockMarker('error'));
        reason.appendChild(el('span', null, message));
        return reason;
      }

      /**
       * `currentPackage` ({ tire, rim }, from PackageController#pkg) only matters in package
       * mode: it decides whether this pick completes the package and whether it fits it.
       * It is null on a page with no package controller yet.
       */
      // Numbers each card's title id; ids only need to be unique on the page.
      var resultCardCount = 0;

      buildResultCard = function (root, vm, isStale, settings, packageModeActive, quoteAction, currentPackage, cardPrimaryClass) {
        var card = el('li', 'tt-pro-card');
        // Which tire this is, so focus can find its card again after a re-render (ProSearch#_settleCardFocus).
        if (vm.raw && vm.raw.variantId != null) card.setAttribute('data-pro-variant', String(vm.raw.variantId));
        // Always drawn (a glyph stands in for a missing photo), so the card never shifts.
        card.appendChild(buildResultPhoto(vm));
        var titleRow = el('div', 'tt-pro-row tt-pro-row--title', vm.title);
        // Every card button reads as "Generate quote, 225/55R18 ..." to a screen reader.
        resultCardCount += 1;
        var titleId = 'tt-pro-card-title-' + resultCardCount;
        titleRow.setAttribute('id', titleId);
        function cardButton(className, label) {
          var button = el('button', className, label);
          button.type = 'button';
          button.setAttribute('aria-describedby', titleId);
          return button;
        }
        // Package (plan 2026-09-11) — the fitment line lives inside the title
        // cell, so the card's grid areas are untouched and the line always
        // sits right under the title.
        var fitment = vm.raw && vm.raw.fitment;
        var hubRing = fitment && fitment.hubRing;
        // The link needs its words; a page cached before the string existed keeps the ring in the line.
        var showRing = !!(hubRing && hubRing.href && hubRing.outerMm != null && hubRing.innerMm != null && text(root, 'hub-ring'));
        var fitmentLine = buildFitmentLine(fitment, text(root, 'fitment-delta'), showRing, {
          hubRing: text(root, 'fitment-hub-ring'),
          oe: text(root, 'fitment-oe'),
          hints: {
            diameter: text(root, 'fitment-hint-diameter'),
            bolt: text(root, 'fitment-hint-bolt'),
            bore: text(root, 'fitment-hint-bore')
          }
        });
        if (fitmentLine) titleRow.appendChild(el('span', 'tt-pro-card__fitment', fitmentLine));
        if (showRing) {
          // "Hub ring 73.1 → 64.1 ›": a text link to the ring product, same tab.
          var ringText = text(root, 'hub-ring').replace('{from}', String(hubRing.outerMm)).replace('{to}', String(hubRing.innerMm));
          var ringLink = el('a', 'tt-pro-link tt-pro-card__hub-ring', ringText);
          ringLink.setAttribute('href', hubRing.href);
          ringLink.setAttribute('data-pro-hub-ring', '');
          titleRow.appendChild(ringLink);
        }
        card.appendChild(titleRow);
        card.appendChild(buildStockRow(root, vm.stock));
        var promiseRow = buildPromiseRow(root, vm.promise, isStale);
        if (promiseRow) card.appendChild(promiseRow);
        card.appendChild(
          buildPriceRow(root, vm.price, vm.raw && vm.raw.requestedQuantity, settings, vm.packageSide === 'rim' ? 'price-per-wheel' : 'price-per-tire')
        );
        // Everything the dealer can press (or, in package mode, why they cannot)
        // goes in one row: it dissolves into the card's grid on a phone and is
        // the stacked rail from 768px (see the card CSS).
        var actions = el('div', 'tt-pro-row tt-pro-row--actions');
        // DP-7 — only offer a quote once there's a real, authoritative
        // price to build one from; a guest/call-to-confirm price has
        // nothing a dealer can stand behind on a card.
        // Until the markup is decided the price slot holds the secondary "Set your markup to
        // see prices" button, which opens first-run settings: a blue "Generate quote"
        // beside it would only do the same thing. ("Use as front / rear" quotes nothing.)
        var markupSet = !!(settings && settings.markupConfigured);
        var canQuote = !!(vm.price && vm.price.state === 'ok' && vm.raw && quoteAction !== false && (quoteAction || markupSet));
        // Package (2026-09-23) — owner ruling: while a package is in
        // progress (packageModeActive), a card shows at most ONE action
        // button, never two, to cut misclick risk. A package-eligible card
        // (vm.packageSide) gets a single primary wired exactly like the
        // ordinary "Make tire & wheel package" one (data-pro-package-add/
        // -quote). Rollout Task 2: its label says what the tap does — "Generate
        // quote" when the other half is already in the package (this pick
        // completes it, and PackageController.addToPackage auto-generates the
        // combined quote the instant that happens, see assets/toee-pro-package.js),
        // "Add to package" otherwise. A card that cannot go in (no fitment on
        // this result, or it clashes with what the package holds) gets no
        // button, only the reason — the owner confirmed an unrelated
        // single-item quote mid-package is exactly the misclick this exists to
        // prevent.
        //
        // Owner ruling (2026-10-22), a hard block: a card whose stock cannot fill the
        // quantity (the short-stock band above) offers no quote of any kind and carries
        // no quote JSON, in every mode. Its reason, and one secondary button that sets
        // the results bar to what there is (ProSearch#_onResultsClick re-asks quote-batch).
        // With no count (a fresh "cannot fill" beside a capped row, or a promise asked at
        // another number that the rows cannot prove), the reason alone.
        var shortfall = vm.promise && vm.promise.shortStock;
        if (shortfall && quoteAction !== false) {
          var reason = buildCardReason(shortfallText('card', text(root, 'short-blocked-card'), shortfall, text(root, 'short-unproven-card')));
          reason.setAttribute('id', titleId + '-reason');
          actions.appendChild(reason);
          // A number to set, and only on a card that would otherwise quote (review M2): a guest or a
          // dealer with no markup could not quote at that number either. It re-prices the list there.
          if (shortfall.count >= 1 && canQuote) {
            var lower = cardButton('tt-pro-btn2 tt-pro-set-quantity', shortfallText('setQuantity', text(root, 'short-set-quantity'), shortfall));
            lower.setAttribute('data-pro-set-quantity', String(shortfall.count));
            lower.setAttribute('aria-describedby', titleId + ' ' + titleId + '-reason');
            if (vm.raw && vm.raw.variantId != null) lower.setAttribute('data-pro-variant', String(vm.raw.variantId));
            actions.appendChild(lower);
          }
        } else if (packageModeActive) {
          if (vm.packageSide && vm.raw) {
            var conflict = packageConflictFor(currentPackage, vm.packageSide, vm.raw);
            if (conflict) {
              var conflictReason = buildCardReason(text(root, 'conflict-' + conflict) || text(root, 'conflict-unknown'));
              if (conflictReason) actions.appendChild(conflictReason);
            } else {
              var completes = !!(currentPackage && currentPackage[vm.packageSide === 'tire' ? 'rim' : 'tire']);
              // A page cached before "Add to package" existed reads "Generate quote", the label this button had.
              var pkgOnlyBtn = cardButton(
                cardPrimaryClass || 'tt-pro-generate-quote',
                completes ? text(root, 'generate-quote') : text(root, 'package-add-to') || text(root, 'generate-quote')
              );
              pkgOnlyBtn.setAttribute('data-pro-package-add', vm.packageSide);
              pkgOnlyBtn.setAttribute('data-pro-package-quote', JSON.stringify(vm.raw));
              actions.appendChild(pkgOnlyBtn);
            }
          } else {
            var ineligibleReason = buildCardReason(text(root, 'package-ineligible'));
            if (ineligibleReason) actions.appendChild(ineligibleReason);
          }
        } else {
          if (canQuote) {
            // The quote bar owns the one filled action. A card chooses a tire; it is not a second blue Quote.
            var genBtn = cardButton(cardPrimaryClass || 'tt-pro-generate-quote', quoteAction || text(root, 'generate-quote'));
            genBtn.setAttribute('data-pro-generate-quote', JSON.stringify(vm.raw));
            // What markCardQuoted turns this button into once the quote is made.
            // "Use as front / rear" picks a tire for an axle, it quotes nothing.
            var quotedLabel = quoteAction ? '' : text(root, 'quoted');
            if (quotedLabel) genBtn.setAttribute('data-pro-quoted-label', quotedLabel);
            actions.appendChild(genBtn);
          }
          // Package (plan 2026-09-11) — guests included: fitment is not a
          // price, and the package bar is where a guest is told to log in.
          // Front/rear picking uses this same button for the axle, so the
          // package action stays off until the dealer is back to one size.
          if (vm.packageSide && vm.raw && quoteAction == null) {
            var pkgBtn = cardButton('tt-pro-btn2 tt-pro-package-add', text(root, 'package-add'));
            pkgBtn.setAttribute('data-pro-package-add', vm.packageSide);
            pkgBtn.setAttribute('data-pro-package-quote', JSON.stringify(vm.raw));
            actions.appendChild(pkgBtn);
          }
        }
        if (actions.children.length) card.appendChild(actions);
        return card;
      };

      /**
       * DP-3 no-results fallback (PRD §9.2). Never an empty node: with an
       * alternative it offers a clickable nearest in-stock size (re-runs
       * the same search via data-pro-alt-query, wired in ProSearch below);
       * without one it still always offers the call-to-transfer action.
       */
      buildNoResultsFallback = function (root, view) {
        var wrap = el('div', 'tt-pro-empty-fallback');
        // "No match for 225/55R19." when we know the size and have a nearby one to offer;
        // the generic sentence otherwise. A passive message, so a status.
        var forSize = text(root, 'empty-for-size');
        var sentence = view.hasAlternative && view.searched && forSize
          ? forSize.replace('{size}', view.searched)
          : text(root, 'empty');
        wrap.appendChild(statusLine('tt-pro-state__text', sentence));
        if (view.hasAlternative) {
          // The older markup only has the prefix attribute ("Closest in-stock size: ").
          wrap.appendChild(el('span', 'tt-pro-empty-fallback__label', text(root, 'empty-alt-label') || text(root, 'empty-alt-prefix')));
          var alt = el('button', 'tt-pro-btn2 tt-pro-alt-size', view.altLabel);
          alt.type = 'button';
          alt.setAttribute('data-pro-alt-query', view.altQuery);
          wrap.appendChild(alt);
        }
        // Always there: a dealer on slow wifi always has something to do. The locale
        // string ends in its own "›".
        var call = el('a', 'tt-pro-link tt-pro-call');
        call.href = 'tel:' + view.phone;
        call.appendChild(svgIcon(CARD_ICONS.phone, 16, 24, 2));
        call.appendChild(el('span', null, text(root, 'empty-call-transfer')));
        wrap.appendChild(call);
        return wrap;
      };

      // Tracks every currently-running countdown interval so a fresh
      // render can stop them all — otherwise each search leaks one
      // setInterval per card, left ticking against DOM nodes nothing
      // shows any more.
      var activeCountdownTimers = [];

      /** Clears every currently-running countdown interval without
       * starting new ones — the half of startCountdowns a stale/offline
       * render needs on its own (final review finding #4): its cards
       * replace whatever a previous live render put in the DOM, so that
       * render's timers must stop even though this one never starts any
       * of its own. */
      function stopCountdowns() {
        activeCountdownTimers.forEach(function (timerId) {
          clearInterval(timerId);
        });
        activeCountdownTimers = [];
      }

      /**
       * Ticks every live countdown row once a second by rebuilding and
       * re-assigning its text — never writing the same string back, which
       * is what froze the display in finding #1. No CSS transition is
       * applied to the digits (Motion rules: countdowns replace the
       * value, they don't animate it).
       */
      startCountdowns = function (list) {
        stopCountdowns();

        var rows = list.querySelectorAll('[data-pro-countdown]');
        for (var i = 0; i < rows.length; i++) {
          (function (row) {
            var remaining = parseInt(row.getAttribute('data-pro-countdown'), 10) || 0;
            var prefix = row.getAttribute('data-pro-countdown-prefix') || '';
            var suffix = row.getAttribute('data-pro-countdown-suffix') || '';
            var line = row.querySelector('.tt-pro-promise__line');
            var timerId = setInterval(function () {
              remaining -= 1;
              if (line) {
                line.textContent = prefix + fmtHMS(Math.max(remaining, 0)) + suffix;
              }
              if (remaining <= 0) {
                clearInterval(timerId);
              }
            }, 1000);
            activeCountdownTimers.push(timerId);
          })(rows[i]);
        }
      };

      /**
       * DP-3 fix round, finding #3 — walks the reused size-options cascade
       * (comboUrl + parseSizeOptions, both from window.ToeeSizeCombo) to
       * find a real, mutually-compatible in-stock alternative nearest to a
       * size that came back with no results. Rim diameter is the one
       * dimension that physically cannot flex (a 17" wheel takes a 17"
       * tire) so it is locked *first*, straight from the query — the
       * diameter_<n> handle is built locally with combo.tagHandle, never
       * discovered from an unfiltered fetch — and width, then aspect, are
       * narrowed to their nearest in-stock value within that fixed
       * diameter. Two requests, not three, and diameter never drifts.
       */
      resolveNearestAlternative = function (baseUrl, parsed, combo) {
        function fetchGroups(handles) {
          return fetch(combo.comboUrl(baseUrl, handles), { credentials: 'same-origin' })
            .then(function (r) {
              return r.ok ? r.json() : Promise.reject(r.status);
            })
            .then(function (json) {
              return combo.parseSizeOptions(json).groups || {};
            });
        }
        var diameterHandle = combo.tagHandle('Diameter', parsed.diameter);
        if (!diameterHandle) return Promise.resolve(null);

        return fetchGroups([diameterHandle]).then(function (groups) {
          var width = nearestOption(parsed.width, groups.Width || []);
          if (!width) return null;
          return fetchGroups([diameterHandle, width.handle]).then(function (groups2) {
            var aspect = nearestOption(parsed.aspect, groups2.Aspect || []);
            if (!aspect) return null;
            // Never offer back the size that was just searched. "Closest
            // in-stock size: 205/55R16" under a search for 205/55R16 is what
            // a dealer actually saw, and it reads as the tool being broken
            // rather than as an alternative. nearestOption minimises
            // distance, so zero distance — an exact match — is its most
            // likely answer whenever the size does exist in the catalogue.
            if (
              parseFloat(width.label) === parsed.width &&
              parseFloat(aspect.label) === parsed.aspect
            ) {
              return null;
            }
            return { width: width.label, aspect: aspect.label, diameter: String(parsed.diameter) };
          });
        });
      };

      ProSearch = function (formEl) {
        this.form = formEl;
        this.input = formEl.querySelector('[data-pro-search-input]');
        this.results = document.querySelector(formEl.getAttribute('data-pro-results-target'));
        this.staggered = staggeredState();
        this.staggeredReplacing = false;
        this.quantityTouched = false;
        this.season = 'all';
        this._sizeVariants = null;
        // Fix round finding #2 — grabbed once here, same as this.results,
        // so _setStale below never has to search the whole document.
        this.offlineBanner = document.querySelector('[data-pro-offline-banner]');
        this.requestId = 0;
        formEl.addEventListener('submit', this._onSubmit.bind(this));

        // Design round 3 — quantity stepper (sections/toee-pro-search.liquid).
        // Seeded from the saved default. A change reprices the rows already
        // on screen (locally for fewer tires every promise can still fill,
        // quote-batch for more, for a short tire and at the bulk tier) so the
        // price and the promise shown are the quantity shown (final review
        // finding #1; the short-stock block, 2026-10-22).
        this.quantityInput = document.querySelector('[data-pro-quantity]');
        this.lastQuery = '';
        if (this.quantityInput) {
          var self = this;
          this.quantityInput.value = String(readSearchQuantity(null, readLocalSettings() || settingsDefaults()));
          this.quantityInput.addEventListener('change', this._onQuantityChange.bind(this));
          // Typing or pasting moves the buttons' limits too, before the field is left.
          this.quantityInput.addEventListener('input', this.syncStepperBounds.bind(this));
          this.quantityStepButtons = Array.prototype.slice.call(document.querySelectorAll('[data-pro-quantity-step]'));
          this.quantityStepButtons.forEach(function (btn) {
            btn.addEventListener('click', function () {
              var step = Number(btn.getAttribute('data-pro-quantity-step')) || 0;
              self.quantityInput.value = String(readSearchQuantity({ value: readSearchQuantity(self.quantityInput, {}) + step }, {}));
              self._onQuantityChange();
            });
          });
          this.syncStepperBounds();
        }

        // DP-3 cascade — width/aspect/diameter dropdowns feed the exact
        // same _search() the text input uses (see _onCascadeChange below).
        // Population and narrowing of the selects themselves is handled
        // entirely by assets/toee-home.js's existing cascade wiring
        // (data-toee-search / data-mode-fields / data-combo-src) against
        // window.ToeeSizeCombo — not duplicated here.
        var cascadeWrap = document.querySelector('[data-pro-cascade]');
        this.cascadeTargetUrl = cascadeWrap ? cascadeWrap.getAttribute('data-target-url') || '' : '';
        this.widthSelect = document.querySelector('[data-pro-size-field="width"]');
        this.aspectSelect = document.querySelector('[data-pro-size-field="aspect"]');
        this.diameterSelect = document.querySelector('[data-pro-size-field="diameter"]');
        if (this.diameterSelect) {
          this.diameterSelect.addEventListener('change', this._onCascadeChange.bind(this));
        }
        if (this.results) {
          this.results.addEventListener('click', this._onResultsClick.bind(this));
        }
        this._initStaggeredControls();
        this._initSeasonControls();
        document.addEventListener('toee-pro:settings-closed', this.rerender.bind(this));
        // Package (2026-09-23) - a card already on screen when package
        // mode starts or ends does not redraw itself; PackageController
        // dispatches this the same way settings-closed already works
        // above, so the currently-visible results pick up the new
        // packageModeActive value without a network re-fetch.
        document.addEventListener('toee-pro:package-changed', this.rerender.bind(this));
      };

      ProSearch.prototype._initSeasonControls = function () {
        var self = this;
        var bar = document.querySelector('.tt-pro-results-bar');
        if (!bar) return;
        this.seasonLabels = winterSeasonLabels(bar);

        this.allSeasonButton = document.createElement('button');
        this.allSeasonButton.type = 'button';
        this.allSeasonButton.textContent = this.seasonLabels.all;
        this.allSeasonButton.setAttribute('data-pro-season', 'all');
        this.allSeasonButton.setAttribute('aria-pressed', 'true');
        bar.appendChild(this.allSeasonButton);

        this.winterSeasonButton = document.createElement('button');
        this.winterSeasonButton.type = 'button';
        this.winterSeasonButton.textContent = this.seasonLabels.winter;
        this.winterSeasonButton.setAttribute('data-pro-season', 'winter');
        this.winterSeasonButton.setAttribute('aria-pressed', 'false');
        bar.appendChild(this.winterSeasonButton);

        this.allSeasonButton.addEventListener('click', function () {
          self._chooseSeason('all');
        });
        this.winterSeasonButton.addEventListener('click', function () {
          self._chooseSeason('winter');
        });
      };

      ProSearch.prototype._chooseSeason = function (season) {
        var decision = applySearchSeason({
          season: this.season,
          staggered: this.staggered,
          sizeVariants: this._sizeVariants,
          limit: RESULT_LIMIT
        }, season);
        this.season = decision.season;
        this.staggered = decision.staggered;
        this._paintSeason();
        if (!this._sizeVariants) return;
        if (decision.emptyWinter) {
          this._showSeasonEmpty();
          return;
        }
        var cached = quotesForSeason(this._quotedById, this._sizeVariants, this.season, RESULT_LIMIT);
        if (cached) {
          this.lastQuotes = cached;
          this._render(cached, this.lastQuery, this.requestId, this.lastOfflineInfo);
          return;
        }
        if (!decision.quoteIds || !decision.quoteIds.length) return;
        var quantity = readSearchQuantity(this.quantityInput, readLocalSettings() || settingsDefaults());
        var fitmentById = {};
        var size = this._sizeLabel || '';
        decision.quoteIds.forEach(function (id) {
          fitmentById[id] = { kind: 'tire', size: size };
        });
        this._quoteAndRender(decision.quoteIds, this.lastQuery, ++this.requestId, quantity, fitmentById, true);
      };

      ProSearch.prototype._paintSeason = function () {
        if (this.allSeasonButton) {
          this.allSeasonButton.setAttribute('aria-pressed', this.season === 'all' ? 'true' : 'false');
        }
        if (this.winterSeasonButton) {
          this.winterSeasonButton.setAttribute('aria-pressed', this.season === 'winter' ? 'true' : 'false');
        }
      };

      ProSearch.prototype._showSeasonEmpty = function () {
        this.lastQuotes = null;
        this.lastOfflineInfo = null;
        if (typeof this._setStale === 'function') this._setStale(null);
        if (!this.results) return;
        this._setSearchBusy(false);
        this.results.replaceChildren();
        var state = buildSeasonEmpty(this.seasonLabels || winterSeasonLabels(null));
        this.results.appendChild(state);
        this._settleFocus(state);
      };

      // `reprice`: the list the stepper moved could not be asked again; its Try again re-asks it
      // (a season switch's Try again switches again).
      ProSearch.prototype._appendKeptQuoteError = function (reprice) {
        if (!this.results) return;
        this._setSearchBusy(false);
        var previous = this.results.querySelector('[data-pro-quote-error]');
        if (previous) previous.remove();
        var row = buildKeptQuoteError(this.results, reprice ? 'data-pro-reprice-retry' : 'data-pro-season-retry');
        this.results.appendChild(row);
        this._settleFocus(row);
      };

      // "Try again", "Try again ›" and "Show all seasons ›" sit in a block that the very
      // render they start replaces, and a removed element drops focus to <body>. When the
      // pressed control had focus, the block that takes its place takes it: tabindex -1
      // lets script focus a block without making it another stop in the tab order. The
      // hold lasts until a render that ends the wait (a "searching" block keeps it for the
      // results that follow); focus the dealer has moved elsewhere is left alone.
      ProSearch.prototype._holdFocus = function (control) {
        this._focusPending = !!(control && typeof document !== 'undefined' && document.activeElement === control);
      };

      /** After "Set quantity" from the keyboard: the same tire's card primary in the new `list`
       * takes focus, without scrolling. False when there is none to take it (the list does). */
      ProSearch.prototype._settleCardFocus = function (list) {
        var variant = this._focusVariant;
        if (!this._focusPending || variant == null || !list) return false;
        this._focusVariant = null;
        var card = list.querySelector('[data-pro-variant="' + String(variant).replace(/["\\]/g, '\\$&') + '"]');
        var primary = card && (card.querySelector('[data-pro-generate-quote]') || card.querySelector('[data-pro-package-add]') ||
          card.querySelector('[data-pro-set-quantity]'));
        if (!primary || typeof primary.focus !== 'function') return false;
        this._focusPending = false;
        primary.focus({ preventScroll: true });
        return true;
      };

      ProSearch.prototype._settleFocus = function (block, keepHolding) {
        this._focusVariant = null;
        if (!this._focusPending || !block) return;
        var active = typeof document !== 'undefined' ? document.activeElement : null;
        if (active && active !== document.body) {
          this._focusPending = false;
          return;
        }
        block.setAttribute('tabindex', '-1');
        if (typeof block.focus === 'function') block.focus();
        if (!keepHolding) this._focusPending = false;
      };

      // The Search button is off while a search is out, so a second tap cannot start a
      // second one (and pressing Enter in the field, which submits through that button,
      // waits too). Every way a search can end turns it back on, and so does the clock:
      // no request here has a timeout, so one that never settles (a captive portal that
      // hangs) would otherwise leave the button off for good. Turning it on early is safe,
      // a newer search supersedes an older one through requestId.
      var SEARCH_BUSY_LIMIT_MS = 15000;
      ProSearch.prototype._setSearchBusy = function (busy) {
        var button = this.form && this.form.querySelector && this.form.querySelector('.tt-pro-search__button');
        if (button) button.disabled = !!busy;
        if (this._busyTimer) clearTimeout(this._busyTimer);
        this._busyTimer = null;
        if (!busy) return;
        var self = this;
        this._busyTimer = setTimeout(function () {
          self._busyTimer = null;
          self._setSearchBusy(false);
        }, SEARCH_BUSY_LIMIT_MS);
        // Node (the unit tests) hands back a Timeout object; it must not hold the process open.
        if (this._busyTimer && typeof this._busyTimer.unref === 'function') this._busyTimer.unref();
      };

      // The hint sits above the three doors. A search, an empty list, an error,
      // or a vehicle or VIN lookup hides it.
      ProSearch.prototype.dismissFirstHint = function () {
        var hint = null;
        var scope = this.form && this.form.closest && this.form.closest('.tt-pro-scope');
        if (scope && scope.querySelector) hint = scope.querySelector('[data-pro-first-hint]');
        if (!hint && this.results && this.results.querySelector) hint = this.results.querySelector('[data-pro-first-hint]');
        if (hint) hint.hidden = true;
      };

      ProSearch.prototype._initStaggeredControls = function () {
        var self = this;
        var bar = document.querySelector('.tt-pro-results-bar');
        if (!bar || !this.form || !this.form.parentNode) return;
        this.staggeredLabels = staggeredControlLabels(this.results);
        // The two sentences the cells add. They read from the data-i18n-* attributes the
        // other results strings use; an older cached page has neither, so an empty cell
        // falls back to the dash it always showed and the "choosing" suffix is left off.
        this.staggeredText = {
          empty: text(this.results, 'staggered-empty') || '—',
          choosing: text(this.results, 'staggered-choosing')
        };

        // The words stay; the track is the on/off switch (blue, square corners).
        // aria-checked follows the strip, which gateStaggered sets from `hidden`.
        var toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'tt-pro-switch';
        toggle.setAttribute('data-pro-staggered-toggle', '');
        toggle.setAttribute('role', 'switch');
        toggle.setAttribute('aria-checked', 'false');
        var switchLabel = document.createElement('span');
        switchLabel.className = 'tt-pro-switch__label';
        switchLabel.textContent = this.staggeredLabels.open;
        var track = document.createElement('span');
        track.className = 'tt-pro-switch__track';
        track.setAttribute('aria-hidden', 'true');
        var knob = document.createElement('span');
        knob.className = 'tt-pro-switch__knob';
        track.appendChild(knob);
        toggle.appendChild(switchLabel);
        toggle.appendChild(track);
        bar.appendChild(toggle);

        var strip = document.createElement('div');
        strip.className = 'tt-pro-axles';
        strip.setAttribute('data-pro-staggered-strip', '');
        var cells = document.createElement('div');
        cells.className = 'tt-pro-axles__cells';
        strip.appendChild(cells);
        this.axleParts = {};
        function axleButton(axle) {
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'tt-pro-axle';
          button.setAttribute('data-pro-staggered-axle', axle);
          var label = document.createElement('span');
          label.className = 'tt-pro-axle__label';
          var value = document.createElement('span');
          value.className = 'tt-pro-axle__value';
          button.appendChild(label);
          button.appendChild(value);
          cells.appendChild(button);
          self.axleParts[axle] = { label: label, value: value, button: button };
          return button;
        }
        this.frontCell = axleButton('front');
        this.rearCell = axleButton('rear');
        var foot = document.createElement('div');
        foot.className = 'tt-pro-axles__foot';
        strip.appendChild(foot);
        this.oneSizeButton = document.createElement('button');
        this.oneSizeButton.type = 'button';
        this.oneSizeButton.className = 'tt-pro-link';
        this.oneSizeButton.textContent = this.staggeredLabels.exit;
        foot.appendChild(this.oneSizeButton);
        this.staggeredQuoteButton = document.createElement('button');
        this.staggeredQuoteButton.type = 'button';
        this.staggeredQuoteButton.textContent = this.staggeredLabels.quote;
        this.staggeredQuoteButton.className = 'tt-pro-generate-quote';
        foot.appendChild(this.staggeredQuoteButton);
        // Next to the form, but outside the size panel the Find-by switch hides in
        // Vehicle and VIN mode: a front / rear fitment from either would otherwise
        // render its strip into a hidden panel. Older markup, with no panel, keeps the
        // strip directly above the form.
        var host = this.form.parentNode;
        if (host.getAttribute && host.getAttribute('data-pro-find-panel') !== null && host.parentNode) {
          host.parentNode.insertBefore(strip, host);
        } else {
          host.insertBefore(strip, this.form);
        }
        this.staggeredStrip = strip;

        function openStaggered() {
          self.quantityTouched = false;
          var quantity = self.quantityInput
            ? staggeredOpenQuantity(self.quantityInput.value, self.quantityTouched)
            : 2;
          self.staggered = staggeredState({ mode: 'front' });
          self.staggeredReplacing = false;
          if (self.quantityInput) {
            self.quantityInput.value = String(quantity);
            self.syncStepperBounds();
          }
          self._renderStaggered();
          if (self.lastQuery) self._search(self.lastQuery);
          else self.rerender();
        }
        function closeStaggered() {
          var frontQuery = self.staggered.front && self.staggered.front._proQuery;
          self.staggered = staggeredExit(self.staggered);
          self.staggeredReplacing = false;
          if (self.quantityInput) {
            self.quantityInput.value = '4';
            self.syncStepperBounds();
          }
          if (frontQuery && self.input) self.input.value = frontQuery;
          self._renderStaggered();
          if (frontQuery || self.lastQuery) self._search(frontQuery || self.lastQuery);
          else self.rerender();
        }
        toggle.addEventListener('click', function () {
          if (self.staggered.mode === 'single') openStaggered();
          else closeStaggered();
        });
        this.oneSizeButton.addEventListener('click', closeStaggered);
        [this.frontCell, this.rearCell].forEach(function (cell) {
          cell.addEventListener('click', function () {
            self._activateStaggeredAxle(cell.getAttribute('data-pro-staggered-axle'));
          });
        });
        this.staggeredQuoteButton.addEventListener('click', function () {
          self._onStaggeredQuote();
        });
        this._renderStaggered();
      };

      ProSearch.prototype._activateStaggeredAxle = function (axle) {
        if (this.staggered.mode === 'single') return;
        this.staggered.mode = axle;
        var picked = this.staggered[axle];
        this.staggeredReplacing = !!picked;
        var qty = picked ? tireQuoteQuantity(picked) : 2;
        if (this.quantityInput) {
          this.quantityInput.value = String(qty);
          this.syncStepperBounds();
        }
        if (picked && picked._proQuery) {
          if (this.input) this.input.value = picked._proQuery;
          this._search(picked._proQuery);
        }
        this._renderStaggered();
        this.rerender();
      };

      function addMoneyFields(left, right) {
        var out = Object.assign({}, left || {});
        ['lineSubtotal', 'recyclingFee', 'deliverySurcharge', 'tax', 'total'].forEach(function (key) {
          out[key] = fromCents(toCents(left && left[key]) + toCents(right && right[key]));
        });
        return out;
      }

      ProSearch.prototype._combinedStaggeredQuote = function () {
        var front = this.staggered.front;
        var rear = this.staggered.rear;
        if (!front || !rear) return null;
        var combined = Object.assign({}, front);
        combined.price = addMoneyFields(front.price, rear.price);
        combined.requestedQuantity = tireQuoteQuantity(front) + tireQuoteQuantity(rear);
        combined.stock = {
          anyAvailable: !!(front.stock && front.stock.anyAvailable) &&
            !!(rear.stock && rear.stock.anyAvailable)
        };
        combined.promise = Object.assign({}, front.promise || rear.promise || {});
        // Each axle's own stock and promise, at its own quantity: the combined stock above
        // has no rows to count, and quoteShortfall must see a short axle (owner ruling 2026-10-22).
        // The same tire on both axles is one line at the total, which neither axle's promise was
        // asked at: only rows that prove the total let it through (review I3; the strip's Quote
        // does not open it anyway, see _onStaggeredQuote).
        var axles = String(front.variantId) === String(rear.variantId)
          ? [Object.assign({}, front, { qty: combined.requestedQuantity })]
          : [front, rear];
        combined.axleQuotes = axles.map(function (axle) {
          return { title: axle.title || '', requestedQuantity: tireQuoteQuantity(axle), promiseQuantity: axle.promiseQuantity, stock: axle.stock, promise: axle.promise, errors: axle.errors };
        });
        if (
          !front.promise || front.promise.promiseWindow !== 'same_day' ||
          !rear.promise || rear.promise.promiseWindow !== 'same_day'
        ) {
          combined.promise.promiseWindow = '';
        }
        if (String(front.variantId) === String(rear.variantId)) return combined;
        combined.rearVariantId = rear.variantId;
        combined.rearQty = tireQuoteQuantity(rear);
        combined.frontQty = tireQuoteQuantity(front);
        combined.lines = [
          { axle: 'front', title: front.title || '', quantity: combined.frontQty },
          { axle: 'rear', title: rear.title || '', quantity: combined.rearQty }
        ];
        return combined;
      };

      ProSearch.prototype._renderStaggered = function () {
        if (typeof this._paintQuoteBar === 'function') this._paintQuoteBar();
        if (!this.staggeredStrip) return;
        var single = this.staggered.mode === 'single';
        this.staggeredStrip.hidden = single;
        if (single) return;
        var state = this.staggered;
        var parts = this.axleParts;
        var words = this.staggeredText || {};
        // The cell the next card tap fills is the active (ink) one. Once both axles are
        // picked, and the dealer has not tapped one to replace it, nothing is left to choose.
        var picking = !(state.front && state.rear && !this.staggeredReplacing);
        function renderCell(axle, label) {
          var tire = state[axle];
          var active = picking && state.mode === axle;
          parts[axle].button.className = 'tt-pro-axle' + (active ? ' tt-pro-axle--active' : '');
          parts[axle].button.setAttribute('aria-pressed', active ? 'true' : 'false');
          parts[axle].label.textContent = label + (active && words.choosing ? ' \u00b7 ' + words.choosing : '');
          parts[axle].value.className = 'tt-pro-axle__value' + (tire ? '' : ' tt-pro-axle__value--empty');
          parts[axle].value.textContent = tire ? tire.title + ' \u00d7 ' + tireQuoteQuantity(tire) : (words.empty || '\u2014');
        }
        renderCell('front', this.staggeredLabels.front);
        renderCell('rear', this.staggeredLabels.rear);
        var complete = !!(state.front && state.rear);
        // Review I3: the same tire on both axles is one line whose stock was only checked per axle.
        // The button carries no quote then; it goes back to one size (_onStaggeredQuote).
        var same = complete && String(state.front.variantId) === String(state.rear.variantId);
        var quote = complete && !same && this._combinedStaggeredQuote();
        // Review I2: the same rule as every quote. A stepper change moves the active pick to a number
        // its promise was not asked at: off until quote-batch answers for it there (_render refreshes
        // the pick), unless its rows prove that number.
        this.staggeredQuoteButton.disabled = !complete || !!(quote && quoteShortfall(quote));
        if (quote) this.staggeredQuoteButton.setAttribute('data-pro-generate-quote', JSON.stringify(quote));
        else this.staggeredQuoteButton.removeAttribute('data-pro-generate-quote');
      };

      /** The strip's Quote with the same tire on both axles (review I3): no combined quote. Back to
       * one size at the total, where that tire's card is asked again at that number and the normal
       * block applies; the dealer quotes from the card. A pair of two tires is opened by the page's
       * [data-pro-generate-quote] handler instead. */
      ProSearch.prototype._onStaggeredQuote = function () {
        var front = this.staggered.front;
        var rear = this.staggered.rear;
        if (!front || !rear || String(front.variantId) !== String(rear.variantId)) return;
        var total = tireQuoteQuantity(front) + tireQuoteQuantity(rear);
        this.staggered = staggeredExit(this.staggered);
        this.staggeredReplacing = false;
        this._renderStaggered();
        if (!this.quantityInput) return;
        this.quantityInput.value = String(total);
        this._onQuantityChange();
      };

      // Makes the no-results alternative size (buildNoResultsFallback)
      // clickable — re-runs the same _search() with that size, same as
      // typing it or picking it from the cascade.
      ProSearch.prototype._onResultsClick = function (e) {
        var quoteButton = e.target && e.target.closest && e.target.closest('[data-pro-generate-quote]');
        if (quoteButton && this.staggered.mode !== 'single') {
          e.preventDefault();
          e.stopPropagation();
          var tire = null;
          try {
            tire = JSON.parse(quoteButton.getAttribute('data-pro-generate-quote'));
          } catch (err) {
            tire = null;
          }
          if (tire) this._pickStaggeredTire(tire);
          return;
        }
        if (quoteButton && this.staggered && this.staggered.mode === 'single' &&
            !(quoteButton.getAttribute && quoteButton.getAttribute('data-pro-quote-go') != null)) {
          var quoteHost = typeof document !== 'undefined' && document.querySelector && document.querySelector('[data-pro-quote-group]');
          if (quoteHost && typeof this._chooseForQuote === 'function') {
            this._chooseForQuote(quoteButton);
          }
        }
        var setup = e.target && e.target.closest && e.target.closest('[data-pro-markup-needed]');
        if (setup) {
          // The prompt goes along as the opener: Safari does not focus a clicked button.
          openSettingsFirstRun(setup);
          return;
        }
        function hit(selector) {
          return e.target && e.target.closest && e.target.closest(selector);
        }
        // The status family's actions: re-run the failed search, re-run the failed season
        // re-quote, re-ask a list the stepper moved, or leave a winter-empty size for All.
        this._focusPending = false;
        this._focusVariant = null;
        var retry = hit('[data-pro-retry]');
        if (retry) {
          this._holdFocus(retry);
          this._retryLast();
          return;
        }
        var repriceRetry = hit('[data-pro-reprice-retry]');
        if (repriceRetry) {
          this._holdFocus(repriceRetry);
          this._repriceOpenList(readSearchQuantity(this.quantityInput, readLocalSettings() || settingsDefaults()));
          return;
        }
        var seasonRetry = hit('[data-pro-season-retry]');
        if (seasonRetry) {
          this._holdFocus(seasonRetry);
          this._chooseSeason(this.season);
          return;
        }
        var showAll = hit('[data-pro-show-all-seasons]');
        if (showAll) {
          this._holdFocus(showAll);
          this._chooseSeason('all');
          return;
        }
        // A short card's "Set quantity to 2": the results bar's quantity, set the way the stepper
        // sets it. Pressed from the keyboard, that tire's new primary takes the focus the button
        // had (review M5, see _settleCardFocus); a tap leaves focus alone (Chrome and Android
        // focus a tapped button too, and a focus jump would scroll a phone).
        var setQuantity = hit('[data-pro-set-quantity]');
        if (setQuantity && this.quantityInput) {
          if (lastInputWasKeyboard && typeof document !== 'undefined' && document.activeElement === setQuantity) {
            this._focusPending = true;
            this._focusVariant = setQuantity.getAttribute('data-pro-variant');
          }
          this.quantityInput.value = setQuantity.getAttribute('data-pro-set-quantity');
          this._onQuantityChange();
          return;
        }
        var btn = e.target && e.target.closest && e.target.closest('[data-pro-alt-query]');
        var query = btn && btn.getAttribute('data-pro-alt-query');
        if (query) this._search(query);
      };

      ProSearch.prototype._pickStaggeredTire = function (tire) {
        var axle = this.staggered.mode;
        var quantity = readSearchQuantity(this.quantityInput, {});
        var picked = Object.assign({}, tire, {
          qty: quantity,
          requestedQuantity: quantity,
          _proQuery: this.lastQuery || (this.input && this.input.value) || ''
        });
        var vm = buildQuoteCardViewModel(
          picked,
          readLocalSettings() || settingsDefaults(),
          {},
          {},
          {}
        );
        picked.dealerTotal = vm.eligible ? vm.total : '0.00';
        this.staggered = staggeredAfterPick(this.staggered, axle, picked);
        this.staggeredReplacing = false;
        var vehicleRear = activeVehicleController().state.chosen;
        vehicleRear = vehicleRear && vehicleRear.rearTireSize;
        if (axle === 'front' && !this.staggered.rear && vehicleRear) {
          if (this.input) this.input.value = vehicleRear;
          this._search(vehicleRear);
        } else if (axle === 'front' && !this.staggered.rear && this.input) {
          this.input.value = '';
          // The size field is hidden while Vehicle or VIN mode is showing: focusing it then does nothing useful.
          if (typeof this.input.focus === 'function' && this.input.offsetParent !== null) this.input.focus();
        }
        this._renderStaggered();
        this.rerender();
      };

      /** Round 5 — after the settings panel closes (markup just saved, or
       * not), redraw the cards from the quotes already fetched so the
       * prompt turns into prices without a second network round trip. */
      function listHasRims(quotes) {
        if (!quotes || !quotes.length) return false;
        for (var i = 0; i < quotes.length; i++) {
          var fitment = quotes[i] && quotes[i].fitment;
          if (fitment && fitment.kind === 'rim') return true;
        }
        return false;
      }

      ProSearch.prototype.rerender = function () {
        if (!this.lastQuotes || !this.results) return;
        // Package mode redraws the cards in place. On the Tires tab that must
        // not put the wheel list back over a tire search that has not landed yet.
        var pkg = this.form && this.form._proPackage;
        if (pkg && pkg.mode === 'tires' && listHasRims(this.lastQuotes)) return;
        this._render(this.lastQuotes, this.lastQuery, this.requestId, this.lastOfflineInfo);
      };

      // First bulk discount is 50 tires (quote-draft-order bulkDiscountPercent).
      // The stepper stops at 99, so 50 is the only price change this screen can hit.
      var BULK_PRICE_QUANTITY = 50;

      function sameGoodsPriceTier(left, right) {
        return (left >= BULK_PRICE_QUANTITY) === (right >= BULK_PRICE_QUANTITY);
      }

      function scaleQuotedMoney(amount, fromQty, toQty) {
        if (!fromQty || fromQty === toQty) return amount;
        return fromCents(Math.round(toCents(amount) * toQty / fromQty));
      }

      function repriceStoredQuote(quote, quantity) {
        var fromQty = (quote && quote.requestedQuantity) || 1;
        if (quote && quote.price && fromQty !== quantity) {
          var lineSubtotal = scaleQuotedMoney(quote.price.lineSubtotal, fromQty, quantity);
          quote.price = Object.assign({}, quote.price, {
            lineSubtotal: lineSubtotal,
            recyclingFee: scaleQuotedMoney(quote.price.recyclingFee, fromQty, quantity),
            // One piece, delivery: 10% of the goods line. Two or more pieces: none.
            deliverySurcharge: quantity === 1 ? fromCents(Math.round(toCents(lineSubtotal) * 0.1)) : '0.00'
          });
        }
        if (quote) quote.requestedQuantity = quantity;
        return quote;
      }

      // The minus / plus buttons turn off at the input's limits (syncStepperButtons). Called
      // after every write to the input.
      ProSearch.prototype.syncStepperBounds = function () {
        if (!this.quantityInput || !this.quantityStepButtons) return;
        syncStepperButtons(this.quantityInput, this.quantityStepButtons, 'data-pro-quantity-step');
      };

      ProSearch.prototype._onQuantityChange = function () {
        if (!this.quantityInput) return;
        this.quantityInput.value = String(readSearchQuantity(this.quantityInput, readLocalSettings() || settingsDefaults()));
        this.syncStepperBounds();
        if (this.staggered.mode !== 'single') {
          this.quantityTouched = true;
          var axle = this.staggered.mode;
          if (this.staggered[axle]) {
            this.staggered[axle] = Object.assign({}, this.staggered[axle], {
              qty: Number(this.quantityInput.value),
              requestedQuantity: Number(this.quantityInput.value)
            });
            this._renderStaggered();
          }
        }
        var quantity = readSearchQuantity(this.quantityInput, readLocalSettings() || settingsDefaults());
        var pkg = this.form && this.form._proPackage;
        if (this._catchUpTimer) clearTimeout(this._catchUpTimer);
        this._catchUpTimer = null;
        if (!this.lastQuotes || !this.lastQuotes.length) {
          if (pkg && pkg.applyQuantity) pkg.applyQuantity(quantity);
          this._retryLast();
          return;
        }
        // A package in progress is re-asked with the list, in one request (_repriceOpenList).
        if (!packageVariantIds(pkg).length && this._canRepaintAt(quantity)) {
          this._repaintAt(quantity);
          // The promises shown were asked at the old number: ask once more when the taps stop,
          // so they catch up (buildShortStock and the same-day suppression cover the gap).
          var self = this;
          var ticket = this.requestId;
          this._catchUpTimer = setTimeout(function () {
            self._catchUpTimer = null;
            if (ticket === self.requestId) self._repriceOpenList(quantity, true);
          }, CATCH_UP_DELAY_MS);
          if (this._catchUpTimer && typeof this._catchUpTimer.unref === 'function') this._catchUpTimer.unref();
          return;
        }
        this._repriceOpenList(quantity);
      };

      var CATCH_UP_DELAY_MS = 600;

      /** The package halves' ids, for one quote-batch with the list (none without a package). */
      function packageVariantIds(controller) {
        var pkg = controller && controller.pkg;
        var ids = [];
        if (pkg && pkg.tire && pkg.tire.variantId != null) ids.push(String(pkg.tire.variantId));
        if (pkg && pkg.rim && pkg.rim.variantId != null) ids.push(String(pkg.rim.variantId));
        return ids;
      }

      /** Review I6: the open list may be repainted at `quantity` with no request only when every
       * card on it, and every one kept for All / Winter, is proven at that number (no failed
       * lookup, and buildShortStock lets it through: a promise asked at it that can fill, or rows
       * that prove it), stays in its price tier, and none on screen is blocked now (a short card
       * must be asked again to learn whether fewer fill). */
      ProSearch.prototype._canRepaintAt = function (quantity) {
        var cached = this._quotedById || {};
        var all = this.lastQuotes.concat(Object.keys(cached).map(function (id) { return cached[id]; }));
        if (this.lastQuotes.some(function (q) { return !!quoteShortfall(q); })) return false;
        return all.every(function (q) {
          return !!q && !getFieldError(q.errors, 'stock') && !getFieldError(q.errors, 'promise') &&
            sameGoodsPriceTier(q.requestedQuantity || 1, quantity) && !buildShortStock(q, quantity);
        });
      };

      /** The open list at `quantity` from what it already has: each price scaled within its tier,
       * each card then blocked or not by quoteShortfall. No request; drops one still in flight. */
      ProSearch.prototype._repaintAt = function (quantity) {
        this.requestId++;
        var cached = this._quotedById || {};
        this.lastQuotes.forEach(function (q) { repriceStoredQuote(q, quantity); });
        Object.keys(cached).forEach(function (id) { repriceStoredQuote(cached[id], quantity); });
        var label = this.lastQuery || (this._lastVariantCall && this._lastVariantCall.label) || '';
        this._render(this.lastQuotes, label, this.requestId, this.lastOfflineInfo);
      };

      /** While the list is re-asked at a new number nothing on it may be quoted: its buttons still
       * carry the old one. The render that answers replaces them. */
      ProSearch.prototype._holdListQuotes = function () {
        var list = this.results && this.results.querySelector && this.results.querySelector('.tt-pro-results');
        if (!list) return;
        list.setAttribute('aria-busy', 'true');
        var buttons = list.querySelectorAll('[data-pro-generate-quote]');
        var adds = list.querySelectorAll('[data-pro-package-add]');
        for (var i = 0; i < buttons.length; i++) buttons[i].disabled = true;
        for (var j = 0; j < adds.length; j++) adds[j].disabled = true;
      };

      /** Runs the last search again: the typed (or cascaded, or vehicle) size, or else the last fitment / package lookup. */
      ProSearch.prototype._retryLast = function () {
        if (this.lastQuery) {
          this._search(this.lastQuery);
        } else if (this._lastVariantCall) {
          this.quoteVariants(this._lastVariantCall.ids, this._lastVariantCall.label, this._lastVariantCall.fitmentById);
        }
      };

      ProSearch.prototype._onSubmit = function (e) {
        e.preventDefault();
        var query = (this.input && this.input.value || '').trim();
        if (!query) return;
        activeVehicleController().typedSearch(query);
        this._search(query);
      };

      /** A tapped Fitment searches its own size. Staggered goes through
       * the front / rear flow: front size first, rear size after the
       * front tire is picked (see _pickStaggeredTire). */
      ProSearch.prototype.searchVehicle = function (chosen) {
        if (!chosen || !chosen.tireSize) return;
        if (chosen.rearTireSize && chosen.rearTireSize !== chosen.tireSize) {
          this.staggered = staggeredState({ mode: 'front' });
          this.staggeredReplacing = false;
          if (this.quantityInput) {
            this.quantityInput.value = '2';
            this.syncStepperBounds();
          }
          if (typeof this._renderStaggered === 'function') this._renderStaggered();
        }
        if (this.input) this.input.value = chosen.tireSize;
        this._search(chosen.tireSize);
      };

      // Fires only once every field is filled (rebuildAfterChange clears
      // this select back to "" whenever width or aspect changes, so a
      // partial combo never triggers a search — see
      // buildCascadeSizeString).
      ProSearch.prototype._onCascadeChange = function () {
        var sizeString = buildCascadeSizeString(
          this.widthSelect && this.widthSelect.value,
          this.aspectSelect && this.aspectSelect.value,
          this.diameterSelect && this.diameterSelect.value
        );
        if (!sizeString) return;
        activeVehicleController().typedSearch(sizeString);
        this._search(sizeString);
      };

      // `size` is what is being looked for ("225/55R18"); a lookup with none to name
      // (a fitment or package list) gets the plain "Searching…".
      ProSearch.prototype._showLoading = function (size) {
        if (!this.results) return;
        // A stale banner from a previous offline search must not linger
        // onto a fresh one (fix round finding #2).
        this._setStale(null);
        this._setSearchBusy(true);
        this.results.replaceChildren();
        var state = buildLoadingState(this.results, size);
        this.results.appendChild(state);
        this._settleFocus(state, true);
      };

      /**
       * Fix round finding #2 — the service worker's offline fallback
       * (assets/toee-pro-sw.js handleQuoteRequest) answers a failed
       * quote-batch call with an ordinary 200: {success: true, quotes,
       * offline: true, capturedAt}. Nothing about that response looks
       * different from a live one unless something here actually checks
       * `offline` — reusing navigator.onLine doesn't help, since weak wifi
       * and captive portals (the normal condition in a tire shop) leave it
       * `true` while requests still fail. `offlineInfo` is null for a live
       * result, which hides the banner again.
       */
      ProSearch.prototype._setStale = function (offlineInfo) {
        var banner = this.offlineBanner;
        if (!banner) return;
        if (offlineInfo && offlineInfo.capturedAt != null) {
          // The sentence goes in the row's status line, beside the warn marker; a banner
          // from before that line existed takes the text itself.
          // Show the row first and write the sentence second: a role=status line only
          // announces text that is added while it is on screen.
          banner.hidden = false;
          var line = (banner.querySelector && banner.querySelector('[data-pro-offline-text]')) || banner;
          line.textContent = formatOfflineLabel(offlineInfo.capturedAt, text(banner, 'offline-label'));
        } else {
          banner.hidden = true;
        }
      };

      /**
       * DP-3 no-results fallback (PRD §9.2) — replaces the old bare
       * "no matches" text for both the predictive-search-empty and
       * quote-batch-empty paths. Never renders an empty list.
       *
       * Fix round finding #2: the call-to-transfer action renders
       * immediately — a dealer on slow wifi always has something
       * actionable on screen — and is upgraded in place if a nearest
       * in-stock alternative resolves. It never waits on the alternative
       * lookup before showing anything.
       */
      ProSearch.prototype._showEmptyFallback = function (query, requestId) {
        var self = this;
        if (!this.results) return;
        this._setSearchBusy(false);
        var phone = text(this.results, 'call-phone') || undefined;
        var shown = buildNoResultsFallback(this.results, buildNoResultsView(null, phone));
        this.results.replaceChildren(shown);
        this._settleFocus(shown);

        var parsed = parseSizeQuery(query);
        var combo = window.ToeeSizeCombo;
        if (!parsed || !combo || !this.cascadeTargetUrl) return;
        var searched = formatSizeString(parsed.width, parsed.aspect, parsed.diameter);

        resolveNearestAlternative(this.cascadeTargetUrl, parsed, combo)
          .then(function (alt) {
            if (!alt || requestId !== self.requestId || !self.results) return;
            // The block above may hold the focus; the one that replaces it takes it over.
            var held = typeof document !== 'undefined' ? document.activeElement : null;
            self._focusPending = !!(held && (held === shown || (shown.contains && shown.contains(held))));
            var upgraded = buildNoResultsFallback(self.results, buildNoResultsView(alt, phone, searched));
            self.results.replaceChildren(upgraded);
            self._settleFocus(upgraded);
          })
          .catch(function () {
            // Already showing the call-only fallback — nothing to upgrade to.
          });
      };

      ProSearch.prototype._showError = function () {
        if (!this.results) return;
        this._setSearchBusy(false);
        this.results.replaceChildren();
        var callout = buildSearchError(this.results);
        this.results.appendChild(callout);
        this._settleFocus(callout);
      };

      function loadedPackageApi() {
        if (typeof ToeeProPackage !== 'undefined') return ToeeProPackage;
        if (typeof require === 'function') {
          try { return require('./toee-pro-package.js'); } catch (e) { return null; }
        }
        return null;
      }

      /** What quoteBar needs, plus the tire, the wheel and the open door the row uses. */
      ProSearch.prototype._quoteSelection = function () {
        var pkg = this.form && this.form._proPackage && this.form._proPackage.pkg;
        var mode = this.staggered && this.staggered.mode;
        var staggered = !!(mode && mode !== 'single');
        var tire = this.quoteTire || (pkg && pkg.tire) || null;
        var wheel = this.quoteWheel || (pkg && pkg.rim) || null;
        if (staggered && this.staggered && (this.staggered.front || this.staggered.rear)) {
          tire = this.staggered.front || this.staggered.rear || tire;
        }
        var door = this.quoteDoor || null;
        if (!door && typeof document !== 'undefined' && document.querySelector) {
          var marked = document.querySelector('[data-pro-open-door]');
          door = (marked && marked.getAttribute && marked.getAttribute('data-pro-open-door')) || null;
        }
        return {
          tire: tire,
          wheel: wheel,
          staggered: staggered,
          door: door,
          packageController: (this.form && this.form._proPackage) || null,
          search: this
        };
      };

      /** Size door: the specs finder at this tire's diameter. Any other door: wheels for the tire. */
      function openWheelRow(state) {
        var pkg = state && state.packageController;
        var tire = state && state.tire;
        if (!pkg || !tire) return null;
        if (state.door === 'size') {
          var api = loadedPackageApi();
          var parsed = api && api.parseTireSize
            ? api.parseTireSize((tire.fitment && tire.fitment.size) || tire.title || '')
            : null;
          if (parsed && typeof pkg.showDiameter === 'function') return pkg.showDiameter(parsed.rimIn);
          if (typeof pkg.chooseWheelMode === 'function') {
            pkg.chooseWheelMode('specs');
          }
          return null;
        }
        if (typeof pkg.findWheelsForTire === 'function') return pkg.findWheelsForTire(tire);
        return null;
      }

      function paintQuoteBar(root, state) {
        if (!root || typeof root.querySelector !== 'function') return;
        var group = root.querySelector('[data-pro-quote-group]');
        if (!group || typeof group.replaceChildren !== 'function') return;
        var s = state || {};
        var Counter = loadedCounterApi();
        var view = Counter && Counter.quoteBar
          ? Counter.quoteBar({ tire: !!s.tire, wheel: !!s.wheel, staggered: !!s.staggered })
          : { primary: null, wheel: 'hidden' };
        var wheelsTab = root.querySelector('[data-pro-mode-tab="wheels"]');
        var tiresTab = root.querySelector('[data-pro-mode-tab="tires"]');
        if (s.staggered) {
          if (wheelsTab) {
            wheelsTab.hidden = true;
            wheelsTab.setAttribute('aria-selected', 'false');
            wheelsTab.setAttribute('tabindex', '-1');
          }
          if (tiresTab) {
            tiresTab.setAttribute('aria-selected', 'true');
            tiresTab.setAttribute('tabindex', '0');
          }
          var wheelsPanel = root.querySelector('[data-pro-mode-panel="wheels"]');
          var tiresPanel = root.querySelector('[data-pro-mode-panel="tires"]');
          if (wheelsPanel) wheelsPanel.hidden = true;
          if (tiresPanel) tiresPanel.hidden = false;
        } else if (wheelsTab) {
          wheelsTab.hidden = false;
        }
        var pkgGenerate = root.querySelector('[data-pro-package-generate]');
        if (pkgGenerate && view.primary === 'quote') pkgGenerate.hidden = true;
        group.replaceChildren();
        // The card's Generate quote already opens the quote. This row is only
        // the optional wheel, with no second quote button and no rules around it.
        if (view.primary !== 'quote' || view.wheel === 'hidden') {
          group.hidden = true;
          return;
        }
        group.hidden = false;
        var searchForm = root.querySelector('[data-pro-search]');
        var resultsSel = searchForm && searchForm.getAttribute ? searchForm.getAttribute('data-pro-results-target') : '';
        var resultsHost = null;
        if (resultsSel) {
          try { resultsHost = root.querySelector(resultsSel); } catch (err) { resultsHost = null; }
        }
        var rowLabel = view.wheel === 'named'
          ? ((s.wheel && (s.wheel.title || s.wheel.name)) || '')
          : (text(group, 'add-wheel') || text(resultsHost, 'add-wheel'));
        var row = el('button', 'tt-pro-wheel-row' + (view.wheel === 'named' ? ' tt-pro-wheel-row--named' : ''), rowLabel);
        row.type = 'button';
        row.setAttribute('data-pro-wheel-row', '');
        row.addEventListener('click', function () {
          var door = s.door;
          if (!door && root.querySelector) {
            var marked = root.querySelector('[data-pro-open-door]');
            door = (marked && marked.getAttribute && marked.getAttribute('data-pro-open-door')) || null;
          }
          return openWheelRow({ tire: s.tire, door: door, packageController: s.packageController });
        });
        group.appendChild(row);
      }

      ProSearch.prototype._paintQuoteBar = function () {
        var root = typeof document !== 'undefined' ? document : null;
        if (!root || typeof root.querySelector !== 'function') return;
        paintQuoteBar(root, this._quoteSelection());
      };

      ProSearch.prototype._chooseForQuote = function (button) {
        var picked = null;
        try {
          picked = JSON.parse(button.getAttribute('data-pro-generate-quote'));
        } catch (err) {
          picked = null;
        }
        if (!picked) return;
        if (picked.fitment && picked.fitment.kind === 'rim') this.quoteWheel = picked;
        else {
          this.quoteTire = picked;
          this.quoteWheel = null;
          var controller = this.form && this.form._proPackage;
          if (controller && controller.pkg && controller.pkg.rim) {
            if (typeof controller.removeFromPackage === 'function') controller.removeFromPackage('rim');
            else controller.pkg.rim = null;
          }
        }
        this._paintQuoteBar();
      };

      ProSearch.prototype._render = function (quotes, query, requestId, offlineInfo) {
        if (!this.results) return;
        this._setSearchBusy(false);
        // Filter before the empty check, not after: a batch where every tire is
        // sold out has to land on the same nearest-size fallback a zero-quote
        // response takes, never on a blank result list.
        quotes = sellableQuotes(quotes);
        if (!quotes.length) {
          this._paintQuoteBar();
          this._showEmptyFallback(query, requestId);
          return;
        }
        var list = el('ul', 'tt-pro-results');
        var self = this;
        var phone = text(this.results, 'call-phone') || undefined;
        var isStale = !!offlineInfo;
        // Round 5 — cards render the dealer's selling price, so they need
        // the markup; re-read here so a save in the settings panel (which
        // writes localStorage and tells nobody) is picked up on re-render.
        var settings = readLocalSettings() || settingsDefaults();
        if (this.staggered && this.staggered.mode !== 'single' && this.staggered[this.staggered.mode]) {
          var activeAxle = this.staggered.mode;
          var activePick = this.staggered[activeAxle];
          var refreshed = quotes.find(function (quote) {
            return String(quote.variantId) === String(activePick.variantId);
          });
          if (refreshed) {
            var refreshedPick = Object.assign({}, refreshed, {
              qty: refreshed.requestedQuantity,
              _proQuery: this.lastQuery
            });
            var refreshedVm = buildQuoteCardViewModel(refreshedPick, settings, {}, {}, {});
            refreshedPick.dealerTotal = refreshedVm.eligible ? refreshedVm.total : '0.00';
            this.staggered[activeAxle] = refreshedPick;
            this._renderStaggered();
          }
        }
        this.lastQuotes = quotes;
        this.lastOfflineInfo = offlineInfo || null;
        var quoteAction = null;
        if (this.staggered && this.staggered.mode !== 'single') {
          // staggeredLabels is built with the front / rear strip; a page with no results bar has none.
          var axleLabels = this.staggeredLabels || staggeredControlLabels(this.results);
          quoteAction = this.staggered.front && this.staggered.rear && !this.staggeredReplacing
            ? false
            : (this.staggered.mode === 'front' ? axleLabels.useAsFront : axleLabels.useAsRear);
        }
        // Package (2026-09-23) — every card in this render pass shares the
        // same package-mode state, so it's read once here, not per card.
        // Guarded like the data-pro-results-count lookup below: the fake
        // DOM this file's tests run under has no document.querySelector at
        // all, which must fall back to "no bar" (packageModeActive false),
        // not throw.
        var packageBarEl =
          typeof document !== 'undefined' && document.querySelector ? document.querySelector('[data-pro-package-bar]') : null;
        var packageModeActive = quoteAction == null && !!(packageBarEl && !packageBarEl.hidden);
        // What the package holds so far decides each card's button label and
        // whether the card fits at all (see buildResultCard); null until the
        // controller (assets/toee-pro-package.js) has attached itself.
        var packageController = this.form && this.form._proPackage;
        var currentPackage = (packageController && packageController.pkg) || null;
        var quoteGroup = typeof document !== 'undefined' && document.querySelector ? document.querySelector('[data-pro-quote-group]') : null;
        var cardPrimaryClass = quoteGroup ? 'tt-pro-btn2' : 'tt-pro-generate-quote';
        quotes.forEach(function (q) {
          list.appendChild(
            buildResultCard(self.results, buildResultViewModel(q, phone), isStale, settings, packageModeActive, quoteAction, currentPackage, cardPrimaryClass)
          );
        });
        // A logged-in dealer with real prices but no markup decided is sent
        // to settings (first-run mode) once per page load — the owner's
        // ruling is that no price shows before the markup exists.
        var hasPrice = quotes.some(function (q) { return q && q.price; });
        if (hasPrice && !settings.markupConfigured && !this._markupPromptShown) {
          this._markupPromptShown = true;
          openSettingsFirstRun();
        }
        this.results.replaceChildren();
        var head = buildResultsHead(this.results, quotes.length, query);
        // Design round 3 — the count sits in the static results bar next to
        // the quantity stepper when the page has one; older markup keeps
        // the in-list heading.
        var countEl =
          typeof document !== 'undefined' && document.querySelector ? document.querySelector('[data-pro-results-count]') : null;
        if (head && countEl) countEl.textContent = head.textContent;
        else if (head) this.results.appendChild(head);
        this.results.appendChild(list);
        if (!this._settleCardFocus(list)) this._settleFocus(list);
        this._setStale(offlineInfo);
        // Fix round finding #3 / final review finding #3 — a countdown
        // ticking (or even just sitting there as static text) against the
        // current clock on a cached quote fabricates a promise ("order
        // within 02:14") that no longer means anything once the data
        // itself is stale. Only ever tick — or show — a countdown on a
        // live result; a stale render still has to clear whatever
        // countdown timer the PREVIOUS live render left running, since its
        // cards are gone from the DOM the moment replaceChildren runs
        // above (final review finding #4 — this is what left
        // activeCountdownTimers ticking against detached nodes).
        if (isStale) {
          stopCountdowns();
        } else {
          startCountdowns(list);
        }
        this._paintQuoteBar();
      };

      /** Asks quote-batch again for the cards already on screen at `quantity`, and leaves them up:
       * busy and unquotable until the answer (unless `background`, the catch-up after a repaint the
       * rows already prove). A package in progress rides along in the same request. */
      ProSearch.prototype._repriceOpenList = function (quantity, background) {
        var ids = [];
        var fitmentById = {};
        (this.lastQuotes || []).forEach(function (q) {
          if (!q || q.variantId == null) return;
          var id = String(q.variantId);
          ids.push(id);
          if (q.fitment) fitmentById[id] = q.fitment;
        });
        if (!ids.length) return;
        // Review I5: tires kept for All / Winter were priced at the old number; a season switch
        // asks for them again rather than show them at it.
        this._quotedById = {};
        var pkg = this.form && this.form._proPackage;
        var extra = packageVariantIds(pkg).filter(function (id) { return ids.indexOf(id) === -1; });
        var label = this.lastQuery || (this._lastVariantCall && this._lastVariantCall.label) || '';
        var requestId = ++this.requestId;
        if (!background) this._holdListQuotes();
        return this._quoteAndRender(ids.concat(extra), label, requestId, quantity, fitmentById, true, {
          listIds: ids,
          background: !!background,
          pkg: pkg && pkg.applyQuantity && packageVariantIds(pkg).length ? pkg : null
        });
      };

      /** The catch-up redraws the list under the dealer, unasked: a card control that has focus
       * hands it to the same tire's new primary (_settleCardFocus) instead of dropping it to <body>. */
      ProSearch.prototype._keepCardFocus = function () {
        var active = typeof document !== 'undefined' ? document.activeElement : null;
        var card = active && typeof active.closest === 'function' ? active.closest('[data-pro-variant]') : null;
        if (!card || !this.results || typeof this.results.contains !== 'function' || !this.results.contains(card)) return;
        this._focusPending = true;
        this._focusVariant = card.getAttribute('data-pro-variant');
      };

      ProSearch.prototype._search = function (query) {
        var self = this;
        var requestId = ++this.requestId;
        this._quotedById = {};
        this._filterSeason = false;
        // Name what is being looked for: a size as the normal "225/55R18", anything else
        // as typed.
        var parsed = parseSizeQuery(query);
        this._showLoading(parsed ? formatSizeString(parsed.width, parsed.aspect, parsed.diameter) : query);

        // Final review finding #1 (CRITICAL) — the quote card renders at
        // the dealer's configured defaultQuantity, so the price fetched
        // here must be for that exact same quantity, or price.lineSubtotal
        // isn't authoritative for what gets drawn on the card. Read once,
        // used for both the request AND stamped onto every returned quote
        // below (see buildQuoteCardViewModel's own comment on
        // requestedQuantity) — one number, not two that merely happen to
        // agree today.
        this.lastQuery = query;
        this._listKind = 'tires';
        this._lastVariantCall = null;
        var searchingPkg = this.form && this.form._proPackage;
        if (searchingPkg && typeof searchingPkg._armList === 'function') searchingPkg._armList();
        var quantity = readSearchQuantity(this.quantityInput, readLocalSettings() || settingsDefaults());

        // Prefer the collection view: it is the only source here that
        // actually returns variant ids. /search/suggest.json comes back with
        // an empty `variants` array on this store, which is why a search for
        // an in-stock size used to show nothing at all. Predictive search
        // stays as the path for a query that is not a parseable size — it
        // still resolves those to products, and losing that would be a
        // regression for anyone typing a brand or a SKU.
        var lookupUrl = parsed ? proVariantsUrl(this.cascadeTargetUrl, parsed) : null;
        var bySize = !!lookupUrl;

        // No Accept: application/json on the collection view. Shopify honours
        // that header ahead of ?view= and answers with its own collection
        // object — {"collection":{"id":...,"products_count":6}} — instead of
        // rendering the alternate template, so the lookup came back with no
        // `variants` key at all and every search still showed nothing. The
        // cascade's own fetch never sent it, which is why that path worked.
        // Predictive search is a real JSON endpoint and still gets the header.
        return fetch(
          lookupUrl || predictiveSearchUrl(query),
          bySize
            ? { credentials: 'same-origin' }
            : { credentials: 'same-origin', headers: { Accept: 'application/json' } }
        )
          .then(function (r) {
            return r.json();
          })
          .then(function (payload) {
            if (requestId !== self.requestId) return null;
            self._sizeVariants = rememberSizeVariants(bySize, payload && payload.variants);
            self._sizeLabel = bySize ? formatSizeString(parsed.width, parsed.aspect, parsed.diameter) : '';
            var variantIds = bySize
              ? seasonVariantIds(payload, 'all', SIZE_QUOTE_LIMIT)
              : extractVariantIds(payload, RESULT_LIMIT);
            self._filterSeason = bySize;
            if (bySize && (!payload || !payload.variants || !payload.variants.length)) {
              self._showEmptyFallback(query, requestId);
              return null;
            }
            if (bySize && self.season === 'winter' && !variantIds.length) {
              self._showSeasonEmpty();
              return null;
            }
            if (!variantIds.length) {
              self._showEmptyFallback(query, requestId);
              return null;
            }
            // Package (plan 2026-09-11) — a size search's results are known
            // tires of this size, so their cards can start a package.
            var fitmentById = null;
            if (bySize) {
              fitmentById = {};
              var size = formatSizeString(parsed.width, parsed.aspect, parsed.diameter);
              variantIds.forEach(function (id) {
                fitmentById[id] = { kind: 'tire', size: size };
              });
            }
            return self._quoteAndRender(variantIds, query, requestId, quantity, fitmentById, false);
          })
          .catch(function () {
            if (requestId !== self.requestId) return;
            self._showError();
          });
      };

      /**
       * The quote-batch half of _search, shared with quoteVariants below.
       * Unchanged behaviour: stamps requestedQuantity (final review finding
       * #1), honours the service worker's offline flag (fix round finding
       * #2). New: stamps each quote's `fitment` when the caller knows it.
       */
      ProSearch.prototype._quoteAndRender = function (variantIds, query, requestId, quantity, fitmentById, keepPrevious, reprice) {
        var self = this;
        function showQuoteError(answer) {
          if (reprice) {
            if (reprice.background) self._keepCardFocus();
            // The package keeps what it had (its quantity was never answered), and the list stays,
            // at the number the stepper shows, each card blocked or not by what its rows prove
            // (review C1 / I6). Across the bulk tier there is no honest local price: its buttons
            // stay off until Try again.
            if (reprice.pkg) reprice.pkg.applyQuantity(quantity, answer || null);
            if (self.lastQuotes && self.lastQuotes.every(function (q) { return sameGoodsPriceTier((q && q.requestedQuantity) || 1, quantity); })) {
              self._repaintAt(quantity);
            } else {
              var list = self.results && self.results.querySelector('.tt-pro-results');
              if (list) list.removeAttribute('aria-busy');
            }
            self._appendKeptQuoteError(true);
            return;
          }
          // "Showing the earlier results" is only true while some are on screen: a season
          // switch from a winter-empty size has none, and that failure is a plain one.
          if (keepPrevious && self.lastQuotes) {
            self._appendKeptQuoteError();
            return;
          }
          self._showError();
        }
        return fetch(proProxyUrl('/quote-batch'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(buildQuoteBatchBody(variantIds, quantity))
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (batch) {
            if (requestId !== self.requestId) return;
            // A missing body is a failed search too (quoteListShouldReplace says no to it):
            // returning quietly here would leave the skeletons up and Search off. A re-ask at a
            // new number never takes the service worker's cached batch (review C1): it was priced
            // and promised at some other number.
            if (!quoteListShouldReplace(variantIds, batch) || (reprice && batch.offline)) {
              showQuoteError(batch);
              return;
            }
            var listed = reprice ? reprice.listIds : null;
            batch.quotes.forEach(function (q) {
              q.requestedQuantity = quantity;
              // The quantity this delivery promise answers for: a live answer only, never the
              // worker's cached batch (buildShortStock trusts it).
              if (batch.offline) delete q.promiseQuantity;
              else q.promiseQuantity = quantity;
              if (fitmentById && fitmentById[q.variantId]) q.fitment = fitmentById[q.variantId];
            });
            if (reprice && reprice.pkg) reprice.pkg.applyQuantity(quantity, batch);
            self._quotedById = self._quotedById || {};
            batch.quotes.forEach(function (q) {
              if (q && q.variantId != null && (!listed || listed.indexOf(String(q.variantId)) > -1)) self._quotedById[String(q.variantId)] = q;
            });
            var shown = listed
              ? batch.quotes.filter(function (q) { return listed.indexOf(String(q.variantId)) > -1; })
              : batch.quotes;
            if (self._filterSeason && self._sizeVariants) {
              var filtered = quotesForSeason(self._quotedById, self._sizeVariants, self.season, RESULT_LIMIT);
              if (filtered) shown = filtered;
            }
            if (reprice && reprice.background) self._keepCardFocus();
            self._render(shown, query, requestId, batch.offline ? { capturedAt: batch.capturedAt } : null);
          })
          .catch(function () {
            if (requestId !== self.requestId) return;
            showQuoteError(null);
          });
      };

      /**
       * Package (plan 2026-09-11) — render a fitment answer (GET
       * {base}/fitment/rims|tires) through the same quote-batch + card path
       * a typed search uses. `label` is what the results head shows next to
       * the count; `fitmentById` is ToeeProPackage.fitmentFromRims/Tires().byId.
       */
      ProSearch.prototype.quoteVariants = function (variantIds, label, fitmentById) {
        var pkgForList = this.form && this.form._proPackage;
        // Clear vehicle bumped the generation. A wheel or tire list that was
        // already on its way belongs to the car that was just cleared.
        if (pkgForList && (pkgForList._listGeneration || 0) !== (pkgForList._activeListGeneration || 0)) {
          return Promise.resolve();
        }
        var rimList = false;
        if (fitmentById) {
          var fitmentKeys = Object.keys(fitmentById);
          for (var f = 0; f < fitmentKeys.length; f++) {
            var row = fitmentById[fitmentKeys[f]];
            if (row && row.kind === 'rim') rimList = true;
          }
        }
        var pkg = this.form && this.form._proPackage;
        // The dealer is on Tire. A wheel search that finishes now would leave
        // wheel cards under that tab, including while a package is open.
        if (pkg && pkg.mode === 'tires' && rimList) return Promise.resolve();
        var requestId = ++this.requestId;
        // Wheel lists replace the tire cards. Keep the tire search so the Tires tab can put it back.
        // A search that has only just started still has the previous wheel cards in lastQuotes:
        // those are not the tire list.
        if (this._listKind !== 'variants' && this.lastQuery && this.lastQuotes && !listHasRims(this.lastQuotes)) {
          this._tireList = {
            query: this.lastQuery,
            quotes: this.lastQuotes,
            offline: this.lastOfflineInfo || null
          };
        }
        this._listKind = 'variants';
        this.lastQuery = '';
        this._lastVariantCall = { ids: variantIds || [], label: label, fitmentById: fitmentById || null };
        this._showLoading();
        if (!variantIds || !variantIds.length) {
          this._showEmptyFallback(label, requestId);
          return Promise.resolve();
        }
        var quantity = readSearchQuantity(this.quantityInput, readLocalSettings() || settingsDefaults());
        this._filterSeason = false;
        return this._quoteAndRender(variantIds, label, requestId, quantity, fitmentById);
      };

      // ---------------------------------------------------------------
      // DP-10 — install prompt, iOS guide, service worker, offline banner.
      // Every function below bails out immediately when `window`/
      // `navigator` aren't present (also keeps this safe under the fake
      // DOM in tests/toee-pro-fake-dom.js, which stubs `document` only).
      // ---------------------------------------------------------------

      var swControllerReloaded = false;

      /** What the install code reads from the page. Every part can be handed in, so a
       * test needs no browser: the window, the document, the storage the 14-day dismissal
       * lives in (undefined = localStorage), the clock and the user agent. */
      function installContext(env) {
        env = env || {};
        return {
          win: env.win || (typeof window !== 'undefined' ? window : null),
          doc: env.doc || document,
          storage: env.storage,
          ua: env.ua != null ? env.ua : (typeof navigator !== 'undefined' ? navigator.userAgent : null),
          now: function () {
            return (env.now || Date.now)();
          }
        };
      }

      /** "Not now": hide the box for 14 days. The focused button disappears with it, so a
       * keyboard user's focus moves to the size search input instead of falling to <body>. */
      function dismissInstallBox(ctx, box, dismiss) {
        var hadFocus = ctx.doc.activeElement === dismiss;
        box.hidden = true;
        rememberInstallDismissal(ctx.storage, ctx.now());
        var input = hadFocus ? ctx.doc.querySelector('[data-pro-search-input]') : null;
        if (input && input.focus) input.focus();
      }

      function isStandaloneDisplay(win) {
        return !!(
          (win.navigator && win.navigator.standalone) ||
          (win.matchMedia && win.matchMedia('(display-mode: standalone)').matches)
        );
      }

      /** Android Chrome + desktop Chrome/Edge: capture the browser's own install prompt and
       * offer it in the install box (Shell-header artboard D) instead of waiting on the
       * address-bar icon. "Not now", or a declined dialog, hides the box for 14 days; the
       * installed app reports standalone and never shows it. The strings are the box's own
       * Liquid, so this only toggles the hidden attribute. On a page without the wrapper (an older
       * section) the Install button itself is what gets shown and hidden, as it used to be. */
      function initInstallPrompt(env) {
        var ctx = installContext(env);
        if (!ctx.win) return;
        var button = ctx.doc.querySelector('[data-pro-install-button]');
        if (!button) return;
        var banner = ctx.doc.querySelector('[data-pro-install-banner]') || button;
        var dismiss = banner === button ? null : banner.querySelector('[data-pro-install-dismiss]');
        var deferred = null;
        ctx.win.addEventListener('beforeinstallprompt', function (e) {
          e.preventDefault();
          deferred = e;
          if (isStandaloneDisplay(ctx.win) || installDismissedRecently(ctx.storage, ctx.now())) return;
          banner.hidden = false;
        });
        button.addEventListener('click', function () {
          if (!deferred) return;
          var prompted = deferred;
          deferred = null;
          banner.hidden = true;
          prompted.prompt();
          // A declined dialog is a "not now" too; otherwise the box is back on the next load.
          var choice = prompted.userChoice;
          if (choice && choice.then) {
            choice.then(function (result) {
              if (!result || result.outcome !== 'accepted') rememberInstallDismissal(ctx.storage, ctx.now());
            }, function () {});
          }
        });
        if (dismiss) {
          dismiss.addEventListener('click', function () {
            dismissInstallBox(ctx, banner, dismiss);
          });
        }
        ctx.win.addEventListener('appinstalled', function () {
          banner.hidden = true;
        });
      }

      /** iOS Safari can't be prompted (PRD §4.1) — show the manual guide.
       * iOS Chrome/Firefox/Edge never reach this (isIOSSafari is false for
       * all of them), an already-installed instance never shows it either
       * (isStandaloneDisplay), and "Not now" keeps it away for 14 days. */
      function initIOSGuide(env) {
        var ctx = installContext(env);
        if (!ctx.win || ctx.ua == null) return;
        var guide = ctx.doc.querySelector('[data-pro-ios-guide]');
        if (!guide) return;
        var dismiss = guide.querySelector('[data-pro-install-dismiss]');
        if (dismiss) {
          dismiss.addEventListener('click', function () {
            dismissInstallBox(ctx, guide, dismiss);
          });
        }
        if (isIOSSafari(ctx.ua) && !isStandaloneDisplay(ctx.win) && !installDismissedRecently(ctx.storage, ctx.now())) {
          guide.hidden = false;
        }
      }

      /**
       * Registers the offline-degradation worker scoped to the real /pro
       * page — see assets/toee-pro-sw.js for why. Reloads on
       * controllerchange so a stale open tab actually picks up a newly
       * activated worker (skipWaiting + clients.claim there) instead of
       * being left running old JS under a new worker.
       *
       * Fix round finding #1 — the scope comes from data-pro-scope-url
       * (sections/toee-pro-search.liquid), which reads the real
       * pages['pro'].url from Liquid, rather than the "/pro" admin-redirect
       * literal the original ticket assumed.
       *
       * Fix round finding #4 — clients.claim() also fires controllerchange
       * the very first time a worker takes control of a page that had no
       * controller yet (i.e. a dealer's first-ever visit), not only on a
       * genuine update. Reloading then would wipe that first search
       * mid-flight. `hadController` is read synchronously here, before
       * registration kicks off anything async — true only means "this page
       * was already under an existing worker's control", i.e. this really
       * is an update, not an initial claim, so only then is the reload
       * wired up at all.
       */
      function initServiceWorker() {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
        var host = document.querySelector('[data-pro-sw-url]');
        var swUrl = host && host.getAttribute('data-pro-sw-url');
        if (!swUrl) return;
        var scope = (host && host.getAttribute('data-pro-scope-url')) || '/pages/pro';
        var hadController = !!navigator.serviceWorker.controller;
        // Final review finding #5 — the worker has no Liquid access of its
        // own, so the only way it learns the real (possibly localized,
        // e.g. /fr/pages/pro) shell path is by reading it off its own
        // script URL. `{ scope }` below is the browser-enforced
        // registration scope; this query param is what toee-pro-sw.js's
        // own isCacheableRequest gate actually compares against.
        var swUrlWithShell = swUrl + (swUrl.indexOf('?') === -1 ? '?' : '&') + 'shell=' + encodeURIComponent(scope);
        navigator.serviceWorker.register(swUrlWithShell, { scope: scope }).then(function (registration) {
          // A dealer's tablet can leave this tab open for hours (the whole
          // reason this worker exists — spotty shop wifi). Nothing else
          // ever re-checks for a new worker script on an already-open tab;
          // register() only checks once, at this load. Re-checking when the
          // tab regains focus is the standard, near-zero-cost way to catch
          // up (no-cache on /pro/sw makes every check a cheap conditional
          // byte-compare, not a full refetch).
          document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') registration.update();
          });
        }).catch(function () {
          // Fails closed: if the asset host's own path doesn't permit
          // scoping there, no worker installs. Offline degradation just
          // isn't available on that host configuration; live search is
          // unaffected either way.
        });
        if (!hadController) return;
        navigator.serviceWorker.addEventListener('controllerchange', function () {
          if (swControllerReloaded) return;
          swControllerReloaded = true;
          window.location.reload();
        });
      }

      // Final review finding #4 — these two must stay byte-for-byte
      // identical to assets/toee-pro-sw.js's own LAST_RESULT_CACHE /
      // LAST_RESULT_KEY (see tests/toee-pro-offline-load.test.js's
      // cross-file check). The service worker and this page script run in
      // two separate JS execution contexts with no build step between
      // them, so there is no real module to share; the string literals
      // have to be declared in both places, kept from drifting apart by
      // that test instead of by import.
      var LAST_RESULT_CACHE = 'toee-pro-last-result';
      var LAST_RESULT_KEY = '/__toee-pro-last-result';

      /**
       * PRD §9.1 — on load while offline, show the worker's last saved
       * result immediately, labelled with the exact time it was captured.
       *
       * Final review finding #4 — this used to rebuild the results list
       * from scratch against a hardcoded `#tt-pro-results`, a second,
       * untested renderer alongside ProSearch#_render. It now hands the
       * cached record straight to `proSearch`'s own _render — the exact
       * same results target (data-pro-results-target), offline banner,
       * and (final review finding #3) stale-countdown suppression a live
       * offline result already gets. `cachesApi` is `window.caches`,
       * injectable the same way `fetchImpl` is elsewhere in this file.
       */
      loadLastOfflineResult = function (proSearch, cachesApi) {
        if (!proSearch || !cachesApi) return Promise.resolve();
        return cachesApi
          .open(LAST_RESULT_CACHE)
          .then(function (cache) {
            return cache.match(LAST_RESULT_KEY);
          })
          .then(function (res) {
            return res && res.json();
          })
          .then(function (stale) {
            if (!stale || !stale.capturedAt || !Array.isArray(stale.quotes) || !stale.quotes.length) return;
            var requestId = ++proSearch.requestId;
            proSearch._render(stale.quotes, null, requestId, { capturedAt: stale.capturedAt });
          })
          .catch(function () {});
      };

      /**
       * Only runs at page load. A live search already degrades gracefully
       * the moment the network genuinely fails — ProSearch#_search's own
       * fetch/catch plus the service worker's cache fallback — so there is
       * no need to also react to the 'offline' event here. Doing so used
       * to replace whatever live results were already correctly on screen
       * with a single unrelated earlier cached search the instant wifi
       * blipped (final review finding #4's "Worse:"), which is a strictly
       * worse outcome than leaving the current, still-accurate results in
       * place.
       */
      function initOfflineBanner() {
        if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('caches' in window)) return;
        if (navigator.onLine) return;
        var form = document.querySelector('[data-pro-search]');
        var proSearch = form && form._proSearch;
        if (!proSearch || !proSearch.offlineBanner) return;
        loadLastOfflineResult(proSearch, window.caches);
      }

      /** Owner ruling (2026-09-16) — a guest must never reach the real
       * settings form (it edits the shop's saved pricing/markup, not just
       * this browser's own view of it); sections/toee-pro-search.liquid
       * renders this login-required gear instead of the real one whenever
       * `customer` is absent, so the two never coexist in the same page.
       * Toggle, not a timed auto-hide like applyFieldSuccess — this is a
       * "you can't proceed without doing X" message, not a transient
       * success/error state, so it stays until the dealer either follows
       * the login link or dismisses it by clicking the gear again. */
      function initSettingsLoginPrompt() {
        if (typeof window === 'undefined') return;
        var button = document.querySelector('[data-pro-settings-login-required]');
        var note = document.querySelector('[data-pro-settings-login-note]');
        if (!button || !note) return;
        button.addEventListener('click', function () {
          var opening = !!note.hidden;
          note.hidden = !opening;
          button.setAttribute('aria-expanded', opening ? 'true' : 'false');
        });
      }

      // ---------------------------------------------------------------
      // DP-5 — dealer settings panel wiring. Reads/writes go through the
      // pure functions above (saveSettingsSection, resolveSettingsOnLoad,
      // etc.) — this constructor is only DOM plumbing: populate fields
      // from the current settings object, wire each section's own submit,
      // and apply the DESIGN.md Form States on save.
      // ---------------------------------------------------------------

      function readFieldValue(input) {
        if (input.tagName === 'SELECT' && input.multiple) {
          var out = [];
          var opts = input.options || [];
          for (var i = 0; i < opts.length; i++) {
            if (opts[i].selected) out.push(opts[i].value);
          }
          return out;
        }
        if (input.type === 'number') {
          return input.value === '' ? 0 : Number(input.value);
        }
        return input.value;
      }

      function isEmptyValue(value) {
        return value == null || value === '';
      }

      /** Required-field emptiness has to be checked on the raw control,
       * never on readFieldValue's *coerced* output — a type="number" input
       * left blank coerces to 0 in readFieldValue (so a real quote can
       * still be built from it), and isEmptyValue(0) is false, so a
       * required numeric field would never actually block an empty
       * submit. Multi-select empties the same way: readFieldValue([])
       * isn't caught by isEmptyValue either. */
      function isInputEmpty(input) {
        if (input.tagName === 'SELECT' && input.multiple) {
          var opts = input.options || [];
          for (var i = 0; i < opts.length; i++) {
            if (opts[i].selected) return false;
          }
          return true;
        }
        return isEmptyValue(input.value);
      }

      function setFieldValue(input, value) {
        if (input.tagName === 'SELECT' && input.multiple) {
          var opts = input.options || [];
          var selected = Array.isArray(value) ? value : [];
          for (var i = 0; i < opts.length; i++) {
            opts[i].selected = selected.indexOf(opts[i].value) !== -1;
          }
          return;
        }
        input.value = value == null ? '' : value;
      }

      function populateForm(form, settings) {
        var inputs = form.querySelectorAll('[data-pro-field-input]');
        for (var i = 0; i < inputs.length; i++) {
          var name = inputs[i].name;
          if (name && Object.prototype.hasOwnProperty.call(settings, name)) {
            setFieldValue(inputs[i], settings[name]);
          }
        }
      }

      ProSettingsPanel = function (root) {
        this.root = root;
        this.settings = readLocalSettings() || settingsDefaults();
        this.gearButtons = document.querySelectorAll('[data-pro-settings-open]');
        this.closeButton = root.querySelector('[data-pro-settings-close]');
        this.forms = root.querySelectorAll('[data-pro-settings-section]');
        this.firstRunNote = root.querySelector('[data-pro-settings-first-run-note]');
        // DP-8 — card appearance live preview.
        this.cardThemeSelect = root.querySelector('[name="cardTheme"]');
        this.cardPreviewCanvas = root.querySelector('[data-pro-card-preview]');
        // Rollout Task 10 — Shop | Quotes | Card and the footer's one Save. Older cached
        // markup has neither: every section then shows with its own Save, as before.
        this.sectionSwitch = root.querySelector('[data-pro-settings-switch]');
        this.foot = root.querySelector('[data-pro-settings-foot]');
        this.saveButton = this.foot ? this.foot.querySelector('[data-pro-settings-save]') : null;
        this.saveStatus = this.foot ? this.foot.querySelector('[data-pro-settings-status]') : null;
        this.section = 'shop';

        var self = this;
        if (this.sectionSwitch) {
          var tabs = this.sectionSwitch.querySelectorAll('[data-pro-settings-tab]');
          var wireTab = function (tab) {
            return function () { self._showSection(tab.getAttribute('data-pro-settings-tab')); };
          };
          for (var tb = 0; tb < tabs.length; tb++) tabs[tb].addEventListener('click', wireTab(tabs[tb]));
        }
        // Segmented controls standing in for a native control, linked by its id.
        this.segs = [];
        var segEls = root.querySelectorAll('[data-pro-settings-seg]');
        var wireSegButton = function (seg, source, button) {
          return function () {
            setSegSource(source, button.getAttribute('data-pro-seg-value'));
            syncSettingsSeg(seg, source);
          };
        };
        var wireSegSource = function (seg, source) {
          return function () { syncSettingsSeg(seg, source); };
        };
        for (var sg = 0; sg < segEls.length; sg++) {
          var segSource = root.querySelector('[id="' + segEls[sg].getAttribute('data-pro-settings-seg') + '"]');
          if (!segSource) continue;
          this.segs.push({ seg: segEls[sg], source: segSource });
          segSource.addEventListener('change', wireSegSource(segEls[sg], segSource));
          var segButtons = segEls[sg].querySelectorAll('[data-pro-seg-value]');
          for (var sb = 0; sb < segButtons.length; sb++) {
            segButtons[sb].addEventListener('click', wireSegButton(segEls[sg], segSource, segButtons[sb]));
          }
        }
        // The card theme tiles' swatches, in each theme's own accent read from its token
        // (the same lookup the card uses), so no theme colour is ever written in this screen.
        var swatches = root.querySelectorAll('[data-pro-theme-swatch]');
        for (var sw = 0; sw < swatches.length; sw++) {
          var accent = resolveCardThemeAccent(swatches[sw].getAttribute('data-pro-theme-swatch'), readCssVarFromDocument);
          if (accent && swatches[sw].style) swatches[sw].style.background = accent;
        }
        for (var i = 0; i < this.gearButtons.length; i++) {
          this.gearButtons[i].addEventListener('click', function (e) {
            // The button goes along as the opener: Safari does not focus a button on click, so
            // "whatever had focus" would be nothing and focus could not come back to it.
            self.open(needsFirstRun(self.settings), e && e.currentTarget);
          });
        }
        if (this.closeButton) {
          this.closeButton.addEventListener('click', function () {
            self.close();
          });
        }
        // Owner ruling (2026-09-08): first-run must not look like the whole
        // settings screen — one tap opens everything.
        var advanced = root.querySelectorAll('[data-pro-settings-advanced]');
        var wireAdvanced = function (button) {
          return function () {
            self._setFirstRunMode(false);
            // "All settings" hides itself: focus goes to the Shop segment that has just appeared
            // (a button, so no phone keyboard opens over the drawer).
            handOffFocus(button, root.querySelector('[data-pro-settings-tab="shop"]'));
          };
        };
        for (var a = 0; a < advanced.length; a++) {
          advanced[a].addEventListener('click', wireAdvanced(advanced[a]));
        }
        // Owner ruling (2026-09-08) — a business-hours group's time pickers
        // show only while its Open toggle is checked. Re-synced on every
        // _populateAll (a fresh settings load, or a save); wired here too
        // for a live click before any save.
        var hoursGroupsAtInit = root.querySelectorAll('[data-pro-hours-group]');
        var wireHoursGroup = function (groupEl) {
          return function () { syncHoursGroupVisibility(groupEl); };
        };
        for (var hg = 0; hg < hoursGroupsAtInit.length; hg++) {
          var openToggle = hoursGroupsAtInit[hg].querySelector('[data-pro-hours-open]');
          if (openToggle) openToggle.addEventListener('change', wireHoursGroup(hoursGroupsAtInit[hg]));
        }
        // Owner ruling (2026-09-08) — percent markup shows a "%" suffix,
        // a fixed-dollar mode shows a "$" prefix. Re-synced on every
        // _populateAll (a fresh settings load, or a save) below; wired
        // here too for a live change before any save.
        this.markupPairs = root.querySelectorAll('[data-pro-markup-pair]');
        var syncPair = function (pair) {
          return function () {
            syncMarkupPair(pair);
            syncMarkupExtras(pair, root);
          };
        };
        for (var p = 0; p < this.markupPairs.length; p++) {
          var modeSelect = this.markupPairs[p].querySelector('[data-pro-markup-mode]');
          if (modeSelect) modeSelect.addEventListener('change', syncPair(this.markupPairs[p]));
          // The example line follows the amount as it is typed.
          var markupAmount = this.markupPairs[p].querySelector('[data-pro-markup-value]');
          if (markupAmount) markupAmount.addEventListener('input', syncPair(this.markupPairs[p]));
        }
        // Same "$" prefix for Install fee — never a percentage, so only
        // one decoration to sync, on its own mode/value/prefix trio.
        this.installFeePairs = root.querySelectorAll('[data-pro-install-fee-pair]');
        var syncInstallPair = function (pair) {
          return function () { syncInstallFeePair(pair); };
        };
        for (var ip = 0; ip < this.installFeePairs.length; ip++) {
          var installModeSelect = this.installFeePairs[ip].querySelector('[data-pro-install-fee-mode]');
          if (installModeSelect) installModeSelect.addEventListener('change', syncInstallPair(this.installFeePairs[ip]));
        }
        // Rollout Task 10 — default quantity on the results bar's stepper: steps of one,
        // clamped 1-99 by readSearchQuantity; the number field stays what the save reads.
        this.qtyInput = root.querySelector('[name="defaultQuantity"]');
        this.qtyButtons = root.querySelectorAll('[data-pro-settings-qty-step]');
        if (this.qtyInput && this.qtyButtons.length) {
          var qtyInput = this.qtyInput;
          var stepQty = function (button) {
            return function () {
              var step = Number(button.getAttribute('data-pro-settings-qty-step')) || 0;
              qtyInput.value = String(readSearchQuantity({ value: readSearchQuantity(qtyInput, self.settings) + step }, {}));
              self._syncStepper();
            };
          };
          for (var qb = 0; qb < this.qtyButtons.length; qb++) this.qtyButtons[qb].addEventListener('click', stepQty(this.qtyButtons[qb]));
          qtyInput.addEventListener('input', function () { self._syncStepper(); });
        }
        for (var j = 0; j < this.forms.length; j++) {
          this.forms[j].addEventListener('submit', this._onSubmit.bind(this));
        }
        // Rollout Task 10: a field's error goes as soon as the dealer edits it, not on the
        // next save. A segmented control edits its hidden select through "change".
        var clearOnEdit = function (input) {
          return function () {
            if ((input.className || '').indexOf('tt-pro-input--error') < 0) return;
            var wrap = input.closest ? input.closest('[data-pro-field]') : null;
            clearFieldError(input, wrap ? wrap.querySelector('[data-pro-field-message]') : null);
          };
        };
        var fieldInputs = root.querySelectorAll('[data-pro-field-input]');
        for (var fi = 0; fi < fieldInputs.length; fi++) {
          fieldInputs[fi].addEventListener('input', clearOnEdit(fieldInputs[fi]));
          fieldInputs[fi].addEventListener('change', clearOnEdit(fieldInputs[fi]));
        }
        // dp-8 — PRD §5.7.1: the preview must render immediately on
        // selecting a theme, not wait for the section's own Save.
        if (this.cardThemeSelect) {
          this.cardThemeSelect.addEventListener('change', function () {
            self._renderCardPreview();
          });
        }

        this._populateAll();
        this._fetchRemote();
      };

      /** Rollout Task 10: how long "Saved" shows before the drawer closes on its own, and how
       * long a save waits on the network before it counts as saved anyway. Properties, so a
       * test can shorten them. */
      ProSettingsPanel.SAVED_CLOSE_MS = 500;
      ProSettingsPanel.SAVE_WAIT_MS = 4000;

      ProSettingsPanel.prototype._populateAll = function () {
        for (var i = 0; i < this.forms.length; i++) {
          populateForm(this.forms[i], this.settings);
        }
        // Owner ruling (2026-09-08): while the markup is undecided the
        // first-run mode and amount stay blank. Populating them from the
        // defaults ("None", 0) read as a decision already made, and a
        // dealer could save it without ever choosing.
        if (!this.settings || !this.settings.markupConfigured) {
          var firstRunOnly = this.root ? this.root.querySelectorAll('[data-pro-first-run-only]') : [];
          for (var f = 0; f < firstRunOnly.length; f++) {
            var modeSel = firstRunOnly[f].querySelector('[name="markupMode"]');
            var amountEl = firstRunOnly[f].querySelector('[name="markupValue"]');
            if (modeSel) modeSel.value = '';
            if (amountEl) amountEl.value = '';
          }
        }
        for (var mp = 0; mp < (this.markupPairs || []).length; mp++) {
          syncMarkupPair(this.markupPairs[mp]);
          syncMarkupExtras(this.markupPairs[mp], this.root);
        }
        for (var ifp = 0; ifp < (this.installFeePairs || []).length; ifp++) {
          syncInstallFeePair(this.installFeePairs[ifp]);
        }
        if (this.root) populateBusinessHours(this.root, this.settings && this.settings.businessHours);
        this._syncSegs();
        this._syncStepper();
        this._renderCardPreview();
      };

      ProSettingsPanel.prototype._syncStepper = function () {
        if (this.qtyInput && this.qtyButtons && this.qtyButtons.length) {
          syncStepperButtons(this.qtyInput, this.qtyButtons, 'data-pro-settings-qty-step');
        }
      };

      /** Every segmented control back in line with its native control (after a populate,
       * which writes the controls directly). */
      ProSettingsPanel.prototype._syncSegs = function () {
        for (var i = 0; i < (this.segs || []).length; i++) {
          syncSettingsSeg(this.segs[i].seg, this.segs[i].source);
        }
      };

      /**
       * Rollout Task 10 — Shop | Quotes | Card. Only shows and hides the forms: a value
       * typed in one section is still there on the way back, and nothing is saved or
       * cleared. The footer's Save follows, its label and its `form` attribute, so it
       * (and Enter in a field) submits the section on screen through _onSubmit as before.
       * Leaving a section also drops its pending "Saved" and auto-close (the dealer has
       * moved on) and hands the next section a ready Save: saving one never blocks another.
       */
      ProSettingsPanel.prototype._showSection = function (name) {
        this._saveSeq = (this._saveSeq || 0) + 1;
        this.section = name || 'shop';
        this._resetSaveButton();
        if (!this.sectionSwitch) return;
        var active = null;
        for (var i = 0; i < (this.forms || []).length; i++) {
          var mine = this.forms[i].getAttribute('data-pro-settings-section') === this.section;
          this.forms[i].hidden = !mine;
          if (mine) active = this.forms[i];
        }
        var tabs = this.sectionSwitch.querySelectorAll('[data-pro-settings-tab]');
        var at = 0;
        for (var t = 0; t < tabs.length; t++) {
          var on = tabs[t].getAttribute('data-pro-settings-tab') === this.section;
          tabs[t].setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on) at = t;
        }
        var indicator = this.sectionSwitch.querySelector('.tt-pro-seg__ind');
        if (indicator && indicator.style) indicator.style.transform = 'translateX(' + at * 100 + '%)';
        var formId = active && active.getAttribute('id');
        if (this.saveButton && formId) this.saveButton.setAttribute('form', formId);
      };

      /** The footer's Save as the section on screen wants it: enabled, primary, its label. */
      ProSettingsPanel.prototype._resetSaveButton = function () {
        var btn = this.saveButton;
        if (!btn) return;
        setButtonLoading(btn, false);
        toggleClassName(btn, 'tt-pro-settings__save--saved', false);
        btn.textContent = settingsText(this.root, 'save-' + (this.section || 'shop'));
        if (this.saveStatus) this.saveStatus.textContent = '';
      };

      /**
       * dp-8 — draws a real quote card (buildCardPreviewViewModel's fixed
       * sample quote + the dealer's own saved shop name) so the dealer can
       * actually judge a theme, not just read its name. Reads the
       * currently-selected theme straight off the <select> — never gated
       * on Save — falling back to the last-loaded settings.cardTheme only
       * when the select isn't in the DOM at all.
       */
      ProSettingsPanel.prototype._renderCardPreview = function () {
        if (!this.cardPreviewCanvas || !this.cardPreviewCanvas.getContext) return;
        var theme = (this.cardThemeSelect && this.cardThemeSelect.value) || this.settings.cardTheme;
        var vm = buildCardPreviewViewModel(this.settings);
        var accent = resolveCardThemeAccent(theme, readCssVarFromDocument);
        drawQuoteCard(this.cardPreviewCanvas.getContext('2d'), vm, { accentColor: accent });
        redrawWhenFontsReady(this, '_renderCardPreview');
      };

      /** Contract §6.3 — backend is authoritative on load. A guest (401)
       * or an offline dealer simply keeps the local/default copy; this
       * never surfaces an error, matching the same "fail silent" rule as
       * the push side. */
      ProSettingsPanel.prototype._fetchRemote = function () {
        if (typeof fetch === 'undefined') return;
        var self = this;
        fetch(proProxyUrl('/settings'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (body) {
            // The route wraps the settings in { success, settings } — read the
            // wrong level here and the dealer's saved values are replaced by an
            // envelope, then PUT back over his account. See unwrapSettingsPayload.
            var remote = unwrapSettingsPayload(body);
            if (!remote) return;
            self.settings = resolveSettingsOnLoad(self.settings, remote);
            writeLocalSettings(self.settings);
            self._populateAll();
            applyShopIdentity(self.settings);
          })
          .catch(function () {
            // Offline or guest — the local cache already stands.
          });
      };

      /** Round 5 — the results screen redraws its cards on this (the markup may just have been
       * saved). CustomEvent is absent in the fake test DOM; nothing there listens either. */
      function announceSettingsClosed() {
        if (typeof CustomEvent === 'function' && document.dispatchEvent) {
          document.dispatchEvent(new CustomEvent('toee-pro:settings-closed'));
        }
      }

      /** `opener` is the element focus goes back to on close (see ProDrawer#open). */
      ProSettingsPanel.prototype.open = function (firstRun, opener) {
        // First-run mode decides which sections show, and so where focus lands: before opening.
        this._setFirstRunMode(!!firstRun);
        drawerOf(this, announceSettingsClosed).open(opener);
        this._initAddressAutocomplete();
      };

      /** Lazy — the Maps script only loads the first time a dealer
       * actually opens settings (and only once per page load), not on
       * every /pro visit, most of which never touch settings at all. */
      ProSettingsPanel.prototype._initAddressAutocomplete = function () {
        var addressInput = this.root.querySelector('[name="address"]');
        if (!addressInput) return;
        loadPlacesLibrary()
          .then(function (placesLib) {
            attachAddressAutocomplete(addressInput, placesLib);
          })
          .catch(function () {
            // No key configured, offline, or the request failed — the
            // field's own autocomplete="street-address" is the fallback.
          });
      };

      /** Closes by any route (the close button, Esc, the scrim, a save): the drawer runs
       * announceSettingsClosed once, when it really was open. */
      ProSettingsPanel.prototype.close = function () {
        drawerOf(this, announceSettingsClosed).close();
      };

      /** Opens the settings overlay in first-run mode from anywhere on the
       * page (the generate-quote gate, the results' markup prompt). */
      function openSettingsFirstRun(opener) {
        var settingsRoot = document.querySelectorAll('[data-pro-settings]')[0];
        var panel = settingsRoot && settingsRoot._proSettingsPanel;
        if (panel) panel.open(true, opener);
      }

      /** dp-5 — first-run guidance is three fields, not a wizard: every
       * section flagged data-pro-skip-first-run just hides, it is never
       * removed from the DOM or required. data-pro-first-run-only is the
       * mirror image — the markup field only a dealer who hasn't decided
       * yet needs to see; once decided, later tuning happens through the
       * quote-defaults section instead (dp-16). */
      ProSettingsPanel.prototype._setFirstRunMode = function (on) {
        if (this.firstRunNote) this.firstRunNote.hidden = !on;
        var skippable = this.root.querySelectorAll('[data-pro-skip-first-run]');
        for (var i = 0; i < skippable.length; i++) {
          skippable[i].hidden = on;
        }
        var firstRunOnly = this.root.querySelectorAll('[data-pro-first-run-only]');
        for (var j = 0; j < firstRunOnly.length; j++) {
          firstRunOnly[j].hidden = !on;
        }
        // Rollout Task 10: first run is the shop questions only, so there is nothing to switch.
        if (this.sectionSwitch) this.sectionSwitch.hidden = on;
        this._showSection(on ? 'shop' : this.section);
      };

      ProSettingsPanel.prototype._onSubmit = function (e) {
        e.preventDefault();
        var form = e.target;
        var inputs = form.querySelectorAll('[data-pro-field-input]');
        var patch = {};
        var valid = true;

        for (var i = 0; i < inputs.length; i++) {
          var input = inputs[i];
          // The first-run markup copy in the Shop form, while hidden (a decided dealer, or
          // "All settings"), is neither checked nor saved: it holds the page-load markup,
          // and saving it would put an older markup back over the one saved on Quotes.
          var frOnly = input.closest ? input.closest('[data-pro-first-run-only]') : null;
          if (frOnly && frOnly.hidden) continue;
          var fieldWrap = input.closest ? input.closest('[data-pro-field]') : null;
          var messageEl = fieldWrap ? fieldWrap.querySelector('[data-pro-field-message]') : null;
          var value = readFieldValue(input);
          if (input.hasAttribute('required') && isInputEmpty(input)) {
            // Rollout Task 10: the message says what to fix (which fields are required is unchanged).
            applyFieldError(input, messageEl, settingsFieldError(this.root, input.name));
            valid = false;
            continue;
          }
          clearFieldError(input, messageEl);
          patch[input.name] = value;
        }
        if (!valid) return;

        // Owner ruling (2026-09-08) — business hours don't flow through the
        // generic [data-pro-field-input] loop above (a nested object, not
        // one flat value), so the shop-section submit assembles it here.
        var hoursGroups = form.querySelectorAll('[data-pro-hours-group]');
        if (hoursGroups.length) patch.businessHours = readBusinessHours(form);

        // Local write + effect is synchronous; the button only reflects
        // the in-flight *push*, and that push never blocks anything else
        // on the page (contract §6.3 — saving must never block quoting).
        // The footer's Save (rollout Task 10); a form of older markup still has its own.
        var saveBtn = form.querySelector('[data-pro-settings-save]') || this.saveButton;
        this._resetSaveButton();
        setButtonLoading(saveBtn, true);
        var result = saveSettingsSection(this.settings, patch);
        this.settings = result.settings;
        // The other forms show what was just saved (the markup lives in two of them), so
        // their next save cannot carry an older copy. Only the saved names are written:
        // an unsaved edit in another section stays.
        for (var o = 0; o < (this.forms || []).length; o++) {
          if (this.forms[o] !== form) populateForm(this.forms[o], patch);
        }
        this._syncSegs();
        for (var mp = 0; mp < (this.markupPairs || []).length; mp++) {
          syncMarkupPair(this.markupPairs[mp]);
          syncMarkupExtras(this.markupPairs[mp], this.root);
        }
        applyShopIdentity(this.settings);

        // Owner request (2026-09-15): close the whole panel on any
        // section's successful save, not just this one - one consistent
        // rule instead of dealers needing to remember which section
        // closes itself and which doesn't. Rollout Task 10: once the server
        // has the save, the button reads "Saved" and the drawer closes
        // SAVED_CLOSE_MS later. A push the server refused, that could not
        // reach it, or that has not answered after SAVE_WAIT_MS is not
        // "Saved": the local write already stands (the next save retries the
        // push), so the drawer closes as it always did, saying nothing. It is
        // never left waiting on a dead connection.
        var self = this;
        var seq = this._saveSeq = (this._saveSeq || 0) + 1;
        var settled = false;
        var settle = function (ok) {
          if (settled) return;
          settled = true;
          // The shared footer button now belongs to whatever section is on screen; leave it be
          // if the dealer has moved on since (a section switch, a reopen).
          if (saveBtn !== self.saveButton || seq === self._saveSeq) setButtonLoading(saveBtn, false);
          for (var k = 0; ok && k < inputs.length; k++) {
            var wrap = inputs[k].closest ? inputs[k].closest('[data-pro-field]') : null;
            var msg = wrap ? wrap.querySelector('[data-pro-field-message]') : null;
            applyFieldSuccess(inputs[k], msg, 2000);
          }
          if (seq !== self._saveSeq || !self.drawer || !self.drawer.isOpen) return;
          if (!ok) {
            self.close();
            return;
          }
          self._showSaved();
          setTimeout(function () {
            if (seq === self._saveSeq && self.drawer.isOpen) self.close();
          }, ProSettingsPanel.SAVED_CLOSE_MS);
        };
        // pushSettings never rejects: it answers true only when the server took the save.
        result.pushPromise.then(function (ok) { settle(ok === true); });
        if (this.drawer && this.drawer.isOpen) setTimeout(function () { settle(false); }, ProSettingsPanel.SAVE_WAIT_MS);
      };

      /** "Saved": the secondary look with the success border (CSS), said once by the footer's
       * status region (a button's own text change is not announced). */
      ProSettingsPanel.prototype._showSaved = function () {
        var btn = this.saveButton;
        if (!btn) return;
        var saved = settingsText(this.root, 'saved');
        toggleClassName(btn, 'tt-pro-settings__save--saved', true);
        btn.textContent = saved;
        if (this.saveStatus) this.saveStatus.textContent = saved;
      };

      /** dp-9 — a zero-byte probe, only to ask "can you share a PNG at
       * all"; never sent anywhere, and generated fresh (never reused)
       * because it exists purely to answer that one question. Guarded
       * because `File` isn't guaranteed to exist in every environment;
       * canShareFiles already treats a missing/falsy file as "can't
       * share", so a null probe here just means an honest false. */
      function makeShareProbeFile() {
        if (typeof File === 'undefined') return null;
        try {
          return new File([], 'quote-card.png', { type: 'image/png' });
        } catch (e) {
          return null;
        }
      }

      // ---------------------------------------------------------------
      // DP-7 — quote card canvas renderer + screen wiring. drawQuoteCard
      // takes a 2D context directly (not a <canvas>) so it's exercisable
      // with the fake context in tests/toee-pro-quote-card-render.test.js
      // exactly like buildResultCard is exercised with the fake DOM.
      //
      // Every string on the card is either dealer/vehicle data or a
      // CARD_LABELS constant — never a Toee-identifying string, and
      // `opts.accentColor` is the caller's job to source from the
      // `--tt-pro-card-accent` custom property (DESIGN.md "Dealer-Branded
      // Surfaces" — the one {quote-card-themes.*} exception, never
      // {colors.primary}/Toee Blue). The accent fills the header band (and
      // rules the historical warning row) — body copy and figures always
      // use ink/body, per that same section.
      // ---------------------------------------------------------------

      /** The card draws in Inter, which the page loads as a webfont; a
       * canvas painted before the face arrives shows the fallback. Re-run
       * the owner's render method once, when document.fonts settles — a
       * no-op in the fake DOM, which has no document.fonts. */
      function redrawWhenFontsReady(owner, method) {
        if (owner._fontsRedrawn || typeof document === 'undefined' || !document.fonts || !document.fonts.ready) return;
        owner._fontsRedrawn = true;
        document.fonts.ready.then(function () {
          if (typeof owner[method] === 'function') owner[method]();
        });
      }

      /**
       * Shared canvas-text primitives for both the live quote card
       * (drawQuoteCard, below) and the historical one (drawHistoricalQuoteCard,
       * DP-7's own extension for the "recent quotes" feature — design
       * spec: dealer-pro's docs/superpowers/specs/
       * 2026-09-09-dealer-pro-quote-history-design.md). Extracted 2026-09-09
       * from what used to be six local closures inside drawQuoteCard,
       * unchanged in behavior — every value they used to close over
       * (`ctx`, `PAD`, `RIGHT`, `FONT`) is now an explicit parameter
       * instead, so a second drawing function can call them without
       * duplicating them.
       */
      function canvasFont(ctx, weight, size, fontFamily) {
        ctx.font = weight + ' ' + size + 'px ' + fontFamily;
        return size;
      }
      function canvasTextWidth(ctx, text, size) {
        if (ctx.measureText) return ctx.measureText(text).width;
        return String(text).length * size * 0.55;
      }
      function canvasTextRight(ctx, text, y, right) {
        if ('textAlign' in ctx) {
          ctx.textAlign = 'right';
          ctx.fillText(text, right, y);
          ctx.textAlign = 'left';
        } else {
          ctx.fillText(text, right - canvasTextWidth(ctx, text, 20), y);
        }
      }
      function canvasSegment(ctx, x0, y0, x1, y1, color, thickness) {
        ctx.strokeStyle = color;
        ctx.lineWidth = thickness;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
      }
      function canvasRule(ctx, pad, right, y, color, thickness) {
        // An odd-width rule on a whole pixel would smear over two rows; centre it on one.
        var crisp = thickness % 2 ? 0.5 : 0;
        canvasSegment(ctx, pad, y + crisp, right, y + crisp, color, thickness);
      }
      function canvasTracked(ctx, on) {
        if ('letterSpacing' in ctx) ctx.letterSpacing = on ? '1.5px' : '0px';
      }
      // Greedy word wrap for the spec-string title (never mid-token, so a
      // size like 205/55R16 stays whole).
      function canvasWrap(ctx, text, size, maxWidth) {
        var words = String(text || '').split(/\s+/).filter(Boolean);
        var lines = [];
        var line = '';
        for (var i = 0; i < words.length; i++) {
          var next = line ? line + ' ' + words[i] : words[i];
          if (line && canvasTextWidth(ctx, next, size) > maxWidth) {
            lines.push(line);
            line = words[i];
          } else {
            line = next;
          }
        }
        if (line) lines.push(line);
        return lines;
      }

      // ---- Rollout Task 12: the card per the "Quote-card" artboard, drawn at
      // its real 640x800 coordinates. A shared shape for both cards: accent
      // band, a body of blocks, charge lines and the total, a row of footer
      // cells. The greys are DESIGN.md's own values (not Toee brand colours);
      // the only theme colour is the accent the caller passes in.
      var CARD_COLORS = {
        white: '#ffffff',
        onAccentSoft: 'rgba(255, 255, 255, 0.85)',
        ink: '#071426',
        body: '#233047',
        muted: '#66748a',
        hairline: '#dbe3ee',
        warning: '#e07b00'
      };
      // Inter is what the page sets everywhere else; the canvas falls back to
      // sans-serif until the face is loaded (callers re-draw on
      // document.fonts.ready). The fake test contexts have no measureText,
      // so width estimates degrade to a character count there.
      var CARD_FONT = 'Inter, sans-serif';
      // Weight, size and CSS line height of every kind of text on the card.
      var CARD_TYPE = {
        shop: { weight: '700', size: 32, lh: 38 },
        shopSmall: { weight: '700', size: 26, lh: 31 },
        contact: { weight: '400', size: 16, lh: 22 },
        kicker: { weight: '700', size: 14, lh: 18 },
        owner: { weight: '300', size: 22, lh: 31 },
        title: { weight: '700', size: 24, lh: 31 },
        titleSmall: { weight: '700', size: 20, lh: 26 },
        detail: { weight: '300', size: 16, lh: 22 },
        line: { weight: '300', size: 20, lh: 28 },
        total: { weight: '700', size: 36, lh: 43 },
        value: { weight: '700', size: 18, lh: 23 },
        note: { weight: '300', size: 14, lh: 20 }
      };
      var CARD_TOTAL_HEIGHT = 2 + 16 + CARD_TYPE.total.lh; // ink rule, padding, the line
      // "225/40R18 HANKOOK VENTUS ..." -> the size, then the rest.
      var TIRE_SIZE_HEAD = /^(\S*\d\S*R\d\S*)\s+(.+)$/i;

      /** Baseline of a `type` line whose CSS line box starts at `top`: Inter
       * sits half a line plus 0.36em below the top of its box. */
      function cardBaseline(top, type) {
        var t = CARD_TYPE[type];
        return top + t.lh / 2 + t.size * 0.36;
      }

      /** Sets the type's font and wraps `text` to `width` (never mid-word).
       * With `maxLines`, a longer text keeps that many lines and the last one
       * ends in "…" (words dropped until it fits), so no typed text can push
       * the total off the card. */
      function cardLines(ctx, type, text, width, maxLines) {
        var t = CARD_TYPE[type];
        canvasFont(ctx, t.weight, t.size, CARD_FONT);
        var lines = canvasWrap(ctx, text, t.size, width);
        if (maxLines && lines.length > maxLines) {
          lines = lines.slice(0, maxLines);
          var last = lines[maxLines - 1];
          while (last.indexOf(' ') > 0 && canvasTextWidth(ctx, last + '…', t.size) > width) {
            last = last.slice(0, last.lastIndexOf(' '));
          }
          lines[maxLines - 1] = last + '…';
        }
        return lines;
      }

      /** `type` when the text takes two lines or fewer, else `smaller`
       * capped at two lines: { type, lines }. */
      function cardFit(ctx, type, smaller, text, width) {
        var lines = cardLines(ctx, type, text, width);
        if (lines.length <= 2) return { type: type, lines: lines };
        return { type: smaller, lines: cardLines(ctx, smaller, text, width, 2) };
      }

      /** Draws wrapped lines one line box under the other; returns the bottom. */
      function cardDraw(ctx, type, lines, x, top, color) {
        var t = CARD_TYPE[type];
        ctx.fillStyle = color;
        canvasFont(ctx, t.weight, t.size, CARD_FONT);
        for (var i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i], x, cardBaseline(top + i * t.lh, type));
        }
        return top + lines.length * t.lh;
      }

      /** The uppercase tracked label (14 / 700); returns its bottom. */
      function cardKicker(ctx, text, x, top, color) {
        ctx.fillStyle = color;
        canvasFont(ctx, CARD_TYPE.kicker.weight, CARD_TYPE.kicker.size, CARD_FONT);
        canvasTracked(ctx, true);
        ctx.fillText(String(text || '').toUpperCase(), x, cardBaseline(top, 'kicker'));
        canvasTracked(ctx, false);
        return top + CARD_TYPE.kicker.lh;
      }

      function cardFrame(ctx, vm, opts) {
        var W = (ctx.canvas && ctx.canvas.width) || 640;
        var f = {
          W: W,
          H: (ctx.canvas && ctx.canvas.height) || 800,
          pad: 40,
          right: W - 40,
          accent: (opts && opts.accentColor) || CARD_COLORS.body,
          labels: (vm && vm.labels) || CARD_LABELS
        };
        ctx.fillStyle = CARD_COLORS.white;
        ctx.fillRect(0, 0, f.W, f.H);
        return f;
      }

      /** The accent band: shop name, phone · address, hours, and `rightText`
       * top-right. Only the shop name shares its lines with `rightText`; the
       * contact lines under it take the full width. Shop name 2 lines (26px
       * when 32px needs more), contact 3 lines (phone · address up to 2, the
       * hours the rest). Returns the band's bottom. */
      function cardBand(ctx, f, vm, rightText) {
        var width = f.right - f.pad;
        var rightWidth = 0;
        if (rightText) {
          canvasFont(ctx, CARD_TYPE.contact.weight, CARD_TYPE.contact.size, CARD_FONT);
          rightWidth = canvasTextWidth(ctx, rightText, CARD_TYPE.contact.size) + 24;
        }
        var name = cardFit(ctx, 'shop', 'shopSmall', vm.shopName || '', width - rightWidth);
        var contact = cardLines(ctx, 'contact', [vm.phone, vm.address].filter(Boolean).join(' · '), width, 2);
        contact = contact.concat(cardLines(ctx, 'contact', vm.businessHours || '', width, 3 - contact.length));
        var bottom = 32 + name.lines.length * CARD_TYPE[name.type].lh + contact.length * CARD_TYPE.contact.lh + 32;
        ctx.fillStyle = f.accent;
        ctx.fillRect(0, 0, f.W, bottom);
        var y = cardDraw(ctx, name.type, name.lines, f.pad, 32, CARD_COLORS.white);
        cardDraw(ctx, 'contact', contact, f.pad, y, CARD_COLORS.onAccentSoft);
        if (rightText) {
          ctx.fillStyle = CARD_COLORS.onAccentSoft;
          canvasFont(ctx, CARD_TYPE.contact.weight, CARD_TYPE.contact.size, CARD_FONT);
          canvasTextRight(ctx, rightText, cardBaseline(32, 'contact'), f.right);
        }
        return bottom;
      }

      // A body block is measured before anything is drawn, so the body can
      // space its blocks to fit: { height, draw(top) -> bottom }.

      /** "Prepared for" over the owner and vehicle, as one line; null when
       * the card names neither (PRD §5.3.4.2/§8.6: owner details are drawn
       * here only and never leave the canvas). */
      function cardPreparedFor(ctx, f, vm) {
        var owner = vm.owner || {};
        var text = [owner.name, owner.vehicleModel, owner.plate, vm.vehicleLine].filter(Boolean).join(' · ');
        if (!text) return null;
        var lines = cardLines(ctx, 'owner', text, f.right - f.pad, 2);
        return {
          height: CARD_TYPE.kicker.lh + 6 + lines.length * CARD_TYPE.owner.lh,
          draw: function (top) {
            var y = cardKicker(ctx, f.labels.preparedFor, f.pad, top, CARD_COLORS.muted) + 6;
            return cardDraw(ctx, 'owner', lines, f.pad, y, CARD_COLORS.ink);
          }
        };
      }

      /** A product: an optional label over its title, plus optional `below`
       * lines (300 20). The title is 24px, 20px when `small` (two products on
       * one card) or when 24px would take more than two lines; never more
       * than two lines. */
      function cardProduct(ctx, f, label, title, small, below) {
        var width = f.right - f.pad;
        var fit = cardFit(ctx, small ? 'titleSmall' : 'title', 'titleSmall', title, width);
        var type = fit.type;
        var lines = fit.lines;
        var gap = small ? 4 : 6;
        var under = below ? cardLines(ctx, 'line', below, width) : [];
        return {
          height: (label ? CARD_TYPE.kicker.lh + gap : 0) + lines.length * CARD_TYPE[type].lh +
            (under.length ? 6 + under.length * CARD_TYPE.line.lh : 0),
          draw: function (top) {
            var y = label ? cardKicker(ctx, label, f.pad, top, CARD_COLORS.muted) + gap : top;
            y = cardDraw(ctx, type, lines, f.pad, y, CARD_COLORS.ink);
            return under.length ? cardDraw(ctx, 'line', under, f.pad, y + 6, CARD_COLORS.body) : y;
          }
        };
      }

      /** Front | Rear as a bordered grid: per cell the axle, the size (700 20)
       * and "brand × qty" (300 16). A title that does not open with a tire
       * size stays whole on the size line. */
      function cardAxles(ctx, f, axles) {
        var cellW = (f.right - f.pad) / axles.length;
        var inner = cellW - 32;
        var height = 0;
        var cells = axles.map(function (line) {
          var m = TIRE_SIZE_HEAD.exec(String(line.title || ''));
          var cell = {
            axle: line.axle,
            size: cardLines(ctx, 'titleSmall', m ? m[1] : line.title, inner),
            detail: cardLines(ctx, 'detail', (m ? m[2] + ' ' : '') + '× ' + line.quantity, inner, 3)
          };
          height = Math.max(height, 16 + CARD_TYPE.kicker.lh + 4 + cell.size.length * CARD_TYPE.titleSmall.lh +
            2 + cell.detail.length * CARD_TYPE.detail.lh + 16);
          return cell;
        });
        return {
          height: height,
          draw: function (top) {
            var bottom = top + height;
            cells.forEach(function (cell, i) {
              var x = f.pad + i * cellW;
              if (i) canvasSegment(ctx, x, top, x, bottom, CARD_COLORS.hairline, 1);
              var y = cardKicker(ctx, cell.axle, x + 16, top + 16, CARD_COLORS.muted) + 4;
              y = cardDraw(ctx, 'titleSmall', cell.size, x + 16, y, CARD_COLORS.ink) + 2;
              cardDraw(ctx, 'detail', cell.detail, x + 16, y, CARD_COLORS.body);
            });
            // The border as one path, so it never reads as a charge line's hairline.
            ctx.strokeStyle = CARD_COLORS.hairline;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(f.pad, top);
            ctx.lineTo(f.right, top);
            ctx.lineTo(f.right, bottom);
            ctx.lineTo(f.pad, bottom);
            ctx.lineTo(f.pad, top);
            ctx.stroke();
            return bottom;
          }
        };
      }

      /** The footer: equal cells along the bottom edge, a hairline over them
       * and between them. Drawn first so the body knows its room; returns the
       * footer's top. A cell is { label, value } (label over a bold value),
       * { marker, value } (an 8px state square and bold text, on the value
       * line) or { note: [...] } (small print). */
      function cardFooter(ctx, f, cells) {
        if (!cells.length) return f.H;
        var cellW = f.W / cells.length;
        var valueTop = CARD_TYPE.kicker.lh + 4;
        var tallest = 0;
        var laid = cells.map(function (cell, i) {
          var x = i ? i * cellW + 20 : f.pad;
          var width = (i + 1) * cellW - (i === cells.length - 1 ? f.pad : 20) - x;
          var c = { x: x, cell: cell, note: [], value: [] };
          (cell.note || []).forEach(function (n) {
            c.note = c.note.concat(cardLines(ctx, 'note', n, width));
          });
          if (cell.value) c.value = cardLines(ctx, 'value', cell.value, width - (cell.marker ? 16 : 0));
          tallest = Math.max(tallest, c.note.length
            ? c.note.length * CARD_TYPE.note.lh
            : valueTop + c.value.length * CARD_TYPE.value.lh);
          return c;
        });
        var top = f.H - 16 - tallest - 16;
        canvasRule(ctx, 0, f.W, top, CARD_COLORS.hairline, 1);
        laid.forEach(function (c, i) {
          if (i) canvasSegment(ctx, i * cellW, top, i * cellW, f.H, CARD_COLORS.hairline, 1);
          var y = top + 16;
          if (c.note.length) {
            cardDraw(ctx, 'note', c.note, c.x, y, CARD_COLORS.body);
            return;
          }
          if (c.cell.label) cardKicker(ctx, c.cell.label, c.x, y, CARD_COLORS.muted);
          var x = c.x;
          if (c.cell.marker) {
            ctx.fillStyle = CARD_COLORS.ink;
            ctx.fillRect(x, y + valueTop + (CARD_TYPE.value.lh - 8) / 2, 8, 8);
            x += 16;
          }
          cardDraw(ctx, 'value', c.value, x, y + valueTop, CARD_COLORS.ink);
        });
        return top;
      }

      /**
       * The body between the band (`top`) and the footer (`bottom`): blocks
       * from the top, the total (700 36 over a 2px ink rule) anchored above
       * the footer, one charge line per row between them, each under its own
       * hairline, a pad between the last one and the total. Pads, gaps and
       * the row pitch tighten together (k from 1 to 0) on a crowded card
       * instead of running a line into the total. k = 0 is the floor; the
       * line caps (band, Prepared for, titles, front/rear cells) keep every
       * real card above it — tests/toee-pro-quote-card-render.test.js draws
       * the worst case of each card kind.
       */
      function cardBody(ctx, f, top, bottom, blocks, rows, total) {
        function spacing(k) {
          var s = { pad: 12 + 20 * k, gap: 8 + 12 * k, pitch: 32 + 21 * k };
          s.height = 3 * s.pad + CARD_TOTAL_HEIGHT + rows.length * s.pitch;
          blocks.forEach(function (b) { s.height += b.height + s.gap; });
          return s;
        }
        var tight = spacing(0).height;
        var k = Math.max(0, Math.min(1, (bottom - top - tight) / (spacing(1).height - tight)));
        var s = spacing(k);
        var y = top + s.pad;
        blocks.forEach(function (b) { y = b.draw(y) + s.gap; });
        rows.forEach(function (row) {
          canvasRule(ctx, f.pad, f.right, y, CARD_COLORS.hairline, 1);
          var base = cardBaseline(y + (s.pitch - CARD_TYPE.line.lh) / 2, 'line');
          ctx.fillStyle = CARD_COLORS.body;
          canvasFont(ctx, CARD_TYPE.line.weight, CARD_TYPE.line.size, CARD_FONT);
          ctx.fillText(row[0], f.pad, base);
          ctx.fillStyle = CARD_COLORS.ink;
          // The card is English for the car owner (CARD_LABELS), its money too.
          canvasTextRight(ctx, formatMoney(row[1], 'en'), base, f.right);
          y += s.pitch;
        });
        var totalTop = bottom - s.pad - CARD_TOTAL_HEIGHT;
        canvasRule(ctx, f.pad, f.right, totalTop, CARD_COLORS.ink, 2);
        var totalBase = cardBaseline(totalTop + 2 + 16, 'total');
        ctx.fillStyle = CARD_COLORS.ink;
        canvasFont(ctx, CARD_TYPE.total.weight, CARD_TYPE.total.size, CARD_FONT);
        ctx.fillText(f.labels.total, f.pad, totalBase);
        canvasTextRight(ctx, formatMoney(total, 'en'), totalBase, f.right);
      }

      drawQuoteCard = function (ctx, vm, opts) {
        // The card is a 640x800 image the car owner reads on a phone at
        // ~60% scale, so nothing is drawn under 14px.
        var f = cardFrame(ctx, vm, opts);
        var labels = f.labels;
        var top = cardBand(ctx, f, vm, vm.validUntil ? labels.validUntil + ' ' + vm.validUntil : '');

        var axles = vm.lines && vm.lines.length > 1 ? vm.lines : null;
        var product;
        if (axles) {
          product = cardAxles(ctx, f, axles);
        } else if (vm.wheelTitle) {
          // Package (2026-09-11): a labelled block each for the tires and the wheels.
          var tires = cardProduct(ctx, f, labels.tires, vm.title, true);
          var wheels = cardProduct(ctx, f, labels.wheels, vm.wheelTitle, true);
          product = {
            height: tires.height + 12 + wheels.height,
            draw: function (y) { return wheels.draw(tires.draw(y) + 12); }
          };
        } else {
          // When the delivery footnote takes the footer's quantity cell, the
          // quantity goes under the title instead (as on the historical card).
          var footnote = vm.deliveryFeeVisible && labels.deliveryFeeNote;
          product = cardProduct(ctx, f, labels.tires, vm.title, false, footnote ? labels.quantity + ' ' + vm.quantity : '');
        }

        // The same charge lines, in the same order and on the same
        // conditions as before the redesign — tests/toee-pro-quote-card-
        // figures.test.js pins every figure.
        var rows = [[labels.tires, vm.tires]];
        if (vm.wheels != null) rows.push([labels.wheels, vm.wheels]);
        rows.push([labels.recyclingFee, vm.recyclingFee]);
        if (vm.deliveryFeeVisible) rows.push([labels.deliveryFee + '*', vm.deliveryFee]);
        // dp-7 hard constraint: zero install fee hides this whole line.
        if (vm.installFeeVisible) rows.push([labels.installation, vm.installFee]);
        rows.push([labels.hst, vm.hst]);

        var cells = [];
        if (vm.inStock) cells.push({ marker: true, value: labels.inStock });
        if (vm.sameDay) cells.push({ marker: true, value: labels.sameDay });
        if (vm.deliveryFeeVisible && labels.deliveryFeeNote) {
          cells.push({ note: [labels.deliveryFeeNote, labels.deliveryFeeNoteMore].filter(Boolean) });
        } else if (vm.quantity) {
          cells.push({
            label: vm.wheelTitle ? labels.tirePackage : labels.quantity,
            value: axles
              ? axles.map(function (line) { return line.quantity; }).join(' + ')
              : (vm.wheelTitle ? vm.quantity + ' + ' + vm.quantity : String(vm.quantity))
          });
        }

        cardBody(ctx, f, top, cardFooter(ctx, f, cells), [cardPreparedFor(ctx, f, vm), product].filter(Boolean), rows, vm.total);
        return ctx;
      };

      /**
       * A reopened historical quote (design spec: dealer-pro's
       * docs/superpowers/specs/2026-09-09-dealer-pro-quote-history-design.md).
       * Deliberately NOT a branch inside drawQuoteCard above: there is no
       * recycling fee / install fee / HST to draw, because dealerProEvent
       * never recorded them — so no charge lines at all, only the stored
       * total. It shares drawQuoteCard's band, blocks, body and footer
       * helpers. Same DESIGN.md "Dealer-Branded Surfaces" rules apply: no
       * Toee mark, no Toee Blue.
       */
      drawHistoricalQuoteCard = function (ctx, vm, opts) {
        var f = cardFrame(ctx, vm, opts);
        var labels = f.labels;
        var top = cardBand(ctx, f, vm, '');

        // The one thing this card exists to say: a warning square and the
        // badge, the row closed by an accent rule.
        var badge = cardLines(ctx, 'value', labels.historicalBadge, f.right - f.pad - 24);
        ctx.fillStyle = CARD_COLORS.warning;
        ctx.fillRect(f.pad, top + 14 + (CARD_TYPE.value.lh - 12) / 2, 12, 12);
        top = cardDraw(ctx, 'value', badge, f.pad + 24, top + 14, CARD_COLORS.ink) + 14;
        canvasRule(ctx, 0, f.W, top, f.accent, 1);

        // A front/rear quote names each axle. A package stays one title.
        var product = vm.lines && vm.lines.length > 1
          ? cardAxles(ctx, f, vm.lines)
          : cardProduct(ctx, f, '', vm.title, false, labels.quantity + ' ' + vm.quantity);
        var footer = cardFooter(ctx, f, [{ label: labels.historicalQuotedOn, value: vm.quotedAt }]);
        cardBody(ctx, f, top + 1, footer, [cardPreparedFor(ctx, f, vm), product].filter(Boolean), [], vm.total);
        return ctx;
      };

      /**
       * DP-7 screen wiring — opened by any [data-pro-generate-quote]
       * button (delegated on `document`, so it works for a button built
       * fresh into any result card without per-card listener wiring).
       * Reads dealer settings + owner details, applies a temporary
       * override from its own fields, and re-renders the canvas on any
       * change. The override fields are read straight off the form and
       * handed to buildQuoteCardViewModel each render — never written
       * into `this.settings` or pushed anywhere (dp-7: "临时改价不写回默认值").
       *
       * `nav` is DP-9's share/save/copy controls' only dependency on the
       * outside world — injected the same way `fetchImpl`/`storage` are
       * injected elsewhere in this file (pushSettings, readLocalSettings),
       * defaulting to the real `navigator`. Real callers never pass one.
       */
      ProQuoteScreen = function (root, nav, fetchImpl) {
        this.root = root;
        if (!root) return;
        // 2026-09-09 — injectable the same way nav already is; real callers
        // still get the real global fetch (see postDealerProEvent).
        this.fetchImpl = fetchImpl;
        this.canvas = root.querySelector('[data-pro-quote-canvas]');
        this.body = root.querySelector('[data-pro-quote-body]');
        this.dealerDetail = root.querySelector('[data-pro-quote-detail]');
        this.ineligibleEl = root.querySelector('[data-pro-quote-ineligible]');
        this.backButton = root.querySelector('[data-pro-quote-back]');
        this.overrideInputs = root.querySelectorAll('[data-pro-quote-override]');
        this.ownerInputs = root.querySelectorAll('[data-pro-quote-owner]');
        // Package (2026-09-11) — the one free-form Installation amount a
        // package card carries (owner ruling: no mounting/balancing lines of
        // Toee's own). Visible only while a package quote is open; unlike the
        // removed markup override, this number is printed on the card anyway.
        this.packageInstallField = root.querySelector('[data-pro-package-install-field]');
        this.packageInstallInput = root.querySelector('[data-pro-package-install]');
        // DP-9 — share controls (dp-9-share-sheet.md).
        this.sharePrimaryButton = root.querySelector('[data-pro-share-primary]');
        this.shareSaveButton = root.querySelector('[data-pro-share-save]');
        this.shareCopyButton = root.querySelector('[data-pro-share-copy]');
        this.shareStatusEl = root.querySelector('[data-pro-share-status]');
        // The row that holds the ok marker beside the status text; hidden as one until there is a message.
        this.shareStatusRow = root.querySelector('[data-pro-share-status-row]');
        // Top bar: "Valid until <time>", shown only for a quote whose view model carries a time.
        this.validEl = root.querySelector('[data-pro-quote-valid]');
        this.nav = nav || (typeof navigator !== 'undefined' ? navigator : null);
        // Checked once per screen instance, not per render — a browser's
        // file-sharing support doesn't change mid-session, and this is a
        // capability check (navigator.canShare), never a UA sniff.
        this._canShareFiles = canShareFiles(this.nav, makeShareProbeFile());
        this._lastVm = null;
        this.settings = readLocalSettings() || settingsDefaults();
        this.quote = null;

        var self = this;
        // Place one COD order — the same two actions serve the live card and
        // the reopened historical card (one screen, one pair of buttons), so
        // open()/openHistorical() only re-point them at the current quoteId.
        var placeDelivery = root.querySelector('[data-pro-place-delivery]');
        var placePickup = root.querySelector('[data-pro-place-pickup]');
        if (placeDelivery && placePickup) {
          var placeStatus = root.querySelector('[data-pro-place-status]');
          var placeProgress = root.querySelector('[data-pro-place-progress]');
          if (!placeProgress && placeStatus) {
            placeProgress = document.createElement('p');
            placeProgress.className = 'tt-pro-place-order__progress';
            placeProgress.setAttribute('data-pro-place-progress', '');
            placeProgress.hidden = true;
            if (typeof placeStatus.after === 'function') placeStatus.after(placeProgress);
            else placeStatus.parentNode.insertBefore(placeProgress, placeStatus.nextSibling);
          }
          // The pickup warehouse list: one GET for the page, read by every set of buttons.
          this._pickupWarehousesLoad = loadPickupWarehouses(fetchImpl);
          this.placeOrderActions = createPlaceOrderActions(
            {
              delivery: placeDelivery,
              pickup: placePickup,
              select: root.querySelector('[data-pro-pickup-select]'),
              status: placeStatus,
              progress: placeProgress
            },
            {
              fetchImpl: fetchImpl,
              getQuoteId: function () { return self.quoteId; },
              text: placeOrderText(root),
              // "Refresh quote" on a stale answer: the order needs today's price,
              // so run the search again and go back to the results, where
              // Generate quote makes a new quote from them.
              refreshQuote: function () {
                var forms = document.querySelectorAll('[data-pro-search]');
                if (forms.length && forms[0]._proSearch) forms[0]._proSearch._retryLast();
                self.close();
                // At 1024px+ the pane stays open by CSS: clear the stale callout and its button.
                self.placeOrderActions.refresh();
              },
              // A reopened history quote has no search of its own: re-running the page's
              // last one would refresh something unrelated, so it gets no such button.
              canRefreshQuote: function () { return !self._historicalEntry; },
              // Share stays the filled button on a new quote. Place order stays
              // on the same screen, under that row, so the dealer can still order.
            }
          );
        }
        document.addEventListener('click', function (e) {
          var btn = e.target && e.target.closest && e.target.closest('[data-pro-generate-quote]');
          if (!btn) return;
          // Owner ruling (dp-16) — a quote can't be generated until markup
          // is decided (see needsFirstRun). Re-read localStorage fresh
          // here rather than trust `self.settings`, for the same reason
          // _render already does (tests/toee-pro-quote-screen-settings.
          // test.js, final review finding #2): a save in ProSettingsPanel
          // writes straight to localStorage and never tells this screen.
          if (needsFirstRun(readLocalSettings())) {
            openSettingsFirstRun(btn);
            return;
          }
          var raw = btn.getAttribute('data-pro-generate-quote');
          var quote = null;
          try {
            quote = JSON.parse(raw);
          } catch (err) {
            quote = null;
          }
          if (quote) {
            // A card already marked Quoted shows the quote it made, under its own id: no new
            // quote, no new "quote" event.
            self.open(quote, btn.getAttribute('data-pro-quoted-id'));
            // A result card's primary button reads "Quoted" from here until the
            // next render, but only if a card was actually drawn: an ineligible-
            // province quote shows a message instead (_lastVm stays null). The
            // package bar's and the front / rear Generate buttons carry no label
            // for it and stay as they are.
            if (self._lastVm) markCardQuoted(btn, self.quoteId);
          }
        });

        document.addEventListener('click', function (e) {
          var item = e.target && e.target.closest && e.target.closest('[data-pro-quote-history-item]');
          if (!item) return;
          var raw = item.getAttribute('data-pro-quote-history-entry');
          var entry = null;
          try {
            entry = JSON.parse(raw);
          } catch (err) {
            entry = null;
          }
          if (entry) self.openHistorical(entry);
        });

        if (this.backButton) {
          this.backButton.addEventListener('click', function () {
            self._onBack();
          });
        }
        for (var i = 0; i < this.overrideInputs.length; i++) {
          this.overrideInputs[i].addEventListener('input', function () {
            self._rerenderCurrent();
          });
        }
        if (this.packageInstallInput) {
          this.packageInstallInput.addEventListener('input', function () {
            self._rerenderCurrent();
          });
        }
        if (this.sharePrimaryButton) {
          // dp-9 acceptance: "桌面浏览器上主按钮自动变为「保存图片」" — the
          // only thing that ever picks between these two labels is
          // this._canShareFiles, itself only ever set from canShareFiles().
          this.sharePrimaryButton.textContent = this._canShareFiles ? text(root, 'share') : text(root, 'share-save');
          this.sharePrimaryButton.addEventListener('click', function () {
            self._onSharePrimaryTap();
          });
        }
        if (this.shareSaveButton) {
          // Where files cannot be shared the primary button already reads "Save image"
          // (above), so a second one beside it is a duplicate: hide that one. A phone
          // that can share keeps "Save image" next to "Share quote card" (it showed there
          // before `.tt-pro-share-btn[hidden]` took effect, and Android saves fine).
          // With no primary button at all, this is the only way to save.
          this.shareSaveButton.hidden = !!this.sharePrimaryButton && !this._canShareFiles;
          this.shareSaveButton.addEventListener('click', function () {
            self._saveImage();
          });
        }
        if (this.shareCopyButton) {
          this.shareCopyButton.addEventListener('click', function () {
            self._copyText();
          });
        }
        this.requoteButton = root.querySelector('[data-pro-requote]');
        if (this.requoteButton) {
          this.requoteButton.addEventListener('click', function () {
            self._onRequote();
          });
        }
        for (var j = 0; j < this.ownerInputs.length; j++) {
          this.ownerInputs[j].addEventListener('input', function () {
            self._onOwnerInput();
          });
        }

        this._populateOwnerInputs(readOwnerDetails());
      };

      ProQuoteScreen.prototype._readOverrides = function () {
        var overrides = {};
        for (var i = 0; i < this.overrideInputs.length; i++) {
          var input = this.overrideInputs[i];
          var key = input.getAttribute('data-pro-quote-override');
          var value = input.value;
          if (key === 'markupValue' || key === 'installFeeValue') {
            overrides[key] = value === '' ? undefined : Number(value);
          } else if (value !== '') {
            overrides[key] = value;
          }
        }
        return overrides;
      };

      ProQuoteScreen.prototype._readOwner = function () {
        var owner = {};
        for (var i = 0; i < this.ownerInputs.length; i++) {
          var input = this.ownerInputs[i];
          owner[input.getAttribute('data-pro-quote-owner')] = input.value;
        }
        return owner;
      };

      ProQuoteScreen.prototype._populateOwnerInputs = function (owner) {
        owner = owner || {};
        for (var i = 0; i < this.ownerInputs.length; i++) {
          var input = this.ownerInputs[i];
          input.value = owner[input.getAttribute('data-pro-quote-owner')] || '';
        }
      };

      /** PRD §5.3.4.2/§8.6 — localStorage only, never a network payload. */
      ProQuoteScreen.prototype._onOwnerInput = function () {
        var owner = this._readOwner();
        writeOwnerDetails(owner);
        recordOwnerHistorySnapshot(this.quoteId, owner);
        this._rerenderCurrent();
      };

      // `sameQuoteId` is a card's "Quoted" button asking for the quote it already made: the
      // screen shows it again under that id and makes nothing new (no id, no owner snapshot,
      // no "quote" event).
      /** Moves `node` to `parent`, before `before` (or at the end). Works in the
       * browser and in the fake DOM, which has no insertBefore. */
      function moveQuoteNode(node, parent, before) {
        if (!node || !parent || node === before) return;
        if (typeof parent.insertBefore === 'function') {
          parent.insertBefore(node, before || null);
          return;
        }
        var old = node.parentNode;
        if (old && old.children) {
          var at = -1;
          for (var i = 0; i < old.children.length; i++) if (old.children[i] === node) at = i;
          if (at > -1) old.children.splice(at, 1);
        }
        var kids = parent.children;
        if (!kids) return;
        var index = -1;
        if (before) {
          for (var j = 0; j < kids.length; j++) if (kids[j] === before) index = j;
        }
        if (index < 0) kids.push(node);
        else kids.splice(index, 0, node);
        node.parentNode = parent;
      }

      /** The filled button is primary; the other action buttons are secondary. */
      function paintQuoteButton(btn, filled) {
        if (!btn) return;
        var keep = (btn.className || '').split(/\s+/).filter(function (name) {
          return name && name !== 'tt-pro-share-btn' && name !== 'tt-pro-share-btn--primary' && name !== 'tt-pro-share-btn--secondary' && name !== 'tt-pro-btn2';
        });
        keep.unshift('tt-pro-share-btn');
        keep.push(filled ? 'tt-pro-share-btn--primary' : 'tt-pro-share-btn--secondary');
        if (!filled) keep.push('tt-pro-btn2');
        btn.className = keep.join(' ');
        if (filled) btn.setAttribute('data-pro-quote-primary', '');
        else btn.removeAttribute('data-pro-quote-primary');
      }

      /** quoteActions decides the filled button. On a new quote that button is
       * share, and Place order stays visible under it. History makes Place order
       * the filled button. A stale history quote fills the row with re-quote
       * and disables place order. */
      ProQuoteScreen.prototype._syncQuoteActions = function () {
        var row = this.root && this.root.querySelector ? this.root.querySelector('[data-pro-quote-actions]') : null;
        if (!row) return;
        var Counter = loadedCounterApi();
        if (!Counter || !Counter.quoteActions) return;
        var actions = Counter.quoteActions(this._quoteSource || 'new');
        var secondary = row.querySelector('.tt-pro-quote-share__secondary');
        var share = this.sharePrimaryButton;
        var requote = this.requoteButton || this.root.querySelector('[data-pro-requote]');
        var start = this.root.querySelector('[data-pro-place-start]');
        var place = this.root.querySelector('[data-pro-place-order]');
        var ordered = hasLiveOrder(this.quoteId);
        var primary = actions.primary;

        if (requote) {
          var again = text(this.root, 'requote');
          if (again) requote.textContent = again;
          requote.hidden = primary !== 'requote';
        }
        if (share) share.hidden = false;

        if (primary === 'order' && start && !ordered) {
          moveQuoteNode(start, row, secondary);
          paintQuoteButton(start, true);
          start.hidden = false;
          if (share && secondary) moveQuoteNode(share, secondary, secondary.firstChild || null);
          paintQuoteButton(share, false);
          paintQuoteButton(requote, false);
        } else if (primary === 'order' && ordered) {
          // The order number and progress are already on screen. Share stays a
          // secondary action; it is not the filled button.
          if (share && secondary) moveQuoteNode(share, secondary, secondary.firstChild || null);
          paintQuoteButton(share, false);
          paintQuoteButton(requote, false);
          if (requote) requote.hidden = true;
          if (start) {
            paintQuoteButton(start, false);
            start.hidden = true;
          }
        } else if (primary === 'requote' && requote) {
          moveQuoteNode(requote, row, secondary);
          paintQuoteButton(requote, true);
          requote.hidden = false;
          if (share && secondary) moveQuoteNode(share, secondary, secondary.firstChild || null);
          paintQuoteButton(share, false);
          if (start && place) moveQuoteNode(start, place, place.firstChild || null);
          paintQuoteButton(start, false);
          if (start && !ordered) start.hidden = false;
        } else {
          if (share) moveQuoteNode(share, row, secondary || requote);
          paintQuoteButton(share, true);
          paintQuoteButton(requote, false);
          if (requote) requote.hidden = true;
          if (start && place) moveQuoteNode(start, place, place.firstChild || null);
          paintQuoteButton(start, false);
          if (start) start.hidden = false;
        }
        if (start) start.disabled = !actions.orderEnabled || !!ordered;
      };

      /** Prices the same goods again. A history row already names its variants, so
       * quoteVariants asks quote-batch for those ids at today's price. It does not
       * re-run whatever search the page did last. A live quote still retries that search. */
      ProQuoteScreen.prototype._onRequote = function () {
        var entry = this._historicalEntry;
        var forms = typeof document !== 'undefined' && document.querySelectorAll ? document.querySelectorAll('[data-pro-search]') : [];
        var search = forms.length ? forms[0]._proSearch : null;
        if (entry && search && typeof search.quoteVariants === 'function') {
          var ids = [];
          if (entry.variantId != null && entry.variantId !== '') ids.push(String(entry.variantId));
          if (entry.rearVariantId != null && entry.rearVariantId !== '') ids.push(String(entry.rearVariantId));
          if (entry.rimVariantId != null && entry.rimVariantId !== '') ids.push(String(entry.rimVariantId));
          if (search.quantityInput) search.quantityInput.value = String(entry.qty || 1);
          search.quoteVariants(ids, entry.title || '');
          this.close();
          return;
        }
        if (search && search._retryLast) search._retryLast();
        this.close();
        if (this.placeOrderActions) this.placeOrderActions.refresh();
      };

      ProQuoteScreen.prototype.open = function (quote, sameQuoteId) {
        this.quote = quote;
        this._quoteSource = 'new';
        // A new package starts from the saved install fee, the same rule as a tire-only card.
        // The dealer can clear the field to leave the line off this quote.
        if (!sameQuoteId && quote && quote.kind === 'package' && this.packageInstallInput) {
          var packageSettings = readLocalSettings() || settingsDefaults();
          var packageInstallCents = installFeeCentsTotal(
            packageSettings.installFeeMode,
            packageSettings.installFeeValue,
            quote.requestedQuantity || 1
          );
          this.packageInstallInput.value = packageInstallCents > 0 ? fromCents(packageInstallCents) : '';
        }
        // A fresh live quote always supersedes any historical view that
        // might still be flagged from a previous openHistorical() call —
        // without this, an owner-info edit made after opening a NEW live
        // quote would incorrectly re-render the OLD historical entry
        // instead (see _rerenderCurrent).
        this._historicalEntry = null;
        // Owner ruling (2026-10-22): a quote the stock cannot fill is refused here, whatever
        // opened it (a cached card, the package bar, the front / rear button). It gets no id,
        // so no owner snapshot and no event; _render shows why instead of a card.
        var shortfall = quoteShortfall(quote);
        // One id for this generated quote — every action taken on the card
        // it produces (share/save/copy) reports against this same id, and
        // a re-render from an override edit must not replace it (see _render).
        this.quoteId = shortfall ? null : sameQuoteId || generateQuoteId();
        if (this.placeOrderActions) {
          loadPickupWarehouses(this.fetchImpl);
          this.placeOrderActions.refresh();
        }
        // 2026-09-10 — snapshot whatever the owner fields currently say
        // against this new quoteId, so the Recent Quotes list can later
        // show "whose car" even if the dealer never touches these fields
        // again for this quote (matches what the card's own "Prepared
        // for" footer already shows right now).
        if (!sameQuoteId) recordOwnerHistorySnapshot(this.quoteId, this._readOwner());
        this.root.hidden = false;
        this._rerenderCurrent();
        // Only once the card actually rendered — an ineligible-province
        // quote never shows a card at all (_lastVm stays null) and must
        // not be counted as one.
        if (this._lastVm && !sameQuoteId) this._logEvent('quote');
        this._syncQuoteActions();
        this._scrollToSelfIfNarrow();
      };

      /**
       * Reopens a past quote from the "recent quotes" panel (design spec:
       * dealer-pro's docs/superpowers/specs/
       * 2026-09-09-dealer-pro-quote-history-design.md). `entry` is one row
       * from GET /pro/quotes: { quoteId, variantId, title, qty,
       * dealerTotal, createdAt }.
       *
       * Reuses the ORIGINAL quoteId (this is the same quote being looked
       * at again, not a new one) and does not call _logEvent('quote') —
       * that would misreport reopening as generating. this.quote carries
       * only variantId, the one field _logEvent's own event body needs
       * (buildDealerProEventBody reads quote.variantId) if the dealer
       * shares/saves/copies this reopened card.
       */
      ProQuoteScreen.prototype.openHistorical = function (entry) {
        this._quoteSource = entry && entry.stale ? 'stale' : 'history';
        this.quote = { variantId: entry.variantId };
        if (entry.rimVariantId) this.quote.rimVariantId = entry.rimVariantId;
        if (entry.rearVariantId) {
          this.quote.rearVariantId = entry.rearVariantId;
          this.quote.rearQty = entry.rearQty;
          this.quote.frontQty = entry.qty;
        }
        this.quoteId = entry.quoteId;
        this._historicalEntry = entry;
        rememberPlacedOrder(entry.quoteId, entry.orderNumber, entry.orderedAt, entry.orderStatus, entry.replacedOrderNumber, entry.progress);
        if (this.placeOrderActions) {
          loadPickupWarehouses(this.fetchImpl);
          this.placeOrderActions.refresh();
        }
        // 2026-09-09 fix (final review finding) — the "recent quotes"
        // panel is a full-screen, higher-z-index overlay (see
        // sections/toee-pro-quote-history.liquid); without closing it
        // here, this method redraws the card correctly but it sits
        // BEHIND that overlay, invisible until the dealer separately
        // closes the panel themselves. Same cross-panel-reach pattern
        // openSettingsFirstRun already uses elsewhere in this file.
        var histRoots = document.querySelectorAll('[data-pro-quote-history]');
        if (histRoots.length && histRoots[0]._proQuoteHistoryPanel) {
          histRoots[0]._proQuoteHistoryPanel.close();
        }
        this.root.hidden = false;
        this._renderHistorical();
        this._syncQuoteActions();
        // The dealer opened this from an icon near the top of the page;
        // the card itself can be well below the fold on a stacked (narrow)
        // layout — bring it into view rather than leaving the scroll
        // position wherever it was. Gated by _scrollToSelfIfNarrow the
        // same way open() is (2026-09-10): the desktop two-column layout
        // already puts the card beside search at the top of its column
        // (measured live: top: 32px), so scrolling there would just be an
        // unwanted jump with nothing to show for it.
        this._scrollToSelfIfNarrow();
      };

      /** dp-7/2026-09-10 — see shouldScrollToGeneratedQuote's own comment
       * for why only below the two-column breakpoint. Shared by open()
       * and openHistorical() — both leave a dealer looking at whatever was
       * on screen before, with the actual card now stacked somewhere
       * below it. */
      ProQuoteScreen.prototype._scrollToSelfIfNarrow = function () {
        // No window to read a width from — not a real browser at all, so
        // there is no meaningful "narrow" to detect. Defaults to the wide
        // (desktop, no-scroll) side rather than 0, which would otherwise
        // read as the narrowest possible viewport and scroll unwanted.
        var width = typeof window !== 'undefined' ? window.innerWidth : Infinity;
        if (!shouldScrollToGeneratedQuote(width)) return;
        if (this.root && typeof this.root.scrollIntoView === 'function') {
          this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      /** Parallel to _render(), for the historical case — see
       * buildHistoricalQuoteCardViewModel/drawHistoricalQuoteCard for why
       * this is a separate, smaller path rather than a branch inside
       * _render(): there is no ledger to recompute here at all. Takes no
       * argument (reads this._historicalEntry) so redrawWhenFontsReady's
       * later, argument-less re-invocation still works. */
      ProQuoteScreen.prototype._renderHistorical = function () {
        this.settings = readLocalSettings() || settingsDefaults();
        var vm = buildHistoricalQuoteCardViewModel(this._historicalEntry, this.settings);
        this._lastVm = vm;
        if (this.packageInstallField) this.packageInstallField.hidden = true;
        if (this.ineligibleEl) this.ineligibleEl.hidden = true;
        if (this.body) this.body.hidden = false;
        if (this.dealerDetail) {
          this.dealerDetail.textContent = vm.detailLine || '';
          this.dealerDetail.hidden = !vm.detailLine;
        }
        // A past quote's window is long gone and its view model carries no time: no caption.
        this._setValidUntil('');
        if (this.canvas && this.canvas.getContext) {
          var ctx = this.canvas.getContext('2d');
          var accent = resolveCardThemeAccent(this.settings.cardTheme, readCssVarFromDocument);
          drawHistoricalQuoteCard(ctx, vm, { accentColor: accent });
          this._nameCanvas(vm);
          redrawWhenFontsReady(this, '_rerenderCurrent');
        }
      };

      /**
       * 2026-09-09 fix (task review finding) — owner-info edits must
       * re-render WHATEVER is currently showing, not unconditionally the
       * live path. Before this existed, editing owner details while a
       * reopened historical card was showing fell through to _render(),
       * which rebuilds from this.quote — but openHistorical only ever
       * sets this.quote to { variantId }, so _render()'s own guard
       * (`if (!this.quote) return;`) never caught it, and the screen
       * would silently redraw as a broken "live" card (blank title,
       * garbage totals) while still carrying the REOPENED quote's id —
       * sharing at that point would send a corrupted card under the
       * wrong-looking quoteId.
       */
      ProQuoteScreen.prototype._rerenderCurrent = function () {
        if (this._historicalEntry) {
          this._renderHistorical();
        } else {
          this._render();
        }
      };

      ProQuoteScreen.prototype.close = function () {
        this.root.hidden = true;
      };

      /** Back: the screen, and the button the keyboard was on, are gone. Focus returns to the
       * card the quote was made from (its "Quoted" button carries the quote's id, markCardQuoted),
       * else the first card's button, else the size field when there are no results. */
      ProQuoteScreen.prototype._onBack = function () {
        this.close();
        if (typeof document.querySelector !== 'function') return;
        // The id as a CSS string: only `"` and `\` need escaping inside the quotes.
        var quoted = this.quoteId ? document.querySelector('[data-pro-quoted-id="' + String(this.quoteId).replace(/["\\]/g, '\\$&') + '"]') : null;
        handOffFocus(this.backButton, quoted || document.querySelector('.tt-pro-card button') || document.querySelector('[data-pro-search-input]'));
      };

      /** Top bar caption "Valid until <time>". With a `label` it fills and shows the caption;
       * with none it empties and hides it. Every caller passes '' today (see _render for why a
       * clock time derived from Date.now() cannot be shown here), so the caption stays hidden
       * until the server sends a real expiry. */
      ProQuoteScreen.prototype._setValidUntil = function (label) {
        if (!this.validEl) return;
        var template = text(this.root, 'valid-until') || 'Valid until {time}';
        this.validEl.textContent = label ? template.replace('{time}', label) : '';
        this.validEl.hidden = !label;
      };

      /** dp-9/2026-09-09 — reports one dealerProEvent for an action taken
       * on the currently open quote card. See postDealerProEvent for the
       * fail-silent network contract. */
      ProQuoteScreen.prototype._logEvent = function (type) {
        postDealerProEvent(type, this.quoteId, this.quote, this._lastVm, this.fetchImpl);
      };

      /** The Ontario-only callout also says why a short quote is refused (owner ruling
       * 2026-10-22): `message` replaces its sentence and drops Open settings, which cannot
       * fix stock; '' puts the Ontario sentence and its button back. */
      ProQuoteScreen.prototype._setCalloutMessage = function (message) {
        var box = this.ineligibleEl;
        if (!box) return;
        var line = box.querySelector('.tt-pro-callout__message');
        if (line) {
          if (this._ontarioMessage == null) this._ontarioMessage = line.textContent;
          line.textContent = message || this._ontarioMessage;
        }
        var settingsButton = box.querySelector('[data-pro-settings-open]');
        if (settingsButton) settingsButton.hidden = !!message;
      };

      ProQuoteScreen.prototype._render = function () {
        if (!this.quote) return;
        // Final review finding #2 (CRITICAL) — settings used to be
        // snapshotted once in the constructor and never refreshed, so a
        // shop name/markup/theme saved in ProSettingsPanel (which writes
        // straight to localStorage and tells nobody) never reached a quote
        // screen already on the page until a full reload. readOwnerDetails
        // below was already re-read on every render; settings now gets the
        // same treatment for the same reason.
        this.settings = readLocalSettings() || settingsDefaults();
        var isPackage = this.quote.kind === 'package';
        if (this.packageInstallField) this.packageInstallField.hidden = !isPackage;
        var shortfall = quoteShortfall(this.quote);
        var vm = shortfall
          ? { eligible: false, reason: 'short_stock' }
          : isPackage
          ? buildPackageQuoteCardViewModel(this.quote, this.settings, this.packageInstallInput ? this.packageInstallInput.value : '', readOwnerDetails())
          : buildQuoteCardViewModel(this.quote, this.settings, this._readOverrides(), readOwnerDetails(), {
            vehicleCard: activeVehicleController().state.card,
            vehicleDetail: activeVehicleController().state.detail
          });
        // dp-9 — the share/save/copy handlers read this, never rebuild
        // their own vm, so the copied text can never drift from the card.
        this._lastVm = vm.eligible ? vm : null;
        if (!vm.eligible) {
          // Shown first, written second (review M4): the callout is role="alert", and a live region
          // only announces text added while it is on screen.
          if (this.ineligibleEl) this.ineligibleEl.hidden = false;
          this._setCalloutMessage(shortfall
            ? shortfallText('screen', text(this.root, 'short-blocked-screen'), shortfall, text(this.root, 'short-unproven-screen'))
            : '');
          if (this.body) this.body.hidden = true;
          if (this.dealerDetail) {
            this.dealerDetail.textContent = '';
            this.dealerDetail.hidden = true;
          }
          this._setValidUntil('');
          return;
        }
        if (this.ineligibleEl) this.ineligibleEl.hidden = true;
        if (this.body) this.body.hidden = false;
        if (this.dealerDetail) {
          this.dealerDetail.textContent = vm.detailLine || '';
          this.dealerDetail.hidden = !vm.detailLine;
        }
        // No "Valid until" caption in the top bar: vm.validUntil is derived from a countdown
        // fixed when the quote was fetched plus Date.now(), so it moves later on every
        // re-render (a keystroke in an owner field, a reopen). The card prints it once per
        // draw; a stable caption needs a real expiry from the server. The element stays.
        this._setValidUntil('');
        if (this.canvas && this.canvas.getContext) {
          var ctx = this.canvas.getContext('2d');
          var accent = resolveCardThemeAccent(this.settings.cardTheme, readCssVarFromDocument);
          drawQuoteCard(ctx, vm, { accentColor: accent });
          this._nameCanvas(vm);
          redrawWhenFontsReady(this, '_rerenderCurrent');
        }
      };

      /** The canvas is role="img": its name is the share text, the card in words. That text
       * carries no owner name or plate (buildShareText never reads them), so the PIPEDA rule
       * "owner details stay on the canvas" still holds for the accessibility tree. */
      ProQuoteScreen.prototype._nameCanvas = function (vm) {
        if (this.canvas.setAttribute) this.canvas.setAttribute('aria-label', buildShareText(vm));
      };

      /** The share status row: shown first, then written, because a role="status" line only
       * announces text added while it is on screen. The marker beside it shows with the row. */
      ProQuoteScreen.prototype._showShareStatus = function (message) {
        if (!this.shareStatusEl) return;
        this.shareStatusEl.hidden = false;
        if (this.shareStatusRow) this.shareStatusRow.hidden = false;
        this.shareStatusEl.textContent = message;
      };

      // ---------------------------------------------------------------
      // DP-9 — share/save/copy. Everything past this point touches a real
      // <canvas> (toBlob/toDataURL), navigator.share, or the clipboard —
      // none of which this repo's fake DOM provides (see
      // tests/toee-pro-share-wiring.test.js's file header), so these
      // three methods are exercised only manually/on-device. The decision
      // logic they call into (shareQuoteCard, canShareFiles,
      // buildShareText) is pure and fully covered under
      // tests/toee-pro-share-logic.test.js.
      // ---------------------------------------------------------------

      /** Generates the actual PNG only at tap time — never on every
       * override-field keystroke render — then hands it to the pure
       * capability branch. Returns the share promise (resolves once the
       * system sheet / save-fallback settles) so callers — tests included —
       * can observe completion; the real click handler ignores it. */
      ProQuoteScreen.prototype._onSharePrimaryTap = function () {
        var self = this;
        return new Promise(function (resolve) {
          self._canvasToFile(function (file) {
            resolve(shareQuoteCard(self.nav, file, buildShareText(self._lastVm), {
              // Only a share that actually went through counts — a
              // cancelled or failed one reports nothing (shareQuoteCard's
              // own contract already keeps those out of onSaveFallback too).
              onShared: function () { self._logEvent('share'); },
              onSaveFallback: function () { self._saveImage(); }
            }));
          });
        });
      };

      ProQuoteScreen.prototype._canvasToFile = function (cb) {
        if (!this.canvas || typeof this.canvas.toBlob !== 'function') {
          cb(null);
          return;
        }
        this.canvas.toBlob(function (blob) {
          if (!blob) {
            cb(null);
            return;
          }
          cb(typeof File !== 'undefined' ? new File([blob], 'quote-card.png', { type: 'image/png' }) : blob);
        }, 'image/png');
      };

      /** dp-9 — "保存图片": downloads the canvas as a PNG via a throwaway
       * anchor, then shows the saved message so the dealer knows to
       * attach it himself (dp-9-share-sheet.md's desktop degrade path).
       * ponytail: iOS Safari ignores the `download` attribute and opens
       * the image instead of saving it — a real limitation of the native
       * anchor-download approach, not fixable without a new dependency.
       * 2026-09-09 — shareSavedMessageKey keeps the status honest on iOS
       * rather than claiming a save that didn't happen. It runs from the
       * secondary "Save image" button (shown on phones; hidden on desktop,
       * where the primary already says "Save image", see the constructor)
       * and as the primary's fallback when files cannot be shared. */
      ProQuoteScreen.prototype._saveImage = function () {
        this._logEvent('save_image');
        if (!this.canvas || typeof this.canvas.toDataURL !== 'function') return;
        try {
          var a = document.createElement('a');
          a.href = this.canvas.toDataURL('image/png');
          a.download = 'quote-card.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (e) {
          // Same rationale as writeLocalSettings — a failed save must
          // never throw out of a click handler.
        }
        // 2026-09-09 — the message must match what actually happened:
        // iOS Safari never honoured the download above, it just opened
        // the image (shareSavedMessageKey's own comment has the why).
        var ua = this.nav && this.nav.userAgent;
        this._showShareStatus(text(this.root, shareSavedMessageKey(ua)));
      };

      /** dp-9 — "复制文案". Same silent-failure contract as _saveImage: a
       * denied clipboard permission must not throw or pop an error. Once the
       * clipboard says yes, the share status row says so (Task 13: the button
       * gave no sign it had done anything). A page cached before the string
       * existed gets the English sentence. */
      ProQuoteScreen.prototype._copyText = function () {
        this._logEvent('copy_text');
        if (!this._lastVm || !this.nav || !this.nav.clipboard) return;
        var self = this;
        try {
          Promise.resolve(this.nav.clipboard.writeText(buildShareText(this._lastVm))).then(function () {
            self._showShareStatus(text(self.root, 'copy-done') || 'Text copied.');
          }, function () {});
        } catch (e) {
          // Silent — same rationale as _saveImage.
        }
      };

      /**
       * "Recent quotes" panel (design spec: dealer-pro's
       * docs/superpowers/specs/2026-09-09-dealer-pro-quote-history-design.md;
       * the drawer's content is rollout Task 9). Modelled on ProSettingsPanel's own
       * shape: a root looked up by data attribute, one or more open buttons, a close
       * button. The one real difference is that this panel fetches ONLY on open, never
       * at construction — most /pro visits never open it.
       *
       * It is in one of five states: guest ("Log in"), loading (a skeleton, on the first
       * look only), empty ("No quotes yet"), error (with "Try again"), or ready (the
       * quotes). The quotes are grouped by day and filtered here, by the search box and
       * the chips, over what was loaded; filtering never fetches. A row is one <button>
       * carrying the entry: ProQuoteScreen's document click handler reopens it. Every
       * part is found by its data hook and may be missing (an older cached page); a
       * missing part is skipped.
       */
      var HISTORY_CHIPS = ['all', 'tire', 'package', 'ordered'];
      var HISTORY_CHIP_KEY = { all: 'chipAll', tire: 'chipTire', package: 'chipPackage', ordered: 'chipOrdered' };
      // The design's footer says the list stops here; it shows once the server sent at least this
      // many (it sends up to 30), with the number actually loaded.
      var HISTORY_LIMIT = 20;

      function historyEl(tag, className, text) {
        var el = document.createElement(tag);
        if (className) el.className = className;
        if (text != null) el.textContent = text;
        return el;
      }

      function showHistoryPart(el, on) {
        if (el) el.hidden = !on;
      }

      function focusHistoryPart(el) {
        if (el && typeof el.focus === 'function') el.focus();
      }

      ProQuoteHistoryPanel = function (root, fetchImpl) {
        this.root = root;
        this.fetchImpl = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
        this.text = quoteHistoryText(root);
        // Liquid marks the root for a visitor with no customer, so a guest's page needs no request to know.
        this.signedOut = root.getAttribute('data-pro-quote-history-signed-out') !== null;
        function part(name) {
          return root.querySelector('[data-pro-quote-history-' + name + ']');
        }
        this.listEl = part('list');
        this.emptyEl = part('empty');
        this.closeButton = part('close');
        this.countEl = part('count');
        this.findEl = part('find');
        this.searchEl = part('search');
        this.chipsEl = part('chips');
        this.guestEl = part('guest');
        this.loadingEl = part('loading');
        this.errorEl = part('error');
        this.noMatchEl = part('nomatch');
        this.noMatchTextEl = part('nomatch-text');
        this.limitEl = part('limit');
        this.chipEls = {};
        for (var c = 0; c < HISTORY_CHIPS.length; c++) {
          this.chipEls[HISTORY_CHIPS[c]] = root.querySelector('[data-pro-quote-history-chip="' + HISTORY_CHIPS[c] + '"]');
        }
        this.items = [];
        this.state = '';
        this.filter = 'all';
        this.query = '';
        this.requestId = 0;

        var self = this;
        function wire(el, method) {
          if (el) el.addEventListener('click', function () { self[method](); });
        }
        var openButtons = document.querySelectorAll('[data-pro-quote-history-open]');
        for (var i = 0; i < openButtons.length; i++) {
          openButtons[i].addEventListener('click', function (e) {
            self.open(e && e.currentTarget);
          });
        }
        wire(this.closeButton, 'close');
        wire(part('retry'), '_retry');
        wire(part('search-tires'), '_searchTires');
        wire(part('clear'), '_clearSearch');
        if (this.searchEl) {
          this.searchEl.addEventListener('input', function () {
            self._setQuery(self.searchEl.value);
          });
        }
        HISTORY_CHIPS.forEach(function (kind) {
          if (self.chipEls[kind]) {
            self.chipEls[kind].addEventListener('click', function () {
              self._setFilter(kind);
            });
          }
        });
      };

      /** The dealer's clock; a test sets its own. */
      ProQuoteHistoryPanel.prototype._now = function () {
        return new Date();
      };

      /** `opener` is the element focus goes back to on close (see ProDrawer#open). */
      ProQuoteHistoryPanel.prototype.open = function (opener) {
        drawerOf(this).open(opener);
        // Every look starts with an empty box and every quote.
        this.filter = 'all';
        this.query = '';
        if (this.searchEl) this.searchEl.value = '';
        if (this.signedOut) {
          this.requestId++;
          this._clearItems();
          this._showState('guest');
          return Promise.resolve();
        }
        // A second look shows the quotes it already has while it asks again; the first waits behind a skeleton.
        if (this.items.length) {
          this._showState('ready');
          this._render();
        }
        return this._load();
      };

      ProQuoteHistoryPanel.prototype._load = function () {
        var self = this;
        // 2026-09-09 fix (task review finding) — a dealer can trigger
        // overlapping opens (double-tap, or close+reopen before the
        // first fetch settles); an older response arriving after a
        // newer one must not silently overwrite it. Same requestId
        // pattern as ProSearch#_search above, the only other place in
        // this file that re-fetches on every call rather than once at
        // construction.
        var requestId = ++this.requestId;
        if (!this.fetchImpl) {
          this._clearItems();
          this._showState('error');
          return Promise.resolve();
        }
        if (!this.items.length) this._showState('loading');
        var guest = {};
        // 2026-09-09 fix (final review finding) — calling this.fetchImpl(...)
        // as a method sets `this` inside it to the panel, and native
        // fetch's WebIDL brand-check throws "Illegal invocation" for any
        // `this` other than the global object. Every other fetch call in
        // this file (pushSettings, postDealerProEvent) already avoids this
        // by calling through a local variable — this one skipped it.
        var f = this.fetchImpl;
        return f(proProxyUrl('/quotes'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) {
            // The route answers a guest with 401: that is "log in", not "no quotes yet".
            if (r.status === 401) return guest;
            if (!r.ok) throw new Error('quotes ' + r.status);
            return r.json();
          })
          .then(function (body) {
            if (requestId !== self.requestId) return;
            if (body === guest) {
              self._clearItems();
              self._showState('guest');
              return;
            }
            if (!body || !Array.isArray(body.quotes)) throw new Error('quotes body');
            self._setQuotes(body.quotes);
          })
          .catch(function () {
            if (requestId !== self.requestId) return;
            // Offline, a server error or an answer with no list: say so, and offer to try again.
            self._clearItems();
            self._showState('error');
          });
      };

      ProQuoteHistoryPanel.prototype.close = function () {
        drawerOf(this).close();
      };

      /** "Try again": the Try again button is about to disappear, so focus moves to the status block that replaces it. */
      ProQuoteHistoryPanel.prototype._retry = function () {
        var pending = this._load();
        if (this.state === 'loading') focusHistoryPart(this.loadingEl);
        return pending;
      };

      /** The empty state's "Search tires": back to the page, with the cursor in the size box. */
      ProQuoteHistoryPanel.prototype._searchTires = function () {
        this.close();
        var input = typeof document.querySelector === 'function' ? document.querySelector('[data-pro-search-input]') : null;
        focusHistoryPart(input);
      };

      ProQuoteHistoryPanel.prototype._clearSearch = function () {
        this.query = '';
        this.filter = 'all';
        if (this.searchEl) this.searchEl.value = '';
        this._render();
        focusHistoryPart(this.searchEl);
      };

      ProQuoteHistoryPanel.prototype._setQuery = function (value) {
        this.query = String(value == null ? '' : value).replace(/^\s+|\s+$/g, '');
        this._render();
      };

      ProQuoteHistoryPanel.prototype._setFilter = function (kind) {
        this.filter = kind;
        this._render();
      };

      ProQuoteHistoryPanel.prototype._emptyList = function () {
        while (this.listEl && this.listEl.firstChild) this.listEl.removeChild(this.listEl.firstChild);
      };

      ProQuoteHistoryPanel.prototype._clearItems = function () {
        this.items = [];
        this._orderText = null;
        this._emptyList();
      };

      /** Shows the parts of one state and hides the rest; the list itself is _render's. */
      ProQuoteHistoryPanel.prototype._showState = function (name) {
        this.state = name;
        showHistoryPart(this.guestEl, name === 'guest');
        showHistoryPart(this.loadingEl, name === 'loading');
        showHistoryPart(this.emptyEl, name === 'empty');
        showHistoryPart(this.errorEl, name === 'error');
        // While it loads the field waits, disabled and with no chips: there is nothing to count yet.
        showHistoryPart(this.findEl, name === 'loading' || name === 'ready');
        showHistoryPart(this.chipsEl, name === 'ready');
        showHistoryPart(this.listEl, name === 'ready');
        if (this.searchEl) this.searchEl.disabled = name !== 'ready';
        if (name !== 'ready') {
          showHistoryPart(this.noMatchEl, false);
          showHistoryPart(this.limitEl, false);
        }
        // The caption counts what was loaded: "0 quotes" for an empty answer, nothing while we do not know.
        if (name === 'empty') this._setCount(0);
        else if (name !== 'ready') showHistoryPart(this.countEl, false);
      };

      ProQuoteHistoryPanel.prototype._setCount = function (n) {
        if (!this.countEl) return;
        this.countEl.hidden = false;
        this.countEl.textContent = fillPlaceOrderText(n === 1 ? this.text.countOne : this.text.countOther, { n: n });
      };

      /** The quotes the server sent, newest first (it answers ordered ones first, and the list is by day). */
      ProQuoteHistoryPanel.prototype._setQuotes = function (quotes) {
        var self = this;
        this._orderText = null;
        this.items = quotes.map(function (entry, at) {
          var item = self._describe(entry);
          item.at = at;
          return item;
        });
        this.items.sort(function (a, b) {
          var ta = a.time === null ? -Infinity : a.time;
          var tb = b.time === null ? -Infinity : b.time;
          if (ta !== tb) return tb > ta ? 1 : -1;
          return a.at - b.at;
        });
        if (!this.items.length) {
          this._emptyList();
          this._showState('empty');
          return;
        }
        this._showState('ready');
        this._render();
      };

      /** Everything about one quote that the list, the chips and the search need, worked out once per load. */
      ProQuoteHistoryPanel.prototype._describe = function (entry) {
        entry = entry || {};
        var t = this.text;
        var isPackage = !!(entry.rimVariantId || entry.rimTitle);
        var order = historyOrderState(entry);
        var date = historyDate(entry.createdAt);
        var title = historyRowTitle(entry);
        var folded = foldHistoryText(title + ' ' + ownerHistorySearchText(entry.quoteId));
        return {
          entry: entry,
          title: title,
          date: date,
          time: date ? date.getTime() : null,
          isPackage: isPackage,
          kind: isPackage ? 'package' : 'tire',
          tag: isPackage ? t.tagPackage : entry.rearVariantId ? t.tagFrontRear : '',
          owner: ownerHistoryLabel(entry.quoteId),
          order: order,
          // A cancelled order is not an ordered quote.
          ordered: !!order && order.status !== 'cancelled',
          haystack: folded,
          squashed: squashHistoryText(folded)
        };
      };

      /** Every word of the search has to be in the title or the owner text, as typed or without punctuation. */
      ProQuoteHistoryPanel.prototype._matches = function (item, terms) {
        for (var i = 0; i < terms.length; i++) {
          var squashed = squashHistoryText(terms[i]);
          if (item.haystack.indexOf(terms[i]) === -1 && !(squashed && item.squashed.indexOf(squashed) > -1)) return false;
        }
        return true;
      };

      /** Draws the chips, the caption, the day groups, the no-match line and the footer from what was loaded. */
      ProQuoteHistoryPanel.prototype._render = function () {
        if (this.state !== 'ready') return;
        var self = this;
        var t = this.text;
        var terms = foldHistoryText(this.query).split(/\s+/).filter(Boolean);
        var matching = this.items.filter(function (item) { return self._matches(item, terms); });
        var counts = { all: matching.length, tire: 0, package: 0, ordered: 0 };
        matching.forEach(function (item) {
          counts[item.kind] += 1;
          if (item.ordered) counts.ordered += 1;
        });
        // No words to blame and a chip with nothing behind it (the data changed under it): back to All.
        if (!terms.length && !counts[this.filter]) this.filter = 'all';
        var filter = this.filter;
        var visible = matching.filter(function (item) {
          return filter === 'all' || (filter === 'ordered' ? item.ordered : item.kind === filter);
        });

        HISTORY_CHIPS.forEach(function (kind) {
          var chip = self.chipEls[kind];
          if (!chip) return;
          chip.textContent = t[HISTORY_CHIP_KEY[kind]] + ' · ' + counts[kind];
          chip.setAttribute('aria-pressed', filter === kind ? 'true' : 'false');
          // A chip with nothing behind it is not a choice, unless it is the one that is on.
          chip.disabled = counts[kind] === 0 && filter !== kind;
        });
        this._setCount(this.items.length);

        this._emptyList();
        if (this.listEl) {
          var now = this._now();
          var group = null;
          var rows = null;
          visible.forEach(function (item) {
            var key = item.date ? item.date.getFullYear() + '-' + item.date.getMonth() + '-' + item.date.getDate() : '';
            if (!group || group.key !== key) {
              group = { key: key, day: historyEl('li', 'tt-pro-quote-history__day') };
              var label = historyDayLabel(item.date, now, t);
              if (label) group.day.appendChild(historyEl('h3', 'tt-pro-quote-history__day-label', label));
              rows = historyEl('ul', 'tt-pro-quote-history__rows');
              group.day.appendChild(rows);
              self.listEl.appendChild(group.day);
            }
            var li = historyEl('li', 'tt-pro-quote-history__item');
            li.appendChild(self._buildRow(item.entry, item));
            rows.appendChild(li);
          });
        }

        // Show first, then write: the no-match line is a status region.
        var none = visible.length === 0;
        showHistoryPart(this.noMatchEl, none);
        if (none && this.noMatchTextEl) this.noMatchTextEl.textContent = fillPlaceOrderText(t.noMatch, { q: this.query });
        var whole = !terms.length && filter === 'all' && this.items.length >= HISTORY_LIMIT;
        showHistoryPart(this.limitEl, whole);
        if (whole && this.limitEl) this.limitEl.textContent = fillPlaceOrderText(t.limitNote, { n: this.items.length });
      };

      /** The words of "Ordered · #1042" / "Cancelled · #1038", the same ones the Quote screen's Place order block uses. */
      ProQuoteHistoryPanel.prototype._statusEl = function (order) {
        var o = this._orderText || (this._orderText = placeOrderText());
        var cancelled = order.status === 'cancelled';
        var label = order.number
          ? fillPlaceOrderText(cancelled ? o.cancelledNumber : o.orderedNumber, { n: order.number })
          : cancelled ? o.cancelled : o.ordered;
        var status = historyEl('span', 'tt-pro-quote-history__status');
        // The words carry the state; the square beside them only decorates.
        var marker = historyEl('span', 'tt-pro-marker ' + (cancelled ? 'tt-pro-marker--muted' : 'tt-pro-marker--ok'));
        marker.setAttribute('aria-hidden', 'true');
        status.appendChild(marker);
        status.appendChild(historyEl('span', 'tt-pro-quote-history__status-text', label));
        return status;
      };

      /** One quote as ONE button (no nested control: ordering lives on the Quote screen). */
      ProQuoteHistoryPanel.prototype._buildRow = function (entry, item) {
        item = item || this._describe(entry);
        var row = historyEl('button', 'tt-pro-quote-history__row');
        row.setAttribute('type', 'button');
        row.setAttribute('data-pro-quote-history-item', '');
        row.setAttribute('data-pro-quote-history-entry', JSON.stringify(entry));
        if (item.tag) row.appendChild(historyEl('span', 'tt-pro-quote-history__tag', item.tag));
        row.appendChild(historyEl('span', 'tt-pro-quote-history__name', item.title));
        if (entry.dealerTotal != null && entry.dealerTotal !== '') {
          row.appendChild(historyEl('span', 'tt-pro-quote-history__price', formatMoney(entry.dealerTotal)));
        }
        if (item.owner) row.appendChild(historyEl('span', 'tt-pro-quote-history__owner', item.owner));
        row.appendChild(historyEl('span', 'tt-pro-quote-history__qty', historyQuantityText(entry, item.isPackage)));
        var time = historyTimeLabel(item.date, this.text);
        if (time || item.order) {
          var meta = historyEl('span', 'tt-pro-quote-history__meta');
          if (time) meta.appendChild(historyEl('span', 'tt-pro-quote-history__time', time));
          if (item.order) meta.appendChild(this._statusEl(item.order));
          row.appendChild(meta);
        }
        return row;
      };

      function initQuoteScreen() {
        // querySelectorAll, not querySelector — same reason as
        // initSettingsPanel just above: keeps this requirable under the
        // minimal fake document in tests/toee-pro-fake-dom.js.
        var roots = document.querySelectorAll('[data-pro-quote-screen]');
        if (!roots.length) return;
        var root = roots[0];
        if (!root._proQuoteScreen) root._proQuoteScreen = new ProQuoteScreen(root);
      }

      function initSettingsPanel() {
        // querySelectorAll, not querySelector — matches the lookup style
        // ProSearch's own init already uses (see `forms` above), which is
        // also what keeps this requirable under the minimal fake document
        // in tests/toee-pro-fake-dom.js (querySelector-only elements, but
        // no document.querySelector).
        var roots = document.querySelectorAll('[data-pro-settings]');
        if (!roots.length) return;
        var root = roots[0];
        if (!root._proSettingsPanel) root._proSettingsPanel = new ProSettingsPanel(root);
      }

      function initChangelog() {
        var roots = document.querySelectorAll('[data-pro-changelog]');
        if (!roots.length || roots[0]._proChangelog) return;
        var root = roots[0];
        var drawer = new ProDrawer(root);
        root._proChangelog = drawer;
        var openButtons = document.querySelectorAll('[data-pro-changelog-open]');
        for (var i = 0; i < openButtons.length; i++) {
          openButtons[i].addEventListener('click', function (e) {
            drawer.open(e && e.currentTarget);
          });
        }
        var closeButton = root.querySelector('[data-pro-changelog-close]');
        if (closeButton) closeButton.addEventListener('click', function () { drawer.close(); });
      }

      function initQuoteHistoryPanel() {
        var roots = document.querySelectorAll('[data-pro-quote-history]');
        if (!roots.length) return;
        var root = roots[0];
        if (!root._proQuoteHistoryPanel) root._proQuoteHistoryPanel = new ProQuoteHistoryPanel(root);
      }

      function vehiclePanelLabels(root) {
        function label(attribute, fallback) {
          var value = root && root.getAttribute && root.getAttribute(attribute);
          return value == null ? fallback : value;
        }
        return {
          vin: label('data-pro-vehicle-vin', 'VIN'),
          decode: label('data-pro-vehicle-decode', 'Find by VIN'),
          year: label('data-pro-vehicle-year', 'Year'),
          make: label('data-pro-vehicle-make', 'Make'),
          model: label('data-pro-vehicle-model', 'Model'),
          trim: label('data-pro-vehicle-trim', 'Trim'),
          find: label('data-pro-vehicle-find', 'Find fitments'),
          none: label('data-pro-vehicle-none', 'No fitment found. Type the tire size instead.'),
          us: label('data-pro-vehicle-us', 'US'),
          offset: label('data-pro-vehicle-offset', 'Offset'),
          photo: label('data-pro-vehicle-photo', 'Photo of the VIN'),
          findBy: label('data-pro-vehicle-find-by', 'Find tires by'),
          scanPhoto: label('data-pro-vehicle-scan-photo', 'Scan VIN photo'),
          vinHint: label('data-pro-vehicle-vin-hint', 'A VIN has 17 letters and digits. The letters I, O and Q are never part of a VIN.'),
          chainHint: label('data-pro-vehicle-chain-hint', 'Each list opens once the one before it is chosen.')
        };
      }

      function fitmentButtonText(fitment, labels) {
        var parts = [];
        if (fitment.market === 'US') parts.push(labels.us);
        var front = fitment.front || {};
        var rear = fitment.staggered && fitment.rear ? fitment.rear : null;
        parts.push(rear ? front.tireSize + ' / ' + rear.tireSize : front.tireSize);
        if (fitment.boltPattern) parts.push(fitment.boltPattern);
        if (fitment.hubBoreMm != null) parts.push(fitment.hubBoreMm + ' mm');
        if (front.offsetMm != null) parts.push(labels.offset + ' ' + front.offsetMm);
        return parts.join(' · ');
      }

      // Pure VIN / vehicle-list helpers live in assets/toee-pro-find.js. A page can run
      // without them (that script blocked or cached out of step), so fall back to how the
      // panel behaved before: no list gating, a VIN is trimmed and needs 17 characters, and the
      // Size / Vehicle / VIN switch shows exactly the panel it names.
      var LEGACY_FIND = {
        sanitizeVin: function (raw) { return String(raw == null ? '' : raw).trim().slice(0, 17); },
        vinReady: function (vin) { return vin.length === 17; },
        vehicleChainState: function () { return { makeOff: false, modelOff: false, trimOff: false, findOff: false }; },
        clearAfter: function (sel) { return sel; },
        findPanelHidden: function (mode) {
          var active = mode === 'vehicle' || mode === 'vin' ? mode : 'size';
          return { size: active !== 'size', vehicle: active !== 'vehicle', vin: active !== 'vin' };
        }
      };

      function loadedFindApi() {
        var api = null;
        if (typeof ToeeProFind !== 'undefined') api = ToeeProFind;
        else if (typeof require === 'function') {
          try { api = require('./toee-pro-find.js'); } catch (e) { api = null; }
        }
        return api && api.sanitizeVin && api.findPanelHidden ? api : LEGACY_FIND;
      }

      function loadedCounterApi() {
        var api = null;
        if (typeof ToeeProCounter !== 'undefined') api = ToeeProCounter;
        else if (typeof require === 'function') {
          try { api = require('./toee-pro-counter.js'); } catch (e) { api = null; }
        }
        return api && api.counterView ? api : null;
      }

      function initVehiclePanel(form) {
        if (!form || form._proVehiclePanel || !form.parentNode) return;
        var labels = vehiclePanelLabels(document.querySelector(form.getAttribute('data-pro-results-target')));
        var Find = loadedFindApi();

        function create(tag, className, text) {
          var node = document.createElement(tag);
          if (className) node.className = className;
          if (text != null) node.textContent = text;
          return node;
        }
        function blank(label) {
          var option = create('option', null, label);
          option.value = '';
          return option;
        }
        function button(className, text) {
          var node = create('button', className, text);
          node.type = 'button';
          return node;
        }
        // The visible label wraps its control: a tap on the text focuses the control and
        // a screen reader names the control from it.
        function field(text, control) {
          var wrap = create('label', 'tt-pro-find__field');
          wrap.appendChild(create('span', 'tt-pro-find__label', text));
          wrap.appendChild(control);
          return wrap;
        }

        // Outer wrapper: both lookups plus the one fitment list they share. The two inner
        // panels start hidden; the Size / Vehicle / VIN switch decides which one shows.
        var panel = create('div');
        panel.setAttribute('data-pro-vehicle-panel', '');

        // --- Vehicle: Year / Make / Model / Trim, then Find fitments.
        var vehiclePanel = create('div', 'tt-pro-find__panel');
        vehiclePanel.setAttribute('data-pro-find-panel', 'vehicle');
        vehiclePanel.hidden = true;
        var listRow = create('div', 'tt-pro-find__row');
        function select(label) {
          var el = create('select', 'tt-pro-select');
          el.setAttribute('aria-label', label);
          el.appendChild(blank(label));
          listRow.appendChild(field(label, el));
          return el;
        }
        var yearSelect = select(labels.year);
        var makeSelect = select(labels.make);
        var modelSelect = select(labels.model);
        var trimSelect = select(labels.trim);
        var findButton = button('tt-pro-button-secondary', labels.find);
        listRow.appendChild(findButton);
        vehiclePanel.appendChild(listRow);
        vehiclePanel.appendChild(create('p', 'tt-pro-find__hint', labels.chainHint));

        // --- VIN: the typed VIN, its counter, Find by VIN, and a photo of the VIN.
        var vinPanel = create('div', 'tt-pro-find__panel');
        vinPanel.setAttribute('data-pro-find-panel', 'vin');
        vinPanel.hidden = true;
        var vinInput = create('input', 'tt-pro-input');
        vinInput.type = 'text';
        vinInput.setAttribute('aria-label', labels.vin);
        vinInput.setAttribute('autocapitalize', 'characters');
        vinInput.setAttribute('autocorrect', 'off');
        vinInput.setAttribute('spellcheck', 'false');
        vinInput.setAttribute('aria-describedby', 'tt-pro-vin-hint tt-pro-vin-counter');
        // The counter is described, not announced: a live region would speak every keystroke.
        var vinCounter = create('span', 'tt-pro-find__counter', '0 / 17');
        vinCounter.id = 'tt-pro-vin-counter';
        var vinHint = create('p', 'tt-pro-find__hint', labels.vinHint);
        vinHint.id = 'tt-pro-vin-hint';
        var decodeButton = button('tt-pro-search__button', labels.decode);
        decodeButton.disabled = true;
        var photoInput = create('input', 'visually-hidden');
        photoInput.type = 'file';
        photoInput.accept = 'image/*';
        photoInput.setAttribute('aria-label', labels.photo || 'Photo');
        // The label, styled as a button, is the tap target; the file input inside it stays in the page, hidden.
        var scanLabel = create('label', 'tt-pro-button-secondary tt-pro-find__scan');
        scanLabel.appendChild(create('span', null, labels.scanPhoto));
        scanLabel.appendChild(photoInput);
        var vinRow = create('div', 'tt-pro-find__row');
        vinRow.appendChild(field(labels.vin, vinInput));
        vinRow.appendChild(vinCounter);
        var vinActions = create('div', 'tt-pro-find__row');
        vinActions.appendChild(decodeButton);
        vinActions.appendChild(scanLabel);
        vinPanel.appendChild(vinRow);
        vinPanel.appendChild(vinHint);
        vinPanel.appendChild(vinActions);
        photoInput.addEventListener('change', function () {
          var file = photoInput.files && photoInput.files[0];
          if (!file || !file.arrayBuffer) return;
          file.arrayBuffer().then(function (buf) {
            var bytes = new Uint8Array(buf);
            var binary = '';
            for (var i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            return controller.readPhoto(btoa(binary));
          }).then(listedOrFailed, failed);
        });

        // Both lookups list their fitments here, so it sits outside the two panels the
        // switch hides: a VIN decoded while Vehicle is hidden still shows its result.
        var fitmentList = create('div', 'tt-pro-chips');
        fitmentList.setAttribute('data-pro-vehicle-fitments', '');

        panel.appendChild(vehiclePanel);
        panel.appendChild(vinPanel);
        panel.appendChild(fitmentList);
        // Next to the size panel when the page has one (the switch shows them one at a
        // time); older markup, with the form on its own, keeps the wrapper above the form.
        var sizePanel = form.parentNode;
        if (sizePanel.getAttribute && sizePanel.getAttribute('data-pro-find-panel') === 'size' && sizePanel.parentNode) {
          sizePanel.parentNode.insertBefore(panel, sizePanel.nextSibling);
        } else {
          form.parentNode.insertBefore(panel, form);
        }

        function fill(el, options, label, selected, selectedLabel) {
          el.replaceChildren();
          el.appendChild(blank(label));
          var have = false;
          (options || []).forEach(function (o) {
            var option = create('option', null, String(o.label));
            option.value = String(o.value);
            if (selected && String(o.value) === String(selected)) have = true;
            el.appendChild(option);
          });
          if (selected && !have) {
            var extra = create('option', null, String(selectedLabel || selected));
            extra.value = String(selected);
            el.appendChild(extra);
          }
          el.value = selected ? String(selected) : '';
        }

        function renderFitments(state) {
          fitmentList.replaceChildren();
          (state.fitments || []).forEach(function (fitment, index) {
            var chip = button('tt-pro-chip', fitmentButtonText(fitment, labels));
            chip.setAttribute('aria-pressed', state.chosenIndex === index ? 'true' : 'false');
            chip.addEventListener('click', function () {
              controller.pickFitment(index, { source: 'tires' });
            });
            fitmentList.appendChild(chip);
          });
        }

        function carryLabels() {
          var results = document.querySelector(form.getAttribute('data-pro-results-target') || '');
          return {
            front: results && results.getAttribute('data-pro-staggered-use-front'),
            rear: results && results.getAttribute('data-pro-staggered-use-rear')
          };
        }
        function renderCarry(state) {
          var bar = document.querySelector('[data-pro-carry]');
          var lineEl = bar && bar.querySelector('[data-pro-carry-line]');
          if (!bar || !lineEl) return;
          var api = loadedVehicleApi();
          var line = api && api.sessionVehicleLine
            ? api.sessionVehicleLine(state && state.card, carryLabels())
            : '';
          if (!line && state && (state.year || (state.chosen && state.chosen.year))) {
            var named = state.card || state.chosen || {};
            var bits = [
              state.yearName || state.year || named.year,
              (state.card && state.card.makeName) || state.makeName || named.make,
              (state.card && state.card.modelName) || state.modelName || named.model
            ].filter(Boolean);
            var size = (state.chosen && state.chosen.tireSize) || named.tireSize || '';
            line = bits.join(' ');
            if (size) line = line ? line + ' \u00b7 ' + size : size;
          }
          lineEl.textContent = line;
          bar.hidden = !line;
        }
        var clearButton = document.querySelector('[data-pro-carry-clear]');
        if (clearButton && !clearButton._proCarryBound) {
          clearButton._proCarryBound = true;
          clearButton.addEventListener('click', function () {
            controller.clearVehicle();
          });
        }

        var controller = createVehicleController({
          onSearchSize: function (chosen) {
            if (afterFitmentPicked(form, chosen)) return;
            if (form._proSearch) form._proSearch.searchVehicle(chosen);
          },
          onChange: function (state, event) {
            renderFitments(state);
            renderCarry(state);
            if (event && event.type === 'clearVehicle') resetChain();
          }
        });
        useVehicleController(controller);
        renderCarry(controller.state);
        renderFitments(controller.state);

        // A lookup that has run, found or not, ends the "pick a size, a vehicle or a VIN"
        // hint under the form: it sits in the results area, which these lookups never fill.
        function lookupRan() {
          if (form._proSearch && form._proSearch.dismissFirstHint) form._proSearch.dismissFirstHint();
        }
        function failed() {
          lookupRan();
          fitmentList.replaceChildren();
          fitmentList.appendChild(create('p', 'tt-pro-find__hint', labels.none));
        }
        function listedOrFailed(state) {
          if (!state || !state.fitments || !state.fitments.length) failed();
          else lookupRan();
        }

        // A list opens once the one before it is chosen; Find fitments needs year, make and
        // model (trim stays optional). A changed list drops every choice after it.
        function selectedLabel(el) {
          var opt = el && el.options && el.selectedIndex >= 0 ? el.options[el.selectedIndex] : null;
          return opt && opt.value ? String(opt.textContent || '').trim() : '';
        }
        function chainValues() {
          return { year: yearSelect.value, make: makeSelect.value, model: modelSelect.value, trim: trimSelect.value };
        }
        function applyChain(sel) {
          var state = Find.vehicleChainState(sel);
          makeSelect.disabled = state.makeOff;
          modelSelect.disabled = state.modelOff;
          trimSelect.disabled = state.trimOff;
          findButton.disabled = state.findOff;
        }
        function chainChanged(changed) {
          var kept = Find.clearAfter(chainValues(), changed);
          yearSelect.value = kept.year;
          makeSelect.value = kept.make;
          modelSelect.value = kept.model;
          trimSelect.value = kept.trim;
          applyChain(kept);
          fitmentList.replaceChildren();
        }
        applyChain(chainValues());

        var restoreToken = 0;
        function resetChain() {
          restoreToken += 1;
          yearSelect.value = '';
          if (typeof yearSelect.selectedIndex === 'number') yearSelect.selectedIndex = 0;
          fill(makeSelect, [], labels.make);
          fill(modelSelect, [], labels.model);
          fill(trimSelect, [], labels.trim);
          if (vinInput) {
            vinInput.value = '';
            vinTyped();
          }
          applyChain({ year: '', make: '', model: '', trim: '' });
          fitmentList.replaceChildren();
          if (document.querySelectorAll) {
            var lists = document.querySelectorAll('[data-pro-vehicle-fitments]');
            for (var i = 0; i < lists.length; i++) {
              if (lists[i] !== fitmentList && lists[i].replaceChildren) lists[i].replaceChildren();
            }
          }
          var pkg = form._proPackage;
          if (pkg && typeof pkg.clearVehicleContext === 'function') pkg.clearVehicleContext();
          var search = form._proSearch;
          if (search) {
            // A search already in flight must not paint the old list after this.
            search.requestId = (search.requestId || 0) + 1;
            search._tireList = null;
            search._listKind = null;
            search.lastQuery = '';
            search.lastQuotes = null;
            search._lastVariantCall = null;
            search._quotedById = {};
            search._sizeVariants = null;
            search._filterSeason = false;
            search.quoteTire = null;
            search.quoteWheel = null;
            search.season = 'all';
            search.staggered = staggeredState();
            search.staggeredReplacing = false;
            if (search.input) search.input.value = '';
            [search.widthSelect, search.aspectSelect, search.diameterSelect].forEach(function (el) {
              if (el && typeof el.selectedIndex === 'number') el.selectedIndex = 0;
            });
            if (search.quantityInput) {
              search.quantityInput.value = String(readSearchQuantity(null, readLocalSettings() || settingsDefaults()));
              if (search.syncStepperBounds) search.syncStepperBounds();
            }
            if (typeof search._paintSeason === 'function') search._paintSeason();
            if (typeof search._renderStaggered === 'function') search._renderStaggered();
            if (typeof search._setStale === 'function') search._setStale(null);
            if (search.results && search.results.replaceChildren) search.results.replaceChildren();
            var count = document.querySelector && document.querySelector('[data-pro-results-count]');
            if (count) count.textContent = '';
          }
          var quoteRoots = document.querySelectorAll ? document.querySelectorAll('[data-pro-quote-screen]') : [];
          if (quoteRoots.length) {
            var quoteBody = quoteRoots[0].querySelector('[data-pro-quote-body]');
            var quoteBlock = quoteRoots[0].querySelector('[data-pro-quote-ineligible]');
            if (quoteBody) quoteBody.hidden = true;
            if (quoteBlock) quoteBlock.hidden = true;
            if (quoteRoots[0]._proQuoteScreen) {
              quoteRoots[0]._proQuoteScreen.quote = null;
              quoteRoots[0]._proQuoteScreen.quoteId = null;
            }
          }
        }

        function restoreKnownVehicle() {
          var token = ++restoreToken;
          var state = controller.state || {};
          var year = state.year;
          var make = state.make;
          var model = state.model;
          var trim = state.trim || '';
          if (!year || !make || !model) {
            return controller.loadOptions({}).then(function (options) {
              if (token !== restoreToken) return;
              fill(yearSelect, options, labels.year, yearSelect.value);
            }).catch(function () {});
          }
          var card = state.card || {};
          var names = {
            year: state.yearName || year,
            make: card.makeName || state.makeName || make,
            model: card.modelName || state.modelName || model,
            trim: state.trimName || trim
          };
          return controller.loadOptions({}).then(function (years) {
            if (token !== restoreToken) return;
            fill(yearSelect, years, labels.year, year, names.year);
            return controller.loadOptions({ year: year });
          }).then(function (makes) {
            if (token !== restoreToken || !makes) return;
            fill(makeSelect, makes, labels.make, make, names.make);
            return controller.loadOptions({ year: year, make: make });
          }).then(function (models) {
            if (token !== restoreToken || !models) return;
            fill(modelSelect, models, labels.model, model, names.model);
            return controller.loadOptions({ year: year, make: make, model: model });
          }).then(function (trims) {
            if (token !== restoreToken || !trims) return;
            fill(trimSelect, trims, labels.trim, trim, names.trim);
            applyChain(chainValues());
          }).catch(function () {});
        }
        restoreKnownVehicle();
        yearSelect.addEventListener('change', function () {
          fill(makeSelect, [], labels.make);
          fill(modelSelect, [], labels.model);
          fill(trimSelect, [], labels.trim);
          chainChanged('year');
          if (!yearSelect.value) return Promise.resolve();
          return controller.loadOptions({ year: yearSelect.value })
            .then(function (options) { fill(makeSelect, options, labels.make); })
            .catch(function () {});
        });
        makeSelect.addEventListener('change', function () {
          fill(modelSelect, [], labels.model);
          fill(trimSelect, [], labels.trim);
          chainChanged('make');
          if (!makeSelect.value) return Promise.resolve();
          return controller.loadOptions({ year: yearSelect.value, make: makeSelect.value })
            .then(function (options) { fill(modelSelect, options, labels.model); })
            .catch(function () {});
        });
        modelSelect.addEventListener('change', function () {
          fill(trimSelect, [], labels.trim);
          chainChanged('model');
          if (!modelSelect.value) return Promise.resolve();
          return controller.loadOptions({ year: yearSelect.value, make: makeSelect.value, model: modelSelect.value })
            .then(function (options) { fill(trimSelect, options, labels.trim); })
            .catch(function () {});
        });
        findButton.addEventListener('click', function () {
          if (!yearSelect.value || !makeSelect.value || !modelSelect.value) return Promise.resolve();
          return controller.findFitments({
            year: yearSelect.value,
            make: makeSelect.value,
            model: modelSelect.value,
            trim: trimSelect.value || undefined,
            yearName: selectedLabel(yearSelect),
            makeName: selectedLabel(makeSelect),
            modelName: selectedLabel(modelSelect),
            trimName: selectedLabel(trimSelect)
          }).then(listedOrFailed, failed);
        });

        // A VIN keeps only its valid characters as it is typed, so the counter and the
        // button always describe what would actually be decoded. A rewrite keeps the caret
        // where the user was typing, and waits out an IME composition (Android keyboards
        // break when the field is rewritten under them): compositionend does the rewrite.
        function vinTyped(event) {
          var raw = vinInput.value;
          var fixed = Find.sanitizeVinWithCaret
            ? Find.sanitizeVinWithCaret(raw, typeof vinInput.selectionStart === 'number' ? vinInput.selectionStart : null)
            : { value: Find.sanitizeVin(raw), caret: null };
          var vin = fixed.value;
          if (raw !== vin && !(event && event.isComposing)) {
            vinInput.value = vin;
            if (fixed.caret != null && typeof vinInput.setSelectionRange === 'function' && document.activeElement === vinInput) {
              vinInput.setSelectionRange(fixed.caret, fixed.caret);
            }
          }
          vinCounter.textContent = vin.length + ' / 17';
          decodeButton.disabled = !Find.vinReady(vin);
        }
        vinInput.addEventListener('input', vinTyped);
        vinInput.addEventListener('compositionend', function () { vinTyped(); });
        vinTyped();
        decodeButton.addEventListener('click', function () {
          var vin = Find.sanitizeVin(vinInput.value);
          if (!Find.vinReady(vin)) return Promise.resolve();
          return controller.decodeVin(vin).then(listedOrFailed, failed);
        });
        form._proVehiclePanel = panel;
      }

      // The Size / Vehicle / VIN switch: one lookup panel visible at a time. It only shows and
      // hides, so it never clears a value, starts a search or stores a choice - going back to a
      // mode finds what was typed there. The vehicle wrapper stays visible in Vehicle and VIN
      // mode (the fitment list both lookups share lives in it) and is hidden in Size mode.
      function initFindModes(root) {
        root = root || document;
        if (!root || typeof root.querySelector !== 'function' || typeof root.querySelectorAll !== 'function') return;
        var group = root.querySelector('[data-pro-find-switch]');
        if (!group) {
          // Older cached HTML has no switch, but this script still builds the Vehicle and VIN
          // panels hidden: show them rather than strand the lookups with no way back.
          Array.prototype.forEach.call(root.querySelectorAll('[data-pro-find-panel]'), function (panel) { panel.hidden = false; });
          return;
        }
        var Find = loadedFindApi();
        var buttons = group.querySelectorAll('[data-pro-find-mode]');
        var indicator = group.querySelector('.tt-pro-seg__ind');
        var panels = root.querySelectorAll('[data-pro-find-panel]');
        var wrapper = root.querySelector('[data-pro-vehicle-panel]');
        var staggeredNote = group.getAttribute('data-pro-find-staggered-note') || '';
        var ENTER = 'tt-pro-find__panel--enter';

        // Each button names the panel it shows. The Vehicle and VIN panels are built by script,
        // so the ids are given here, unless the markup already gave one.
        var panelOf = {};
        Array.prototype.forEach.call(panels, function (panel) {
          var mode = panel.getAttribute('data-pro-find-panel');
          panel.id = panel.id || 'tt-pro-find-panel-' + mode;
          panelOf[mode] = panel;
        });
        Array.prototype.forEach.call(buttons, function (button) {
          var panel = panelOf[button.getAttribute('data-pro-find-mode')];
          if (panel && !button.getAttribute('aria-controls')) button.setAttribute('aria-controls', panel.id);
        });

        // The reveal (fade + 8px rise) is a class a click adds, never a rule on :not([hidden]),
        // so first paint and a return from the Wheels tab show the panel at once. It comes
        // off when the panel's own animation ends or is cancelled (hidden again mid-way).
        Array.prototype.forEach.call(panels, function (panel) {
          if (!panel.classList || typeof panel.addEventListener !== 'function') return;
          function done(event) {
            if (!event || !event.target || event.target === panel) panel.classList.remove(ENTER);
          }
          panel.addEventListener('animationend', done);
          panel.addEventListener('animationcancel', done);
        });

        function reveal(panel) {
          if (!panel.classList) return;
          panel.classList.remove(ENTER);
          void panel.offsetWidth; // a class still on from an earlier switch restarts the animation
          panel.classList.add(ENTER);
        }

        // Front / rear sizes are typed into the Size panel's field, which is hidden in the
        // other modes, so the Refine bar's toggle is disabled there unless a front / rear
        // session is on screen (the strip is visible). The reason shows as the visible note
        // under the Refine label, which the toggle points at while it is off, and as its
        // title. Looked up on every call because ProSearch builds the toggle and the strip.
        // The gate is re-applied whenever the strip is shown or hidden, so a session that
        // ends while Vehicle or VIN is showing puts it back; Size always gives the toggle back.
        var sizeShown = true;
        var watchedStrip = null;
        var stripObserver = null;

        function watchStrip(strip) {
          if (!strip || strip === watchedStrip || typeof MutationObserver !== 'function') return;
          if (stripObserver) stripObserver.disconnect();
          watchedStrip = strip;
          stripObserver = new MutationObserver(function () { gateStaggered(); });
          stripObserver.observe(strip, { attributes: true, attributeFilter: ['hidden'] });
        }

        function gateStaggered() {
          var toggle = root.querySelector('[data-pro-staggered-toggle]');
          var note = root.querySelector('[data-pro-staggered-note]');
          var strip = root.querySelector('[data-pro-staggered-strip]');
          watchStrip(strip);
          var blocked = !!toggle && !sizeShown && !(strip && !strip.hidden);
          if (note) note.hidden = !blocked;
          if (!toggle) return;
          // The toggle says whether its strip is open, in step with the strip's own hidden.
          if (strip) {
            strip.id = strip.id || 'tt-pro-staggered-strip';
            toggle.setAttribute('aria-controls', strip.id);
            var open = strip.hidden ? 'false' : 'true';
            toggle.setAttribute('aria-expanded', open);
            toggle.setAttribute('aria-checked', open);
          }
          toggle.disabled = blocked;
          toggle.title = blocked ? staggeredNote : '';
          if (blocked && note) {
            note.id = note.id || 'tt-pro-staggered-note';
            toggle.setAttribute('aria-describedby', note.id);
          } else {
            toggle.removeAttribute('aria-describedby');
          }
        }

        var door = null;
        var ready = false;
        var splitSize = false;

        function setHidden(list, hidden) {
          for (var i = 0; i < list.length; i++) list[i].hidden = hidden;
        }

        function paint(animate) {
          var Counter = loadedCounterApi();
          var view = Counter ? Counter.counterView({ door: door, ready: ready, splitSize: splitSize }) : null;
          var hidden = view ? view.panels : (door ? Find.findPanelHidden(door) : { size: true, vehicle: true, vin: true });
          var at = -1;
          for (var i = 0; i < buttons.length; i++) {
            var modeName = buttons[i].getAttribute('data-pro-find-mode') || buttons[i].getAttribute('data-pro-door');
            var shown = hidden[modeName] === false;
            buttons[i].setAttribute('aria-pressed', shown ? 'true' : 'false');
            if (shown) at = i;
          }
          if (indicator) {
            indicator.hidden = at < 0;
            if (at >= 0 && indicator.style) indicator.style.transform = 'translateX(' + (at * 100) + '%)';
          }
          for (var j = 0; j < panels.length; j++) {
            var wasHidden = panels[j].hidden;
            var nowHidden = hidden[panels[j].getAttribute('data-pro-find-panel')] !== false;
            panels[j].hidden = nowHidden;
            if (animate && wasHidden && !nowHidden) reveal(panels[j]);
          }
          if (wrapper) wrapper.hidden = !(hidden.vehicle === false || hidden.vin === false);
          sizeShown = hidden.size === false;
          gateStaggered();
          var showCascade = view ? !!view.showCascade : false;
          var showCatalog = view ? !!view.showCatalog : false;
          setHidden(root.querySelectorAll('[data-pro-cascade]'), !showCascade);
          setHidden(root.querySelectorAll('.tt-pro-cascade'), !showCascade);
          setHidden(root.querySelectorAll('[data-pro-search]'), showCascade);
          setHidden(root.querySelectorAll('[data-pro-mode-tabs]'), !showCatalog);
          setHidden(root.querySelectorAll('[data-pro-results-bar]'), !showCatalog);
          var splitBtn = root.querySelector('[data-pro-size-split]');
          if (splitBtn) splitBtn.setAttribute('aria-pressed', splitSize ? 'true' : 'false');
          if (group && group.setAttribute) group.setAttribute('data-pro-open-door', door || '');
        }

        function show(mode, animate) {
          Find.findPanelHidden(mode);
          door = mode === 'vehicle' || mode === 'vin' || mode === 'size' ? mode : 'size';
          paint(animate);
          // Size, Vehicle and VIN live in the Tires panel. A click while Wheels
          // is open has to leave that tab, or the door lights up and nothing moves.
          var form = root.querySelector('[data-pro-search]');
          var pkg = form && form._proPackage;
          if (pkg && pkg.mode === 'wheels' && typeof pkg.chooseMode === 'function') pkg.chooseMode('tires');
        }

        Array.prototype.forEach.call(buttons, function (button) {
          button.addEventListener('click', function () {
            show(button.getAttribute('data-pro-find-mode') || button.getAttribute('data-pro-door'), true);
          });
        });

        var splitControl = root.querySelector('[data-pro-size-split]');
        if (splitControl) {
          splitControl.addEventListener('click', function () {
            splitSize = !splitSize;
            paint(false);
          });
        }

        var searchForm = root.querySelector('[data-pro-search]');
        if (searchForm) {
          searchForm.addEventListener('submit', function () {
            var field = searchForm.querySelector('[data-pro-search-input]');
            if (field && !String(field.value || '').replace(/^\s+|\s+$/g, '')) return;
            door = 'size';
            ready = true;
            paint(false);
          });
        }

        var fitmentList = root.querySelector('[data-pro-vehicle-fitments]');
        if (fitmentList) {
          fitmentList.addEventListener('click', function (event) {
            var target = event && event.target;
            if (!target || target === fitmentList) return;
            var tag = target.tagName ? String(target.tagName).toLowerCase() : '';
            if (tag !== 'button') return;
            if (door !== 'vehicle' && door !== 'vin') door = 'vehicle';
            ready = true;
            paint(false);
          });
        }

        paint(false);
      }

      function initAll() {
        applyThemeColor();
        var forms = document.querySelectorAll('[data-pro-search]');
        for (var i = 0; i < forms.length; i++) {
          if (!forms[i]._proSearch) forms[i]._proSearch = new ProSearch(forms[i]);
        }
        if (forms.length) initVehiclePanel(forms[0]);
        initFindModes();
        initInstallPrompt();
        initIOSGuide();
        initServiceWorker();
        initOfflineBanner();
        initSettingsLoginPrompt();
        initSettingsPanel();
        initQuoteHistoryPanel();
        initChangelog();
        initQuoteScreen();
        // Design round 4 — the header shows the dealer, not Toee.
        applyShopIdentity(readLocalSettings() || settingsDefaults());
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
      } else {
        initAll();
      }
      initVehiclePanelRef = initVehiclePanel;
      initFindModesRef = initFindModes;
      initInstallPromptRef = initInstallPrompt;
      initIOSGuideRef = initIOSGuide;
    })();
  }

  return {
    buildStockView: buildStockView,
    buildPromiseView: buildPromiseView,
    buildPriceView: buildPriceView,
    buildResultViewModel: buildResultViewModel,
    quoteShortfall: quoteShortfall,
    provenStock: provenStock,
    shortfallText: shortfallText,
    buildFitmentLine: buildFitmentLine,
    markCardQuoted: markCardQuoted,
    readSearchQuantity: readSearchQuantity,
    handOffFocus: handOffFocus,
    formatTime12h: formatTime12h,
    formatBusinessHours: formatBusinessHours,
    readBusinessHours: readBusinessHours,
    populateBusinessHours: populateBusinessHours,
    syncHoursGroupVisibility: syncHoursGroupVisibility,
    markupSuffixVisible: markupSuffixVisible,
    syncMarkupPair: syncMarkupPair,
    amountPrefixVisible: amountPrefixVisible,
    syncInstallFeePair: syncInstallFeePair,
    // Rollout Task 10 — the Settings drawer.
    SETTINGS_TEXT: SETTINGS_TEXT,
    settingsText: settingsText,
    settingsFieldError: settingsFieldError,
    markupExampleText: markupExampleText,
    syncMarkupExtras: syncMarkupExtras,
    syncSettingsSeg: syncSettingsSeg,
    shopIdentity: shopIdentity,
    applyShopIdentity: applyShopIdentity,
    applyThemeColor: applyThemeColor,
    buildSellingPrice: buildSellingPrice,
    sellableQuotes: sellableQuotes,
    extractVariantIds: extractVariantIds,
    proVariantsUrl: proVariantsUrl,
    extractProVariantIds: extractProVariantIds,
    chooseSeason: chooseSeason,
    seasonVariantIds: seasonVariantIds,
    winterSeasonLabels: winterSeasonLabels,
    rememberSizeVariants: rememberSizeVariants,
    applySearchSeason: applySearchSeason,
    quoteListShouldReplace: quoteListShouldReplace,
    quotesForSeason: quotesForSeason,
    SIZE_QUOTE_LIMIT: SIZE_QUOTE_LIMIT,
    buildQuoteBatchBody: buildQuoteBatchBody,
    staggeredState: staggeredState,
    staggeredAfterPick: staggeredAfterPick,
    staggeredExit: staggeredExit,
    staggeredControlLabels: staggeredControlLabels,
    staggeredOpenQuantity: staggeredOpenQuantity,
    staggeredQuoteBody: staggeredQuoteBody,
    predictiveSearchUrl: predictiveSearchUrl,
    proProxyBase: proProxyBase,
    proProxyUrl: proProxyUrl,
    proPlacesApiKey: proPlacesApiKey,
    loadPlacesLibrary: loadPlacesLibrary,
    attachAddressAutocomplete: attachAddressAutocomplete,
    // DP-3 — cascading dropdowns + no-results fallback (pure logic).
    suffixOfHandle: suffixOfHandle,
    buildCascadeSizeString: buildCascadeSizeString,
    parseSizeQuery: parseSizeQuery,
    nearestOption: nearestOption,
    buildNoResultsView: buildNoResultsView,
    // DP-10 — PWA install platform detection + offline banner formatting.
    isIOS: isIOS,
    isIOSSafari: isIOSSafari,
    // Rollout Task 11 — the 14-day "Not now" and the two install surfaces' wiring.
    INSTALL_DISMISS_KEY: INSTALL_DISMISS_KEY,
    INSTALL_DISMISS_MS: INSTALL_DISMISS_MS,
    installDismissedRecently: installDismissedRecently,
    rememberInstallDismissal: rememberInstallDismissal,
    initInstallPrompt: initInstallPromptRef,
    initIOSGuide: initIOSGuideRef,
    formatOfflineLabel: formatOfflineLabel,
    // Only defined once `document` exists at require time (see
    // tests/toee-pro-fake-dom.js) — used by the renderer-level tests.
    fmtHMS: fmtHMS,
    buildPromiseRow: buildPromiseRow,
    buildResultCard: buildResultCard,
    startCountdowns: startCountdowns,
    buildNoResultsFallback: buildNoResultsFallback,
    // Fix-round finding #4 — exported so real behavior (not just the pure
    // helpers they call) can be exercised directly under node --test.
    ProSearch: ProSearch,
    resolveNearestAlternative: resolveNearestAlternative,
    // Final review finding #4 — the single, tested offline-load-time
    // renderer (converged with ProSearch#_render).
    loadLastOfflineResult: loadLastOfflineResult,
    // DP-5 — dealer settings (pure logic).
    settingsDefaults: settingsDefaults,
    mergeSettingsSection: mergeSettingsSection,
    resolveSettingsOnLoad: resolveSettingsOnLoad,
    unwrapSettingsPayload: unwrapSettingsPayload,
    needsFirstRun: needsFirstRun,
    readLocalSettings: readLocalSettings,
    writeLocalSettings: writeLocalSettings,
    pushSettings: pushSettings,
    saveSettingsSection: saveSettingsSection,
    // DP-5 — Form States (DESIGN.md first consumer).
    applyFieldError: applyFieldError,
    clearFieldError: clearFieldError,
    applyFieldSuccess: applyFieldSuccess,
    setButtonLoading: setButtonLoading,
    // The shared drawer behaviour (Recent quotes, Settings).
    ProDrawer: ProDrawer,
    // Only defined once `document` exists at require time.
    ProSettingsPanel: ProSettingsPanel,
    // DP-7 — quote card (pure logic).
    CARD_LABELS: CARD_LABELS,
    SHARE_TEXT_LABELS: SHARE_TEXT_LABELS,
    // Release 6 — the one money writer ("$1,609.12" / "1 609,12 $").
    formatMoney: formatMoney,
    buildQuoteLedger: buildQuoteLedger,
    buildPackageLedger: buildPackageLedger,
    isQuoteCardEligible: isQuoteCardEligible,
    detectProvinceCode: detectProvinceCode,
    computeQuoteValidityLabel: computeQuoteValidityLabel,
    buildQuoteCardViewModel: buildQuoteCardViewModel,
    buildPackageQuoteCardViewModel: buildPackageQuoteCardViewModel,
    readOwnerDetails: readOwnerDetails,
    writeOwnerDetails: writeOwnerDetails,
    readOwnerHistorySnapshot: readOwnerHistorySnapshot,
    recordOwnerHistorySnapshot: recordOwnerHistorySnapshot,
    ownerHistoryLabel: ownerHistoryLabel,
    ownerHistorySearchText: ownerHistorySearchText,
    // Only defined once `document` exists at require time.
    drawQuoteCard: drawQuoteCard,
    drawHistoricalQuoteCard: drawHistoricalQuoteCard,
    ProQuoteScreen: ProQuoteScreen,
    ProQuoteHistoryPanel: ProQuoteHistoryPanel,
    // DP-8 — four quote-card colour themes + settings-screen live preview.
    cardThemeAccentVarName: cardThemeAccentVarName,
    resolveCardThemeAccent: resolveCardThemeAccent,
    buildCardPreviewViewModel: buildCardPreviewViewModel,
    formatHistoricalQuoteDate: formatHistoricalQuoteDate,
    buildHistoricalQuoteCardViewModel: buildHistoricalQuoteCardViewModel,
    historyTitle: historyTitle,
    // Recent quotes drawer (pure parts).
    QUOTE_HISTORY_TEXT: QUOTE_HISTORY_TEXT,
    quoteHistoryText: quoteHistoryText,
    historyDayLabel: historyDayLabel,
    historyTimeLabel: historyTimeLabel,
    historyOrderState: historyOrderState,
    // DP-9 — share the quote card (pure logic).
    canShareFiles: canShareFiles,
    buildShareText: buildShareText,
    shareQuoteCard: shareQuoteCard,
    buildDealerProEventBody: buildDealerProEventBody,
    postDealerProEvent: postDealerProEvent,
    // Vehicle on the quote screen.
    afterFitmentPicked: afterFitmentPicked,
    initVehiclePanel: initVehiclePanelRef,
    initFindModes: initFindModesRef,
    createVehicleController: createVehicleController,
    activeVehicleController: activeVehicleController,
    useVehicleController: useVehicleController,
    vehicleChoiceBody: vehicleChoiceBody,
    searchKindForQuery: searchKindForQuery,
    shareSavedMessageKey: shareSavedMessageKey,
    shouldScrollToGeneratedQuote: shouldScrollToGeneratedQuote,
    // Place one COD order from a quote.
    placeOrderView: placeOrderView,
    placeOrderText: placeOrderText,
    buildPlaceOrderParts: buildPlaceOrderParts,
    createPlaceOrderActions: createPlaceOrderActions,
    loadPickupWarehouses: loadPickupWarehouses,
    resetPickupWarehouses: resetPickupWarehouses
  };
});
